// Auto-generated by splitter.py — do not regenerate; edit directly.

import { MOCK } from '../data/mock';
import { authClient } from './authClient';
import { withAbsoluteInstants, toLocalDay } from './absoluteInstant';
import { toast } from './buses';
import { getBackendUrl, derivedHostUrl, getConsumerUrl, getWhatsAppMediaProxyUrl, getRendererUrl } from './backendUrl';
import { resolveDatePreset } from './dateRange';
import { venueTypeLabel, venueTypeIsIndoor, venueModelTypeLabel } from '../constants/venues';
import { MOCK_ORDERS } from '../data/orders';
import { MOCK_CUSTOMERS } from '../data/customers';
import { MOCK_ORG } from '../data/org';
import { PAYOUT_QUEUE } from '../data/payouts';
import { MOCK_VENUES_RICH } from '../data/venues';
import { MOCK_TOURNAMENTS_RICH, MOCK_TEAMS_RICH } from '../data/tournaments';
import { MOCK_SALES_CHANNELS, MOCK_BOX_OFFICES } from '../data/sales';
import { MOCK_AC_INTEGRATIONS_V2 } from '../data/accesscontrol';
import { ADDONS_SEED } from '../data/addons';
import { DESIGN_TEMPLATES, CAMPAIGNS_SEED } from '../data/comms';
import { MOCK_SUPPORT_TICKETS } from '../data/support';
import { SUBSCRIBERS_SEED, WAITLIST_SEED } from '../data/subscriptions';
import { REPORTS_SEED } from '../data/reports';
import { normalizeTicketChannelAccess, normalizeTicketSalesChannels } from './salesChannels';
// An amount and its currency are ONE value. That rule already has one implementation; the
// needs-attention queue below states money, and hand-rolling `toLocaleString('en-UG')` there is
// how a KES figure ends up reading as shillings. (format.ts imports nothing, so this cannot cycle.)
import { fmtMoney } from './format';
import { EXCEPTION_KINDS } from '../constants/finance';
import { BRAND } from './brand';

// Backend URL resolution lives in lib/backendUrl — ONE resolver shared with authClient,
// so the REST calls and the better-auth SDK calls can never point at different hosts
// (they did; see backendUrl.js).
export const API_BASE = getBackendUrl().replace(/\/$/, '');

// A WRITE JUST LANDED. Every successful non-GET request to the backend is announced once, here at
// the one place all of them pass, so a view showing what changed (a history, a count) re-reads
// instead of going stale. The server records an act for each such write (tkt-backend
// utils/auditContext), queued and written within ~250ms of the response — listeners wait past it.
export const WRITE_EVENT = 'tkt:write';
if (typeof window !== 'undefined' && !window.__tktWriteSignal) {
  window.__tktWriteSignal = true;
  const baseFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await baseFetch(input, init);
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    const url = typeof input === 'string' ? input : input?.url || '';
    if (res.ok && method !== 'GET' && method !== 'HEAD' && API_BASE && url.startsWith(API_BASE)) {
      window.dispatchEvent(new CustomEvent(WRITE_EVENT, { detail: { method, url } }));
    }
    return res;
  };
}

// Carry the super-admin CROSS-ORG signal as a QUERY PARAM, never a custom request header.
// A custom header (the old `x-org-scope: all`) makes every cross-origin GET a *preflighted*
// request — the browser fires an OPTIONS round-trip first, on a console that is almost all
// reads. `?orgScope=all` keeps it a CORS "simple request" (no preflight). The backend
// (resolveOrgScope) reads this param and honours it ONLY for a verified platform admin —
// a regular member setting it is ignored — so it's exactly as safe as the header was.
const withOrgScope = (path) => `${path}${path.includes('?') ? '&' : '?'}orgScope=all`;

// THE READER'S CLOCK — a report's buckets are cut in the zone of whoever is looking (tkt-backend
// utils/readerClock), matching the windows this console sends as local midnights. A query param,
// not a header: GETs stay CORS-simple, and the edge cache keys on the URL, so two zones never share
// a cached answer.
const READER_TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();
const withReaderClock = (path) => (path.startsWith('/api/reports') ? `${path}${path.includes('?') ? '&' : '?'}tz=${encodeURIComponent(READER_TZ)}` : path);

// The canonical ticket-PDF ("pass") service — a SEPARATE host from the API. It renders the exact
// branded, QR-signed, per-ticket PDF the buyer receives (verified: apis.tkt.ug/passes/:orderUuid →
// application/pdf). This is ONE shared renderer at apis.tkt.ug for EVERY tenant — NOT derived
// per-domain: there is no apis.moments.ug / apis.fanpass.ke renderer, and the backend's own
// passUrl.ts hardcodes `RENDERER = "https://apis.tkt.ug"` for all tenants. Deriving apis.<domain>
// from the console host (backoffice.moments.ug → apis.moments.ug) pointed downloads at a host that
// doesn't exist, so tickets wouldn't download off any non-tkt.ug console. Always apis.tkt.ug. It
// keys on the ORDER UUID (order.id / normalized `backendId`) — the human order reference (LN48…)
// 401s. Anchor-navigate to this URL (no CORS headers on the service, so fetch()+blob is blocked; a
// browser navigation downloads via the service's Content-Disposition). Returns '' when there's no
// usable order UUID so callers can gate.
/**
 * The pass renderer's URL for an order — taken FROM THE ORDER, built by the backend.
 *
 * The real shape is `/passes/{host}/{orderId}`, where {host} is the backend that OWNS the
 * order. This used to build `/passes/{orderId}` — no host at all — so the renderer read the
 * uuid as the host and never saw an order id. Every "Download tickets" link was broken.
 *
 * The host cannot be derived here. It comes from the deployment's tenant (tkt → backend2.tkt.ug,
 * mookh/party → backend.party.ug, fanpass → backend.fanpass.ke, moments → backend.moments.ug,
 * tix → backend.tix.ug), and it is NOT the API base this console talks to: production points at
 * backend.tkt.ug while the pass host is backend2.tkt.ug. Only the backend knows, so the backend
 * says — `passUrl` now comes back on the order (utils/passUrl.ts, the same builder the WhatsApp
 * "Download Tickets" link and every ticket email use). No extra request; it rides the order.
 *
 * Pass the ORDER (or its raw payload). Returns '' when the backend gave no passUrl, so callers
 * still gate the button instead of rendering a dead link.
 */
export const orderPassPdfUrl = (order) => {
  const url = typeof order === 'string' ? '' : (order?.passUrl || order?.raw?.passUrl || '');
  if (!url) return '';
  // Cache-bust so a re-download after an edit (e.g. holder-name change) gets the regenerated pass,
  // not the service's 4h-cached copy.
  return `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
};
const PAGE_LIMIT = 80; // "fetch-all" cap for small lazy domain lists (channels, designs, box offices…) that have no load-more UI
// EVERY LIST A PERSON SEES LOADS THIS MANY ROWS, and "Load more" fetches the next as many. One number for the whole
// console — a list that picks its own (30, 50, 100…) is how a page ends up loading what nobody is looking at.
export const LIST_PAGE_SIZE = 25;
const DEV_AUTH_EMAIL = import.meta.env.VITE_TKT_DEV_AUTH_EMAIL || 'michaeltendossemwanga4543@gmail.com';
const DEV_AUTH_OTP = import.meta.env.VITE_TKT_DEV_AUTH_OTP || '123456';
const DEV_AUTH_ENABLED = import.meta.env.DEV
  && import.meta.env.VITE_TKT_DISABLE_DEV_AUTH !== 'true'
  && (/^https?:\/\/(localhost|127\.0\.0\.1|backend\.loft\.ug)(:\d+)?$/i.test(API_BASE) || API_BASE.startsWith('/'));
let devAuthPromise = null;

export const ENDPOINTS = {
  eventPages: `/api/event-pages?limit=${LIST_PAGE_SIZE}&includeTotal=false`,
  eventPagesFallback: `/api/event-pages/all?limit=${PAGE_LIMIT}`,
  eventStatsOrg: '/api/events/stats/cached',
  eventStatsAdmin: '/api/events/admin-stats/cached?allEvents=true',
  orders: `/api/orders?limit=${PAGE_LIMIT}`,
  // includeStats/includeTotal default ON server-side and are catastrophic for a
  // super-admin (full-table JSONB aggregation + count across ALL orders → ~17s).
  // The back-office derives its own KPIs client-side, so we never need them.
  ordersAll: `/api/orders/all?limit=${PAGE_LIMIT}&includeStats=false&includeTotal=false`,
  venues: '/api/venues',
  organizations: `/api/organizations?limit=${PAGE_LIMIT}`,
  organizationProfiles: `/api/organization-profiles?limit=${PAGE_LIMIT}`,
  eventRequests: `/api/event-chat-requests?limit=${PAGE_LIMIT}`,
  tournaments: `/api/sports/tournaments?limit=${PAGE_LIMIT}&includeSeasons=true`,
  teams: `/api/sports/teams?limit=${PAGE_LIMIT}`,
  payouts: `/api/payouts/admin?limit=${PAGE_LIMIT}&includeTotal=false`,
  paymentAccounts: `/api/payment-accounts?limit=${PAGE_LIMIT}`,
  payoutFinanceStats: '/api/payouts/finance-stats/cached', // glance metric (collected gross/vitals) → Redis-cached twin; window-keyed, big-horizon so staleness is fine
  payoutSettlements: '/api/payouts/settlements?limit=25', // each settlement row aggregates that event's orders — keep the page small (80 was ~5s, 25 is ~175ms)
  auditLogs: '/api/audit-logs?limit=12&includeTotal=false',
  health: '/health',
  notifications: `/api/notifications?limit=${PAGE_LIMIT}`,
  wallets: `/api/wallets`,
  channels: `/api/distribution-channels?limit=${PAGE_LIMIT}&includeTotal=false`,
  boxOffices: `/api/boxOffices?limit=${PAGE_LIMIT}&includeTotal=false`,
  accessControl: `/api/access-control-integrations?limit=${PAGE_LIMIT}&includeTotal=false`,
  designs: `/api/designs?limit=${PAGE_LIMIT}&includeTotal=false`,
  messageTemplates: `/api/message-templates?limit=${PAGE_LIMIT}&includeTotal=false`,
  campaigns: `/api/campaigns?limit=${PAGE_LIMIT}&includeTotal=false`,
  subscribers: `/api/subscriptions?limit=${PAGE_LIMIT}&includeTotal=false`,
};

// `venueId` is a real, server-honored filter on this route (eventPageRoutes builds
// it straight into the where clause) — not a client-side post-filter. It's an axis
// on the ONE events engine, so any surface that asks "which events use X" gets the
// same normalized rows + real stats as the Events page itself.
const eventPagesPath = ({ page = 1, limit = LIST_PAGE_SIZE, venueId } = {}) =>
  `/api/event-pages?limit=${limit}&page=${page}&includeTotal=false${venueId ? `&venueId=${encodeURIComponent(venueId)}` : ''}`;

// Core domains hydrated on app startup — only what the dashboard, command
// palette, global event pickers and KPI derivation need before the user
// navigates. Everything else is loaded lazily by its own page on navigation
// (see loadDomain / the per-page fetch hooks), which keeps the startup
// waterfall small instead of fetching the whole platform at once.
// Only domains that feed GLOBAL chrome (org switcher, notifications bell) load
// at startup. eventPages / eventRequests / orders / payouts are each fetched by
// their own page on mount (EventsPage, EventRequestsPage, OrdersPage,
// PayoutsPageV2) — eager-loading them here only duplicated those page loads
// (and the dashboard prefetch) and slowed every route's first paint.
const STARTUP_ENDPOINTS = {
  organizations: ENDPOINTS.organizationProfiles,
  notifications: ENDPOINTS.notifications,
};

// Lazily-hydrated domains — fetched on demand by loadDomain() when their page
// mounts, then cached for the session. Keys map to the same normalizePayload
// consumers as the startup set.
const LAZY_DOMAIN_ENDPOINTS = {
  venues: ENDPOINTS.venues,
  tournaments: ENDPOINTS.tournaments,
  teams: ENDPOINTS.teams,
  boxOffices: ENDPOINTS.boxOffices,
  accessControl: ENDPOINTS.accessControl,
  messageTemplates: ENDPOINTS.messageTemplates,
  campaigns: ENDPOINTS.campaigns,
  subscribers: ENDPOINTS.subscribers,
};

// v1 venue endpoints (NOT v2 — per product directive: tkt-frontend uses /api/venues).
// Note: v1 has NO /events, /seat-plans, or /audit-logs children (those were v2-only);
// seat-plans come from /api/seating-templates, gates from /api/entrances/venue/:id.
export const venueEndpoint = (venueId, child = '') => `/api/venues/${encodeURIComponent(venueId)}${child}`;
export const payoutEndpoint = (path = '') => `/api/payouts${path}`;
export const eventPageEndpoint = (path = '') => `/api/event-pages${path}`;

const unwrap = (payload) => {
  if (!payload) return payload;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.orders)) return payload.orders;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.logs)) return payload.logs;
  if (Array.isArray(payload.organizations)) return payload.organizations;
  if (Array.isArray(payload.payouts)) return payload.payouts;
  if (Array.isArray(payload.teams)) return payload.teams;
  if (payload.data && typeof payload.data === 'object') return unwrap(payload.data);
  return payload;
};

const list = (payload) => {
  const value = unwrap(payload);
  return Array.isArray(value) ? value : [];
};

/**
 * EVERY ROW OF A SMALL REFERENCE DOMAIN, not the first page of one.
 *
 * `PAGE_LIMIT` calls itself a "fetch-all cap", and for years it was one: 80 was comfortably more
 * than the number of teams, tournaments or seasons that existed. That is not a property of the
 * code, it is a property of how much data there happened to be — and the day the data passes the
 * cap, the list silently shows a slice with nothing to say it did. That is exactly how a team
 * became unlinkable from a season: a window that had never been reached, until it was.
 *
 * These endpoints already answer `hasMore`. Following it costs one extra request only when there
 * IS more, and returns the same plain array every caller already expects — so nothing downstream
 * changes shape, and the truncation simply stops existing.
 *
 * `maxPages` is a real ceiling, not decoration: a backend that always answers `hasMore: true`
 * (a bug, or an endpoint that ignores `page`) must not spin here. Hitting it is logged, because a
 * silent stop is the failure this function exists to remove.
 */
const fetchAllPages = async (buildPath, { signal, maxPages = 25 } = {}) => {
  const rows = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const r = await fetchBackOfficeJson(buildPath(page), { signal });
    if (!r.ok) {
      // Page 1 failing is a real error the caller must see; a later page failing means we have
      // most of the set, and returning it beats throwing away what already arrived.
      if (page === 1) return { rows, response: r, ok: false };
      console.warn(`[backOfficeApi] page ${page} of ${buildPath(page)} failed (${r.status}); returning ${rows.length} rows`);
      return { rows, response: r, ok: true, truncated: true };
    }
    const batch = list(r.payload);
    rows.push(...batch);
    if (!r.payload?.hasMore || batch.length === 0) return { rows, response: r, ok: true };
    if (page === maxPages) {
      console.warn(`[backOfficeApi] stopped at ${maxPages} pages for ${buildPath(page)} with hasMore still true — ${rows.length} rows so far`);
      return { rows, response: r, ok: true, truncated: true };
    }
  }
  return { rows, ok: true };
};

export const summarizePayload = (payload) => {
  const rows = list(payload);
  if (rows.length) {
    return { kind: 'list', count: rows.length, sample: rows.slice(0, 3) };
  }
  if (payload && typeof payload === 'object') {
    const keys = Object.keys(payload);
    return { kind: 'object', count: keys.length, keys: keys.slice(0, 24), sample: payload };
  }
  return { kind: payload == null ? 'empty' : typeof payload, count: payload == null ? 0 : 1, sample: payload };
};

// THE REASON an operator gives for an act travels with the request that performs it; the server
// records it on that act (tkt-backend middleware/auditActor). Empty means none was given.
export const reasonHeader = (reason) => (reason && String(reason).trim() ? { 'x-reason': encodeURIComponent(String(reason).trim()) } : {});

// Auth endpoints (/api/auth/*) never consult org scope, so they carry no x-org-scope — which
// also keeps the GET ones (e.g. get-session) as CORS simple requests (no preflight). The
// POST ones (sign-in) preflight anyway on their JSON Content-Type; nothing we can shed there.
const authFetch = (path, init = {}) => fetch(`${API_BASE}${path}`, {
  credentials: 'include',
  ...init,
  headers: {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
  },
});

async function ensureDevAuthSession({ signal } = {}) {
  if (!DEV_AUTH_ENABLED) return null;
  if (!devAuthPromise) {
    devAuthPromise = (async () => {
      const sessionResponse = await authFetch('/api/auth/get-session', { signal });
      if (sessionResponse.ok) {
        const sessionPayload = await sessionResponse.json();
        // Already authenticated — reuse it. Gate on the USER, not on
        // activeOrganizationId: a platform super-admin must stay org-UNSCOPED
        // (backend resolveOrgScope → null = see ALL orgs). Pinning an active org
        // silently scoped every directly-activeOrg endpoint to one org, and
        // gating this early-return on activeOrganizationId re-ran sign-in (real
        // OTP email) on every reload of an already-valid session.
        if (sessionPayload?.user) return sessionPayload.session || sessionPayload;
      }

      let signInResponse = await authFetch('/api/auth/sign-in/email-otp', {
        method: 'POST',
        signal,
        body: JSON.stringify({ email: DEV_AUTH_EMAIL, otp: DEV_AUTH_OTP }),
      });
      if (!signInResponse.ok) {
        const otpResponse = await authFetch('/api/auth/email-otp/send-verification-otp', {
          method: 'POST',
          signal,
          body: JSON.stringify({ type: 'sign-in', email: DEV_AUTH_EMAIL }),
        });
        if (!otpResponse.ok) throw new Error(`Dev auth OTP failed: ${otpResponse.status}`);
        signInResponse = await authFetch('/api/auth/sign-in/email-otp', {
          method: 'POST',
          signal,
          body: JSON.stringify({ email: DEV_AUTH_EMAIL, otp: DEV_AUTH_OTP }),
        });
      }
      if (!signInResponse.ok) throw new Error(`Dev auth sign-in failed: ${signInResponse.status}`);

      // Do NOT set an active organization — the super-admin is platform-wide
      // (resolveOrgScope returns null = see ALL orgs for SUPER_ADMIN_EMAILS).
      // Return the freshly-authenticated session as-is, org-unscoped.
      const after = await authFetch('/api/auth/get-session', { signal });
      const afterPayload = after.ok ? await after.json().catch(() => null) : null;
      return afterPayload?.session || afterPayload || true;
    })().catch((error) => {
      devAuthPromise = null;
      throw error;
    });
  }
  return devAuthPromise;
}

// --- Real auth (better-auth, same backend as tkt-frontend) -----------------
// tkt-frontend signs in with authClient.signIn.email → POST /api/auth/sign-in/email.
// We hit the endpoints directly (no better-auth client dep) with cookies.
export const DEV_AUTH_AVAILABLE = DEV_AUTH_ENABLED;

/**
 * "I COULDN'T ASK" IS NOT "NO".
 *
 * This returned `null` for three different situations — the server said there is no session, the
 * server errored, and the server could not be reached at all — and every caller then read that
 * one null as "signed out". So a dropped wifi, a restarting backend or a 502 logged the operator
 * out of a session that was perfectly alive, discarding whatever they were doing. It is the same
 * shape as an empty `catch` that makes a failed read look like an empty result.
 *
 * Three answers now, because there are three:
 *   { status: 'authed',   user }  — the server named a user
 *   { status: 'unauthed' }        — the server ANSWERED, and there is no session
 *   { status: 'unknown'  }        — nobody answered; nothing has been learned, change nothing
 *
 * `.user` is still on the authed result, so existing `s?.user` reads keep working, and a truthy
 * check on the return value no longer means "authed" — callers must look at `status`.
 */
export async function loadSession({ signal } = {}) {
  let r;
  try {
    r = await authFetch('/api/auth/get-session', { signal });
  } catch {
    // Transport failure: offline, DNS, connection refused, CORS, aborted. No information.
    return { status: 'unknown' };
  }
  // 5xx is the server failing to answer, not answering "no". 401/403 here IS a real answer.
  if (r.status >= 500) return { status: 'unknown' };
  if (!r.ok) return { status: 'unauthed' };
  const p = await r.json().catch(() => null);
  const user = p?.user || p?.session?.user || null;
  if (!user) return { status: 'unauthed' };
  return { status: 'authed', user, session: p?.session || null, activeOrganizationId: p?.session?.activeOrganizationId || null, raw: p };
}

// Display label for the signed-in admin — used to stamp actor / owner /
// createdBy / generatedBy fields with the REAL operator instead of a fabricated
// persona ("Victor S." etc). Cached after first resolve; sync accessor returns
// the cached value (or 'Admin') for render paths that can't await.
let _actorCache = null;
export async function getCurrentActor({ signal } = {}) {
  if (_actorCache) return _actorCache;
  const s = await loadSession({ signal }).catch(() => null);
  const u = s?.user;
  const label = (u?.name && u.name.trim()) || (u?.email ? u.email.split('@')[0] : '') || 'Admin';
  _actorCache = label;
  return label;
}
export function getCurrentActorSync() { return _actorCache || 'Admin'; }

// Email-OTP sign-in — the flow tkt-frontend uses (login → verify-otp):
//   sendSignInOtp  → POST /api/auth/email-otp/send-verification-otp {type:'sign-in', email}
//   verifySignInOtp→ POST /api/auth/sign-in/email-otp {email, otp}
/**
 * SIGN IN WITH GOOGLE. better-auth answers with Google's consent URL; the browser goes there, and
 * Google returns to this tenant's backend (backend.<apex>/api/auth/callback/google), which sets the
 * session and sends the browser back to `callbackURL` — this console. A failure comes back to the
 * same page with `?error=…`. Errors here mean the deployment has no Google client configured.
 */
export async function signInWithGoogle() {
  // Back to this page WITH its query (an invitation's id rides in it), less a previous ?error.
  const query = new URLSearchParams(window.location.search);
  query.delete('error');
  const here = window.location.origin + window.location.pathname + (query.toString() ? `?${query}` : '');
  const r = await authFetch('/api/auth/sign-in/social', {
    method: 'POST',
    body: JSON.stringify({ provider: 'google', callbackURL: here, errorCallbackURL: here }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok || !data?.url) throw new Error(data?.message || 'Google sign-in is not available here');
  window.location.href = data.url;
}

export async function sendSignInOtp({ email, signal } = {}) {
  const r = await authFetch('/api/auth/email-otp/send-verification-otp', {
    method: 'POST', signal,
    body: JSON.stringify({ type: 'sign-in', email: String(email || '').trim() }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok || data?.error) {
    const msg = data?.error?.message || data?.message || (r.status === 404 || r.status === 400 ? 'No account found for that email' : `Could not send code (${r.status})`);
    throw new Error(msg);
  }
  return data;
}

export async function verifySignInOtp({ email, otp, signal } = {}) {
  const r = await authFetch('/api/auth/sign-in/email-otp', {
    method: 'POST', signal,
    body: JSON.stringify({ email: String(email || '').trim(), otp: String(otp || '').trim() }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok || data?.error) {
    const msg = data?.error?.message || data?.message || (r.status === 401 || r.status === 400 ? 'Invalid or expired code' : `Sign-in failed (${r.status})`);
    throw new Error(msg);
  }
  devAuthPromise = null; // a real session now owns the cookie
  // Intentionally NOT pinning an active organization — a platform super-admin
  // stays org-unscoped (resolveOrgScope → null = see all orgs).
  return data;
}

// One-click developer sign-in (local only) — reuses the OTP dev flow.
export async function devSignIn({ signal } = {}) {
  if (!DEV_AUTH_ENABLED) throw new Error('Developer sign-in is disabled in this environment');
  devAuthPromise = null;
  return ensureDevAuthSession({ signal });
}

export async function signOut({ signal } = {}) {
  try { await authFetch('/api/auth/sign-out', { method: 'POST', signal, body: JSON.stringify({}) }); } catch { /* ignore */ }
  devAuthPromise = null;
}

// A stalled connection (flaky network, a backend that accepts the socket but never answers)
// leaves a plain `fetch` pending FOREVER — nothing here ever times out on its own. A caller's
// `signal` only aborts on unmount/re-trigger, which never fires while the page just sits there.
// That silence is exactly what surfaced as the Dashboard's KPI cards being permanently stuck on
// "updating": `refreshDashboard` sets status:'loading', the fetch never settles either way, and
// nothing ever calls `setState` again — so the "updating" pulse has no way to end.
//
// Distinguishable from a caller-initiated abort ON PURPOSE: many callers do
// `if (error?.name === 'AbortError') return;` to silently ignore their OWN cancellation (a
// re-trigger superseding a stale request) — if a timeout also threw plain AbortError, those same
// call sites would silently swallow it too, and the page would stay stuck exactly as before. A
// distinct `TimeoutError` falls through to each caller's real error handling instead.
export class TimeoutError extends Error {
  constructor(ms) { super(`No response after ${Math.round(ms / 1000)}s`); this.name = 'TimeoutError'; }
}

/**
 * A CALLER'S `signal` IS EITHER AN AbortSignal OR IT IS NOTHING.
 *
 * `onClick={reloadNotes}` hands React's SyntheticEvent to a function whose first parameter is the
 * options bag, so `signal` arrives as a click event. It is truthy, so every guard below passed, and
 * `signal.addEventListener('abort', …)` then threw a TypeError — on the one control whose entire
 * job was to recover from a failure, which therefore could never succeed. The same shape sits in
 * ~20 places across this console (`onClick={refresh}`, `onClick={load}`, `onClick={loadMore}`),
 * because passing a bare function to a handler is the obvious thing to write and the failure is
 * invisible until the error path runs.
 *
 * Fixing twenty call sites leaves the twenty-first. Narrowing here means a mis-wired handler makes
 * an UNCANCELLABLE request instead of a failing one — the request the user asked for, every time —
 * and says so loudly in dev so the wiring still gets fixed.
 */
const asAbortSignal = (signal) => {
  if (!signal) return undefined;
  if (typeof signal.addEventListener === 'function' && 'aborted' in signal) return signal;
  if (import.meta.env?.DEV) {
    console.error(
      '[backOfficeApi] `signal` is not an AbortSignal — this request cannot be cancelled.\n' +
      'Almost always `onClick={fn}` where it should be `onClick={() => fn()}`: the handler is\n' +
      'called with a SyntheticEvent, which lands in the options parameter.',
      signal,
    );
  }
  return undefined;
};

const withTimeout = (signal, ms) => {
  signal = asAbortSignal(signal);
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort);
  }
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, ms);
  return {
    signal: controller.signal,
    didTimeOut: () => timedOut,
    cleanup: () => { clearTimeout(timer); signal?.removeEventListener('abort', onAbort); },
  };
};

async function readBackOfficeJson(url, signal, timeoutMs) {
  // No auto-login here. You sign in explicitly — real OTP, or the
  // "Continue as developer" button (devSignIn) on the sign-in page. Data
  // fetches rely on the session cookie; the app's auth gate blocks them
  // until you're signed in, so there's no second get-session round-trip.
  const t = withTimeout(signal, timeoutMs);
  try {
    const response = await fetch(url, {
      signal: t.signal,
      credentials: 'include',
      // No custom header → this GET is a CORS "simple request" (no preflight). Cross-org scope
      // rides the `?orgScope=all` query param instead (see withOrgScope).
      headers: { Accept: 'application/json' },
    });
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      payload,
      summary: summarizePayload(payload),
    };
  } catch (error) {
    if (t.didTimeOut()) throw new TimeoutError(timeoutMs);
    throw error;
  } finally {
    t.cleanup();
  }
}

/**
 * THE SAME QUESTION, ASKED ONCE.
 *
 * Every component in this console fetches for itself, which is right — a card that needs the
 * ticket types should say so, not wait to be handed them. What is wrong is what that adds up to:
 * a page mount is a burst of GETs in which the SAME URL appears more than once, because two
 * components each asked at the same moment and neither could know about the other. Measured on
 * one event page: `seat-configs/:id/full`, `venue-booking/.../venue-summary` and
 * `ticketTypes/event/:id` each went out twice, concurrently, for byte-identical answers.
 *
 * Hand-threading the answer down through props fixes the two components you happened to look at
 * and prevents nothing — the next card added to that page duplicates a read again, silently, and
 * somebody has to notice it in a waterfall. So the rule lives here, at the one function all 188
 * reads go through: while a GET for a URL is IN FLIGHT, a second request for that URL joins it
 * instead of opening its own.
 *
 * This is not a cache, and the distinction is the whole design:
 *   - The entry is deleted the instant the request settles. Nothing is ever retained, so there
 *     is no TTL to tune, no staleness window, and no possibility of a mutation being followed by
 *     a read that answers from before it. A refetch always hits the network.
 *   - Two callers asking at the same moment is ONE question. Coalescing them removes work that
 *     never had a reason to exist — it does not hide slow work behind a copy.
 *
 * Cancellation stays honest by refcount: each joiner's own promise rejects on its own signal, and
 * the underlying request is only aborted once EVERY joiner has walked away. One component
 * unmounting can no longer cancel a request another component is still waiting on — which is a
 * bug the naive version of this would have introduced.
 */
const inFlightReads = new Map();

export async function fetchBackOfficeJson(path, { signal, timeoutMs = 20000 } = {}) {
  const url = `${API_BASE}${withOrgScope(withReaderClock(path))}`;
  // Keyed on the timeout too: two callers who disagree about how long to wait are not asking the
  // same question, and silently giving one of them the other's deadline is not deterministic.
  const key = `${timeoutMs}|${url}`;
  // Narrowed here too — this coalescer reads `signal` independently of withTimeout. See asAbortSignal.
  signal = asAbortSignal(signal);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  let shared = inFlightReads.get(key);
  if (!shared) {
    const controller = new AbortController();
    shared = { controller, joiners: 0, promise: null };
    const promise = readBackOfficeJson(url, controller.signal, timeoutMs);
    shared.promise = promise;
    const forget = () => { if (inFlightReads.get(key) === shared) inFlightReads.delete(key); };
    promise.then(forget, forget);
    inFlightReads.set(key, shared);
  }

  shared.joiners += 1;
  let left = false;
  const leave = () => {
    if (left) return;
    left = true;
    shared.joiners -= 1;
    if (shared.joiners > 0) return;
    // WITHDRAW IT BEFORE ABORTING IT. The abort rejects asynchronously, so between this call and
    // the rejection the entry would still be joinable — and a caller who arrives in that window
    // (a panel unmounting while its replacement mounts in the same commit asks for the same URL)
    // would inherit an AbortError for a request it never cancelled. Taking the entry out of the
    // map first means the next caller always opens a live request.
    if (inFlightReads.get(key) === shared) inFlightReads.delete(key);
    shared.controller.abort();
  };

  if (!signal) {
    try { return await shared.promise; } finally { leave(); }
  }
  try {
    return await new Promise((resolve, reject) => {
      const onAbort = () => reject(new DOMException('Aborted', 'AbortError'));
      signal.addEventListener('abort', onAbort, { once: true });
      shared.promise.then(
        (v) => { signal.removeEventListener('abort', onAbort); resolve(v); },
        (e) => { signal.removeEventListener('abort', onAbort); reject(e); },
      );
    });
  } finally { leave(); }
}

// The back-office is a super-admin console — the operator holds every
// permission. Short-circuit to "allowed" instead of round-tripping
// /api/auth/organization/has-permission on every gated surface.
// --- BetterAuth admin (server-rendered direct calls) ----------------------
// The front-end's tanstack-query setup at /Users/loft/tkt-frontend uses
// authClient.admin.listUsers() etc., which under the hood hits the same
// REST endpoints below. We call them directly here so this app doesn't
// need to pull in better-auth as a dependency.

const ADMIN_BASE = '/api/auth/admin';

export async function listUsers({ limit = 200, offset = 0, sortBy = 'updatedAt', sortDirection = 'desc', searchField, searchValue, filterField, filterValue, filterOperator, signal } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDirection) params.set('sortDirection', sortDirection);
  if (searchField) params.set('searchField', searchField);
  if (searchValue) params.set('searchValue', searchValue);
  if (filterField) params.set('filterField', filterField);
  if (filterValue) params.set('filterValue', filterValue);
  if (filterOperator) params.set('filterOperator', filterOperator);
  const response = await fetch(`${API_BASE}${ADMIN_BASE}/list-users?${params.toString()}`, {
    method: 'GET',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `${response.status} ${response.statusText || 'List users failed'}`);
  }
  // BetterAuth returns { users: [...], total } at the top level
  return {
    users: payload?.users || [],
    total: number(payload?.total, (payload?.users || []).length),
  };
}

// Same surface as listUsers but hits the operator /api/users endpoint
// (which the BE has now corrected to query db.User instead of db.EventPage).
// Returns the same shape. Used by PeopleAndUsersPage's non-customer segments.
export async function listUsersOperator({ limit = 200, page = 1, search, searchField = 'name', sort = 'desc', sortBy = 'updatedAt', role, banned, signal } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('page', String(page));
  // The User table is small (~171), so the COUNT is cheap — include it when the
  // caller wants an honest "X of N" + real pagination (the People page does).
  params.set('includeTotal', 'false'); // never a COUNT — see loadOrderPage
  if (search) { params.set('searchQuery', search); if (searchField) params.set('searchField', searchField); }
  if (sort) params.set('sort', sort);
  if (sortBy) params.set('sortBy', sortBy);
  if (role) params.set('role', role);
  if (banned) params.set('banned', banned);
  const response = await fetchBackOfficeJson(`/api/users?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Operator users request failed'}`);
  }
  const users = list(response.payload?.data || []);
  const total = response.payload?.totalItems != null ? number(response.payload.totalItems) : null;
  return {
    users,
    total,
    hasMore: total != null ? (page * limit) < total : users.length === limit,
  };
}

// The ONE real, canonical identity a buyer can have: their better-auth account
// (Order.userId, stamped at checkout — nullable, guest orders have none). This
// is a genuine by-primary-key lookup (GET /api/users/:userId, the same route
// operator/staff accounts already resolve through) — not a search, not a scan.
// Real account fields only (name/email/phone/role/banned/registered/joined);
// order history/spend is a SEPARATE fact, merged in by the caller when wanted
// (see entityById.ts's customer resolver) rather than fabricated here.
export async function loadUserById(userId, { signal } = {}) {
  const id = String(userId || '').trim();
  if (!id) return null;
  const r = await fetchBackOfficeJson(`/api/users/${encodeURIComponent(id)}`, { signal });
  // A 404 is a real answer — "no account with this id" — and the caller's guest path handles it.
  // Anything else is NOT that answer and must not be dressed up as one, or a permissions problem
  // reads on screen as "this person does not exist".
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load user'}`);
  return r.payload?.data || null;
}

// Does a real better-auth account exist for this contact, independent of
// whether any ORDER happened to stamp Order.userId? A buyer can register an
// account without it ever being linked back to an earlier guest order (the
// stamp only happens going forward, at checkout) — so "no order carries a
// userId" is not the same fact as "no account exists". One targeted search
// so the customer resolver's guest fallback can tell the two apart before
// settling for the honest-but-lesser aggregate view.
//
// Deliberately NOT the better-auth admin plugin's /list-users — that route's
// own zod schema only accepts searchField "email"|"name" (confirmed by a
// direct 400 VALIDATION_ERROR), so a phone contact — most contacts, since
// phone is the primary channel here — silently failed every lookup and this
// whole enrichment path never fired for them. /api/users (userRoutes.ts) is
// our own operator endpoint over the same db.User table and genuinely
// supports searchField "phoneNumber" (Op.iLike), so it's the one real
// endpoint for both fields instead of splitting across two.
export async function findUserByContact(contact, { signal } = {}) {
  const raw = String(contact || '').trim();
  if (!raw) return null;
  const searchField = raw.includes('@') ? 'email' : 'phoneNumber';
  const { users } = await listUsersOperator({ limit: 5, search: raw, searchField, signal });
  const needle = raw.toLowerCase();
  // EXACT match only. A prefix/ILIKE hit is what the endpoint returns, and treating "+2567008"
  // as the person who owns "+256700812345" would open the wrong human's account.
  return (users || []).find((u) => String(u?.[searchField === 'email' ? 'email' : 'phoneNumber'] || '').toLowerCase() === needle) || null;
}

// The sales channels an organizer distributes through. A LIST read — /api/distribution-channels
// returns { data, currentPage, hasMore } — used by the cold-link resolver to answer
// /channels/channel/:id, and referenced there long before it existed: entityById called
// `api.loadDistributionChannels` and there was no such export, so every shared channel link
// resolved to "no channel matches this link" for a channel that was right there.
export async function loadDistributionChannels({ limit = 200, page = 1, signal } = {}) {
  const params = new URLSearchParams({ limit: String(limit), page: String(page) });
  const r = await fetchBackOfficeJson(`/api/distribution-channels?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load distribution channels'}`);
  return { rows: r.payload?.data || [], hasMore: Boolean(r.payload?.hasMore) };
}

// ONE payout, by its primary key. The registry used to reach payouts with a page-1 list scan and
// the comment there already recorded what that cost: "a shared link insisting a real payout did
// not exist", because payouts do not fit one page. This is the by-id read that complaint needed —
// GET /api/payouts/:payoutId, org-scoped server-side.
export async function loadPayoutById(payoutId, { signal } = {}) {
  const id = String(payoutId || '').trim();
  if (!id) return null;
  const r = await fetchBackOfficeJson(`/api/payouts/${encodeURIComponent(id)}`, { signal });
  // A 404 is the real answer "no payout with this id". Anything else is a different fact and must
  // not be reported as absence — see loadUserById above for the same distinction.
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load payout'}`);
  // THE SAME SHAPE THE LIST PRODUCES. The detail page is written against a normalized row, so
  // handing it the raw model crashed it outright ("Objects are not valid as a React child" — the
  // included `event` arrives as an object where the page expects a rendered string). One
  // normalizer for both paths, or the by-id read is a second, subtly different payout type.
  const row = r.payload?.data || null;
  return row ? normalizePayout(row) : null;
}

// ONE payment attempt — which is what a "transaction" IS on this console. Light on purpose: the
// attempt and its orderId, so the detail page can fetch the rest of the sequence in a single
// request instead of the resolver and the page each pulling the siblings.
export async function loadTransactionAttempt(attemptId, { signal } = {}) {
  const id = String(attemptId || '').trim();
  if (!id) return null;
  const r = await fetchBackOfficeJson(`/api/payment-attempts/${encodeURIComponent(id)}`, { signal });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load transaction'}`);
  return r.payload?.data || null;
}

// WHO BOUGHT THIS ADD-ON. Real orders, replacing six hardcoded names ("Lillian Achieng · ORD-58231
// · 2h ago") that the drawer presented as recent purchases under a footer citing the real units-sold
// figure. An add-on purchase is an element of Order.selectedItems; GET /api/addOns/:id/orders reads
// it with a GIN-indexed containment (7,252ms → 158ms once the index was actually valid).
// WHICH ORGANISATIONS A PERSON BELONGS TO, and as what. Bounded: one super-admin on mookhv3 is a
// member of 323 organisations, so this returns a screenful plus the honest total rather than a dump.
export async function loadUserOrganizations(userId, { limit = 12, signal } = {}) {
  const id = String(userId || '').trim();
  if (!id) return { rows: [], total: 0 };
  const r = await fetchBackOfficeJson(`/api/users/${encodeURIComponent(id)}/organizations?limit=${limit}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load organisations'}`);
  return { rows: r.payload?.data || [], total: Number(r.payload?.total || 0) };
}

// A page of the orders that bought this add-on, newest first: { rows, hasMore } (the route's look-ahead).
export async function loadAddOnOrders(addOnId, { page = 1, limit = LIST_PAGE_SIZE, signal } = {}) {
  const id = String(addOnId || '').trim();
  if (!id) return { rows: [], hasMore: false };
  const r = await fetchBackOfficeJson(`/api/addOns/${encodeURIComponent(id)}/orders?limit=${limit}&offset=${(page - 1) * limit}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load add-on orders'}`);
  return { rows: r.payload?.data || [], hasMore: Boolean(r.payload?.hasMore) };
}

export async function banUser({ userId, banReason, banExpiresIn = 60 * 60 * 24 * 5000, signal } = {}) {
  const response = await fetch(`${API_BASE}${ADMIN_BASE}/ban-user`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, banReason, banExpiresIn }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `${response.status} Ban user failed`);
  }
  return payload;
}

export async function unbanUser({ userId, signal } = {}) {
  const response = await fetch(`${API_BASE}${ADMIN_BASE}/unban-user`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `${response.status} Unban user failed`);
  }
  return payload;
}

// Admin-edit a user (role / display fields) via PATCH /api/users/:userId. NOTE:
// we deliberately use THIS endpoint, not better-auth's /admin/set-role — the
// better-auth admin plugin authorizes on its own `role` field, but this console's
// super-admins are admins via the SUPER_ADMIN_EMAILS allowlist (role stays 'user'),
// so better-auth's admin endpoints 403 for them. The PATCH route is gated by
// isPlatformAdmin (the same email allowlist) and whitelists editable fields.
export async function updateUserAdmin({ userId, signal, ...fields } = {}) {
  const response = await fetch(`${API_BASE}/api/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || payload?.errors?.[0] || `${response.status} Update user failed`);
  }
  return payload;
}

export async function removeUser({ userId, signal } = {}) {
  const response = await fetch(`${API_BASE}${ADMIN_BASE}/remove-user`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `${response.status} Remove user failed`);
  }
  return payload;
}

// A person's signed-in sessions — better-auth's own record (admin plugin), one row per device
// that holds a live session. POST is better-auth's shape for this read.
const adminPost = async (path, body, { signal } = {}) => {
  const response = await fetch(`${API_BASE}${ADMIN_BASE}${path}`, {
    method: 'POST', signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || payload?.error || `${response.status} ${path} failed`);
  return payload;
};
export async function listUserSessions(userId, { signal } = {}) {
  const payload = await adminPost('/list-user-sessions', { userId }, { signal });
  return (payload?.sessions || []).map((s) => ({
    token: s.token, userAgent: s.userAgent || '', ipAddress: s.ipAddress || null,
    createdAt: s.createdAt, updatedAt: s.updatedAt, expiresAt: s.expiresAt, impersonatedBy: s.impersonatedBy || null,
  }));
}
// Sign one session out (the server records who did it, and whose it was).
export const revokeUserSession = (sessionToken, { signal } = {}) => adminPost('/revoke-user-session', { sessionToken }, { signal });
// Sign a person out everywhere.
export const revokeUserSessions = (userId, { signal } = {}) => adminPost('/revoke-user-sessions', { userId }, { signal });

export async function impersonateUser({ userId, signal } = {}) {
  const response = await fetch(`${API_BASE}${ADMIN_BASE}/impersonate-user`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `${response.status} Impersonate user failed`);
  }
  return payload;
}

// --- Customers (derived from Order contact fields) ---------------------
// Hits /api/users/customers on the BE (new endpoint added in
// tkt-backend/src/routes/userRoutes.ts). Returns a row per distinct
// email/phone pair with commerce aggregates.

// Without a search: buyers newest purchase first, each once. With one: anyone who has ordered, all-time.
export async function listCustomers({ limit = 200, page = 1, search, signal } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('page', String(page));
  if (search) params.set('searchQuery', search);
  const response = await fetchBackOfficeJson(`/api/users/customers?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Customers request failed'}`);
  }
  // This row's `id` has no real primary key behind it — the backend groups Order
  // rows by LOWER(email)/LOWER(phone) and stamps each result with a synthetic
  // `cust_<key>_<pageOffset>` id that embeds its position in THIS page: not
  // stable, the same person gets a different id on a different page, sort, or
  // after a new order shifts the offset.
  //
  // THE REAL IDENTITY, WHEN ONE EXISTS, IS `userId` — the buyer's better-auth
  // account (Order.userId, stamped at checkout; `row.userId` here is `MAX()`ed
  // across their orders on the backend, so it survives even if their FIRST
  // order was a guest checkout and a LATER one was signed in). A person with an
  // account is not "the same kind of entity" as a guest with none — routing
  // every caller by `userId` when it exists means a genuine account resolves by
  // its real, stable id (GET /api/users/:userId — one row, forever correct),
  // not a contact-string search. Only a genuine guest (no order ever stamped a
  // userId) falls back to the contact itself — see entityById.ts's resolver for
  // exactly how that fallback behaves.
  return {
    // A row IS a customer key (its phone, or its email when an order had no phone): that key is the route.
    customers: list(response.payload?.data || []).map((row) => ({ ...row, routeId: row.key })),
    hasMore: Boolean(response.payload?.hasMore),
  };
}

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== '') ?? null;
// A DISPLAY field has to end up a string. Some of these read a property the
// backend sends as a scalar on one path and as a full included ASSOCIATION row
// on another — `order.promoCode` is the whole PromoCode record, not its code —
// and `firstPresent` happily passes an object through, since it only rejects
// null/undefined/''. That object then reaches a React child and takes the entire
// page down with "Objects are not valid as a React child" (observed live on
// /orders/019f72dd… , the one order in the list that actually used a promo).
// Collapse to the human-readable identifier rather than trusting the shape.
const textLabel = (value) => {
  if (value === null || value === undefined || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(textLabel).filter(Boolean).join(', ') || null;
  return firstPresent(value.code, value.name, value.title, value.label);
};
const parseMaybeJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
};
const number = (value, fallback = 0) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const arrayCount = (value) => Array.isArray(value) ? value.length : number(value, 0);
const selectedTicketCount = (value) => {
  if (!Array.isArray(value)) return number(value, 0);
  return value.reduce((sum, item) => sum + number(item?.count ?? item?.quantity ?? item?.qty, 1), 0);
};
const ticketTypeCount = (ticket) => number(
  firstPresent(ticket?.OrderTicketType?.count, ticket?.count, ticket?.quantity, ticket?.qty),
  0,
);
const orderTicketRows = (order) => {
  const candidates = [
    order.orderTicketTypes,
    order.enrichedTickets,
    order.ticketTypes,
    order.tickets,
    order.items,
  ];
  return candidates.find((candidate) => Array.isArray(candidate) && candidate.length) || [];
};
const orderTicketCount = (order) => {
  const ticketRows = orderTicketRows(order);
  if (ticketRows.length) {
    return ticketRows.reduce((sum, ticket) => sum + ticketTypeCount(ticket), 0);
  }
  if (Array.isArray(order.selectedTickets) && order.selectedTickets.length) return selectedTicketCount(order.selectedTickets);
  return arrayCount(firstPresent(order.scanItems, order.items, order.ticketTypes, order.tickets, order.quantity));
};
const selectedTicketId = (ticket) => String(firstPresent(
  ticket?.ticketTypeId,
  ticket?.ticket_type_id,
  ticket?.ticketId,
  ticket?.id,
  ticket?.ticketType?.id,
  '',
));
const selectedTicketQuantity = (ticket) => number(firstPresent(ticket?.count, ticket?.quantity, ticket?.qty), 1);
// THE ORDER'S TIME, as the list shows it: WHEN IT WAS PAID if it was paid, else when it was made.
//
// `order_date` is the generated column that already answers the first half — it is defined as
// COALESCE(fwData.confirmed, paidAt, created_at, lastStatusCheck, paymentLinkGeneratedAt) and is
// NULL unless the order is successful, so a pending/failed order falls through to `createdAt` on
// its own without a status branch here. It is already selected by both list queries, so this
// costs nothing extra.
//
// The fwData digs that used to live here are what `order_date` coalesces internally, so they were
// re-deriving in JS what the column already computed; they remain as the tail for detail-view
// shapes (nested verification/callback payloads) that the column's five keys do not cover.
const paymentCreatedAt = (order) => {
  const fwData = order.fwData || {};
  return firstPresent(
    // 1. PAID — order_date is NULL unless the order succeeded, so this only answers for paid ones.
    order.order_date,
    // 2. NOT PAID, BUT A PAYMENT WAS STARTED. These are the real payment timestamps, and they are
    //    the whole reason the list still carries fwData. They sat BELOW `order.createdAt`, which
    //    is set on every row and therefore always won — so none of them could ever be reached and
    //    a pending order showed when its row was written rather than when the buyer began paying.
    fwData.paymentStartedAt,
    fwData.created_at,
    fwData.paymentLinkGeneratedAt,
    fwData.paymentCallback?.created_date,
    fwData.verification?.payments?.[0]?.timestamp,
    // 3. NOTHING WAS EVER STARTED — an abandoned checkout, a comp, an imported row. Then when the
    //    order was made is the only time there is, and it is the honest last resort.
    order.createdAt,
  );
};
const paymentConfirmedAt = (order) => {
  const fwData = order.fwData || {};
  return firstPresent(
    // When the money actually landed (PaymentAttempt.paidAt). See paymentCreatedAt above.
    fwData.paymentConfirmedAt,
    // `order_date` IS this value: a generated column defined as
    // COALESCE(fwData.confirmed, paidAt, created_at, lastStatusCheck, paymentLinkGeneratedAt)
    // for successful orders only — a superset of the dig below, already selected on every list.
    // The list no longer ships fwData (~880 bytes a row) to re-derive what this column holds;
    // detail views still carry it, so the fallback stays for the nested shapes it covers.
    order.order_date,
    fwData.confirmed,
    fwData.paidAt,
    fwData.verification?.payments?.[0]?.timestamp,
    fwData.paymentCallback?.payments?.[0]?.timestamp,
  );
};
// Seconds between the payer starting and the money landing.
//
// STRICTLY the two fwData keys, both written from the ONE payment that settled the order:
// `paymentStartedAt` when the payment was raised (the STK push / checkout) and `confirmed`
// when it landed. Because they come from the same payment row the pair can never straddle two
// different attempts, and no join or derived column is needed — the order already carries it.
//
// It deliberately does NOT use paymentCreatedAt()'s fallback chain, which ends at
// Order.createdAt: that is when the ORDER was made, so pairing it with a confirmation time
// yields the order's AGE, not how long paying took. On a source that never recorded a payment
// start (momo's non-installment orders, RSVP/comp orders with no payment at all) those are
// different facts by hours, and printing one under the other's name is worse than printing
// nothing. No start stamp -> no duration.
const paymentDurationSeconds = (order) => {
  const fwData = order.fwData || {};
  const a = Date.parse(fwData.paymentStartedAt || '');
  const b = Date.parse(firstPresent(fwData.confirmed, fwData.paidAt, '') || '');
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const secs = Math.round((b - a) / 1000);
  return secs > 0 ? secs : null;
};
// ONE ATTEMPT'S PAYMENT WINDOW — the gateway push to the money landing — and who closed it.
//
// Start is the attempt's own createdAt, which for migrated v3 orders is now the PUSH
// (payments.processed_at); it used to be checkout, which measured how long the buyer took to get
// around to paying and is why one order read "in 25m".
//
// `manual` is why this returns a shape and not a number. 409 of 5,038 v3 payments were closed by
// an ADMIN, and for those the end of the window is when a person reconciled — one sits 1h32m
// after an STK push that had already failed a second later. 353 of the 375 windows over five
// minutes are these. They are not slow checkouts, they are interventions, and saying so is more
// useful than either hiding them or reporting them as the buyer's wait. Organic payments run a
// median of 19s.
const attemptWindow = (raw) => {
  const a = Date.parse(raw?.createdAt || '');
  const b = Date.parse(raw?.paidAt || '');
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const secs = Math.round((b - a) / 1000);
  if (secs < 0) return null;   // start and end from two different payments; nothing to state
  return secs;
};
// "48s" / "2m 01s" / "1h 04m" — compact enough for a table cell.
const formatDuration = (secs) => {
  if (secs == null) return null;
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
  return `${Math.floor(secs / 3600)}h ${String(Math.floor((secs % 3600) / 60)).padStart(2, '0')}m`;
};

const paymentMethodForOrder = (order) => {
  const fwData = order.fwData || {};
  return firstPresent(
    // payment_rail is the backend's generated column: lower(COALESCE(fwData.paymentMethod,
    // paymentMethod, fwData.paymentProvider, fwData.provider)) — the canonical rail with the
    // explicit provider (mtnug/airtelug/mpesa/card…). Prefer it over re-deriving from fwData.
    order.payment_rail,
    fwData.paymentMethod,
    fwData.payment_type,
    fwData.verification?.payment_method,
    fwData.paymentCallback?.payment_method,
    fwData.verification?.order?.pay_mode,
    order.paymentMethod,
  );
};
const paymentProviderForOrder = (order) => {
  const fwData = order.fwData || {};
  // payment_provider is the generated column (utils/paymentProviders.ts vocabulary), so the
  // provider is one folded value rather than whichever of two fwData keys happened to be set.
  return firstPresent(order.payment_provider, fwData.provider, fwData.paymentProvider);
};
// These five values are PROVIDERS/aggregators (who processed the charge),
// never a method (how the money moved) — verified at full platform scale:
// every real order whose payment_rail lands on one of these has ZERO real
// fwData.paymentMethod (the generated column's COALESCE fell all the way
// through to the bare provider name because no method was ever recorded).
// `pesapal` was previously mapped straight to 'Card' below — that wasn't
// just mislabeling a provider as a method, it FABRICATED a specific method
// that was never confirmed (a real Visa/MasterCard/Amex charge shows up as
// its own distinct rail value, not bare 'pesapal'). Same bug class as the
// 'mobilemoney' honesty the function already had — extended to the class.
const PROVIDER_ONLY_RAILS = new Set(['mookhpay', 'flutterwave', 'paynow', 'azampay', 'pesapal']);
const PROVIDER_DISPLAY_NAMES = { mookhpay: 'Mookhpay', flutterwave: 'Flutterwave', paynow: 'Paynow', azampay: 'AzamPay', pesapal: 'Pesapal' };
export const normalizePaymentRail = (value) => {
  const raw = String(value || '').trim();
  const lower = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (!lower) return null;
  // "Unconfirmed" here means the underlying RAIL (mobile money vs card vs bank) — we only
  // ever recorded the PROCESSOR, so we won't fabricate a more specific method (see the
  // comment above). But that word, sitting right next to an order's own "Paid"/"Success"
  // status chip, reads as doubt about whether the PAYMENT itself went through — found live:
  // a genuinely, fully-paid installment order showed "Unconfirmed (via Flutterwave)" next to
  // a green "Paid" pill, which looks like a contradiction. "Rail unconfirmed" says exactly
  // what's actually unknown (the specific payment method) without implying the charge itself
  // is in doubt.
  if (PROVIDER_ONLY_RAILS.has(lower)) return `${PROVIDER_DISPLAY_NAMES[lower]}`;
  if (lower.includes('airtel')) return 'Airtel Money';
  if (lower.includes('mpesa') || lower.includes('m-pesa')) return 'M-Pesa';
  // Only an EXPLICIT mtn value is MTN MoMo. Generic "mobilemoney"/"mobilemoneyug" is the
  // buyer's chosen rail with no confirmed provider (esp. pending orders, and KES tenants
  // where mobile money is M-Pesa/Airtel) — branding it MTN was fabricating a provider.
  // `momo` is MTN's own provider integration and is a REAL PaymentAttempt.provider value on prod
  // (flutterwave, mookhpay, momo, pesapal, mpesa). constants/payments.js — the single list the
  // Rail filter is built from — already labels it "MTN MoMo", and this function did not: it fell
  // through to titleCase and produced "Momo". Two names for one value, so the filter offered
  // "MTN MoMo", sent `momo`, and got back rows labelled "Momo" that matched no rail badge.
  //
  // This is NOT the `mobilemoney` case below. That one is the buyer's chosen rail with no
  // confirmed provider, and branding it MTN would invent one. `momo` names the provider.
  if (lower === 'momo' || lower.includes('mtn')) return 'MTN MoMo';
  if (lower.includes('mobilemoney')) return 'Mobile money';
  if (lower.includes('card') || lower.includes('visa') || lower.includes('mastercard') || lower.includes('amex')) return 'Card';
  if (lower.includes('cash')) return 'Cash';
  if (lower.includes('bank') || lower.includes('eft')) return 'Bank xfer';
  if (lower.includes('wallet')) return 'Wallet';
  return titleCase(raw.replace(/_/g, ' '));
};
// "HOW the money moved, VIA whom" — e.g. "M-Pesa via Mookhpay".
//
// The rail and the processor are two different facts and the list only ever showed the first,
// so 88,346 successful M-Pesa orders never said who actually processed them.
//
// Two things make this fiddlier than it looks, both learned from the data:
//
//  1. THE RAIL IS OFTEN JUST THE PROVIDER. `payment_rail` is generated as
//     COALESCE(fwData.paymentMethod, fwData.paymentProvider, fwData.provider) — note it does
//     NOT consider Order.paymentMethod — so when no method was recorded in fwData it falls
//     straight through to the processor. That is what PROVIDER_ONLY_RAILS exists for. When
//     that happens and the ORDER carries a genuine method (mpesa/mobilemoney/card), we prefer
//     it, recovering "M-Pesa via Mookhpay" from what would otherwise read as bare "Mookhpay".
//
//  2. THE SAME VALUE CAN BE BOTH. An order can have rail 'mpesa' and provider 'mpesa'.
//     Comparing DISPLAY strings missed this ("M-Pesa" vs titleCase → "Mpesa") and produced
//     "M-Pesa via Mpesa". Both sides go through normalizePaymentRail so the comparison is
//     between resolved rails, not spellings.
//
// Never composed unless both are known and genuinely different; "via" is never invented, and
// no method is fabricated when only a processor was recorded (cash: 5,359 orders have no
// provider at all and stay plain "Cash").
const railKey = (v) => String(v || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
const isProviderOnlyRail = (v) => PROVIDER_ONLY_RAILS.has(railKey(v));

export const paymentRailVia = (order) => {
  const provider = normalizePaymentRail(paymentProviderForOrder(order));
  const railRaw = paymentMethodForOrder(order);
  // Prefer the order's own method when the derived rail is only echoing the processor.
  const source = (isProviderOnlyRail(railRaw) && order.paymentMethod && !isProviderOnlyRail(order.paymentMethod))
    ? order.paymentMethod
    : railRaw;
  const rail = normalizePaymentRail(source);
  if (rail && provider && rail !== provider) return `${rail} via ${provider}`;
  return rail || provider || null;
};


// THE currency of an order's money. It is `order.currency`, full stop.
//
// `net_amount` and `currency` are BOTH generated columns off the SAME `fwData` blob:
// net_amount takes the gateway's amount (only when status='successful'), currency takes the
// gateway's currency code. They are one value split across two columns, so the currency that
// belongs with net_amount is simply the one generated beside it.
//
// Verified on prod rather than assumed:
//   • net_amount IS NOT NULL AND currency IS NULL  →  0 rows. Money always has its currency.
//   • of 185,448 orders with settled money, 185,438 get `currency` from real gateway data;
//     only 10 fall through to the DEFAULT_CURRENCY tail — and a pending order, where that
//     tail fires most, has net_amount NULL, so there is no amount for it to mislabel.
//
// This replaces a three-branch chain that was a more elaborate way of computing the same
// thing, and got it wrong more often:
//   • `fwData.fx.to` — 0 rows on prod carry it. Dead branch.
//   • `settledCurrencyOf(fwData)` — hand-re-derived what the generated column already
//     computes, by re-listing its COALESCE sources in JS. Two copies of one rule.
//   • `order.payCurrency` — the buyer's checkout SELECTION, and not even returned by the
//     orders list endpoint, so on /orders it could never fire. It is a different fact from
//     what settled and is surfaced separately as `intendedCurrency`.
// That chain is why some /orders rows rendered an amount with no currency.
const paymentCurrencyForOrder = (order) => order.currency || null;
// A PAYMENT reference — the id a rail or gateway produced for the money movement.
//
// `order.orderId` used to lead this chain, so for almost every order it returned the ORDER
// CODE and the UI presented it as the payment's reference ("Transaction · PD5PPQQ3ZGZ3"). An
// order code identifies a purchase, not a charge on a rail. It is still reachable as the last
// resort further down, but it no longer outranks every real gateway id. Callers needing a true
// payment identity should read the PaymentAttempt's own `reference` (see loadTransactionDetail).
const paymentReferenceForOrder = (order) => {
  const fwData = order.fwData || {};
  return firstPresent(
    order.transactionId,
    order.paymentReference,
    order.txRef,
    // fwRef is a real column and is now selected on list rows; the fwData digs below only
    // apply to detail views, which still carry the blob.
    order.fwRef,
    fwData.tx_ref,
    fwData.paymentReference,
    fwData.transaction_id,
    fwData.transID,
    fwData.payload?.TransID,
    fwData.payload?.CheckoutRequestID,
    fwData.verification?.confirmation_code,
    fwData.paymentCallback?.confirmation_code,
    fwData.verification?.transaction_id,
    fwData.verification?.order?.order_number,
    order.orderId,
    order.id,
  );
};
const explicitPaymentAmountForOrder = (order) => {
  return firstPresent(order.net_amount, order.netAmount);
};

// What the BUYER was actually charged, before platform commission — the same fwData sources
// the `net_amount` generated column subtracts its commission from, so gross and net are two
// ends of one real calculation rather than two unrelated numbers.
//
// The console only ever carried the NET (`total` and `amount` are both net_amount), while the
// order-items table listed GROSS ticket prices. The receipt therefore never reconciled: four
// tickets at 35,000 listed as 140,000 above a Total of 133,000, with nothing accounting for
// the 7,000. Nobody could see it while the line showed a unit price instead of a line amount.
const grossAmountForOrder = (order) => {
  const fw = order.fwData || {};
  return firstPresent(
    fw.amount,
    fw.charged_amount,
    fw.payload?.TransAmount,
    fw.verification?.amount,
    fw.paymentCallback?.amount,
    fw.verification?.order?.amount,
  );
};

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Day-aware time: just the clock for TODAY, but date+time for any other day, so
// a 3-day-old order/scan/audit row doesn't read like it happened at "14:30 today".
export const formatTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const now = new Date();
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  if (sameDay) return time;
  const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) });
  return `${datePart}, ${time}`;
};

const shortText = (value, fallback = '—') => String(value || fallback).replace(/\s+/g, ' ').trim();

const deriveExpectedAttendance = (value) => {
  const text = String(value || '').replace(/,/g, '');
  const scoped = text.match(/(?:expected|expecting|attendees|people|runners|tickets|capacity)[^\d]{0,24}(\d{2,6})(?:\s*[–-]\s*(\d{2,6}))?/i)
    || text.match(/(\d{2,6})(?:\s*[–-]\s*(\d{2,6}))?\s*(?:attendees|people|runners|tickets|capacity)/i);
  if (!scoped) return 0;
  const candidate = number(scoped[2] || scoped[1]);
  const matchedText = String(scoped[0] || '').toLowerCase();
  if (candidate >= 2020 && candidate <= 2035 && !/(expected|expecting|attendees|people|runners|capacity)/.test(matchedText)) {
    return 0;
  }
  return candidate;
};

const titleCase = (value, fallback = '—') => {
  const raw = String(value || fallback).replace(/[_-]+/g, ' ').trim();
  return raw.replace(/\b\w/g, (char) => char.toUpperCase());
};

const initials = (value, fallback = BRAND.name) => String(value || fallback)
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 3)
  .map((part) => part[0])
  .join('')
  .toUpperCase() || fallback;

const lexicalText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const walk = (node) => {
    if (!node || typeof node !== 'object') return [];
    const own = typeof node.text === 'string' ? [node.text] : [];
    const children = Array.isArray(node.children) ? node.children.flatMap(walk) : [];
    return [...own, ...children];
  };
  return walk(value.root || value).join(' ').replace(/\s+/g, ' ').trim();
};

const statusForEvent = (event) => {
  const raw = String(firstPresent(event.status, event.state, event.publishStatus, '') || '').toLowerCase();
  const eventDate = new Date(firstPresent(event.date, event.startDate, event.startsAt));
  if (raw.includes('cancel')) return 'Cancelled';
  if (!Number.isNaN(eventDate.getTime())) {
    const today = new Date();
    const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime();
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    // WHEN IT HAPPENS, not whether it was published — that is a separate dimension (see
    // approvalStatus/publishStatus). A future-dated draft is Upcoming, and so an event whose RUN
    // includes today is Live, draft or not: an accreditation-only conference (badges, no storefront)
    // is never published and was filed under "Draft", below every future event, on the day it ran.
    // The run ends on `endDate` — judged by its start, day 2 of a two-day event read Completed.
    if (eventDay > todayDay) return 'Upcoming';
    const endDate = new Date(firstPresent(event.endDate, event.endDateTime, event.date, event.startDate, event.startsAt));
    const endDay = Number.isNaN(endDate.getTime()) ? eventDay : new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
    if (endDay >= todayDay) return 'Live';
    // Over is over, published or not. A draft event page can still sell — moments' Uganda vs Libya
    // sold 10,923 tickets through the MoMo app as a `draft` — so "Draft" after the run read as
    // "never happened" about an event that happened. Publish state is its own dimension.
    return 'Completed';
  }
  // No usable date — fall back to the textual status.
  if (raw.includes('draft')) return 'Draft';
  if (raw.includes('archiv') || raw.includes('complete') || raw.includes('past')) return 'Completed';
  if (raw.includes('live') || raw.includes('active') || raw.includes('published')) return 'Live';
  return 'Upcoming';
};

// EVERY normalize* BELOW RETURNS THE ROW AS THE BACKEND SENT IT, then what it derives. A normaliser adds or derives
// what a reader needs (a display word, a computed `available`, a camelCase name) — it never decides which of the
// backend's facts reach the console. Rebuilding a row from a list of known fields silently drops every fact not on
// the list (a tier's comp count read 0 for months; refunds never arrived). Derived keys come after the spread, so
// where a name is reused the derived value is the one read.
const normalizeEvent = (event, index = 0) => {
  const title = firstPresent(event.title, event.name, event.eventName, event.slug, `Event ${index + 1}`);
  const org = firstPresent(event.organization?.name, event.Organization?.name, event.organizerName, event.organizationName, event.organizer, 'Unassigned organizer');
  // The venue an operator can actually read, from the keys the DATA actually has.
  //
  // This used to look for address.name / address.venue / address.formatted — and measured
  // against mookh, NONE of those three keys exists on a single EventPage. The keys that hold
  // the address are `formattedAddress` and `address1` (179 rows each), written by this very
  // app (NewEventWizard, EventDetailPageV2) and never read back. Result: 179 of 195 events —
  // 92% — displayed "TBD" while holding a real venue name (PCEA Kahawa Sukari Church, Bliss
  // Resort, Como Tigoni…). No event on that deployment has a linked Venue, so this fallback
  // is the ONLY path, and it was reading a shape nobody writes.
  //
  // Ordered most- to least-specific: a linked Venue record, then the venue's own name, then
  // the address line, and city only as a last resort — a city is not a venue, but it is a
  // real place, which beats asserting the venue is undecided.
  const addr = typeof event.address === 'object' && event.address ? event.address : null;
  const address = addr
    ? firstPresent(addr.venueName, addr.name, addr.venue, addr.formattedAddress, addr.address1, addr.formatted, addr.address, addr.city)
    : event.address;
  // `eventPageVenue` is EventPage's own association name (the list and the single read both carry
  // it); `venue`/`Venue` are kept for payloads shaped by other routes.
  const venue = firstPresent(event.eventPageVenue?.name, event.venue?.name, event.Venue?.name, event.venueName, address, 'TBD');
  const sold = firstPresent(event.soldTickets, event.ticketsSold, event.totalTicketsSold, event.stats?.ticketsSold, event.ordersCount, event.sold);
  const capacity = firstPresent(event.capacity, event.totalCapacity, event.maxTickets, event.ticketLimit, event.stats?.capacity, 0);
  const gross = firstPresent(event.grossSales, event.totalSales, event.revenue, event.stats?.gross, event.sales, 0);
  const eventType = firstPresent(event.eventType, event.type, event.eventTypeLabel);
  const locationType = firstPresent(event.locationType, event.location?.type);
  // Publish state is SEPARATE from timing: a future-dated draft still reads as "Upcoming"
  // (see statusForEvent). `published` is the real publish signal (raw status isn't a draft) —
  // so surfaces like the dashboard "Live and upcoming" can show only what's actually live.
  // The EventPage status enum is exactly draft | published | archived (checked on prod). This
  // read `!includes('draft')`, so an ARCHIVED event counted as published and could surface in
  // "live and upcoming". Excluded by name rather than by strict equality to 'published', so a
  // source that reports 'live'/'active' instead of the enum still resolves as published.
  const publishState = String(firstPresent(event.status, event.state, event.publishStatus, '') || '').toLowerCase();
  const published = !publishState.includes('draft') && !publishState.includes('archiv');
  // The enum itself, not just the boolean. "Published?" is a yes/no, but the column it
  // comes from is draft | published | archived, and an operator filtering the list needs
  // to tell an archived event from a draft — the boolean collapses both to "not published".
  const publishStateLabel = publishState.includes('draft') ? 'draft'
    : publishState.includes('archiv') ? 'archived'
    : publishState ? 'published' : null;
  return {
    ...event,
    id: String(firstPresent(event.id, event.eventId, event.slug, `event-${index + 1}`)),
    displayId: String(firstPresent(event.slug, title, event.id, `event-${index + 1}`)),
    name: title,
    organizer: org,
    // The ids that make `organizer` / `venue` NAVIGABLE references instead of dead text.
    // Both are selected by the list route (eventPageListAttributes) and eager-loaded
    // (`organization`, `venue`), so every reader gets them for free — no extra fetch.
    // Measured on prod: 117/117 events carry organizationId; 66/117 carry a venueId that
    // resolves to a real Venue row (mookh: 0/195 — those hold a typed address only).
    // venueId stays null when there's no linked Venue so the UI shows honest plain text
    // instead of a link that 404s (see EntityLink).
    organizerId: firstPresent(event.organization?.id, event.Organization?.id, event.organizationId, event.organizerId),
    venueId: firstPresent(event.venue?.id, event.Venue?.id, event.venueId),
    date: formatDate(firstPresent(event.date, event.startDate, event.startsAt, event.createdAt)),
    dateIso: /* raw instant — `date` above is display-only */ firstPresent(event.date, event.startDate, event.startsAt, null),
    venue,
    status: statusForEvent(event),
    sales: number(gross),
    sold: number(sold),
    capacity: number(capacity),
    payment: event.paymentStatus || 'Healthy',
    risk: event.riskLevel || 'Low',
    access: event.reservedSeating ? 'Ready' : 'Online',
    updated: event.updatedAt ? formatTime(event.updatedAt) : 'live',
    // EventPage.category is a fixed enum (music|sports|cinema|…). Never fall
    // through to eventType (single|recurring) — that polluted the list filter
    // with schedule shape. Store the raw enum slug; surfaces title-case via
    // categoryLabel(). Default only when genuinely absent.
    category: (() => {
      const raw = firstPresent(event.category, null);
      if (!raw) return 'general';
      const slug = String(raw).toLowerCase().trim();
      // eventType/type are NOT categories — reject schedule/shape values.
      if (slug === 'single' || slug === 'recurring') return 'general';
      return slug;
    })(),
    slug: firstPresent(event.slug, ''),
    // THE BACKEND'S ANSWER, LIFTED — never composed here. Both depend on which deployment the
    // backend is (see its utils/eventUrl.ts): `publicUrl` is the storefront link an operator sends
    // a buyer, `storeUrl` is the same event on tkt-store, which the page preview renders. '' when
    // the event has no slug, or the deployment no store — the caller hides the affordance rather
    // than offering a link that resolves nowhere.
    publicUrl: firstPresent(event.publicUrl, ''),
    storeUrl: firstPresent(event.storeUrl, ''),
    // The same page by the event's number (momoticket.com/137), also built by the backend's
    // utils/eventUrl.ts — '' where that deployment's storefront has no short form.
    shortUrl: firstPresent(event.shortUrl, ''),
    approvalStatus: firstPresent(event.approvalStatus, ''),
    approvalComment: firstPresent(event.approvalComment, ''),
    type: eventType ? titleCase(eventType) : '',
    startTime: firstPresent(event.startTime, event.startsAtTime, ''),
    endTime: firstPresent(event.endTime, event.endsAtTime, ''),
    locationType: locationType ? titleCase(locationType) : '',
    parking: firstPresent(event.parking, ''),
    parentalGuidance: firstPresent(event.parentalGuidance, ''),
    ageRestriction: firstPresent(event.ageRestriction, ''),
    summary: lexicalText(event.summary),
    serviceFeePayer: firstPresent(event.serviceFeePayer, ''),
    backendBacked: Boolean(event.id || event.eventId),
    published,
    publishState: publishStateLabel,
    // THE STOREFRONT LINK, AS THE BACKEND BUILT IT. `utils/eventUrl.ts` composes this because
    // only the server knows which tenant it is, and its header records the back office having
    // hardcoded `tkt.ug/e/{slug}` in six places — a 404 on every deployment. So it is READ, never
    // re-derived here. Null on payloads that don't carry it (list rows); every consumer must
    // degrade honestly rather than guess a domain.
    publicUrl: firstPresent(event.publicUrl, null),
    /**
     * FEATURED — put on the normalized shape, not left for each surface to dig out of `raw`.
     *
     * `featuredAt` is the truth (a timestamp: present = featured, value = strip order); `featured`
     * is the boolean derived from it in ONE place so no surface can invent its own rule. Deriving
     * rather than storing both is the same reason it is a single timestamp — a flag and its date
     * cannot disagree if there is only one of them.
     *
     * It lives in `metadata.featuredAt`, not a column: `metadata` is JSONB that this endpoint
     * already returns, so featuring needed no schema change on any environment. Read from the top
     * level too, because that is where it briefly lived.
     *
     * The old `featured` was a MOCK: `builders.js` computed `event?.id === 'EVT-10029'` against a
     * hardcoded id, and the real-backend path passed a literal `{ featured: false }`, so the
     * Content tab's "Featured on homepage" row has always read "No" for every real event.
     */
    featuredAt: firstPresent(event.metadata?.featuredAt, event.featuredAt, null),
    featured: Boolean(event.metadata?.featuredAt || event.featuredAt),
    /**
     * The strip's explicit rank, set by dragging (see reorderFeaturedEvents). `null` means "never
     * placed", which sorts FIRST — a just-featured event leads until someone arranges the strip.
     * Deliberately not defaulted to 0: 0 is a real rank (the front slot) and conflating the two
     * would drop every new event behind whatever currently sits at the front.
     */
    featuredOrder: Number.isFinite(event.metadata?.featuredOrder) ? event.metadata.featuredOrder : null,
    /**
     * THE POSTER, AS A URL STRING — because the column is not one.
     *
     * `EventPage.photo` is JSON, and on prod it is an OBJECT for 122 of 123 events:
     * `{"url":"https://media.ticket.ug/…","key":"uploads/…"}`. Exactly one row holds a bare string.
     * Handing that object to `<img src=…>` renders `src="[object Object]"`, which fails to load —
     * so the Featured strip's artwork silently degraded to the letter fallback on EVERY prod event
     * while looking deliberate. It was invisible locally, where no fixture has a photo at all.
     *
     * Unwrapped HERE rather than in the strip so the next surface that wants artwork gets a usable
     * value instead of rediscovering the shape. Both forms accepted; anything else becomes null,
     * which the strip already renders honestly as the event's initial.
     */
    photo: (() => {
      const p = firstPresent(event.photo, event.image, event.posterUrl, null);
      if (!p) return null;
      if (typeof p === 'string') return p.trim() || null;
      if (typeof p === 'object') return p.url || p.src || p.href || null;
      return null;
    })(),
    raw: event,
  };
};

const mergeEventStats = (event, stats = {}) => {
  const rawStats = stats[event.id] || stats[event.raw?.id] || {};
  const sold = firstPresent(rawStats.total_tickets_sold_for_event, rawStats.totalTicketsSold, event.sold);
  const capacity = firstPresent(rawStats.total_tickets_for_event, rawStats.totalTickets, event.capacity);
  const salesByCurrency = rawStats.charged_amount && typeof rawStats.charged_amount === 'object'
    ? Object.fromEntries(Object.entries(rawStats.charged_amount)
      // Keyed BY currency — an unkeyed entry has no honest bucket. Dropping it beats
      // folding foreign money into a UGX-labelled total.
      .filter(([currency]) => currency)
      .map(([currency, amount]) => [currency, number(amount)])
      .filter(([, amount]) => amount > 0))
    : {};
  const firstCurrencyAmount = Object.values(salesByCurrency)[0];
  const sales = firstPresent(rawStats.total_receipt_for_event, rawStats.total_revenue_for_event, firstCurrencyAmount, event.sales);
  const orders = firstPresent(rawStats.total_orders_for_event, rawStats.totalOrders);
  return {
    ...event,
    sold: number(sold),
    capacity: number(capacity, event.capacity),
    sales: number(sales),
    salesByCurrency,
    orders: number(orders),
    rawStats,
  };
};

/**
 * `total_available` IS THE CAP, NOT WHAT IS LEFT — and reading it as "remaining" was costing two
 * numbers on this page.
 *
 * The column behind it is `TicketType.availableTickets`, whose own model comment says it plainly:
 * "Enforced as a real cap vs availableTickets at issuance… availableTickets = null means
 * uncapped". `soldCount` is the separate issued counter. So a tier with cap 100 and 17 sold
 * returns `total_available: 100`, and 83 is a number nobody sent.
 *
 * Read as remaining, it produced:
 *   CAPACITY INFLATED BY WHATEVER HAD SOLD. `cap` fell through to `sold + available`, so this
 *   event's six 100-seat tiers reported 629 seats instead of 600 — and the error grows with
 *   sales, so it is largest exactly when the number matters. A 10,000-seat event that has sold
 *   10,580 would have claimed a 20,580 capacity.
 *   A SOLD-OUT TIER READING "On sale". `available` was the cap, so it never reached 0 and the
 *   status below it could never say Sold out.
 */
const normalizeTicketSummary = (row, index = 0) => {
  const sold = number(firstPresent(row.tickets_sold, row.ticketsSold, row.sold));
  const comp = number(firstPresent(row.tickets_comp, row.comp));
  // Uncapped stays 0 — `availableTickets` is null for an uncapped tier, and 0 here means
  // "no cap set", which every reader below already treats as unknown rather than as "no seats".
  const cap = number(firstPresent(row.capacity, row.totalTickets, row.total, row.total_available, row.availableTickets));
  // What is genuinely left, derived rather than assumed. Comps occupy seats too, so they count
  // against the cap exactly as paid tickets do — the same rule the fill bar uses — and so do
  // held tickets (tier holds, funded plans), which every channel sells around.
  const held = number(row.held);
  const available = cap > 0 ? Math.max(0, cap - sold - comp - held) : 0;
  const price = number(firstPresent(row.ticket_price, row.price, row.amount));
  // THE ROW AS SENT, then what is derived. Rebuilding it from a list of known fields is how a fact the endpoint
  // already returns gets silently dropped (comp, below, was a hardcoded 0 for exactly that reason): everything the
  // backend sends passes through, and only what is computed here is added or normalised.
  return {
    ...row,
    id: String(firstPresent(row.ticket_type_id, row.id, `ticket-type-${index + 1}`)),
    tier: firstPresent(row.ticket_type_name, row.name, `Ticket type ${index + 1}`),
    cap,
    sold,
    held,
    available,
    reserved: 0,
    scanned: number(firstPresent(row.scanned, row.tickets_scanned)),
    refunded: number(firstPresent(row.refunded, row.tickets_refunded)),
    transferred: 0,
    // WAS A HARDCODED `0`, while the endpoint has been returning `tickets_comp` and `comp_orders`
    // per tier since the ticket-summary split shipped. So the console read every event as having
    // given away nothing: on one prod event that is 10,503 tickets — the entire crowd — reported
    // as zero, and the same figures rendered correctly on the PUBLIC report the console itself
    // hands out. A field that exists on the wire and is overwritten with a constant is worse than
    // a missing one; nothing looks wrong.
    comp,
    compOrders: number(firstPresent(row.comp_orders, row.compOrders)),
    // The platform commission apportioned to this tier (utils/orderMoney.orderFeeExpr). The
    // remittance advice needs it to state "collected → less commission → payable"; dropping it
    // here is why that document had to ask a human to type the fee in.
    feeAmount: number(firstPresent(row.fee_amount, row.feeAmount)),
    locked: 0,
    price,
    promos: 0,
    currency: firstPresent(row.currency, 'UGX'),
    revenue: number(firstPresent(row.total_revenue, row.revenue)),
    netAmount: number(firstPresent(row.net_amount, row.netAmount)),
    uniqueOrders: number(firstPresent(row.unique_orders, row.uniqueOrders)),
    // `cap === 0` is "uncapped", which is on sale forever — not sold out. Now that `available`
    // is derived from the cap it can actually reach zero, so this branch finally distinguishes
    // the two instead of always taking the first.
    status: available > 0 || cap === 0 ? 'On sale' : 'Sold out',
    raw: row,
  };
};

const normalizeTicketType = (row, index = 0) => {
  const sold = number(firstPresent(row.successfulOrderCount, row.tickets_sold, row.sold));
  const cap = number(firstPresent(row.availableTickets, row.total_available, row.capacity, row.quantity));
  const price = number(firstPresent(row.price, row.ticket_price, row.amount));
  const scanned = number(firstPresent(row.scannedCount, row.scanned));
  const reserved = number(firstPresent(row.reserved, row.reservedCount));
  const seatReservations = number(firstPresent(row.seatReservationCount, row.seatReservations));
  const comp = number(firstPresent(row.complimentaryCount, row.comp));
  return {
    ...row,
    id: String(firstPresent(row.id, row.ticket_type_id, `ticket-type-${index + 1}`)),
    tier: firstPresent(row.name, row.ticket_type_name, `Ticket type ${index + 1}`),
    ticketType: firstPresent(row.ticketType, 'paid'),
    cap,
    sold,
    available: Math.max(cap - sold, 0),
    reserved,
    seatReservations,
    scanned,
    refunded: number(firstPresent(row.refunded, row.refundedCount)),
    transferred: 0,
    comp,
    locked: 0,
    price,
    promos: number(firstPresent(row.promosCount, row.promos)),
    // No 'UGX' tail. A ticket type with no currency has an UNKNOWN price, not a
    // Ugandan one — and because order enrichment reads this value to denominate the
    // order's gross, stamping a default here silently re-fabricated the very currency
    // paymentCurrencyForOrder exists to avoid (caught live: the order page went back to
    // reading UGX after the resolver was already correct, because the leak was here).
    currency: firstPresent(row.currency),
    status: titleCase(firstPresent(row.status, 'Published')),
    salesChannel: firstPresent(row.salesChannel, ''),
    shortCode: firstPresent(row.shortCode, ''),
    channelAccess: normalizeTicketChannelAccess(row),
    salesChannels: normalizeTicketSalesChannels(row),
    // HELD — set aside and off sale on every channel (`heldCount`: count holds + funded payment plans).
    // `holds` are the tier's active count holds (Hold rows with this ticketTypeId); the rest of `held`
    // is plans' claims.
    held: number(firstPresent(row.heldCount), 0),
    holds: (Array.isArray(row.holds) ? row.holds : []).map((h) => ({ id: h.id, name: h.name, qty: number(h.seatCount), note: h.reason || '', createdBy: h.createdBy || '', createdAt: h.createdAt })),
    eventId: firstPresent(row.eventId, null),
    // A BUNDLE: one unit at one price admitting this many people (the backend issues one ticket
    // each). Null for a single ticket — `cap`, `sold` and `price` are per unit either way.
    bundleSize: (() => { const n = number(firstPresent(row.ticketsInBundle), 0); return n > 1 ? n : null; })(),
    isBundle: Boolean(firstPresent(row.isBundle, false)),
    minQuantity: number(firstPresent(row.minimumQuantity), 1),
    maxQuantity: number(firstPresent(row.maximumQuantity), 0),
    salesStartDate: firstPresent(row.salesStartDate, ''),
    salesStartTime: firstPresent(row.salesStartTime, ''),
    salesEndDate: firstPresent(row.salesEndDate, ''),
    salesEndTime: firstPresent(row.salesEndTime, ''),
    description: firstPresent(row.description, ''),
    visibility: firstPresent(row.visibility, ''),
    // Installments (pay-in-parts) opt-in — surfaced so the Edit modal reflects the
    // tier's real saved state (else the checkbox always reads unchecked and the
    // patch-diff misfires: form.allowInstallments !== tier.allowInstallments is
    // always "changed"). Booleanized; maxInstallments kept as a number or null.
    allowInstallments: Boolean(firstPresent(row.allowInstallments, false)),
    maxInstallments: (() => { const m = number(firstPresent(row.maxInstallments), 0); return m >= 2 ? m : null; })(),
    // Split payments, carried for EXACTLY the reason stated above for installments — and it was
    // missing, so the Edit modal could not show a tier's real split state and the patch-diff
    // always read "changed". `minSplitAmount` arrives as a DECIMAL string ("1000.00") from
    // Sequelize, so it is numbered here rather than at each of its readers.
    allowSplit: Boolean(firstPresent(row.allowSplit, false)),
    maxSplitPayers: (() => { const m = number(firstPresent(row.maxSplitPayers), 0); return m >= 2 ? m : null; })(),
    minSplitAmount: (() => { const m = number(firstPresent(row.minSplitAmount), 0); return m > 0 ? m : null; })(),
    countsAreEstimated: row.successfulOrderCount === undefined,
    raw: row,
  };
};

/**
 * A DAY AND A TIME-OF-DAY ARE ONE MOMENT, NOT TWO STRINGS TO CONCATENATE.
 *
 * This was `[date, time].join(' ')`, which assumes `date` is a bare calendar day. It is not: the
 * backend stores promo windows as an ABSOLUTE INSTANT at the day's LOCAL MIDNIGHT (see
 * lib/absoluteInstant, whose contract names promo codes explicitly) plus a separate `startTime`.
 * So a promo running to 23:59 on 25 August rendered as:
 *
 *     2026-08-25T21:00:00.000Z 23:59:00
 *
 * — a UTC instant and a local clock-time glued together, on a row an operator reads to decide
 * whether a code is live. `toLocalDay` is the console's existing reader for exactly this shape, so
 * the day comes from there and the time is applied to it, giving ONE moment.
 */
export const combineDayAndTime = (date, time) => {
  const day = toLocalDay(date);
  if (!day) return null;
  const [h = '00', m = '00'] = String(time || '00:00').split(':');
  const at = new Date(`${day}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
  return Number.isNaN(at.getTime()) ? null : at;
};

const formatDateTimeParts = (date, time) => {
  if (!date && !time) return '';
  const at = combineDayAndTime(date, time);
  if (!at) return [date, time].filter(Boolean).join(' ');
  return at.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const normalizePromotionRule = (row, index = 0, kind = 'promo') => {
  const isPromo = kind === 'promo';
  const limitMode = firstPresent(row.limit, 'unlimited');
  const limit = limitMode === 'limitedTo' ? number(row.limitedTo) : 0;
  /**
   * CLAIMS AND TICKETS ARE DIFFERENT UNITS, AND THE LIMIT GOVERNS CLAIMS.
   *
   * `used` was `ticketsCount` — a COUNT OF ScanItems, i.e. individual tickets on orders that used
   * the code (see the scalar subquery in promoCodeRoutes). It was then printed as `used / limitedTo`,
   * but `limitedTo` is enforced against `claimedCount`, which `claimPromoAllotment` increments ONCE
   * PER ORDER inside the order write. Dividing one by the other compares tickets to orders.
   *
   * On an event selling tables — BrakaLand EA's tiers are Table of 5, Squad of 4, Table of 6 — a
   * single claim can be six tickets, so a 100-claim code reads "600 / 100" while its allotment has
   * 100 slots left untouched. The bar fills at up to 6x the true rate and can pass 100% on a code
   * that has barely been used, while the backend's own `isExhausted` says otherwise.
   *
   * So: `used` is now the claim counter the limit actually governs, and the ticket count keeps its
   * own field and its own label. Both are worth knowing — claims is how many buyers used the
   * offer, tickets is how many seats it moved — but only one of them belongs over `limitedTo`.
   */
  const used = number(firstPresent(row.claimedCount, row.used, row.usageCount));
  const ticketsIssued = number(firstPresent(row.ticketsCount, 0));
  const exhausted = Boolean(row.isExhausted || (limit > 0 && used >= limit));
  const starts = firstPresent(row.start === 'now' ? 'Now' : formatDateTimeParts(row.startDate, row.startTime), 'Now');
  const ends = firstPresent(row.end === 'salesEnd' ? 'Sales end' : formatDateTimeParts(row.endDate, row.endTime), 'Sales end');
  /**
   * THE STATUS HAS TO KNOW ABOUT THE WINDOW IT IS PRINTED NEXT TO.
   *
   * It was `exhausted ? 'Exhausted' : 'Active'` — the usage cap and nothing else — so a code was
   * "Active" forever, including before it started and long after it ended. Seen on prod: YOYO on
   * BrakaLand EA read **Active** beside a window that had closed nine days earlier. The row
   * contradicted itself, and the contradiction was on the one column an operator scans to answer
   * "is this code live right now".
   *
   * Order matters and is the order of the operator's questions: has someone switched it off →
   * has it run out → has it not started → is it over → then it really is live.
   */
  const startsAt = row.start === 'now' ? null : combineDayAndTime(row.startDate, row.startTime);
  const endsAt = row.end === 'salesEnd' ? null : combineDayAndTime(row.endDate, row.endTime);
  const now = Date.now();
  const status = row.pausedAt ? 'Paused'
    : exhausted ? 'Exhausted'
    : (startsAt && startsAt.getTime() > now) ? 'Scheduled'
    : (endsAt && endsAt.getTime() < now) ? 'Expired'
    : 'Active';
  // THE TIERS A SCOPED PROMO APPLIES TO, by name. `applyTo: 'certainTickets'` rendered as the
  // words "Selected ticket types" while the names sat unread in the same payload — the one thing
  // a reader needs in order to know what the code actually does.
  const ticketTypes = (Array.isArray(row.ticketTypesForPromoCode) ? row.ticketTypesForPromoCode : [])
    .map((t) => firstPresent(t?.name, t?.title, '')).filter(Boolean);
  return {
    ...row,
    id: String(firstPresent(row.id, `${kind}-${index + 1}`)),
    code: isPromo ? firstPresent(row.code, row.name, `PROMO-${index + 1}`) : firstPresent(row.name, `Discount ${index + 1}`),
    name: firstPresent(row.name, row.code, isPromo ? `Promo ${index + 1}` : `Discount ${index + 1}`),
    kind,
    type: firstPresent(row.discountType, row.discount?.percentage ? 'percentage' : row.discount?.amount ? 'amount' : isPromo ? 'percentage' : 'amount'),
    value: number(firstPresent(row.discount, row.discount?.percentage, row.discount?.amount)),
    discount: firstPresent(row.discount, 0),
    scope: firstPresent(row.applyTo, 'all'),
    usageLimit: limit,
    used,
    ticketsIssued,
    // Null when unlimited — "∞ remaining" is not a number, and 0 would read as "none left".
    remaining: limit > 0 ? Math.max(0, limit - used) : null,
    perCustomer: number(firstPresent(row.perCustomerLimit, row.maxTicketsPerUse), 0),
    validFrom: starts,
    validTo: ends,
    channels: Array.isArray(row.channelRestrictions) && row.channelRestrictions.length ? row.channelRestrictions : ['all'],
    startsAt,
    endsAt,
    ticketTypes,
    paused: Boolean(row.pausedAt),
    status,
    raw: row,
  };
};

// Never render a raw user id in the UI. Resolve known automated actors to readable names; an id we
// could not resolve to a person collapses to "Staff" rather than being printed.
//
// THE DETECTOR HAD ONE SHAPE AND THE PLATFORM ISSUES TWO. It tested for a hex UUID, which is what
// the app's own tables use — but `Order.createdBy` holds a BETTER-AUTH user id, and those are
// 27-character mixed-case nanoids ("EMDcQ7cmxWiuoxFMdUEKQITrwdz"). No hex match, so every order
// placed by a signed-in operator fell through to titleCase() and rendered its raw id under "Sold
// by". The id is not the fallback anyway — the NAME is, see `operator` in normalizeOrder — so this
// only fires when the join returned nothing.
const looksLikeAnId = (s) =>
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}/i.test(s)          // a UUID, dashed or not
  || /^[0-9a-f]{24,}$/i.test(s)                               // a long hex string
  // An opaque token: 20+ characters of nothing but letters, digits, _ and -, carrying at least one
  // of each. A real name has a space; an email has an @; the slugs below ("comm-config-seed",
  // "whatsapp-bot") carry no digits. Measured against the live column, which holds exactly three
  // kinds of value: a better-auth id, an email address, and one of those slugs.
  || (s.length >= 20 && /^[A-Za-z0-9_-]+$/.test(s) && /[A-Za-z]/.test(s) && /[0-9]/.test(s));

const prettyActor = (v) => {
  const s = String(v ?? '').trim();
  if (!s || s === '—') return '—';
  // An address IS the answer — the one case where the raw value names a person. Title-casing it
  // would only damage it.
  if (s.includes('@')) return s.toLowerCase();
  if (looksLikeAnId(s)) return 'Staff';
  const map = { 'whatsapp-bot': 'WhatsApp bot', 'whatsapp': 'WhatsApp', 'ussd-bot': 'USSD', 'ussd': 'USSD', 'back-office': 'Back office', 'comm-config-seed': 'System', 'system': 'System' };
  return map[s.toLowerCase()] || titleCase(s);
};

/**
 * Where an order came from, in the vocabulary the rest of the console uses. Falls back to a
 * de-slugged title case so a source added by the backend tomorrow reads sensibly without a deploy
 * here, and only to 'Web' when the backend genuinely told us nothing.
 */
const orderSourceLabel = (order) => {
  const raw = firstPresent(order.purchase_source, order.source, order.salesChannel, null);
  if (!raw) return 'Web';
  return SOURCE_LABELS[raw] || titleCase(String(raw).replace(/[-_]/g, ' '));
};

const normalizeOrder = (order, index = 0) => {
  const event = firstPresent(order.event?.title, order.EventPage?.title, order.eventName, order.event?.name, 'Unknown event');
  const customer = [order.firstName, order.lastName].filter(Boolean).join(' ') || firstPresent(order.customerName, order.name, order.email, order.phone, 'Guest customer');
  // net_amount is the ONLY source of truth for an order's money (see
  // backoffice-fees-commission memory) — never assumed from total/amount/price/
  // fwData.* (the gross/intended/pre-fee figures a previous version fell back to,
  // which showed money the operator never actually reconciles net_amount against).
  // A missing net_amount stays 0, not a guess from an unrelated field.
  const amount = number(explicitPaymentAmountForOrder(order), 0);
  return {
    ...order,
    id: String(firstPresent(order.orderId, order.id, `order-${index + 1}`)),
    backendId: String(firstPresent(order.id, order.orderId, `order-${index + 1}`)),
    // routeId = the order UUID, so /orders/<uuid> resolves in one loadOrderDetail
    // call (the human reference on `.id` isn't backend-resolvable).
    routeId: String(firstPresent(order.id, order.orderId, `order-${index + 1}`)),
    // The latest payment attempt on the order and how many tries it has had — { status, reason,
    // detail, at, tries } — or null (list rows only; see orderQueries.latestAttempts).
    lastAttempt: order.lastAttempt || null,
    customer,
    email: firstPresent(order.email, order.customerEmail, order.contactEmail, ''),
    phone: firstPresent(order.phone, order.phoneNumber, order.customerPhone, order.msisdn, ''),
    event,
    eventId: String(firstPresent(order.eventId, order.event?.id, order.EventPage?.id, '')),
    tickets: orderTicketCount(order),
    total: amount,
    amount,
    // Gross (what was charged) alongside net (what the organizer keeps). Null when the order
    // hasn't been charged, so a receipt can tell "no fee yet" from "no fee".
    grossAmount: (() => { const g = grossAmountForOrder(order); return g == null ? null : number(g, null); })(),
    // NULLABLE companion to `amount` above. `amount` defaults a missing net_amount to 0 —
    // right for a list/KPI column that must always be a safe number, wrong for anything that
    // needs to ask "was this order ever actually charged?": a genuinely pending order (no
    // net_amount yet) and a real UGX-0 charge (a settled comp) both read as `amount: 0`, and
    // nothing downstream could tell them apart. `netAmount` stays null until net_amount is
    // real, same reasoning as `grossAmount` just above.
    netAmount: (() => { const n = explicitPaymentAmountForOrder(order); return n == null ? null : number(n, null); })(),
    currency: paymentCurrencyForOrder(order),
    // CHANNEL = the payment rail. Do NOT fall back to order.orderType — that's
    // a classification ('paid'/'free'/'complimentary'), not a rail, and titleCasing
    // it printed "Paid" as the channel on every unpaid order. No rail yet → '—'.
    // "M-Pesa via Mookhpay" — the rail AND who processed it. See paymentRailVia.
    channel: firstPresent(paymentRailVia(order), normalizePaymentRail(order.channel), normalizePaymentRail(order.source), '—'),
    // The rail ALONE, for places that need the method without the processor (the payment-method
    // filter's option labels, which filter by method and would otherwise read "M-Pesa via
    // Mookhpay" for a value that is just `mpesa`).
    rail: firstPresent(normalizePaymentRail(paymentMethodForOrder(order)), normalizePaymentRail(order.channel), '—'),
    // SOURCE = where the order originated (web / box office / migration / AI). orderType is not
    // a source either — leaving it in printed "Paid" as the source subtitle.
    //
    // READ `purchase_source`, WHICH IS WHAT THE BACKEND ACTUALLY SENDS. This looked for `source`
    // and `salesChannel`, and the orders list returns NEITHER — so every order on the page fell
    // through to the literal default and read "Web". Not a migration artefact: a box-office sale
    // and an AI-assisted one read "Web" too. The real column is `Order.purchase_source`, generated
    // from `misc.source`, already selected by orderQueries.ts and already varied in the data
    // (web, checkout-enter, box-office, dashboard-comp, mookh-discover-ai, mookh-v3-migration).
    //
    // Labelled through the SAME `SOURCE_LABELS` the channel-attribution report uses, so one source
    // is not "Box office" on one screen and "box-office" on another. Referenced at call time, not
    // module-evaluation time, so its later definition in this file is not a TDZ hazard.
    source: orderSourceLabel(order),
    // Status and kind AS STORED (constants/orders orderStatusWord is the one place they become a word).
    orderType: order.orderType || null,
    status: order.status || null,
    amountPaid: order.amountPaid ?? null,
    reservationExpiry: order.expiresAt ? formatTime(order.expiresAt) : null,
    // The code an order used. The order detail endpoint includes it as `promoCode`, the lists as
    // `orderPromoCode` (two aliases of the same association) — read both, or the lists show none.
    promo: firstPresent(textLabel(order.orderPromoCode), textLabel(order.promoCode), order.discountCode, '—'),
    // THE PERSON, THEN THE ID. `/api/orders/:id` already joins the creator and returns
    // `createdByUser: { id, name, email }` (orderRoutes.ts:519) — this read the raw `createdBy`
    // column instead and never looked at the join, so "Sold by" showed an operator's user id on
    // every order a signed-in staff member placed. The id stays as the last resort, where
    // prettyActor collapses it to "Staff" rather than printing it.
    operator: prettyActor(firstPresent(order.createdByUser?.name, order.createdByUser?.email, order.createdBy, order.operator, '—')),
    linkedTxn: firstPresent(paymentReferenceForOrder(order), '—'),
    riskFlags: [],
    created: formatTime(paymentCreatedAt(order)),
    // How long the money took — push to landing. `paymentSeconds` drives the chip's colour
    // threshold, `paymentDuration` is the words. Restored after being wrongly deleted: the fix
    // for a misleading number is to correct the number, not to remove the column.
    paymentSeconds: paymentDurationSeconds(order),
    paymentDuration: formatDuration(paymentDurationSeconds(order)),
    confirmed: paymentConfirmedAt(order) ? formatTime(paymentConfirmedAt(order)) : null,
    // HOW LONG THE PAYMENT TOOK — start (payer began) to confirmed (money landed), in seconds.
    // Null unless BOTH ends are real and the window is positive, so a row never shows a
    // duration invented from an order-creation fallback. `paymentSeconds` is the number for
    // sorting/thresholds; `paymentDuration` is the label the table renders.
    paymentSeconds: paymentDurationSeconds(order),
    paymentDuration: formatDuration(paymentDurationSeconds(order)),
    paymentMethod: paymentMethodForOrder(order),
    paymentProvider: paymentProviderForOrder(order),
    // Buyer INTENT, distinct from the CONFIRMED rail above. `order.paymentMethod`
    // is the real DB column — set once, at checkout, from the buyer's own
    // selection (card/mobilemoney/cash/mpesa/other) — never touched again.
    // `order.payCurrency` is the parallel field for currency: the buyer's
    // real checkout-time currency SELECTION, distinct from `currency` above
    // (paymentCurrencyForOrder — what they were actually settled/charged in,
    // sourced from fwData/the generated column). Neither was surfaced before;
    // an operator could only see the outcome, never what the buyer actually
    // asked for.
    intendedPaymentMethod: firstPresent(order.paymentMethod, null),
    intendedCurrency: firstPresent(order.payCurrency, null),
    // The backend ALREADY computes this for every row (`handleGetOrgOrders` — real
    // paid/remaining/next-due, per-checkpoint status, in the plan's frozen currency —
    // `installmentView()`, the same engine the buyer-facing success page reads) and
    // sends it on every order in the list response; this normalizer was simply
    // dropping it, so no back-office surface could ever show which orders are on a
    // payment plan or how far along they are. `null` for a normal (non-installment)
    // order — never fabricated.
    installments: order.installments || null,
    // A SPLIT is the same mechanism with the payer varying instead of the date — one order,
    // several people each paying a part (services/splitPayments.splitView, computed on every
    // list row beside installments). Carried the same way and null on an ordinary order, so
    // every surface that already knows how to show a payment plan can show this one too.
    split: order.split || null,
    backendBacked: Boolean(order.id || order.orderId),
    raw: order,
  };
};

// normalizeTransactionFromOrder / transactionFromOrder were REMOVED here.
//
// They synthesised a "transaction" out of an Order: an id scavenged from whatever ref could
// be found (usually the order code), a `settlement` of 'Pending' inferred from the status, a
// `recon: 'Matched'` asserted for any successful order, and a fee. Nothing in that came from
// a payment record, and it is what made the transaction detail page open on an ORDER uuid.
//
// A transaction is a PaymentAttempt. Read one with loadTransactionDetail(attemptId), or an
// order's real attempts with loadAttemptsForOrder(orderUuid). Neither invents a payment that
// does not exist: an order with no attempts resolves to none, and the caller says so.

// The REAL transaction row — one PaymentAttempt (a single charge on a rail), not an
// order. Carries the actual money movement's grain: its own status lifecycle, expected
// vs SETTLED amount, gateway reference, sequence (installment slice / retry number), and
// the order/customer/event it belongs to. Fed by GET /api/transactions.
// A PAYMENT ATTEMPT, AS THE LEDGER RETURNS IT — every column under its model name (transactionRoutes),
// the order and event under theirs (`order`, `event`). Nothing is renamed here; only what is DERIVED
// is added, under its own name: who the buyer and payer are in words, and how long the money took.
const normalizeTransactionFromAttempt = (row) => {
  const r = row || {};
  const window = attemptWindow(r);
  return {
    ...r,
    customer: [r.order?.firstName, r.order?.lastName].filter(Boolean).join(' ') || r.order?.email || r.order?.phone || '',
    payerName: [r.payerFirstName, r.payerLastName].filter(Boolean).join(' ') || null,
    // Seconds from the push to the money landing, and the same value in words.
    windowSeconds: window,
    windowText: window == null ? null : window < 1 ? 'instantly' : `in ${formatDuration(window)}`,
  };
};

const normalizeUser = (user, index = 0) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || firstPresent(user.name, user.email, user.phone, `User ${index + 1}`);
  const role = titleCase(firstPresent(user.role, user.userType, user.type, 'Customer'));
  return {
    ...user,
    id: String(firstPresent(user.id, user.userId, `user-${index + 1}`)),
    name,
    email: firstPresent(user.email, ''),
    phone: firstPresent(user.phone, user.phoneNumber, ''),
    role,
    status: user.banned || user.suspended ? 'Suspended' : titleCase(firstPresent(user.status, 'Active')),
    kyc: titleCase(firstPresent(user.kycStatus, user.kyc, '—')),
    lastActive: user.updatedAt ? formatTime(user.updatedAt) : '—',
    orders: number(firstPresent(user.ordersCount, user.orderCount, user.orders)),
    spend: number(firstPresent(user.totalSpend, user.spend)),
    risk: titleCase(firstPresent(user.riskLevel, 'Low')),
    raw: user,
  };
};

const normalizeCustomer = (user, index = 0) => {
  const base = normalizeUser(user, index);
  return {
    ...base,
    type: base.role,
    // '' when the record does not say, never a guessed 'Uganda'. The platform sells in at least
    // Uganda and Kenya (venues and orders both prove it), so a default here labels every
    // country-less customer as Ugandan and the console has no way to tell the difference between
    // a fact and the fallback. Same rule as currency: read it from the data or show nothing.
    country: firstPresent(user.country, ''),
    city: firstPresent(user.city, ''),
    signupSource: firstPresent(user.signupSource, user.source, 'Backend'),
    joined: formatDate(user.createdAt),
    lastActive: user.updatedAt ? formatDate(user.updatedAt) : '—',
    riskLevel: base.risk,
    totalSpend: base.spend,
    avgOrder: base.orders ? Math.round(base.spend / base.orders) : 0,
    devices: number(user.devices),
    fraudFlags: number(user.fraudFlags),
    refunds: number(user.refunds),
    disputes: number(user.disputes),
    transfers: number(user.transfers),
    promoUses: number(user.promoUses),
    marketingOptIn: Boolean(user.marketingOptIn),
    channelPref: firstPresent(user.channelPref, '—'),
    railPref: firstPresent(user.railPref, '—'),
    favoriteCategories: [],
    paymentMethods: [],
  };
};

// When they asked to hear before the event — the buyer app's own options.
const REMINDER_LABEL = { '30m_before': '30 min before', '1h_before': '1 hour before', '1d_before': '1 day before', '1w_before': '1 week before' };
// Sign-up form keys that are already shown as their own field.
const SUBSCRIPTION_OWN_KEYS = new Set(['fullName', 'source', 'utmSource', 'origin', 'referer']);
// Where a sign-up was made, as a reader says it: the page (host + path) from the server-recorded
// Referer, else the site from its Origin.
const signupPlace = (metadata) => {
  for (const raw of [metadata.referer, metadata.origin]) {
    if (!raw) continue;
    try { const u = new URL(raw); return `${u.host.replace(/^www\./, '')}${u.pathname === '/' ? '' : u.pathname}`; } catch { /* not a URL */ }
  }
  return null;
};

const normalizeSubscription = (row, index = 0) => {
  const metadata = row.metadata || {};
  const preferences = row.preferences || {};
  const channels = Array.isArray(row.reminderChannels) ? row.reminderChannels : [];
  return {
    ...row,
    id: String(firstPresent(row.id, `subscription-${index + 1}`)),
    // The sign-up form's name (metadata.fullName) — without it every named subscriber read as
    // their email address.
    name: firstPresent(metadata.fullName, row.name, row.customerName, row.email, row.phone, `Subscriber ${index + 1}`),
    email: firstPresent(row.email, '—'),
    phone: firstPresent(row.phone, '—'),
    // How they asked to be reached, and when — what a message to them has to honour.
    channels: channels.map((c) => ({ whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email', push: 'Push' }[String(c).toLowerCase()] || titleCase(c))),
    reminder: REMINDER_LABEL[preferences.reminderFrequency] || null,
    promotions: preferences.promotions === true,
    // A campaign's own label when the caller sent one (gandavibes-insider, whatsapp-agent);
    // otherwise the page it was made on, which the server records from the request since
    // 2026-09-30. Older rows have neither — 137,965 of prod's 138,026 — and read as nothing.
    source: firstPresent(metadata.utmSource, metadata.source, signupPlace(metadata), null),
    // Whatever else the sign-up form asked (city, instagram…), as it was answered.
    answers: Object.fromEntries(Object.entries(metadata).filter(([k, v]) => !SUBSCRIPTION_OWN_KEYS.has(k) && v != null && v !== '')),
    subscribedAt: row.createdAt || null,
    subscribed: formatTime(row.createdAt),
    // The first paid order on this event by the same phone number or email (backend
    // firstPurchasesOf) — { orderId, orderRef, at } — or null.
    bought: row.bought || null,
    changedAt: row.updatedAt || null,
    state: titleCase(firstPresent(row.status, 'active')).toLowerCase(),
    status: titleCase(firstPresent(row.status, 'active')),
    eventId: firstPresent(row.eventId, ''),
    preferences,
    raw: row,
  };
};

const normalizeWebhookLog = (row, index = 0) => {
  const payload = row.payload && typeof row.payload === 'object' ? row.payload : {};
  const rawStatus = String(firstPresent(row.status, 'received')).toLowerCase();
  const status = rawStatus === 'processed' ? 'Processed' : rawStatus === 'error' ? 'Error' : rawStatus === 'received' ? 'Received' : titleCase(rawStatus);
  return {
    ...row,
    id: String(firstPresent(row.id, `webhook-log-${index + 1}`)),
    rawId: row.id,
    provider: titleCase(firstPresent(row.provider, 'Webhook')),
    providerKey: String(firstPresent(row.provider, '')).toLowerCase(),
    eventType: firstPresent(row.eventType, payload.eventType, payload.type, payload.entry?.[0]?.changes?.[0]?.field, 'webhook.received'),
    eventRef: firstPresent(payload.id, payload.data?.id, payload.entry?.[0]?.id, payload.orderId, payload.externalId, '—'),
    status,
    rawStatus,
    error: firstPresent(row.error, ''),
    received: formatTime(row.createdAt),
    processed: row.processedAt ? formatTime(row.processedAt) : '—',
    payload,
    raw: row,
  };
};

// Standing is a short label derived ONLY from real operational signals
// (KYC · event history · payout health). It is deliberately NOT a 0–100
// "trust score" — inventing a composite without a measured model is worse
// than showing the parts (NewEventWizard used to paint every org as 75/Low).
export const deriveOrgStanding = ({
  kyc,
  payoutHealth,
  activeEvents = 0,
  completedEvents = 0,
  totalEvents,
} = {}) => {
  const kycL = String(kyc || '').toLowerCase();
  const pay = String(payoutHealth || 'Unknown').toLowerCase();
  const history = Number(
    totalEvents != null
      ? totalEvents
      : Number(activeEvents || 0) + Number(completedEvents || 0),
  );

  if (/fail|reject/.test(kycL)) {
    return { label: 'Blocked', tone: 'red', detail: 'KYC failed — do not open sales' };
  }
  if (pay === 'frozen') {
    return { label: 'Frozen', tone: 'red', detail: 'Payouts frozen' };
  }
  if (pay === 'watching') {
    return { label: 'Watch', tone: 'amber', detail: 'Open payout issues' };
  }
  if (/verif/.test(kycL) && (pay === 'healthy' || pay === 'unknown')) {
    return {
      label: 'Good standing',
      tone: 'green',
      detail: history > 0
        ? `${history} event${history === 1 ? '' : 's'} on record`
        : 'Verified · no payout issues',
    };
  }
  if (history === 0 && (/pend|await|submit|known/.test(kycL) || !kycL)) {
    return { label: 'New', tone: 'neutral', detail: 'No track record yet' };
  }
  if (/pend|await|submit/.test(kycL)) {
    return { label: 'Pending KYC', tone: 'amber', detail: 'Can draft; sales need verification' };
  }
  if (pay === 'healthy' && history > 0) {
    return {
      label: 'Active',
      tone: 'green',
      detail: `${history} event${history === 1 ? '' : 's'} · payouts healthy`,
    };
  }
  return { label: 'Review', tone: 'amber', detail: 'Check KYC and payouts before sales' };
};

const normalizeOrg = (org, index = 0) => {
  const kyc = titleCase(firstPresent(org.kycStatus, org.kyc, 'Pending'));
  const activeEvents = number(firstPresent(org.activeEvents), 0);
  const completedEvents = number(firstPresent(org.completedEvents), 0);
  const totalEvents = number(firstPresent(org.totalEvents, activeEvents + completedEvents), 0);
  // Prefer backend truth when present; never invent "Healthy" for unknown rows.
  const payoutHealth = firstPresent(org.payoutHealth, 'Unknown');
  const standing = deriveOrgStanding({
    kyc,
    payoutHealth,
    activeEvents,
    completedEvents,
    totalEvents,
  });
  return {
    ...org,
    id: String(firstPresent(org.id, org.organizationId, `org-${index + 1}`)),
    name: firstPresent(org.name, org.legalName, org.slug, `Organization ${index + 1}`),
    legalName: firstPresent(org.legalName, org.name, ''),
    logo: firstPresent(org.logo, ''),
    tin: firstPresent(org.tin, org.taxId, '—'),
    registration: firstPresent(org.registrationNumber, org.registration, '—'),
    // The merchant's primary contact (its own contact, else its owner on the platform; never staff),
    // as GET /api/organizations sends it (services/merchantReview merchantFacts).
    contact: firstPresent(org.primaryContact?.name, org.contactName, org.ownerName, org.primaryContact?.email, org.email, '—'),
    email: firstPresent(org.primaryContact?.email, org.email, ''),
    phone: firstPresent(org.primaryContact?.phone, org.phone, ''),
    settlementAccount: firstPresent(org.settlementAccount, null),
    address: firstPresent(org.address, ''),
    country: firstPresent(org.country, ''),   // never a guessed 'Uganda' — see normalizeCustomer
    onboarded: org.createdAt ? formatDate(org.createdAt) : '',
    // The review's stage, which `kyc` folds together (submitted and in review are both "Pending"), and
    // whether the platform suspended the merchant: the backend's `standing`, kept apart from this
    // console's own derived `standing` below.
    reviewStage: firstPresent(org.verificationStage, null),
    suspended: org.standing === 'suspended',
    // kyc/activeEvents/completedEvents/payoutHealth are real — GET /api/organizations
    // joins OrganizationProfile.verificationStatus + EventPage counts + Payout.status
    // (organizationRoutes.ts). Standing is derived from those only.
    kyc,
    tier: titleCase(firstPresent(org.tier, org.category, 'Standard')),
    // No measured trust model exists in tkt-backend. Pass through only if a
    // real field ever arrives — never default to 75 (that painted every org
    // identically in New Event + organizer risk tiles).
    trustScore: org.trustScore != null && org.trustScore !== ''
      ? number(org.trustScore)
      : (org.score != null && org.score !== '' ? number(org.score) : null),
    standing,
    standingLabel: standing.label,
    standingTone: standing.tone,
    standingDetail: standing.detail,
    activeEvents,
    completedEvents,
    totalEvents,
    grossLifetime: number(firstPresent(org.grossLifetime, org.totalSales)),
    netSettled: number(firstPresent(org.netSettled, org.totalPayouts)),
    pendingReserve: number(org.pendingReserve),
    rollingReserve: number(org.rollingReserve),
    payoutHealth,
    // No fraud model either — leave null so UIs show payouts/standing instead
    // of a hardcoded "Low" that implied risk had been assessed.
    fraudRisk: firstPresent(org.fraudRisk, null),
    teamUsers: [],
    backendBacked: true,
    raw: org,
  };
};

const normalizeOrganizationProfile = (profile, index = 0) => {
  const details = parseMaybeJson(profile.description);
  const settlement = details.settlement || {};
  const primaryContact = details.primaryContact || {};
  const financeContact = details.financeContact || {};
  const documents = details.documents || {};
  const docsComplete = Boolean(documents.certificateOfIncorporation && documents.directorIds);
  const kyc = docsComplete ? 'Pending verification' : 'Awaiting docs';
  const payoutHealth = 'Unknown';
  const standing = deriveOrgStanding({ kyc, payoutHealth, activeEvents: 0, completedEvents: 0, totalEvents: 0 });
  return {
    ...profile,
    id: String(firstPresent(profile.id, `organization-profile-${index + 1}`)),
    organizationId: firstPresent(profile.organizationId, null),
    name: firstPresent(profile.organizerName, details.tradingName, details.legalName, `Organizer profile ${index + 1}`),
    legalName: firstPresent(details.legalName, profile.organizerName, ''),
    tin: firstPresent(details.tin, '—'),
    registration: firstPresent(details.registrationNumber, '—'),
    contact: firstPresent(primaryContact.name, primaryContact.email, financeContact.name, financeContact.email, '—'),
    email: firstPresent(primaryContact.email, financeContact.email, ''),
    phone: firstPresent(primaryContact.phone, financeContact.phone, ''),
    address: '',
    country: firstPresent(details.country, '—'),
    kyc,
    tier: firstPresent(settlement.tier, 'Profile'),
    trustScore: null,
    standing,
    standingLabel: standing.label,
    standingTone: standing.tone,
    standingDetail: standing.detail,
    activeEvents: 0,
    completedEvents: 0,
    totalEvents: 0,
    grossLifetime: 0,
    netSettled: 0,
    pendingReserve: 0,
    rollingReserve: 0,
    payoutHealth,
    fraudRisk: null,
    accountMgr: firstPresent(details.accountManager, ''),
    website: firstPresent(profile.website, ''),
    onboarded: formatDate(firstPresent(profile.createdAt, profile.updatedAt)),
    teamUsers: [],
    backendBacked: true,
    profileBacked: true,
    raw: profile,
  };
};

const deriveOrganizationsFromEvents = (events = []) => {
  const byOrg = new Map();
  events.forEach((event) => {
    const source = event.organization || event.Organization || {};
    const organizationId = firstPresent(source.id, event.organizationId, event.organisationId, `event-org-${event.id}`);
    if (!organizationId) return;
    const metadata = parseMaybeJson(source.metadata);
    const existing = byOrg.get(organizationId) || {
      id: String(organizationId),
      name: firstPresent(source.name, event.organizationName, event.organizerName, event.organizer, 'Unassigned organizer'),
      legalName: firstPresent(source.name, ''),
      slug: firstPresent(source.slug, ''),
      logo: firstPresent(source.logo, ''),
      email: firstPresent(metadata.email, source.email, ''),
      phone: firstPresent(metadata.phone, source.phone, ''),
      website: firstPresent(metadata.website, source.website, ''),
      contact: firstPresent(metadata.email, metadata.phone, source.email, source.phone, '—'),
      country: firstPresent(metadata.country, '—'),
      kyc: 'Known via events',
      tier: 'Event-derived',
      trustScore: null,
      activeEvents: 0,
      completedEvents: 0,
      totalEvents: 0,
      grossLifetime: 0,
      netSettled: 0,
      pendingReserve: 0,
      rollingReserve: 0,
      payoutHealth: 'Unknown',
      fraudRisk: null,
      teamUsers: [],
      backendBacked: true,
      derivedFromEvents: true,
      raw: source,
      eventIds: [],
    };
    const status = statusForEvent(event);
    const next = {
      ...existing,
      eventIds: [...existing.eventIds, event.id].filter(Boolean),
      activeEvents: existing.activeEvents + (['Upcoming', 'Live'].includes(status) ? 1 : 0),
      completedEvents: existing.completedEvents + (status === 'Completed' ? 1 : 0),
      totalEvents: existing.totalEvents + 1,
      grossLifetime: existing.grossLifetime + number(firstPresent(event.grossSales, event.totalSales, event.revenue, event.stats?.gross, event.sales)),
    };
    byOrg.set(organizationId, next);
  });
  return Array.from(byOrg.values())
    .map((o) => {
      const standing = deriveOrgStanding(o);
      return {
        ...o,
        standing,
        standingLabel: standing.label,
        standingTone: standing.tone,
        standingDetail: standing.detail,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const REJECTION_MARK = ' | Rejection reason: ';
const splitPayoutDescription = (description) => {
  const text = String(description || '');
  const at = text.indexOf(REJECTION_MARK);
  const note = (at >= 0 ? text.slice(0, at) : text).trim();
  return { notes: note, rejectionReason: at >= 0 ? text.slice(at + REJECTION_MARK.length).trim() : '' };
};

export const normalizePayout = (payout, index = 0) => {
  const gross = number(firstPresent(payout.gross, payout.amount, payout.totalAmount));
  const fees = number(firstPresent(payout.fees, payout.fee, payout.platformFee));
  const refunds = number(firstPresent(payout.refunds, payout.refundAmount));
  const rawStatus = String(firstPresent(payout.status, payout.stage, 'Pending approval')).toLowerCase();
  const stage = rawStatus.includes('paid') || rawStatus.includes('fulfill') || rawStatus.includes('settled')
    ? 'Paid'
    // REJECTED IS NOT FAILED. A rejection is finance declining a REQUEST — no money was ever
    // sent, so there is nothing to recover; the detail page's `isFailed` branch was painting it
    // "Failed · Recovery required" in red, which reads as a disbursement that went wrong and
    // sends an operator hunting for money that never left. A failure is a transfer that was
    // attempted and did not land. Two different states, two different next actions.
    : rawStatus.includes('reject')
      ? 'Rejected'
    : rawStatus.includes('fail')
      ? 'Failed'
      : rawStatus.includes('partial')
        ? 'Partial'
        : rawStatus.includes('hold')
          ? 'Held'
          : rawStatus.includes('process') || rawStatus.includes('approved')
            ? 'Approved'
            : 'Pending approval';
  return {
    ...payout,
    id: String(firstPresent(payout.id, payout.payoutId, `payout-${index + 1}`)),
    displayId: String(firstPresent(payout.reference, payout.transactionId, payout.event?.slug, payout.event?.title, payout.organization?.name, payout.organizerName, `Payout ${index + 1}`)),
    organizer: firstPresent(payout.organization?.name, payout.organizerName, payout.organizationName, 'Unknown organizer'),
    // A NAME IS NOT A LINK. The route resolves an organizer by id (entityId → routeId/id/code), so
    // handing a nav a bare display string silently degrades to the organizers LIST — a link that
    // looks live and goes nowhere. The include already returns organization {id, name}; this only
    // ever discarded it. Null when genuinely absent, so the surface can decline to render a link
    // rather than render a dead one.
    organizerId: firstPresent(payout.organization?.id, payout.organizationId, payout.paymentAccount?.organizationId, null),
    event: firstPresent(payout.event?.title, payout.eventName, 'Multiple events'),
    // The event is the payout's account — every reader needs its id, not just its title.
    eventId: firstPresent(payout.eventId, payout.event?.id, null),
    gross,
    fees,
    refunds,
    net: number(firstPresent(payout.net, payout.netAmount), gross - fees - refunds),
    amount: number(firstPresent(payout.amount, payout.net, payout.netAmount), gross - fees - refunds),
    // Payout.currency is now a real column and comes FIRST — it records what the payout is in.
    // The destination account's own currency remains the fallback for rows written before that
    // column existed (and is the same evidence the backfill migration uses, so the two agree).
    // The old note here said the Payout row has no currency column; that stopped being true.
    currency: firstPresent(payout.currency, payout.paymentAccount?.accountDetails?.currency, 'UGX'),
    // READ THE SHAPE THE API ACTUALLY RETURNS. The payout include selects
    // paymentAccount { id, name, type, accountDetails, isDefault, organizationId } — there is
    // no `accountName`, no `accountNumber` and no `provider` at the top level, they live inside
    // `accountDetails`. So both of these read undefined on EVERY payout and fell to their
    // defaults: a real "Mobile money · +254798404068" destination rendered as "—", and a
    // mobile-money payout rendered as rail "Bank" with a bank icon. formatPaymentAccount is the
    // same resolver the payment-accounts list uses, so one account reads identically everywhere.
    dest: firstPresent(payout.destination, payout.paymentAccount ? formatPaymentAccount(payout.paymentAccount) : '', payout.bankName, '—'),
    // Real holder + bank off the linked account, same two-convention rule as
    // accountHolderOf: explicit details holder wins, else paymentAccount.name IS
    // the holder (sellers-dashboard convention). destNickname is set only when
    // both exist and differ — surfaces show the holder, never the nickname.
    destAccountName: payout.paymentAccount ? accountHolderOf(payout.paymentAccount, payout.paymentAccount.accountDetails || {}) : '',
    destBank: firstPresent(payout.paymentAccount ? detailsBank(payout.paymentAccount.accountDetails || {}) : '', payout.bankName, ''),
    destSwift: payout.paymentAccount ? detailsSwift(payout.paymentAccount.accountDetails || {}) : '',
    destNickname: payout.paymentAccount ? accountNicknameOf(payout.paymentAccount, payout.paymentAccount.accountDetails || {}) : '',
    // The rail is the account's TYPE (mobile_money | bank_account | …), titled for display.
    // `payout.paymentMethod` stays first — InitiatePayoutModal sends the account type under
    // that name — and 'Bank' is no longer an invented default for an unknown rail.
    rail: titleCase(String(firstPresent(payout.paymentMethod, payout.paymentAccount?.type, '') || '').replace(/_/g, ' ')) || '—',
    status: stage,
    stage,
    risk: firstPresent(payout.risk, '—'),
    requested: formatDate(payout.createdAt),
    submitted: formatDate(firstPresent(payout.requestedAt, payout.createdAt)),
    sla: firstPresent(payout.sla, payout.scheduledDate ? formatDate(payout.scheduledDate) : '—'),
    // The requester as a PERSON. `createdBy` is a better-auth user id, which shortId leaves
    // untouched (it only shortens UUIDs) — so the audit trail printed the raw 32-char id.
    createdByName: firstPresent(payout.creator?.name, payout.creator?.email, ''),
    // The real, unmasked destination number — `dest` is the masked display form, and the
    // detail page's Destination card exists so an operator can verify/copy the actual account
    // they are about to pay. Same intent normalizePaymentAccount already states.
    destAccountNumber: firstPresent(payout.paymentAccount?.accountDetails?.accountNumber, payout.paymentAccount?.accountDetails?.phoneNumber, payout.paymentAccount?.accountDetails?.walletAddress, ''),
    approver: firstPresent(payout.approvedBy, payout.updatedBy, '—'),
    approvedBy: firstPresent(payout.approvedBy, '—'),
    // THE PROVIDER'S reference for the transfer — only `transactionId`, which is what a bank or
    // MoMo gives back when money actually moves. It used to fall back to `payout.reference`, our
    // OWN generated payout number, so the "Bank / MoMo ref" row displayed PAY-2026… on a payout
    // nobody had disbursed — and the detail page's reconciliation row read that as evidence.
    proof: firstPresent(payout.transactionId),
    transactionId: firstPresent(payout.transactionId),
    // THE REQUESTER'S WORDS, and nothing else. `description` is what the organizer typed when
    // asking; the reject route appends " | Rejection reason: …" to it (there is no reason column),
    // so read the two apart. The fallback to our own reference / "Payout request" put words in
    // the organizer's mouth.
    ...splitPayoutDescription(payout.description),
    backendBacked: true,
    raw: payout,
  };
};

// Every key ever written into accountDetails, by every writer:
// - back-office: bankName/accountNumber/accountName/swift/branch/bankCode
// - sellers-dashboard legacy BankForm (buildBankPayload): bankName/accountNumber/
//   branch/currency/status — holder ONLY in top-level `name`, never in details
// - sellers-dashboard wizard: bank_name/bank_code/branch(+code)/account_name/
//   swift_code (snake_case; swift_code+bank_code dropped by its own builder)
// - tkt-new-ui UG form (local-only today): bank/accountName/branch/swift
// The reader below accepts ALL of them — the backend is a JSONB passthrough
// (z.record(z.any())), so normalizing writers is a backend migration, not
// something this console can enforce.
const detailsHolder = (details) => { const v = firstPresent(details.accountName, details.accountHolder, details.accountHolderName, ''); return typeof v === 'string' ? v.trim() : v; };
/**
 * TRIMMED AT THE DOOR. `accountDetails` is free-form jsonb filled in by hand, so values arrive
 * carrying whatever whitespace was typed — a live destination holds "Absa bank " and "Kansanga ".
 * A trailing space is invisible on screen and is NOT invisible in the CSV/XML handed to a bank,
 * where a destination field is matched. The write path trims on save, but rows written before
 * that rule keep their grit until somebody re-saves them; reading through one accessor per fact
 * means no surface has to remember, and none of them can disagree.
 */
const clean = (v) => (typeof v === 'string' ? v.trim() : v);
const detailsBank = (details) => clean(firstPresent(details.bankName, details.bank_name, details.bank, details.provider, details.network, details.cryptoNetwork, ''));
const detailsNumber = (details) => clean(firstPresent(details.accountNumber, details.account_number, details.phoneNumber, details.mobile_number, details.walletAddress, ''));
const detailsSwift = (details) => clean(firstPresent(details.swift, details.swiftCode, details.swift_code, details.bic, ''));
const detailsBranch = (details) => clean(firstPresent(details.branch, details.branchCode, details.branch_code, ''));
const detailsBankCode = (details) => clean(firstPresent(details.bankCode, details.bank_code, ''));
// Holder resolution across writer conventions: an explicit details holder wins
// (back-office/UG convention: name = nickname); otherwise top-level `name` IS
// the holder (sellers-dashboard convention: buildBankPayload puts account_name
// in `name` and stores no holder in details). Never render "Holder not recorded"
// for a KE row whose holder sits in `name`.
const accountHolderOf = (account, details) => clean(firstPresent(detailsHolder(details), account.name, ''));
const accountNicknameOf = (account, details) => {
  const holder = detailsHolder(details);
  // `account.name` is a top-level column, not a detail — it was the one hand-typed string still
  // arriving untrimmed, and it surfaces in quotes ('Saved as "Business account "') where the
  // trailing space is the most visible thing on the line.
  const nickname = clean(account.name);
  return holder && nickname && nickname !== holder ? nickname : '';
};

const formatPaymentAccount = (account) => {
  const details = account.accountDetails || {};
  const bank = detailsBank(details);
  const holder = accountHolderOf(account, details);
  const accountNumber = detailsNumber(details);
  /**
   * NOT MASKED. Masking protects a value from somebody who is not the one acting on it — a card
   * number on a receipt, an IBAN on a shared screen. Every reader of this string is a platform
   * finance operator deciding whether to send money TO this account: the payout header, the
   * payouts list, the destination picker in Initiate payout, the bank-file wizard. Hiding the
   * number from the person about to pay it is the one audience it cannot be hidden from.
   *
   * It was never a policy either, only an inconsistency — the Destination card on the payout page
   * already prints the full number, and says why in its own comment: "this card is where an
   * operator verifies the account they are about to send money to". Two renderings of one fact,
   * one of them unusable, is how "••6422" ended up in the CSV and XML handed to a bank.
   */
  // Holder first: "Daniel Mwesigwa · Stanbic · 0142067890". The nickname is never the
  // display — it rides secondary wherever a surface shows this string.
  return [holder, bank, accountNumber].filter(Boolean).join(' · ') || [bank, accountNumber].filter(Boolean).join(' ') || firstPresent(account.name, account.description, 'Payment account');
};

export const normalizePaymentAccount = (account, index = 0) => ({
  ...account,
  id: String(firstPresent(account.id, account.paymentAccountId, `payment-account-${index + 1}`)),
  name: firstPresent(account.name, `Payment account ${index + 1}`),
  description: firstPresent(account.description, ''),
  type: firstPresent(account.type, 'bank_account'),
  label: `${formatPaymentAccount(account)} · ${firstPresent(account.name, `Payment account ${index + 1}`)}`,
  destination: formatPaymentAccount(account),
  // Real, unmasked details so the operator can actually copy/verify an account
  // when paying an organizer (a masked "••4622" is useless to copy).
  provider: detailsBank(account.accountDetails || {}),
  accountNumber: detailsNumber(account.accountDetails || {}),
  // The REAL holder, both conventions: explicit details holder first, else the
  // top-level `name` (sellers-dashboard stores the holder there). `nickname` is
  // set ONLY when both exist and differ — surfaces show holderName primary.
  holderName: accountHolderOf(account, account.accountDetails || {}),
  accountName: accountHolderOf(account, account.accountDetails || {}),
  nickname: accountNicknameOf(account, account.accountDetails || {}),
  swift: detailsSwift(account.accountDetails || {}),
  branch: detailsBranch(account.accountDetails || {}),
  bankCode: detailsBankCode(account.accountDetails || {}),
  organizationId: firstPresent(account.organizationId, ''),
  organizationName: firstPresent(account.organization?.name, account.Organization?.name, account.organizationName, ''),
  currency: firstPresent(account.accountDetails?.currency, account.currency, 'UGX'),
  accountStatus: firstPresent(account.accountDetails?.status, account.status, 'active'),
  isDefault: Boolean(account.isDefault),
  raw: account,
});

const inferVenueCode = (venue, index = 0) => {
  const value = firstPresent(venue.code, venue.shortName, venue.slug);
  if (value) return String(value).toUpperCase();
  const name = firstPresent(venue.name, `Venue ${index + 1}`);
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || `VEN-${String(index + 1).padStart(3, '0')}`;
};

// A venue's kind is an enum label; how it READS is a label lookup, and whether it is under a
// roof follows from the kind (Venue has no indoor column). Both live with the vocabulary in
// constants/venues.js so the picker, the filter and this reader cannot drift apart.
const normalizedVenueStatus = (venue) => {
  const raw = firstPresent(venue.status, venue.venueStatus, venue.isActive === false ? 'Inactive' : 'Active');
  if (/draft/i.test(raw)) return 'Inactive';
  return titleCase(raw, 'Active');
};

export const normalizeVenue = (venue, index = 0) => {
  // `metadata.venueType` is the legacy stash (see constants/venues.js) — read it so rows written
  // before the column had a working writer still say what they are.
  const venueType = firstPresent(venue.venueType, venue.metadata?.venueType, venue.type) || null;
  const type = venueModelTypeLabel(venue.modelTypeId) || venueTypeLabel(venueType) || titleCase(venueType || '', 'Venue');
  const gateRows = Array.isArray(venue.gates) ? venue.gates : Array.isArray(venue.entrances) ? venue.entrances : [];
  const sectionRows = Array.isArray(venue.sections) ? venue.sections : [];
  const seatPlanRows = Array.isArray(venue.seatPlans) ? venue.seatPlans.map(normalizeVenueSeatPlan) : [];
  // v1 /api/venues exposes entrancesCount/sectionsCount/upcoming+pastEventsCount;
  // v2 used gateCount/sectionCount/activeEventCount. Read both (v1 first where it differs).
  const gateCount = firstPresent(venue.entrancesCount, venue.externalGateCount, venue.gateCount, venue.gatesCount, gateRows.length);
  const sectionCount = firstPresent(venue.sectionsCount, venue.sectionCount, sectionRows.length);
  const seatingPlanCount = firstPresent(venue.seatingPlanCount, venue.seatPlanCount, venue.planCount, seatPlanRows.length);
  const activeEventCount = firstPresent(venue.upcomingEventsCount, venue.activeEventCount, venue.activeEvents, venue.eventsCount, venue.events);
  return {
    ...venue,
    id: String(firstPresent(venue.id, venue.venueId, `venue-${index + 1}`)),
    name: firstPresent(venue.name, `Venue ${index + 1}`),
    code: inferVenueCode(venue, index),
    city: firstPresent(venue.city, ''),
    country: firstPresent(venue.country, ''),
    // The street line itself, kept alongside the derived `location`. The edit form needs the
    // column, not the reading of it — see EditVenueModal.
    address: firstPresent(venue.address, ''),
    location: firstPresent(venue.location, [venue.city, venue.country].filter(Boolean).join(', '), venue.address, '—'),
    type,
    // The enum value itself, for any surface that has to WRITE it back (the edit form, the
    // wizard). `type` is the reading; `venueType` is the fact.
    venueType,
    indoor: typeof venue.indoor === 'boolean' ? venue.indoor : venueTypeIsIndoor(venueType),
    capacity: number(firstPresent(venue.capacity, venue.totalSeats, venue.maxCapacity)),
    status: normalizedVenueStatus(venue),
    // null, not a sentence — the CONSUMER decides how an absent description reads, and
    // "No venue description returned yet" described the HTTP response, not the venue.
    // 9 of 38 venues on prod have one; the other 29 simply haven't had one written.
    description: firstPresent(venue.description, venue.metadata?.description, venue.operationalNotes),
    // null, not formatDate()'s 'TBD' — "to be determined" is a statement about a FUTURE
    // date, and when a venue record was created is not pending. The consumer renders the
    // absence; see VenueDetailPage.
    onboarded: (() => { const t = firstPresent(venue.createdAt, venue.lastUpdated, venue.updatedAt); return t ? formatDate(t) : null; })(),
    // `operator` is GONE. There is no operator on a Venue — not a column, not an
    // association, not a field any route returns (checked the model and prod's
    // information_schema: Venue has createdBy and operationalNotes, and nothing else
    // remotely like it). So this read `'Not returned'` for every venue that has ever
    // existed, on every deployment, and always would have. A row that can only ever say
    // "no" is not an honest gap, it's furniture — the fix is to delete it, not reword it.
    // The one true "who" on a venue is createdBy (13/38 on prod): who typed the record in.
    // That belongs in the record's own provenance, not billed as the venue's operator.
    createdBy: firstPresent(venue.createdBy, venue.metadata?.createdBy),
    gateCount: number(gateCount),
    sectionCount: number(sectionCount),
    seatingPlanCount: number(seatingPlanCount),
    zoneCount: number(firstPresent(venue.zoneCount, venue.zonesCount)),
    contingentCount: number(firstPresent(venue.contingentCount, venue.contingentsCount)),
    activeEventCount: number(activeEventCount),
    seatingModel: sectionCount ? 'Seated' : firstPresent(venue.seatingModel, venue.model, '—'),
    backendBacked: true,
    gates: gateRows,
    sections: sectionRows,
    seatPlans: seatPlanRows,
    access: venue.isActive === false ? 'Offline' : 'Online',
    updated: venue.updatedAt ? formatTime(venue.updatedAt) : '—',
    devices: arrayCount(firstPresent(venue.scanDevices, venue.devices, venue.deviceCount)),
    upcomingEventsCount: number(firstPresent(venue.upcomingEventsCount, activeEventCount)),
    pastEventsCount: number(venue.pastEventsCount),
    recentActivity: Array.isArray(venue.recentActivity) ? venue.recentActivity : [],
    raw: venue,
  };
};

const normalizeVenueGate = (gate, index = 0) => {
  const type = String(firstPresent(gate.type, gate.gateType, '')).toLowerCase();
  const status = firstPresent(gate.status, gate.gateStatus, gate.isActive === false ? 'Inactive' : 'Active');
  const internalGates = Array.isArray(gate.internalGates) ? gate.internalGates : [];
  return {
    ...gate,
    id: String(firstPresent(gate.id, gate.gateId, `gate-${index + 1}`)),
    name: firstPresent(gate.name, `Gate ${index + 1}`),
    code: firstPresent(gate.code, gate.shortName, `G${index + 1}`),
    // Recognize both the backend's reserved-seating vocabulary ('internal') and
    // the venue-builder's UI value ('Indoor gate') so indoor gates created via
    // AddVenueModal aren't mis-categorized as outdoor in the venue detail.
    type: (type.includes('internal') || type.includes('indoor')) ? 'Indoor gate' : 'Outdoor gate',
    capacity: number(firstPresent(gate.capacity, gate.capacityPerHour, gate.throughputPerHour)),
    direction: titleCase(firstPresent(gate.direction, gate.orientation, '—')),
    maps: internalGates.map((item) => String(item.id)).filter(Boolean),
    sections: Array.isArray(gate.sections) ? gate.sections : Array.isArray(gate.sectionIds) ? gate.sectionIds : [],
    zones: Array.isArray(gate.zones) ? gate.zones : Array.isArray(gate.zoneIds) ? gate.zoneIds : [],
    active: /active/i.test(status),
    status: titleCase(status, 'Active'),
    raw: gate,
  };
};

// A "seat plan" in the back-office IS a seating template (GET /api/seating-templates?venueId=).
// Templates carry { sections:[{ type, rows:[{capacity}] }], isActive } but NOT totalCapacity/
// status/gateCount/version — so derive capacity + status from the materialized sections/rows
// instead of showing 0 / always-"Draft". Gates live on the venue, not the template (honest 0).
const normalizeVenueSeatPlan = (plan, index = 0) => {
  const tmplSections = Array.isArray(plan.sections) ? plan.sections
    : Array.isArray(plan.template?.sections) ? plan.template.sections : [];
  const rowsCap = (s) => (Array.isArray(s.rows) ? s.rows : []).reduce((a, r) => a + (Number(r.capacity) || 0), 0);
  const computedTotal = tmplSections.reduce((a, s) => a + rowsCap(s), 0);
  const computedStanding = tmplSections.filter((s) => /stand/i.test(String(s.type || ''))).reduce((a, s) => a + rowsCap(s), 0);
  // Tables are counted from the ROWS, not from the section's type — a row with
  // kind:'table' IS a table, and a section can hold both (a hospitality suite
  // beside a seated tier). Reading the section label instead would miss those
  // and would call a mixed floor whatever its first row happened to be.
  const tableRows = tmplSections.flatMap((s) => (Array.isArray(s.rows) ? s.rows : []))
    .filter((r) => r && r.kind === 'table');
  const tableCount = tableRows.length;
  const tableSeats = tableRows.reduce((a, r) => a + (Number(r.capacity) || 0), 0);
  const totalCapacity = number(firstPresent(plan.totalCapacity, plan.capacity, plan.totalSeats, plan.seatCount, computedTotal || undefined));
  const standingCapacity = number(firstPresent(plan.standingCapacity, plan.standingSeats, computedStanding || undefined));
  const seatedCapacity = number(firstPresent(plan.seatedCapacity, plan.seatedSeats, Math.max(totalCapacity - standingCapacity, 0)));
  // Templates use isActive (boolean), not a status enum.
  const status = firstPresent(plan.status, plan.state, plan.isActive === false ? 'Draft' : plan.isActive === true ? 'Active' : null, 'Draft');
  return {
    ...plan,
    id: String(firstPresent(plan.id, plan.planId, `seat-plan-${index + 1}`)),
    name: firstPresent(plan.name, plan.title, `Seat plan ${index + 1}`),
    totalCapacity,
    seatedCapacity,
    standingCapacity,
    // How many tables and how many places at them. A club floor is otherwise
    // indistinguishable from a stadium tier in this list — both just "seated".
    tableCount,
    tableSeats,
    sections: number(firstPresent(plan.sectionCount, plan.sectionsCount, tmplSections.length)),
    sectionRows: tmplSections, // the materialized sections (for inline preview / edit)
    gates: number(firstPresent(plan.gateCount, plan.gatesCount, Array.isArray(plan.gates) ? plan.gates.length : 0)),
    version: firstPresent(plan.version, plan.versionName, plan.currentVersion, null),
    status: titleCase(status),
    description: firstPresent(plan.description, ''),
    updated: formatDate(firstPresent(plan.updatedAt, plan.createdAt)),
    // null, not 'Not returned' — a seat plan whose author wasn't recorded has no author to
    // name, and saying so in HTTP voice tells an operator nothing. Consumers render the gap.
    updatedBy: firstPresent(plan.updatedBy, plan.createdBy, plan.metadata?.createdBy),
    raw: plan,
  };
};

// Normalize a section for the venue detail Sections tab. Handles BOTH the
// materialized template-section shape ({name,type,rows:[{capacity}]}) and the
// raw /api/sections/venue row ({name,capacity,venueId}). Defaults every field
// the tab reads (tiers:[], capacity:number, seated:bool) so it never crashes on
// a partial shape — the old path returned raw rows and `s.tiers.map` threw.
const normalizeVenueSection = (s, index = 0) => {
  const rowsArr = Array.isArray(s.rows) ? s.rows : [];
  const derivedCap = rowsArr.reduce((a, r) => a + (Number(r.capacity) || 0), 0);
  const capacity = number(firstPresent(s.capacity, derivedCap || undefined));
  const rowCount = number(firstPresent(s.rowCount, s.rowsCount, rowsArr.length || undefined));
  const typeRaw = String(firstPresent(s.type, '')).toLowerCase();
  const seated = typeRaw ? !/stand|ga|floor/.test(typeRaw) : rowsArr.length > 1 || derivedCap > 0;
  return {
    ...s,
    id: String(firstPresent(s.id, s.sectionId, `section-${index + 1}`)),
    name: firstPresent(s.name, `Section ${index + 1}`),
    code: firstPresent(s.code, ''),
    type: titleCase(firstPresent(s.type, seated ? 'seating' : 'standing')),
    seated,
    capacity,
    rows: rowCount,
    seatsPerRow: rowCount ? Math.round(capacity / rowCount) : 0,
    nomenclature: firstPresent(s.nomenclature, s.naming, seated ? 'Numbered seats' : 'General admission'),
    tiers: Array.isArray(s.tiers) ? s.tiers : [],
    gate: firstPresent(s.entranceId, s.gate, null),
    active: s.isActive !== false,
    raw: s,
  };
};

const normalizeTournament = (tournament, index = 0) => {
  const seasonTeams = (tournament.seasons || []).flatMap((season) => season.teams || []);
  const teamIds = Array.isArray(tournament.teams)
    ? tournament.teams.map((team) => String(firstPresent(team.id, team.teamId, team))).filter(Boolean)
    : [...new Set(seasonTeams.map((team) => String(firstPresent(team.id, team.teamId, team))).filter(Boolean))];
  const venueIds = Array.isArray(tournament.venues)
    ? tournament.venues.map((venue) => String(firstPresent(venue.id, venue.venueId, venue))).filter(Boolean)
    : [];
  return {
    ...tournament,
    id: String(firstPresent(tournament.id, tournament.tournamentId, `tournament-${index + 1}`)),
    name: firstPresent(tournament.name, `Tournament ${index + 1}`),
    // The tournament's own code or slug — never one made up from its name ("CHAN2026"): Tournament has no code column.
    code: firstPresent(tournament.code, tournament.slug, ''),
    organizer: firstPresent(tournament.organization?.name, tournament.organizerName, '—'),
    organizerId: firstPresent(tournament.organization?.id, tournament.organizationId, null),
    season: firstPresent(tournament.season, tournament.year, tournament.seasons?.find?.((season) => season.isActive)?.name, tournament.seasons?.[0]?.name, ''),
    status: tournament.isActive === false ? 'Archived' : titleCase(firstPresent(tournament.status, 'Active')),
    teams: teamIds,
    fixtures: arrayCount(firstPresent(tournament.games, tournament.fixtures, tournament.fixtureCount)),
    completed: number(firstPresent(tournament.completed, tournament.completedGames)),
    venues: venueIds,
    eventsCount: arrayCount(firstPresent(tournament.events, tournament.sportEvents, tournament.eventsCount)),
    // These three are derived server-side and were previously dropped on the floor here, which is
    // why the tournament header showed "Played 0/0" and "Payouts UGX 0M" no matter the data.
    // `fixturesUpcoming` stays undefined when absent rather than becoming 0 — the header falls back
    // to (events − played), and a hard 0 would defeat that fallback instead of feeding it.
    fixturesPlayed: number(firstPresent(tournament.fixturesPlayed, tournament.completed, tournament.completedGames)),
    fixturesUpcoming: tournament.fixturesUpcoming == null ? undefined : number(tournament.fixturesUpcoming),
    payoutsTotal: number(firstPresent(tournament.payoutsTotal, tournament.payouts)),
    grossSales: number(firstPresent(tournament.grossSales, tournament.sales)),
    logo: firstPresent(tournament.logo?.text, tournament.logo?.initials, initials(tournament.name)),
    logoUrl: (typeof tournament.logo === 'string' && /^https?:/.test(tournament.logo)) ? tournament.logo : (tournament.logo?.url || null),
    description: firstPresent(tournament.description, ''),
    backendBacked: true,
    raw: tournament,
  };
};

export const normalizeTeam = (team, index = 0) => ({
  ...team,
  id: String(firstPresent(team.id, team.teamId, `team-${index + 1}`)),
  name: firstPresent(team.name, `Team ${index + 1}`),
  code: firstPresent(team.code, team.shortName, ''),
  shortName: firstPresent(team.shortName, team.code, ''),
  logo: firstPresent(team.logo?.text, team.logo?.initials, team.shortName, initials(team.name, 'TM')),
  logoUrl: (typeof team.logo === 'string' && /^https?:/.test(team.logo)) ? team.logo : (team.logo?.url || null),
  country: firstPresent(team.country, '—'),
  city: firstPresent(team.city, ''),
  venue: firstPresent(team.venue?.name, team.homeVenue, '—'),
  contact: firstPresent(team.contact, team.managerName, '—'),
  status: titleCase(firstPresent(team.status, 'Active')),
  tournaments: Array.isArray(team.tournaments) ? team.tournaments : [],
  events: number(firstPresent(team.events, team.eventCount, team.games)),
  backendBacked: true,
  raw: team,
});

const normalizeSimpleNamed = (item, index, prefix) => ({
  ...item,
  id: String(firstPresent(item.id, item[`${prefix}Id`], `${prefix}-${index + 1}`)),
  name: firstPresent(item.name, item.title, item.label, `${titleCase(prefix)} ${index + 1}`),
  status: titleCase(firstPresent(item.status, item.state, 'Active')),
  raw: item,
});

const normalizeEventRequest = (request, index = 0) => ({
  ...request,
  id: String(firstPresent(request.id, request.requestId, `request-${index + 1}`)),
  displayId: String(firstPresent(request.slug, request.eventName, request.title, request.organization?.name, request.organizerName, request.name, request.email, `Request ${index + 1}`)),
  organizer: firstPresent(request.organization?.name, request.organizerName, request.name, request.email, 'Unknown organizer'),
  // See the payout normalizer: a nav needs an ID, not a display name. An event REQUEST often has
  // no organization yet (that is the point of a request — the organiser may not exist), so this is
  // null far more often here, and the surface must say "not linked" instead of faking a link.
  organizerId: firstPresent(request.organization?.id, request.organizationId, null),
  // THE ORGANIZATION BEING ASKED FOR, and its brand — real columns since migration 20260903b,
  // because approving this request CREATES that organization. Null until the requester (or the
  // operator) supplies them; the approval flow treats absent as "ask me", never as a default.
  organizationName: firstPresent(request.organizationName, null),
  organizationLogo: firstPresent(request.organizationLogo, null),
  // WHICH PAGE THIS CAME FROM — the server's own reading of the Referer header. An enquiry with
  // no provenance is answered blind: "we saw your form" is a different conversation from "you
  // were on the Nyege Nyege page when you asked".
  referer: firstPresent(request.referer, request.metadata?.referer, null),
  event: shortText(firstPresent(request.eventName, request.title, request.event?.title, request.eventDetails, request.message, `Event request ${index + 1}`)).slice(0, 90),
  name: firstPresent(request.name, request.organization?.name, request.organizerName, 'Unknown requester'),
  email: firstPresent(request.email, ''),
  phone: firstPresent(request.phone, ''),
  details: shortText(firstPresent(request.eventDetails, request.message, request.description, '')),
  type: titleCase(firstPresent(request.type, request.category, 'General')),
  date: formatDate(firstPresent(request.date, request.eventDate, request.createdAt)),
  // A real request has no top-level venue column — when the organizer names one it arrives in
  // `metadata.venue` ({name, city}), so this chain read past it and printed "TBD" for requests
  // that had answered the question.
  venue: firstPresent(request.venue?.name, request.metadata?.venue?.name, request.metadata?.venue, request.venueName, request.location, 'TBD'),
  expected: number(firstPresent(request.expectedAttendance, request.capacity, request.expected, deriveExpectedAttendance(request.eventDetails))),
  status: (() => {
    const raw = String(firstPresent(request.status, request.state, 'Pending')).toLowerCase();
    if (raw === 'new') return 'Pending';
    if (raw === 'in_progress') return 'Under review';
    if (raw === 'needs_info') return 'Needs info';
    if (raw === 'resolved') return 'Approved';
    if (raw === 'closed') return 'Rejected';
    return titleCase(raw || 'Pending');
  })(),
  risk: number(firstPresent(request.riskScore, request.risk), 25),
  kyc: titleCase(firstPresent(request.kycStatus, request.metadata?.kycStatus, 'Pending')),
  reviewer: firstPresent(request.reviewer, request.resolvedBy, '—'),
  // Whether a request is linked to an event is ONE fact, resolved here. The list and the
  // review page each used to re-derive it from a different fallback chain (the list read
  // `event.slug || event.title || eventName`, the detail read `eventName || eventId`), so a
  // request carrying an eventId but no eventName read "Not linked" in the list and linked on
  // the detail — the same record, two answers. eventId is the link; the label is decoration.
  eventId: firstPresent(request.eventId, request.event?.id, null),
  eventLabel: firstPresent(request.eventName, request.event?.title, request.event?.slug, null),
  submitted: formatDate(request.createdAt),
  contact: firstPresent(request.email, request.phone, ''),
  backendBacked: Boolean(request.id),
  raw: request,
});

const normalizeAddOn = (item, index = 0) => ({
  ...item,
  id: String(firstPresent(item.id, item.addOnId, `addon-${index + 1}`)),
  name: firstPresent(item.name, item.title, `Add-on ${index + 1}`),
  type: firstPresent(item.type, item.category, 'merch'),
  price: number(firstPresent(item.price, item.amount)),
  currency: firstPresent(item.currency, 'UGX'),
  stock: number(firstPresent(item.stock, item.quantity, item.available, item.totalQuantity)),
  qty: number(firstPresent(item.qty, item.totalQuantity, item.stock, item.quantity)),
  sold: number(firstPresent(item.sold, item.soldCount)),
  reserved: number(firstPresent(item.reserved, item.reservedCount)),
  available: number(firstPresent(item.available, item.availableQuantity, item.totalQuantity), number(firstPresent(item.qty, item.totalQuantity, item.stock, item.quantity))),
  state: String(firstPresent(item.state, item.status, item.isActive === false ? 'paused' : 'on-sale')).toLowerCase().replace(/[_\s]+/g, '-'),
  status: titleCase(firstPresent(item.status, item.isActive === false ? 'Inactive' : 'Active')),
  visibility: firstPresent(item.visibility, 'public'),
  fulfillment: firstPresent(item.fulfillment, item.fulfillmentType, 'pickup'),
  gate: firstPresent(item.gate, item.fulfillmentPoint, '—'),
  gateName: firstPresent(item.gateName, item.fulfillmentPointName, ''),
  image: firstPresent(item.image?.url, item.photo?.url, ''),
  eventId: firstPresent(item.eventId, item.event?.id, ''),
  raw: item,
});

const normalizeSupportTicket = (item, index = 0) => ({
  ...item,
  id: String(firstPresent(item.id, item.ticketId, `ticket-${index + 1}`)),
  subject: firstPresent(item.subject, item.title, item.message, `Support item ${index + 1}`),
  customer: firstPresent(item.customerName, item.name, item.email, item.phone, 'Customer'),
  channel: titleCase(firstPresent(item.channel, item.source, 'Backend')),
  status: titleCase(firstPresent(item.status, item.state, 'Open')),
  priority: titleCase(firstPresent(item.priority, item.severity, 'Normal')),
  owner: firstPresent(item.assignee?.name, item.owner, '—'),
  updated: formatDate(firstPresent(item.updatedAt, item.createdAt)),
  raw: item,
});

const replaceArray = (target, rows) => {
  if (!rows?.length) return false;
  target.splice(0, target.length, ...rows);
  return true;
};

const replaceObject = (target, next) => {
  if (!next || Object.keys(next).length === 0) return false;
  for (const key of Object.keys(target)) delete target[key];
  Object.assign(target, next);
  return true;
};

const deriveKpis = () => {
  const paidOrders = MOCK_ORDERS.filter((order) => /paid|success|captured/i.test(`${order.state} ${order.payState}`));
  const grossToday = paidOrders.reduce((sum, order) => sum + number(order.amount || order.total), 0);
  const ticketsSold = paidOrders.reduce((sum, order) => sum + number(order.tickets), 0);
  const failedPayments = MOCK_ORDERS.filter((order) => /fail|declin/i.test(`${order.state} ${order.payState}`)).length;
  MOCK.kpi = {
    ...MOCK.kpi,
    grossToday: grossToday || MOCK.kpi.grossToday,
    grossYesterday: MOCK.kpi.grossYesterday || grossToday,
    netRevenue: Math.round((grossToday || MOCK.kpi.grossToday) * 0.045),
    ticketsSold: ticketsSold || MOCK.kpi.ticketsSold,
    orders: MOCK_ORDERS.length || MOCK.kpi.orders,
    paymentSuccessRate: MOCK_ORDERS.length ? Number(((paidOrders.length / MOCK_ORDERS.length) * 100).toFixed(1)) : MOCK.kpi.paymentSuccessRate,
    failedPayments,
    pendingPayouts: PAYOUT_QUEUE.filter((p) => /pending|approval|held/i.test(p.status)).length || MOCK.kpi.pendingPayouts,
    pendingPayoutsValue: PAYOUT_QUEUE.reduce((sum, p) => /pending|approval|held/i.test(p.status) ? sum + number(p.net) : sum, 0) || MOCK.kpi.pendingPayoutsValue,
    eventsLive: MOCK.events.filter((event) => event.status === 'Live').length,
  };
  MOCK.topEvents = [...MOCK.events]
    .sort((a, b) => number(b.sales) - number(a.sales))
    .slice(0, 5)
    .map((event, index, rows) => ({
      name: event.name,
      value: number(event.sales),
      share: rows[0]?.sales ? Math.round((number(event.sales) / number(rows[0].sales)) * 100) : 0,
    }));
};

const normalizePayload = (raw) => {
  const normalized = {};
  const events = [...list(raw.eventStats), ...list(raw.eventPages)];
  if (events.length) normalized.events = events.map(normalizeEvent);

  const requests = list(raw.eventRequests);
  if (requests.length) normalized.eventRequests = requests.map(normalizeEventRequest);

  const orders = list(raw.orders);
  if (orders.length) normalized.orders = orders.map(normalizeOrder);

  const users = list(raw.users);
  if (users.length) {
    normalized.users = users.map(normalizeUser);
    normalized.customers = users.map(normalizeCustomer);
  }

  const orgs = list(raw.organizations);
  if (orgs.length) {
    normalized.organizations = Object.fromEntries(orgs.map((org, index) => {
      const row = normalizeOrg(org, index);
      return [row.name, row];
    }));
  }

  const payouts = list(raw.payouts);
  if (payouts.length) normalized.payouts = payouts.map(normalizePayout);

  const incidents = [...list(raw.incidents), ...list(raw.systemErrors), ...list(raw.feedback)];
  if (incidents.length) normalized.supportTickets = incidents.map(normalizeSupportTicket);

  const venues = list(raw.venues);
  if (venues.length) normalized.venues = venues.map(normalizeVenue);

  const addOns = list(raw.addOns);
  if (addOns.length) normalized.addOns = addOns.map(normalizeAddOn);

  const tournaments = list(raw.tournaments);
  if (tournaments.length) normalized.tournaments = tournaments.map(normalizeTournament);

  const teams = list(raw.teams);
  if (teams.length) normalized.teams = teams.map(normalizeTeam);

  const channels = list(raw.channels);
  if (channels.length) normalized.channels = channels.map((item, index) => ({ ...normalizeSimpleNamed(item, index, 'channel'), type: titleCase(item.type || 'Online'), health: 'Healthy' }));

  const boxOffices = list(raw.boxOffices);
  // Use the RICH normalizer (agents:[], soldToday:0, status…) — the thin
  // normalizeSimpleNamed left MOCK_BOX_OFFICES without `agents`, which crashed
  // consumers reading the full seed shape (e.g. SalesControlsPage `b.agents.length`).
  if (boxOffices.length) normalized.boxOffices = boxOffices.map((item, index) => normalizeBoxOffice(item, index));

  const access = list(raw.accessControl);
  if (access.length) normalized.accessControl = access.map(normalizeAccessControlIntegration);

  const designs = list(raw.designs);
  if (designs.length) normalized.designs = designs.map((item, index) => ({ ...normalizeSimpleNamed(item, index, 'design'), type: titleCase(item.artifactType || item.type || 'Ticket design'), channel: firstPresent(item.channel, 'PDF / Wallet'), linked: firstPresent(item.event?.title, item.linked, 'Global'), version: firstPresent(item.version, '1'), archived: item.archived === true }));

  const messageTemplates = list(raw.messageTemplates);
  if (messageTemplates.length) normalized.messageTemplates = messageTemplates.map((item, index) => ({ id: String(firstPresent(item.id, `template-${index + 1}`)), name: firstPresent(item.name, item.title, item.key, `Template ${index + 1}`), type: titleCase(firstPresent(item.type, item.channel, 'Message')), channel: titleCase(firstPresent(item.channel, item.provider, 'Email')), linked: firstPresent(item.event?.title, item.linked, 'Global'), linkedId: firstPresent(item.eventId, '—'), lang: firstPresent(item.language, 'English'), v: firstPresent(item.version, '1'), version: firstPresent(item.version, '1'), status: titleCase(firstPresent(item.status, 'Live')), sends: number(firstPresent(item.sends, item.sentCount)), edited: formatDate(firstPresent(item.updatedAt, item.createdAt)), by: firstPresent(item.updatedBy, item.createdBy, 'Backend'), editor: firstPresent(item.updatedBy, item.createdBy, 'Backend'), raw: item }));

  const campaigns = list(raw.campaigns);
  if (campaigns.length) normalized.campaigns = campaigns.map((item, index) => ({ ...normalizeSimpleNamed(item, index, 'campaign'), audience: firstPresent(item.audience, item.segment, 'Customers'), sent: number(item.sent), delivered: number(item.delivered), failed: number(item.failed), scheduled: formatDate(firstPresent(item.scheduledAt, item.createdAt)) }));

  const subscriptions = list(raw.subscriptions);
  if (subscriptions.length) normalized.subscribers = subscriptions.map((item, index) => ({ ...normalizeSimpleNamed(item, index, 'subscriber'), email: firstPresent(item.email, ''), phone: firstPresent(item.phone, ''), event: firstPresent(item.event?.title, item.eventName, '—'), state: String(firstPresent(item.status, item.state, 'active')).toLowerCase(), joined: formatDate(item.createdAt) }));

  const reports = list(raw.reports);
  if (reports.length) normalized.reports = reports.map((item, index) => ({ name: firstPresent(item.name, item.reportName, `Report ${index + 1}`), desc: firstPresent(item.description, item.desc, 'Backend report'), owner: firstPresent(item.owner, 'Operations'), last: formatDate(firstPresent(item.updatedAt, item.createdAt)), schedule: firstPresent(item.schedule, 'Manual'), format: firstPresent(item.format, 'CSV') }));

  const audits = list(raw.auditLogs);
  if (audits.length) normalized.auditLogs = audits.map((item) => ({ time: formatTime(item.createdAt), actor: firstPresent(item.actorName, item.actor, item.user?.email, 'System'), role: firstPresent(item.role, 'system'), action: firstPresent(item.action, item.event, 'activity'), object: firstPresent(item.objectId, item.target, item.entityId, '—'), sev: titleCase(firstPresent(item.severity, 'Info')), ip: firstPresent(item.ip, item.ipAddress, '—') }));

  const notifications = list(raw.notifications);
  if (notifications.length) normalized.attention = notifications.map((item, index) => ({ id: firstPresent(item.id, index + 1), kind: firstPresent(item.type, item.kind, 'notification'), label: firstPresent(item.title, item.subject, item.message, `Notification ${index + 1}`), meta: firstPresent(item.description, item.body, item.target, 'Backend notification'), severity: String(firstPresent(item.severity, item.priority, 'amber')).toLowerCase().includes('critical') ? 'red' : 'amber', time: item.createdAt ? formatTime(item.createdAt) : 'now' }));

  return normalized;
};

export const applyBackOfficeData = (normalized) => {
  let changed = false;
  changed = replaceArray(MOCK.events, normalized.events) || changed;
  changed = replaceArray(MOCK.eventRequests, normalized.eventRequests) || changed;
  changed = replaceArray(MOCK_ORDERS, normalized.orders) || changed;
  changed = replaceArray(MOCK.orders, normalized.orders) || changed;
  changed = replaceArray(MOCK.users, normalized.users) || changed;
  changed = replaceArray(MOCK_CUSTOMERS, normalized.customers) || changed;
  changed = replaceObject(MOCK_ORG, normalized.organizations) || changed;
  changed = replaceArray(PAYOUT_QUEUE, normalized.payouts) || changed;
  changed = replaceArray(MOCK.payouts, normalized.payouts) || changed;
  changed = replaceArray(MOCK.venues, normalized.venues) || changed;
  changed = replaceArray(MOCK_VENUES_RICH, normalized.venues) || changed;
  changed = replaceArray(MOCK.tournaments, normalized.tournaments) || changed;
  changed = replaceArray(MOCK_TOURNAMENTS_RICH, normalized.tournaments) || changed;
  changed = replaceArray(MOCK.teams, normalized.teams) || changed;
  changed = replaceArray(MOCK_TEAMS_RICH, normalized.teams) || changed;
  // NOTE: MOCK_SALES_CHANNELS is intentionally NOT hydrated from
  // /api/distribution-channels. Those are ticket *delivery-routing* channels
  // (channelType email/whatsapp/print/app) — a different concept from the UI's
  // "Sales Channels" (web/mobile/in-person revenue), whose detail page reads a
  // rich shape (soldToday/paymentMethods/allocation/pricingMode) the backend
  // rows lack. Hydrating it crashed SalesChannelDetailPage. Until a real
  // sales-channel backend exists, this stays the static catalog.
  changed = replaceArray(MOCK_BOX_OFFICES, normalized.boxOffices) || changed;
  changed = replaceArray(MOCK_AC_INTEGRATIONS_V2, normalized.accessControl) || changed;
  changed = replaceArray(ADDONS_SEED, normalized.addOns) || changed;
  // NOTE: DESIGN_TEMPLATES is the *static starter-template catalog* that
  // NewDesignModal renders (rich preview shape: headerColor/blocks/etc). It is
  // intentionally NOT hydrated from /api/designs — backend design rows lack
  // that shape and would crash the modal. The user-facing "Designs" list reads
  // MOCK.templates (message templates) below.
  changed = replaceArray(MOCK.templates, normalized.messageTemplates) || changed;
  changed = replaceArray(CAMPAIGNS_SEED, normalized.campaigns) || changed;
  changed = replaceArray(SUBSCRIBERS_SEED, normalized.subscribers) || changed;
  changed = replaceArray(REPORTS_SEED, normalized.reports) || changed;
  changed = replaceArray(MOCK.auditLogs, normalized.auditLogs) || changed;
  changed = replaceArray(MOCK.attention, normalized.attention) || changed;
  changed = replaceArray(MOCK_SUPPORT_TICKETS, normalized.supportTickets) || changed;
  if (normalized.subscribers?.length) replaceArray(WAITLIST_SEED, normalized.subscribers.filter((row) => row.state === 'waiting'));
  if (changed) deriveKpis();
  return changed;
};

// Truthful empty states: when a backend-owned domain is *reachable* (its
// endpoint returned a response) but produced no rows, drop the static seed so
// the page shows a real empty state instead of fake demo data. If the endpoint
// was unreachable (not fetched), the seed is left intact as an offline
// fallback. Domains without a backend endpoint (e.g. field sales agents) are
// intentionally absent. `seeds` lists every seed array the domain hydrates into.
const SEED_BY_DOMAIN = {
  boxOffices:       { seeds: [MOCK_BOX_OFFICES],                    norm: 'boxOffices' },
  accessControl:    { seeds: [MOCK_AC_INTEGRATIONS_V2],             norm: 'accessControl' },
  messageTemplates: { seeds: [MOCK.templates],                      norm: 'messageTemplates' },
  campaigns:        { seeds: [CAMPAIGNS_SEED],                      norm: 'campaigns' },
  subscribers:      { seeds: [SUBSCRIBERS_SEED],                    norm: 'subscribers' },
  venues:           { seeds: [MOCK.venues, MOCK_VENUES_RICH],       norm: 'venues' },
  tournaments:      { seeds: [MOCK.tournaments, MOCK_TOURNAMENTS_RICH], norm: 'tournaments' },
  teams:            { seeds: [MOCK.teams, MOCK_TEAMS_RICH],         norm: 'teams' },
};

const clearSeedsForEmptyDomain = (key, normalized) => {
  const entry = SEED_BY_DOMAIN[key];
  if (!entry) return;
  const hasRows = Array.isArray(normalized[entry.norm]) && normalized[entry.norm].length > 0;
  if (hasRows) return;
  entry.seeds.forEach((seed) => { if (Array.isArray(seed) && seed.length) seed.splice(0, seed.length); });
};

// Maps a lazy-domain key to the raw key normalizePayload consumes (mostly 1:1;
// `subscribers` is served by the subscriptions endpoint).
const DOMAIN_RAW_KEY = {
  venues: 'venues', tournaments: 'tournaments', teams: 'teams', channels: 'channels',
  boxOffices: 'boxOffices', accessControl: 'accessControl', designs: 'designs',
  messageTemplates: 'messageTemplates', campaigns: 'campaigns', subscribers: 'subscriptions',
};

const domainCache = {};
export const isDomainLoaded = (key) => Boolean(domainCache[key]);

// Lazily hydrate a single domain on demand (called when its page mounts).
// Caches the in-flight/settled promise so repeat visits don't refetch unless
// `force` is passed. Mutates the shared MOCK singletons in place, exactly like
// the bulk startup loader, so existing pages keep reading their singleton.
export async function loadDomain(key, { signal, force } = {}) {
  if (!force && domainCache[key]) return domainCache[key];
  const path = LAZY_DOMAIN_ENDPOINTS[key];
  if (!path) return false;
  const promise = (async () => {
    const response = await fetchBackOfficeJson(path, { signal });
    if (!response.ok) throw new Error(`${response.status} ${response.statusText || 'Request failed'}`);
    const rawKey = DOMAIN_RAW_KEY[key] || key;
    const normalized = normalizePayload({ [rawKey]: response.payload });
    applyBackOfficeData(normalized);
    clearSeedsForEmptyDomain(key, normalized);
    return true;
  })();
  domainCache[key] = promise;
  promise.catch(() => { delete domainCache[key]; });
  return promise;
}

const sameLocalDay = (value, day = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === day.getFullYear()
    && date.getMonth() === day.getMonth()
    && date.getDate() === day.getDate();
};
const previousLocalDay = () => {
  const value = new Date();
  value.setDate(value.getDate() - 1);
  return value;
};
const formatRelativeAge = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'now';
  const diffMs = Math.max(Date.now() - date.getTime(), 0);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};
/**
 * HOW LONG UNTIL THE DOORS OPEN, read the way a person says it.
 *
 * The mirror of `formatRelativeAge` above, for the one queue whose deadline is in the future.
 * Coarse on purpose and never in machine units: "in 2 days", not "48h" and not "05 Sept, 00:00" —
 * an event stored at 21:00Z IS local midnight, so a timestamp there prints a time nobody set.
 * Past bound included because an event that started tonight is still one someone can be let into.
 */
const doorsIn = (value) => {
  if (!value) return null;
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return null;
  const days = Math.round((at - Date.now()) / 86400000);
  if (days < 0) return 'doors already opened';
  if (days === 0) return 'doors today';
  if (days === 1) return 'doors tomorrow';
  if (days < 14) return `doors in ${days} days`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `doors in ${weeks} weeks`;
  return `doors in ${Math.round(days / 30)} months`;
};
const currencyTotal = (payload) => {
  if (!payload || typeof payload !== 'object') return 0;
  const totals = payload.providerTotals || payload.totals || {};
  return Object.values(totals).reduce((sum, provider) => {
    if (provider && typeof provider === 'object') {
      return sum + number(provider.UGX ?? Object.values(provider)[0]);
    }
    return sum + number(provider);
  }, 0);
};
const orderCurrencyBuckets = (orders = []) => orders.reduce((acc, order) => {
  // No 'UGX' tail: an order with no resolvable currency is not a Ugandan one.
  const currency = order.currency ? String(order.currency).toUpperCase() : null;
  acc[currency] = (acc[currency] || 0) + number(order.amount);
  return acc;
}, {});
const providerCurrencyBuckets = (providerTotals = {}) => Object.values(providerTotals || {}).reduce((acc, totals) => {
  if (!totals || typeof totals !== 'object') return acc;
  Object.entries(totals).forEach(([currency, amount]) => {
    if (!currency) return acc;
    const key = String(currency).toUpperCase();
    acc[key] = (acc[key] || 0) + number(amount);
  });
  return acc;
}, {});
const bucketTotal = (buckets = {}) => Object.values(buckets).reduce((sum, value) => sum + number(value), 0);
const multiplyBuckets = (buckets = {}, factor = 1) => Object.fromEntries(
  Object.entries(buckets).map(([currency, value]) => [currency, Math.round(number(value) * factor)]),
);
const auditText = (value, fallback = 'updated') => titleCase(String(firstPresent(value, fallback)).replace(/[_-]+/g, ' '));
const isUuidLike = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || ''));
const auditTarget = (log, index = 0) => {
  const metadata = parseMaybeJson(log.metadata, {});
  const named = firstPresent(
    metadata.subjectLabel,
    metadata.orderId,
    metadata.ticket_ref,
    metadata.ticketRef,
    metadata.slug,
    metadata.eventSlug,
    metadata.name,
    metadata.title,
    log.orderId,
    log.ticket_ref,
    log.object,
    log.target,
  );
  if (named) return String(named);
  const entityType = firstPresent(log.entityType, 'record');
  const entityId = firstPresent(log.entityId, '');
  return entityId && !isUuidLike(entityId) ? `${entityType} ${entityId}` : titleCase(entityType, `audit-${index + 1}`);
};
// WHAT AN ACT SAYS, as one line a person reads. The server records specifics in the act's own
// terms; one convention makes them legible everywhere: `[before, after]` is a change, a list of
// objects is one entry per thing changed (its first field names it), anything else is a fact.
// The reason and note are not here — they are the `why`, shown on their own line.
const WHY_KEYS = new Set(['reason', 'note', 'via']);
const words = (k) => String(k).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
const shown = (v) => (v == null || v === '' ? 'none' : typeof v === 'number' ? v.toLocaleString() : String(v));
const isChange = (v) => Array.isArray(v) && v.length === 2 && v.every((x) => x == null || typeof x !== 'object');
const factsOf = (o) => Object.entries(o).filter(([k, v]) => !WHY_KEYS.has(k) && (isChange(v) || (v != null && v !== '' && typeof v !== 'object')))
  .map(([k, v]) => (isChange(v) ? `${words(k)} ${shown(v[0])} → ${shown(v[1])}` : `${words(k)} ${shown(v)}`));
export const actSummary = (said) => {
  if (!said || typeof said !== 'object') return '';
  const parts = [];
  // An amount reads with its currency ("amount UGX 720,000"), so the currency is not a fact of its own.
  const cur = typeof said.currency === 'string' && /^[A-Z]{3}$/.test(said.currency) && Object.keys(said).some((k) => /amount$/i.test(k)) ? said.currency : null;
  for (const [k, v] of Object.entries(said)) {
    if (WHY_KEYS.has(k) || v == null || v === '') continue;
    // An id says nothing to the person reading — the act's subject is linked on the line already.
    if (typeof v === 'string' && isUuidLike(v)) continue;
    if (cur && k === 'currency') continue;
    if (cur && /amount$/i.test(k) && typeof v === 'number') { parts.push(`${words(k)} ${cur} ${shown(v)}`); continue; }
    // A flag reads as words: "raised by platform", "not visible".
    if (typeof v === 'boolean') { parts.push(v ? words(k) : `not ${words(k)}`); continue; }
    // An instant reads as a date, not as ISO.
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) && !Number.isNaN(Date.parse(v))) { parts.push(`${words(k)} ${new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`); continue; }
    if (isChange(v)) parts.push(`${words(k)} ${shown(v[0])} → ${shown(v[1])}`);
    else if (Array.isArray(v) && v.length && typeof v[0] === 'object') {
      const each = v.slice(0, 5).map((o) => { const [[, name], ...rest] = Object.entries(o); return `${name}: ${factsOf(Object.fromEntries(rest)).join(', ')}`; });
      parts.push(each.join(' · ') + (v.length > 5 ? ` · +${v.length - 5} more` : ''));
    } else if (typeof v !== 'object') parts.push(`${words(k)} ${shown(v)}`);
  }
  return parts.join(' · ');
};
const changeSummary = (oldValues, newValues) => {
  if (!newValues || typeof newValues !== 'object') return '';
  // No "before": the entry is what the act SET — the previous entry holds what it was. Ids are left out. A before that
  // is all empty (a create writes every field as null) is no before either: "name none → X" said nothing.
  const hasBefore = oldValues && typeof oldValues === 'object' && Object.values(oldValues).some((v) => v != null && v !== '');
  if (!hasBefore) {
    return actSummary(Object.fromEntries(Object.entries(newValues).filter(([, v]) => !isUuidLike(String(v ?? '')))));
  }
  const keys = Object.keys(newValues).filter((k) => k in oldValues);
  const flat = (v) => (v != null && typeof v === 'object' ? 'changed' : shown(v));
  return keys.slice(0, 4).map((k) => `${words(k)} ${flat(oldValues[k])} → ${flat(newValues[k])}`).join(' · ') + (keys.length > 4 ? ` · +${keys.length - 4} more` : '');
};
const normalizeAuditLog = (log, index = 0) => ({
  ...log,
  id: String(firstPresent(log.id, log.auditLogId, `audit-${index + 1}`)),
  // A person's name when the act recorded it; an email is shown as written, never re-cased.
  actor: (() => { const v = firstPresent(log.metadata?.actorName, log.userEmail, log.actorName, log.actor, log.source, log.metadata?.confirmedBy, 'System'); return /@/.test(String(v)) ? String(v) : auditText(v, 'System'); })(),
  actorRole: firstPresent(log.role, log.userRole, log.source, 'System'),
  // An act named by its route is shown as that route, verbatim; a named act reads as words.
  // Dotted names (inventory.hold.release_seats) read as words, as underscored ones do.
  action: String(log.action || '').includes('/') ? String(log.action) : auditText(log.action && String(log.action).replace(/\./g, ' '), 'updated'),
  target: auditTarget(log, index),
  detail: shortText(firstPresent(log.detail, log.description, log.message, log.metadata?.message, '')),
  ip: firstPresent(log.ip, log.ipAddress, '—'),
  // The part of the platform it belongs to (the owner of what was changed) and what the person
  // said about it — the reason they gave, a note, the specifics the code recorded.
  system: log.system || null,
  said: log.metadata?.details || null,
  // What changed, before → after, AND what the act said about it. It was one or the other, so any act that recorded
  // details hid its change: a corrected phone read "tickets 2" with the old and new numbers on the row, unshown.
  summary: [changeSummary(log.oldValues, log.newValues), actSummary(log.metadata?.details)].filter(Boolean).join(' · '),
  evidence: Array.isArray(log.metadata?.changes) ? log.metadata.changes : [],
  route: log.metadata?.route || null,
  eventId: firstPresent(log.eventId, log.metadata?.eventId, ''),
  entityType: firstPresent(log.entityType, '').toLowerCase() || null,
  entityId: firstPresent(log.entityId, '') || null,
  ts: formatTime(firstPresent(log.createdAt, log.updatedAt)),
  time: formatRelativeAge(firstPresent(log.createdAt, log.updatedAt)),
  raw: log,
});

// CORE (money) slice of the dashboard: collected gross, platform revenue,
// tickets, paid-order count, top events, system health — all off the
// successful-orders partial index, fast. Loaded on its own so the dashboard
// PAINTS THE MONEY immediately, without waiting for the ops queue. Ops data
// (attention queue, active events) comes from loadDashboardOps and merges in
// when ready — the money is never hostage to it.
export async function loadDashboardCore({ signal, financeWindow = null } = {}) {
  // financeWindow drives the money figures (collected gross, est. revenue,
  // top-events breakdown, finance snapshot) so the dashboard's time switch
  // steers them together. Shape: { from, to } | { allTime: true } | null.
  // Default to the SAME "Last 30 days" window the DashboardPage selector uses
  // (resolveDatePreset('30d')) — NOT a separate windowDays computation — so the
  // startup prefetch and the page's own load resolve to one identical window and
  // the headline doesn't flash between two different totals.
  const financeArgs = financeWindow && (financeWindow.from || financeWindow.allTime)
    ? financeWindow
    : resolveDatePreset('30d');
  const errors = {};
  const capture = async (key, task, fallback) => {
    try {
      return await task();
    } catch (error) {
      errors[key] = error?.message || 'Request failed';
      return fallback;
    }
  };

  // Each load is the VALUE the dashboard shows, fetched LEAN — small limit + a
  // date/status filter, never a full list pulled to count/slice client-side, and
  // never a COUNT(*):
  //  • finance-stats — the money story (collected/revenue/tickets/per-event), on
  //    the successful-orders partial index. The core.
  //  • upcoming events (≤9) — `upcoming=true` + `status=published` filter at the DB,
  //    today-onward, for the "Active events" widget. (`upcoming=true` alone is DATE-ONLY;
  //    this comment used to claim it filtered to published, which is why the limit was
  //    being spent on drafts.)
  //  • pending requests (status='new') — the "needs attention" review queue.
  //  • pending payouts (status=requested/approved/partial) — the "needs attention"
  //    disbursement queue. Both FILTERED AT THE DB to the actionable rows.
  // Core fetches ONLY the money story (finance) + health — the two fast,
  // indexed reads. Ops (events/requests/payouts) load separately so they can't
  // delay the money. Ops fields below resolve to empty here and are merged in by
  // the caller once loadDashboardOps returns.
  // (Health is not asked here: the Dashboard's System health card reads every deployed backend itself — lib/deploymentHealth.)
  const [financeStats] = await Promise.all([
    capture('finance', () => loadPayoutFinanceStats({ ...financeArgs, signal }), { providerTotals: {} }),
  ]);

  const events = [];
  const requests = [];
  const payouts = [];
  const requestTotal = 0;
  const eventStatsEventCount = 0;   // includeStats:false — per-event sales not loaded (heavy), not shown here
  const eventStatsTicketsIssued = 0;
  const reportedChargedByCurrency = {};
  const financeProviderTotals = financeStats?.providerTotals || {};
  const financeGrossByCurrency = providerCurrencyBuckets(financeProviderTotals);
  // Finance-stats is the source of truth for the collected headline over the
  // selected window. When it succeeded, use it EVEN IF EMPTY (an empty window =
  // UGX 0 collected) — don't fall back to loaded-order gross, which ignores the
  // window and would show a non-zero headline while the breakdown says "no sales
  // in this period". Only fall back to loaded orders if finance actually errored.
  const financeOk = !errors.finance;
  // The money story is finance-stats only — no loaded-order fallback now that the
  // dashboard doesn't fetch order rows. If finance errored we show 0 + the error.
  const grossByCurrency = financeOk ? financeGrossByCurrency : {};
  const grossProcessed = bucketTotal(grossByCurrency);
  // REAL platform revenue = the commission the DB actually kept (gross − net),
  // returned per-currency by finance-stats. Falls back to the legacy 4.5%-of-
  // collected estimate only when the backend doesn't expose it (older deploy).
  const financePlatformRevByCurrency = financeStats?.platformRevenueByCurrency || null;
  const hasRealPlatformRev = financeOk && financePlatformRevByCurrency
    && Object.keys(financePlatformRevByCurrency).length > 0;
  const platformRevByCurrency = hasRealPlatformRev
    ? financePlatformRevByCurrency
    : multiplyBuckets(grossByCurrency, 0.045);
  const platformRevenue = bucketTotal(platformRevByCurrency);
  // Windowed tickets/orders from finance-stats (same window as the collected
  // headline). Falls back to the loaded-orders tally only if the BE didn't
  // return windowTotals (older backend).
  const windowTotals = financeStats?.windowTotals || null;
  const ticketsSold = windowTotals ? number(windowTotals.tickets) : 0;
  const windowOrderCount = windowTotals ? number(windowTotals.orders) : 0;
  const pendingPayouts = payouts.filter((payout) => /pending|approval|held|processing/i.test(`${payout.status} ${payout.stage}`));
  const pendingRequests = requests.filter((request) => /pending|review/i.test(request.status));
  const activeEvents = events.filter((event) => (event.status === 'Live' || event.status === 'Upcoming') && event.published);
  // Preferred breakdown: the per-event gross the BE computes over the SAME
  // window/filter as the collected headline (providerTotals). Top-N is a real
  // subset of the same grand total, so the breakdown reconciles with the
  // headline by construction — no more "30 loaded orders → 1 event" mirage.
  const statusByEventName = new Map(events.map((event) => [event.name, event.status]));
  // ALL per-event gross rows are kept (not a cross-currency top-5): revenue in one
  // currency cannot be ranked against another (UGX 17M is NOT "more" than KES 2M),
  // so the UI groups these by currency and ranks WITHIN a currency. `share` is each
  // event's share of ITS OWN currency's collected total — the only honest denominator.
  const financeEventTotals = (financeStats?.eventTotals || [])
    .filter((row) => number(row.gross) > 0)
    .map((row) => {
      // per-event gross IS in a specific currency — and if the row doesn't say which,
      // naming it UGX doesn't make it so.
      const currency = row.currency || row.cur || null;
      const currencyTotal = number(grossByCurrency[currency]);
      const value = number(row.gross);
      return {
        eventId: row.eventId || null,                       // carried through so the row can link to the event
        name: row.eventName || 'Unknown event',
        value,
        currency,
        orders: number(row.orders),
        status: statusByEventName.get(row.eventName) || null,
        metric: 'revenue',
        // Share of this event's OWN currency's collected revenue (reconciles per-currency).
        share: currencyTotal > 0 ? Math.round((value / currencyTotal) * 100) : 0,
      };
    });
  // The breakdown is STRICTLY the windowed per-event gross from finance-stats —
  // a real subset of the collected headline. No fallback to lifetime event.sales
  // or to the loaded-orders tally: those ignore the selected window and produced
  // nonsense (e.g. "Today" → 0 collected but a lifetime/loaded breakdown). When
  // there are no sales in the window, topEvents is empty → the UI shows an
  // honest "no sales in this period" empty state.
  const topEvents = financeEventTotals;
  // Attention feed retired with its fetches — it surfaced stale (33–56 day-old)
  // requests, seed/test payouts (UGX 0), and an always-100% success rate. A real
  // The operator's queue: event requests awaiting review + payouts awaiting
  // action. Built from the lean pending-only fetches above (status='new' requests,
  // the small payout set) — the few that are actually shown, not a sampled scan.
  const attention = [
    ...pendingRequests.slice(0, 3).map((request) => ({
      id: `request-${request.id}`,
      label: `Event request "${request.event}" awaiting review`,
      // The name falls back to the email when none was given — so the same address printed twice.
      meta: [...new Set([request.name, request.email || request.phone].filter(Boolean))].join(' · ') || request.organizer,
      severity: 'amber',
      time: formatRelativeAge(request.raw?.createdAt),
      target: 'event-requests',
      recordType: 'request',
      record: request,
    })),
    ...pendingPayouts.slice(0, 3).map((payout) => ({
      id: `payout-${payout.id}`,
      label: `Payout for ${payout.organizer || payout.event || payout.displayId || 'organizer'} awaiting action`,
      meta: `${payout.organizer || 'Organizer'}${payout.currency ? ` · ${payout.currency} ${number(payout.net || payout.amount).toLocaleString('en-UG')}` : ''}`,
      severity: /held|failed/i.test(`${payout.status} ${payout.stage}`) ? 'red' : 'amber',
      time: formatRelativeAge(payout.raw?.createdAt),
      target: 'payouts',
      recordType: 'payout',
      record: payout,
    })),
  ].slice(0, 6);

  return {
    source: {
      events: null,
      orgStats: null,
      orders: ENDPOINTS.payoutFinanceStats,
      requests: null,
      payouts: ENDPOINTS.payouts,
      finance: ENDPOINTS.payoutFinanceStats,
      audit: null,//auditResponse.ok ? ENDPOINTS.auditLogs : null,
      health: null,
      errors,
    },
    kpi: {
      grossToday: grossProcessed,
      grossScope: financeOk ? 'finance-stats' : 'unavailable',
      grossByCurrency,
      loadedGrossByCurrency: {},
      grossYesterday: grossProcessed,
      netRevenue: Math.round(platformRevenue),
      netRevenueByCurrency: platformRevByCurrency,
      loadedNetRevenueByCurrency: {},
      platformRevenueIsReal: hasRealPlatformRev,
      ticketsSold,
      ticketsIssued: ticketsSold,
      windowOrderCount,
      eventStatsCount: eventStatsEventCount || events.length,
      reportedChargedByCurrency,
      // Paid-order count from finance-stats (successful, windowed, indexed). The
      // order-health stats (success rate / failed / abandoned) are intentionally
      // not computed here — they required scanning all order statuses (defeating
      // the successful-orders partial index) to feed an always-100% tile.
      orders: windowOrderCount,
      paymentSuccessRate: null,
      failedPayments: null,
      ordersPending: 0,
      ordersAbandoned: 0,
      ordersAbandonedOldestHours: 0,
      ordersLoaded: windowOrderCount,
      checkoutConversion: null,
      pendingPayouts: pendingPayouts.length,
      pendingPayoutsValue: pendingPayouts.reduce((sum, payout) => sum + number(payout.net || payout.amount), 0),
      eventsLive: events.filter((event) => event.status === 'Live').length,
      highRiskAlerts: attention.filter((item) => item.severity === 'red').length,
      devicesOnline: 0,
      devicesTotal: 0,
    },
    activeEvents: activeEvents.slice(0, 9),
    // `share` is already per-currency (event / its own currency's collected total) —
    // keep it; overriding it with a cross-currency denominator was the mixing bug.
    topEvents: topEvents.map((event) => ({
      ...event,
      sub: event.metric === 'revenue' ? `${event.orders || 0} orders` : `${event.orders || 0} loaded orders`,
    })),
    // NOTE: the needs-attention queue is owned SOLELY by loadDashboardOps. Core and
    // ops load in parallel; emitting `attention` from both let a late-resolving core
    // clobber ops's richer list (ops alone includes pending ticket-action approvals).
    // The chrome-cache merge already treats ops as authoritative (attention: ops?...);
    // core no longer returns it so the live merge is consistent and race-free.
    finance: {
      grossProcessed,
      grossByCurrency,
      loadedGrossByCurrency: {},
      platformFees: Math.round(platformRevenue),
      platformFeesByCurrency: platformRevByCurrency,
      loadedPlatformFeesByCurrency: {},
      platformRevenueIsReal: hasRealPlatformRev,
      refunds: 0,
      pendingSettlement: pendingPayouts.reduce((sum, payout) => sum + number(payout.net || payout.amount), 0),
      providerTotals: financeProviderTotals,
    },
    recentActivity: [],//logs,
    counts: {
      events: events.length,
      eventStats: eventStatsEventCount,
      orders: windowOrderCount,
      requests: requestTotal,
      payouts: payouts.length,
    },
  };
}

// OPS slice: the "needs attention" queue (pending requests + payouts) and the
// active-events list. Loaded SEPARATELY from the money so the dashboard never
// waits on this block — the caller paints loadDashboardCore first and merges
// this in when it lands. Each fetch is DB-filtered to its actionable rows.
// `withEvents: false` — the queues only (the top bar's alerts), without the active-events list.
export async function loadDashboardOps({ signal, financeWindow = null, withEvents = true } = {}) {
  const financeArgs = financeWindow && (financeWindow.from || financeWindow.allTime)
    ? financeWindow
    : resolveDatePreset('30d');
  const windowFrom = financeArgs.allTime ? null : (financeArgs.from || null);
  const errors = {};
  const capture = async (key, task, fallback) => {
    try { return await task(); } catch (error) { errors[key] = error?.message || 'Request failed'; return fallback; }
  };
  const [eventsResult, requestPage, payouts, approvalsPage, kycPage, exceptions] = await Promise.all([
    // status=published is applied AT THE DB, not after the fetch. `upcoming=true` filters by
    // DATE only (the published predicate in eventPageRoutes is commented out with a TODO), so
    // asking for 9 upcoming events returned 9 rows of ANY status and the client then dropped
    // the drafts — spending the limit on rows it was about to throw away, with nothing pulled
    // in to replace them. Measured on prod: of the first 9 upcoming events, 6 are drafts (test
    // fixtures) and 3 are published, so the "Active events" card showed 3 while 5 published
    // upcoming events existed. Two were unreachable from the dashboard entirely.
    withEvents ? capture('events', () => loadEventPages({ includeStats: false, upcoming: true, status: 'published', limit: 9, signal }), { rows: [] }) : { rows: [] },
    // PENDING IS PENDING, HOWEVER OLD. These queues took the page's date window, so a payout waiting 40 days fell off
    // the card on the default 30 — on prod, every payout awaiting action was older than that, and the card showed none.
    capture('requests', () => loadEventRequestPage({ status: 'new', limit: 1, signal }), { rows: [] }),
    capture('payouts', () => loadPayouts({ limit: 1, status: 'requested,approved', signal }), []),
    capture('approvals', () => loadTicketActionRequests({ status: 'pending', limit: 1, signal }), { rows: [] }),
    // KYC orgs that have SUBMITTED docs and await admin review (not status=pending,
    // which is "org hasn't submitted yet" — not actionable for us). A submitted org
    // is blocked from publishing events, so verifying it promptly is revenue-relevant.
    capture('kyc', () => loadKycOrganizations({ status: 'submitted', limit: 1, signal }), { rows: [] }),
    // MONEY OWED BACK TO A BUYER — a voided order still holding it, or a refund to send: the newest one in the window.
    // (Overpaid and unplaced are worked in Treasury.) All-time is not a window Treasury reads, so it is not asked then.
    windowFrom ? capture('exceptions', () => loadTreasuryExceptions({ kind: 'voided,refund', limit: 1, from: windowFrom, to: new Date().toISOString(), signal }), { rows: [] }) : { rows: [] },
    // /api/orders/unfulfilled IS NOT FETCHED HERE ANY MORE — it lives on Support > Needs help.
    // Measured on prod: that endpoint averaged 104 SECONDS over 14 calls (24 minutes of server
    // time) while its own SQL EXPLAIN ANALYZEs at 24ms — so the cost is not the query, and this
    // page fired it on every console open as one of six parallel calls. An obligation that is
    // owed until the event happens does not need re-asking every time somebody looks at the home
    // page; it needs a place an operator goes to work it. That place already exists.
  ]);
  // ONE OF EACH QUEUE IS ASKED AND ONE IS SHOWN — no counts from these lists.
  const events = eventsResult.rows || [];
  // Backend already filtered to the actionable rows (status=new / requested,approved / pending).
  const pendingRequests = requestPage.rows || [];
  const pendingPayouts = Array.isArray(payouts) ? payouts : [];
  const pendingApprovals = approvalsPage.rows || [];
  const pendingKyc = kycPage.rows || [];
  const activeEvents = events.filter((event) => (event.status === 'Live' || event.status === 'Upcoming') && event.published).slice(0, 9);
  // ONE LINE PER QUEUE — its newest — so the card is five lines at most, money held for buyers first. Each opens its queue.
  const attention = [
    ...(exceptions.rows || []).map((r) => ({
      id: `exception-${r.kind}-${r.key}`,
      label: `${EXCEPTION_KINDS[r.kind]?.label || r.kind} · ${fmtMoney(r.amount, r.currency)}`,
      meta: [r.who, r.eventName].filter(Boolean).join(' · '),
      severity: 'amber',
      time: formatRelativeAge(r.at),
      target: 'treasury', search: { section: 'exceptions' },
    })),
    ...pendingPayouts.map((payout) => ({
      id: `payout-${payout.id}`,
      label: `Payout for ${payout.organizer || payout.event || payout.displayId || 'organizer'} awaiting action`,
      meta: `${payout.organizer || 'Organizer'}${payout.currency ? ` · ${payout.currency} ${number(payout.net || payout.amount).toLocaleString('en-UG')}` : ''}`,
      severity: /held|failed/i.test(`${payout.status} ${payout.stage}`) ? 'red' : 'amber',
      time: formatRelativeAge(payout.raw?.createdAt),
      target: 'payouts',
      recordType: 'payout',
      record: payout,
    })),
    // A buyer's request about their ticket, waiting on an operator.
    ...pendingApprovals.map((r) => ({
      id: `approval-${r.id}`,
      label: `${r.actionLabel} requested${r.requestedByName && r.requestedByName !== 'Unknown' ? ` by ${r.requestedByName}` : ''}`,
      meta: [r.orderRef && `Order ${r.orderRef}`, r.reason].filter(Boolean).join(' · '),
      severity: 'amber',
      time: formatRelativeAge(r.createdAt),
      target: 'issues-approvals',
    })),
    ...pendingKyc.map((org) => ({
      id: `kyc-${org.id}`,
      label: `${org.org || 'Organization'} awaiting KYC verification`,
      meta: `${org.documents > 0 ? `${org.documents} document${org.documents === 1 ? '' : 's'} to review` : 'Submitted for review'} · blocked from publishing`,
      severity: 'amber',
      time: formatRelativeAge(org.createdAt),
      target: 'organizer-detail',
      recordType: 'kyc',
      // OrganizerDetailPage resolves by name + uses id for the KYC tab; give it both.
      record: { id: org.id, name: org.org, routeId: org.id },
    })),
    ...pendingRequests.map((request) => ({
      id: `request-${request.id}`,
      label: `Event request "${request.event}" awaiting review`,
      // The name falls back to the email when none was given — so the same address printed twice.
      meta: [...new Set([request.name, request.email || request.phone].filter(Boolean))].join(' · ') || request.organizer,
      severity: 'amber',
      time: formatRelativeAge(request.raw?.createdAt),
      target: 'event-requests',
      recordType: 'request',
      record: request,
    })),
  ];
  return {
    attention,
    activeEvents,
    counts: { events: events.length },
    errors,
  };
}

export async function loadEventStatsForEvents(eventIds = [], { signal } = {}) {
  const ids = [...new Set((eventIds || []).filter(Boolean))];
  if (!ids.length) return { stats: {}, source: null, error: null };
  const statsPath = `/api/events/admin-stats/cached?eventIds=${encodeURIComponent(ids.join(','))}`;
  const statsResponse = await fetchBackOfficeJson(statsPath, { signal });
  if (!statsResponse.ok) {
    return {
      stats: {},
      source: null,
      error: statsResponse.status === 401
        ? 'permission denied'
        : `${statsResponse.status || ''} ${statsResponse.payload?.error || 'Event stats unavailable'}`.trim(),
    };
  }
  return {
    stats: statsResponse.payload?.statsbyevent || {},
    source: statsPath,
    error: null,
  };
}

export const mergeEventsWithStats = (events = [], stats = {}) => events.map((event) => mergeEventStats(event, stats));

export async function loadEventPages({ page = 1, limit = LIST_PAGE_SIZE, includeStats = true, upcoming = false, sort, searchQuery, status, approvalStatus, venueId, signal } = {}) {
  const q = (searchQuery || '').trim();
  // includeTotal defaults false (skip the COUNT query) for the normal upcoming-browse
  // path, which never shows a page-N-of-M total. Callers that DO show a real total
  // (past-events/search browsing) can opt in — worth the one-time COUNT cost since
  // it's only run when the operator explicitly asks to browse past/search results.
  const primaryPath = `${eventPagesPath({ page, limit, venueId })}${upcoming ? '&upcoming=true' : ''}${sort ? `&sort=${sort}` : ''}${q ? `&searchQuery=${encodeURIComponent(q)}` : ''}${status ? `&status=${encodeURIComponent(status)}` : ''}${approvalStatus ? `&approvalStatus=${encodeURIComponent(approvalStatus)}` : ''}`;
  const eventsResponse = await fetchBackOfficeJson(primaryPath, { signal });
  let source = primaryPath;
  let response = eventsResponse;
  // The 401 fallback is the UNFILTERED /all list. Falling back to it under a
  // venueId would hand the caller every event on the platform while it believes
  // it asked "which events use this venue" — a silent degradation that reads as
  // data. A scoped query either answers scoped or fails loudly.
  if (!response.ok && response.status === 401 && !venueId) {
    response = await fetchBackOfficeJson(ENDPOINTS.eventPagesFallback, { signal });
    source = ENDPOINTS.eventPagesFallback;
  }
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Event pages request failed'}`);
  }
  const eventRows = list(response.payload);
  const normalizedRows = eventRows.map(normalizeEvent);
  const statsResult = includeStats
    ? await loadEventStatsForEvents(normalizedRows.map((event) => event.id), { signal }).catch((error) => ({ stats: {}, source: null, error: error.message }))
    : { stats: {}, source: null, error: null };
  return {
    rows: includeStats ? mergeEventsWithStats(normalizedRows, statsResult.stats) : normalizedRows,
    source,
    statsSource: statsResult.source,
    statsError: statsResult.error,
    // The server's total, or null — never the page size, which read "All 25" for 146 events and fed every tab badge.
    total: response.payload?.totalItems ?? response.payload?.pagination?.total ?? (response.payload?.hasMore ? null : eventRows.length),
    page: number(response.payload?.currentPage || response.payload?.pagination?.page, page),
    hasMore: Boolean(response.payload?.hasMore),
  };
}

// AN EVENT'S CARD DESIGN — the artwork its badges / invitation cards are drawn in. The designs are
// folders the backend ships (static/<name>/badge.html); an event names one, and every credential
// surface — the print sheet, TKT Print, the on-demand PDF a send attaches — draws in it.
export async function loadBadgeDesigns({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/event-pages/badge-designs', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Designs unavailable'}`);
  return r.payload?.data || [];
}
export async function setEventBadgeDesign(eventId, template, { signal } = {}) {
  return writeJson(`/api/event-pages/${encodeURIComponent(eventId)}/badge-design`, { method: 'PUT', body: { template: template || null }, signal });
}

// AN EVENT'S LIVE STREAM (tkt-backend routes/eventStreamRoutes, on Cloudflare Stream). The event
// keeps only `metadata.stream = { liveInputId, playbackOrigin }`; everything else, including the
// stream key, is read from Cloudflare through these. `missing`: the event points at a stream that
// was deleted in Cloudflare's own dashboard.
const streamPath = (eventId, rest = '') => `/api/event-streams/${encodeURIComponent(eventId)}${rest}`;
export async function loadEventStream(eventId, { signal } = {}) {
  const r = await fetchBackOfficeJson(streamPath(eventId), { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} Live stream unavailable`);
  return { stream: r.payload?.data || null, missing: !!r.payload?.missing };
}
/** `settings`: { lowLatency, keepRecordingsDays (null = until deleted), reconnectSeconds, showViewerCount }. */
export const createEventStream = async (eventId, settings) => (await writeJson(streamPath(eventId), { method: 'POST', body: { settings } }))?.data || null;
export const updateEventStreamSettings = async (eventId, settings) => (await writeJson(streamPath(eventId), { method: 'PATCH', body: { settings } }))?.data || null;
export const rotateEventStreamKey = async (eventId) => (await writeJson(streamPath(eventId, '/rotate-key'), { method: 'POST' }))?.data || null;
export const removeEventStream = (eventId) => writeJson(streamPath(eventId), { method: 'DELETE' });
// Restreaming: Cloudflare forwards the broadcast to another platform's RTMP(S) server with that platform's key.
export const addEventStreamOutput = async (eventId, { url, streamKey }) => (await writeJson(streamPath(eventId, '/outputs'), { method: 'POST', body: { url, streamKey } }))?.data || null;
export const setEventStreamOutputEnabled = async (eventId, outputId, enabled) => (await writeJson(streamPath(eventId, `/outputs/${encodeURIComponent(outputId)}`), { method: 'PATCH', body: { enabled } }))?.data || null;
export const removeEventStreamOutput = (eventId, outputId) => writeJson(streamPath(eventId, `/outputs/${encodeURIComponent(outputId)}`), { method: 'DELETE' });
/** Cloudflare's public answer to "is it on air?" — no token, CORS-open, so a page asks it directly. */
export async function loadStreamLifecycle(stream, { signal } = {}) {
  const r = await fetch(`${stream.playbackOrigin}/${stream.liveInputId}/lifecycle`, { signal });
  if (!r.ok) throw new Error(`${r.status} Stream status unavailable`);
  const j = await r.json();
  return { live: !!j.live, videoId: j.videoUID || null };
}

// GET /api/event-pages/:id → ONE event, by id, normalized to the same row shape as
// loadEventPages. This is the true by-id resolver behind a cold deep-link / shared
// URL (the events list-find only covered page 1, so /events/<id> for any event past
// the first page degraded to the list — see entityById.ts). Returns null on 404.
export async function loadEventById(id, { signal } = {}) {
  if (!id) return null;
  const r = await fetchBackOfficeJson(`/api/event-pages/${encodeURIComponent(id)}`, { signal });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Event unavailable'}`);
  const raw = r.payload?.data || r.payload?.eventPage || r.payload || null;
  return raw && (raw.id || raw.eventPageId) ? normalizeEvent(raw) : null;
}

// Create/update go through writeJson like every other write in this file — which is what puts
// their `date` under the one contract (lib/absoluteInstant) instead of a per-function reminder.
export async function createEventPage(payload, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const body = await writeJson(eventPageEndpoint(), { body: payload, signal });
  return normalizeEvent(body.event || body.eventPage || body.data || body);
}

export async function updateEventPage(eventId, payload, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const body = await writeJson(eventPageEndpoint(`/${encodeURIComponent(eventId)}`), { method: 'PATCH', body: payload, signal });
  return normalizeEvent(body.event || body.eventPage || body.data || body);
}

/**
 * ONE SHAPE FOR EVERY `PATCH /<verb>/:eventId` LIFECYCLE CALL.
 *
 * The backend exposes these as verb-named pairs — publish/unpublish, archive/unarchive,
 * feature/unfeature — all taking no body and returning `{ event }`. On this side they were
 * hand-copied per verb: `publishEventPage` and `unpublishEventPage` were byte-identical apart from
 * the path and the word in the error message, which is how a fourth and fifth copy get written
 * without anyone deciding to. Adding feature/unfeature as two more copies would have made five.
 *
 * The verb IS the parameter. A new lifecycle verb is now one line, and the dev-auth priming,
 * org-scope header, content-type handling and error shape cannot drift between them.
 */
async function eventLifecyclePatch(verb, eventId, { signal, failLabel } = {}) {
  if (!eventId) throw new Error('eventId is required');
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const response = await fetch(`${API_BASE}${eventPageEndpoint(`/${verb}/${encodeURIComponent(eventId)}`)}`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    throw new Error(body?.error || body?.errors?.[0] || `${response.status} ${response.statusText || failLabel || `${verb} failed`}`);
  }
  return normalizeEvent(body.event || body.eventPage || body.data || body);
}

export async function publishEventPage(eventId, opts = {}) {
  return eventLifecyclePatch('publish', eventId, { ...opts, failLabel: 'Publish failed' });
}

export async function unpublishEventPage(eventId, opts = {}) {
  return eventLifecyclePatch('unpublish', eventId, { ...opts, failLabel: 'Unpublish failed' });
}

/**
 * FEATURE / UNFEATURE — which events the back office is actively putting forward.
 *
 * Persisted as `EventPage.metadata.featuredAt` (a timestamp: presence is the flag, the value is the
 * order), so the Featured strip survives reload and is the same for every operator — it is a
 * property of the EVENT, not a per-user pin. In `metadata` rather than its own column because no
 * migration should be needed to try a curation flag out.
 *
 * Featuring an already-featured event is allowed and restamps it to the front of the strip; the
 * backend deliberately does not treat that as an error, unlike archive.
 */
export async function featureEventPage(eventId, opts = {}) {
  return eventLifecyclePatch('feature', eventId, { ...opts, failLabel: 'Could not feature event' });
}

export async function unfeatureEventPage(eventId, opts = {}) {
  return eventLifecyclePatch('unfeature', eventId, { ...opts, failLabel: 'Could not unfeature event' });
}

/**
 * REORDER THE FEATURED STRIP — persists the arrangement an operator dragged.
 *
 * Sends the WHOLE strip in its new order rather than "move id X to slot 3", because a single
 * event's rank only means something relative to its neighbours: the backend writes
 * `metadata.featuredOrder = index` down the list it is given, which makes the call idempotent and
 * the stored ranks dense from 0. Ids the caller omits keep whatever rank they had — reordering is
 * not a curation edit and must not unfeature anything off-screen.
 */
export async function reorderFeaturedEvents(eventPageIds, { signal } = {}) {
  return writeJson(eventPageEndpoint('/featured/order'), { method: 'PATCH', body: { eventPageIds }, signal });
}

// Generate a secure, shareable read-only report link for an event (POST
// /:eventId/generate-report-code → { code, publicUrl }). The signed code lets a
// sponsor/partner view the event's live report at /public/events/report/:code
// for 7 days WITHOUT an account. Returns { code, publicUrl }.
// `window` signs a period into the link (tkt-backend utils/salesWindow). A windowed link is a
// period's sales only: it gets no remittance URL, because a remittance settles the whole event.
export async function generateEventReportCode(eventId, { window = null, signal } = {}) {
  await ensureDevAuthSession({ signal }).catch(() => {});
  const response = await fetch(`${API_BASE}${eventPageEndpoint(`/${encodeURIComponent(eventId)}/generate-report-code`)}`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(window ? { window } : {}),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok || body?.success === false) {
    throw new Error(body?.error || `${response.status} ${response.statusText || 'Could not generate report link'}`);
  }
  const publicUrl = body?.data?.publicUrl || null;
  // The remittance view is the SAME signed code on a different path. The buy-side console
  // derived it by string-replacing the segment at the call site, which silently produces a
  // dead link the day that route moves — so the substitution lives here, once, and an
  // unrecognised shape is returned untouched rather than mangled into a URL that looks
  // right and resolves to nothing.
  // HOSTED BY tkt-new-ui at panel.<domain> (backoffice.moments.ug → panel.moments.ug); locally,
  // the backend's own configured origin. Same signed code, the remittance path.
  const code = body?.data?.code || null;
  const remittanceUrl = code && !window
    ? `${derivedHostUrl('panel', () => { try { return new URL(publicUrl).origin; } catch { return ''; } })}/public/remittance-advice/${encodeURIComponent(code)}`
    : null;
  return { code, publicUrl, remittanceUrl };
}

export async function updateEventApproval(eventId, approvalStatus, { signal, comment } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const response = await fetch(`${API_BASE}${eventPageEndpoint(`/approval/${encodeURIComponent(eventId)}`)}`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    // `comment` is required by the backend when approvalStatus is 'blocked'
    // (mirrors the org-verification reject-with-reason pattern) — the
    // organizer needs something concrete to act on, not a bare status flip.
    body: JSON.stringify({ approvalStatus, ...(comment ? { comment } : {}) }),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = Array.isArray(body?.errors) ? body.errors.join(', ') : body?.error || `${response.status} ${response.statusText || 'Approval update failed'}`;
    throw new Error(message);
  }
  return normalizeEvent(body.event || body.eventPage || body.data || body);
}

export async function duplicateEventPage(eventId, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const response = await fetch(`${API_BASE}${eventPageEndpoint(`/${encodeURIComponent(eventId)}/duplicate`)}`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = Array.isArray(body?.errors) ? body.errors.join(', ') : body?.error || `${response.status} ${response.statusText || 'Duplicate failed'}`;
    throw new Error(message);
  }
  return normalizeEvent(body.event || body.eventPage || body.data || body);
}

// Delete an event page. Backend does Model.destroy + cache invalidation; a bare
// destroy can fail if FK-constrained rows (orders/tickets) reference it.
export async function deleteEventPage(eventId, { signal } = {}) {
  return writeJson(eventPageEndpoint(`/${encodeURIComponent(eventId)}`), { method: 'DELETE', signal });
}

export async function loadEventStats(eventId, { signal } = {}) {
  const path = `/api/events/${encodeURIComponent(eventId)}/stats/cached`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Event stats unavailable'}`);
  }
  return {
    stats: response.payload?.stats || response.payload?.data?.stats || {},
    statsByTicketType: response.payload?.statsbyttickettpe || response.payload?.statsByTicketType || null,
    statsByDate: response.payload?.statsbydate || response.payload?.statsByDate || null,
    ordersByPaymentType: list(response.payload?.ordersByPaymentType),
    ordersByBrowser: list(response.payload?.ordersByBrowser),
    ordersByDeviceType: list(response.payload?.ordersByDeviceType),
    source: path,
    raw: response.payload,
  };
}

// Event showtimes / recurring schedules (cinema, multi-day, weekly fixtures).
// Backed by /api/schedules (GET /event/:eventId list, POST create, DELETE /:id).
// A Schedule = a date (+ optional recurrence) carrying one or more time slots.
const WEEKDAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
export function normalizeSchedule(raw = {}, index = 0) {
  const slots = list(raw.slots).map((s) => ({
    startTime: String(firstPresent(s.startTime, s.start, '') || '').slice(0, 5),
    endTime: String(firstPresent(s.endTime, s.end, '') || '').slice(0, 5),
    capacity: s.capacity != null && s.capacity !== '' ? Number(s.capacity) : null,
    venueId: s.venueId || null,
  })).filter((s) => s.startTime || s.endTime);
  const weekdays = list(raw.weekdays).map((d) => WEEKDAY_LABELS[d] || d);
  return {
    ...raw,
    id: String(firstPresent(raw.id, `schedule-${index + 1}`)),
    eventId: raw.eventId || null,
    startDate: raw.startDate || raw.start_date || null,
    endDate: raw.endDate || raw.end_date || null,
    repeat: raw.repeat || 'once',
    weekdays,
    cadence: raw.cadence || null,
    repeatEvery: Number(raw.repeatEvery) || null,
    occursOn: raw.occursOn || null,
    timeType: raw.timeType || (slots.length > 1 ? 'multiple' : 'single'),
    slots,
    raw,
  };
}

export async function loadEventSchedules(eventId, { signal } = {}) {
  const path = `/api/schedules/event/${encodeURIComponent(eventId)}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Schedules unavailable'}`);
  }
  return list(response.payload).map(normalizeSchedule);
}

export async function createEventSchedule({ eventId, startDate, endDate, repeat = 'once', weekdays, timeType, slots = [] } = {}, { signal } = {}) {
  if (!eventId) throw new Error('eventId is required');
  if (!startDate) throw new Error('A start date is required');
  if (!slots.length) throw new Error('At least one time slot is required');
  const body = {
    eventId,
    startDate,
    repeat,
    timeType: timeType || (slots.length > 1 ? 'multiple' : 'single'),
    // Carry per-showtime capacity (the screen's seats) + venueId (the screen/auditorium) through to
    // the backend, which stores them on the schedule and stamps each generated occurrence with them.
    slots: slots.map((s) => ({
      startTime: s.startTime,
      endTime: s.endTime,
      ...(s.capacity != null && s.capacity !== '' ? { capacity: Number(s.capacity) } : {}),
      ...(s.venueId ? { venueId: s.venueId } : {}),
    })),
    ...(endDate ? { endDate } : {}),
    ...(Array.isArray(weekdays) && weekdays.length ? { weekdays } : {}),
  };
  const payload = await writeJson('/api/schedules', { body, signal });
  return payload?.schedule || payload;
}

export async function deleteEventSchedule(scheduleId, { signal } = {}) {
  if (!scheduleId) throw new Error('A schedule id is required');
  return writeJson(`/api/schedules/${encodeURIComponent(scheduleId)}`, { method: 'DELETE', signal });
}

// Expand a Schedule (recurrence rule) into concrete dated ScheduleSlot rows.
// POST /api/schedule-slots/generate-from-schedule/:scheduleId → { count }.
// Idempotent on the backend (unique index + ignoreDuplicates).
export async function generateScheduleSlots(scheduleId, { signal } = {}) {
  if (!scheduleId) throw new Error('A schedule id is required');
  const payload = await writeJson(`/api/schedule-slots/generate-from-schedule/${encodeURIComponent(scheduleId)}`, { body: {}, signal });
  return { count: Number(payload?.count) || 0, raw: payload };
}

// Concrete occurrences for an event, with per-slot inventory.
// GET /api/schedule-slots/event/:eventId/availability → each slot + remaining.
export function normalizeScheduleSlot(raw = {}, index = 0) {
  const capacity = Number(firstPresent(raw.capacity, 0)) || 0;
  const booked = Number(firstPresent(raw.bookedCount, raw.booked, 0)) || 0;
  const remaining = raw.remaining != null ? Number(raw.remaining) : Math.max(0, capacity - booked);
  return {
    ...raw,
    id: String(firstPresent(raw.id, `slot-${index + 1}`)),
    eventId: raw.eventId || null,
    scheduleId: raw.scheduleId || null,
    startDateTime: raw.startDateTime || raw.start || null,
    endDateTime: raw.endDateTime || raw.end || null,
    name: raw.name || null,
    capacity,
    booked,
    remaining,
    venueId: raw.venueId || raw.venue?.id || null,
    venueName: raw.venueName || raw.venue?.name || null, // the screen/auditorium label
    isActive: raw.isActive !== false, // undefined (pre-migration rows) reads as active
    raw,
  };
}

// Per-showtime command rollup: every showtime of a (recurring) event with its sell-through + REVENUE +
// ORDER COUNT — the cinema operator's "how is each show doing" view. Returns a Map keyed by slotId.
export async function loadScheduleSlotRollup(eventId, { signal } = {}) {
  if (!eventId) return new Map();
  const response = await fetchBackOfficeJson(`/api/schedule-slots/event/${encodeURIComponent(eventId)}/rollup`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || 'Rollup unavailable'}`);
  const map = new Map();
  for (const r of list(response.payload)) {
    map.set(r.id, { revenue: Number(r.revenue) || 0, orderCount: Number(r.orderCount) || 0, currency: r.currency || null, admitted: Number(r.admitted) || 0 });
  }
  return map;
}

// The orders for ONE showtime (the drill from the command view). Reuses normalizeOrder so the rows
// match the rest of the dashboard.
// A page of one showtime's orders, newest first: { rows, hasMore } (the route's look-ahead).
export async function loadSlotOrders(slotId, { page = 1, limit = LIST_PAGE_SIZE, signal } = {}) {
  if (!slotId) return { rows: [], hasMore: false };
  const response = await fetchBackOfficeJson(`/api/schedule-slots/${encodeURIComponent(slotId)}/orders?limit=${limit}&offset=${(page - 1) * limit}`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || 'Orders unavailable'}`);
  return { rows: list(response.payload).map(normalizeOrder), hasMore: Boolean(response.payload?.hasMore) };
}

// Lightweight venue options for the per-showtime "screen" picker. Venue is optional on a slot, so a
// failed/empty load degrades to "no screens" rather than blocking schedule setup.
export async function loadVenueOptions({ signal } = {}) {
  try {
    // limit=100 (the endpoint max) so the screen picker isn't silently truncated at the default 50.
    const response = await fetchBackOfficeJson('/api/venues?limit=100', { signal });
    if (!response.ok) return [];
    return list(response.payload)
      .map((v) => ({ id: v.id, name: v.name || v.title || 'Venue', address: v.address || v.city || '' }))
      .filter((v) => v.id);
  } catch { return []; }
}

// Occurrences for an event. Pass { from, to } (ISO) to window the fetch — the
// calendar loads one visible month at a time so thousands of slots stay bounded.
// Active DAYS are derived from these slots client-side (no distinct-days endpoint).
export async function loadScheduleSlots(eventId, { from, to, signal } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  // The admin agenda manages/reactivates cancelled occurrences too, so it must see them
  // (buyer-facing pickers hitting this same endpoint omit this and get active-only).
  qs.set('includeInactive', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const path = `/api/schedule-slots/event/${encodeURIComponent(eventId)}/availability${suffix}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Occurrences unavailable'}`);
  }
  return list(response.payload).map(normalizeScheduleSlot)
    .sort((a, b) => String(a.startDateTime || '').localeCompare(String(b.startDateTime || '')));
}

// Upcoming occurrences, paginated (startDateTime >= now, ascending). This is the
// spine of the occurrences agenda — you page FORWARD through what's next instead
// of ever loading the whole (possibly thousands-strong) slot set. Returns
// { slots, total, hasMore, offset }.
export async function loadUpcomingSlots(eventId, { limit = LIST_PAGE_SIZE, offset = 0, signal } = {}) {
  // includeInactive=true: the admin agenda manages/reactivates cancelled occurrences too
  // (buyer-facing pickers hitting this same endpoint omit this and get active-only).
  const path = `/api/schedule-slots/event/${encodeURIComponent(eventId)}/upcoming?limit=${limit}&offset=${offset}&includeInactive=true`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Occurrences unavailable'}`);
  }
  const p = response.payload || {};
  const slots = list(p).map(normalizeScheduleSlot);
  const pg = p.pagination || {};
  return {
    slots,
    total: Number(pg.total) || slots.length,
    offset: Number(pg.offset) || offset,
    hasMore: pg.hasMore != null ? !!pg.hasMore : false,
  };
}

export async function deleteScheduleSlot(slotId, { signal } = {}) {
  if (!slotId) throw new Error('A slot id is required');
  return writeJson(`/api/schedule-slots/${encodeURIComponent(slotId)}`, { method: 'DELETE', signal });
}

// Edit a single occurrence after generation — e.g. move the 9pm to a bigger screen, or change its
// capacity/time. Only sends the fields provided. venueId can be null to clear the per-slot screen.
export async function updateScheduleSlot(slotId, patch = {}, { signal } = {}) {
  if (!slotId) throw new Error('A slot id is required');
  const body = {};
  if (patch.capacity != null && patch.capacity !== '') body.capacity = Number(patch.capacity);
  if (patch.venueId !== undefined) body.venueId = patch.venueId || null;
  if (patch.startDateTime) body.startDateTime = patch.startDateTime;
  if (patch.endDateTime) body.endDateTime = patch.endDateTime;
  if (patch.name !== undefined) body.name = patch.name || null;
  if (patch.isActive !== undefined) body.isActive = !!patch.isActive;
  return writeJson(`/api/schedule-slots/${encodeURIComponent(slotId)}`, { method: 'PATCH', body, signal });
}

// Add ONE dated occurrence directly — no recurrence rule needed. Same backend call
// (POST /api/schedule-slots) sellers-dashboard's AddMovieForm already uses for per-showtime
// authoring; for events whose showtimes don't follow a repeating pattern, this is faster than
// authoring a one-off Schedule just to Generate a single slot from it.
export async function createScheduleSlot({ eventId, scheduleId, startDateTime, endDateTime, capacity, name, venueId } = {}, { signal } = {}) {
  if (!eventId) throw new Error('eventId is required');
  if (!startDateTime || !endDateTime) throw new Error('Start and end time are required');
  const body = {
    eventId,
    startDateTime,
    endDateTime,
    capacity: Number(capacity) || 100,
    ...(scheduleId ? { scheduleId } : {}),
    ...(name ? { name } : {}),
    ...(venueId ? { venueId } : {}),
  };
  return writeJson('/api/schedule-slots', { body, signal });
}

// `window` (lib/reportPeriod periodWindow) narrows the summary to a period's sales; null = the whole event.
export async function loadEventTicketSummary(eventId, { window = null, signal } = {}) {
  const q = !window ? '' : window.days ? `?days=${window.days}` : `?from=${window.from}&to=${window.to}`;
  const path = eventPageEndpoint(`/${encodeURIComponent(eventId)}/ticket-summary${q}`);
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Ticket summary unavailable'}`);
  }
  const data = response.payload?.data || response.payload;
  const rows = Array.isArray(data?.summary) ? data.summary : list(data);
  return {
    tickets: rows.map(normalizeTicketSummary),
    totals: data?.totals || {},
    // What the platform keeps back from the organizer, saved on the event (remittance advice).
    deductions: Array.isArray(data?.deductions) ? data.deductions : [],
    generatedAt: data?.generatedAt || null,
    // The period these figures cover (with the instants used), or null for the whole event.
    window: data?.window || null,
    source: path,
  };
}

// --- Deductions (backend /api/deductions, models/Deduction) ---------------------------------
// What the platform keeps back from an organizer: on an EVENT (comes off its balance) or on a
// PAYOUT as well (a cost of that transfer — comes out of what it delivers). Platform writes.
export async function loadDeductions({ eventId, payoutId, organizationId, signal } = {}) {
  const q = eventId ? `eventId=${encodeURIComponent(eventId)}` : payoutId ? `payoutId=${encodeURIComponent(payoutId)}` : `organizationId=${encodeURIComponent(organizationId)}`;
  const response = await fetchBackOfficeJson(`/api/deductions?${q}`, { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Deductions unavailable`);
  return Array.isArray(response.payload?.deductions) ? response.payload.deductions : [];
}
export async function addDeduction(input, { signal } = {}) {
  return (await writeJson('/api/deductions', { method: 'POST', body: input, signal }))?.deduction;
}
export async function updateDeduction(id, changes, { signal } = {}) {
  return (await writeJson(`/api/deductions/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes, signal }))?.deduction;
}
export async function removeDeduction(id, { signal } = {}) {
  return writeJson(`/api/deductions/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

/** When this event's ticket types sell out, if ever — backend routes/selloutRoutes.ts. Returned as
 *  the server computed it: probabilities, times (unix seconds) and paths are the model's, and the
 *  wording belongs to the view (modules/events/SellOutOutlook.jsx). */
export async function loadEventSellOutOutlook(eventId, { ids = [], focus = null, signal } = {}) {
  const params = new URLSearchParams();
  if (ids.length) params.set('ids', ids.join(','));
  if (focus) params.set('focus', focus);
  const path = `/api/sell-out/event/${encodeURIComponent(eventId)}${params.size ? `?${params}` : ''}`;
  const response = await fetchBackOfficeJson(path, { signal, timeoutMs: 30000 });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Sell-out outlook unavailable'}`);
  }
  return response.payload?.data || null;
}

export async function loadEventAdmissionStats(eventId, { signal } = {}) {
  const path = `/api/events/${encodeURIComponent(eventId)}/admissions/stats/cached`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok || response.payload?.success === false) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Admission stats unavailable'}`);
  }
  return {
    stats: response.payload?.data || response.payload,
    source: path,
  };
}

export async function loadEventRecentAdmissions(eventId, { signal, limit = 20 } = {}) {
  const path = `/api/events/${encodeURIComponent(eventId)}/admissions/recent?limit=${limit}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok || response.payload?.success === false) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Recent admissions unavailable'}`);
  }
  return {
    admissions: list(response.payload?.data || response.payload),
    source: path,
  };
}

const normalizeScanItem = (row, index = 0) => {
  const seat = row.seat || {};
  const section = seat.row?.section || {};
  const entrance = section.entrance || {};
  const metadata = row.metadata || {};
  const ticketRef = firstPresent(
    row.ticket_ref,
    row.ticketRef,
    metadata.ticket_ref,
    metadata.ticketRef,
    row.ticket?.ticket_ref,
    row.ticket?.ticketRef,
    row.ticket?.ticketRefCode,
    row.signature
  );
  return {
    ...row,
    id: String(firstPresent(row.id, `scan-item-${index + 1}`)),
    displayId: String(firstPresent(ticketRef, row.orderId, row.email, `Credential ${index + 1}`)),
    ticketRef: firstPresent(ticketRef, ''),
    signature: firstPresent(row.signature, '—'),
    holder: [row.firstName, row.lastName].filter(Boolean).join(' ') || firstPresent(row.email, row.phone, 'Guest holder'),
    email: firstPresent(row.email, ''),
    phone: firstPresent(row.phone, ''),
    type: titleCase(firstPresent(row.type, 'ticket')),
    orderId: firstPresent(row.orderId, ''),
    ticketTypeId: firstPresent(row.ticketType?.id, row.ticketTypeId, ''),
    ticketType: firstPresent(row.ticketType?.name, row.ticketTypeName, 'Ticket'),
    currency: firstPresent(row.ticketType?.currency, row.currency, 'UGX'),
    price: number(firstPresent(row.ticketType?.price, row.price)),
    seatId: firstPresent(row.seatId, seat.id, ''),
    seat: firstPresent(seat.name, seat.seatNumber ? `Seat ${seat.seatNumber}` : ''),
    row: firstPresent(seat.row?.name, ''),
    section: firstPresent(section.name, ''),
    entranceId: firstPresent(entrance.id, ''),
    entrance: firstPresent(entrance.name, ''),
    usedAt: firstPresent(row.usedAt, row.scannedAt, ''),
    scheduleSlotId: firstPresent(row.scheduleSlotId, row.slotId, null),
    raw: row,
  };
};

const normalizeScanDevice = (row, index = 0) => {
  const ticketTypes = Array.isArray(row.scanDeviceTicketTypes) ? row.scanDeviceTicketTypes : [];
  const state = Number(firstPresent(row.state, 0));
  const name = firstPresent(row.name, row.label, row.deviceName, `Scan device ${index + 1}`);
  return {
    ...row,
    id: String(firstPresent(row.id, `scan-device-${index + 1}`)),
    displayId: String(firstPresent(row.serialNumber, row.serial, row.deviceCode, row.code, name)),
    name,
    role: firstPresent(row.role, 'validate'),
    state,
    stateLabel: state === 0 ? 'Active' : state === 1 ? 'Paused' : state === 2 ? 'Revoked' : `State ${state}`,
    battery: row.battery == null ? null : number(row.battery),
    userEmail: firstPresent(row.userEmail, ''),
    totalSynced: number(firstPresent(row.totalSynced), 0),
    ticketTypes: ticketTypes.map((ticketType) => firstPresent(ticketType.name, ticketType.id)).filter(Boolean),
    raw: row,
  };
};

// Look up ONE credential by ticket ref / signature / id (or 6+ char id prefix) — the
// gate-exception tool: a guest's ticket won't scan, you search it and see its real
// state (admitted vs issued, where/when scanned, the device). GET /api/scanItems/:id.
export async function lookupScanItem(ref, { signal } = {}) {
  const q = String(ref || '').trim();
  if (!q) return { found: false };
  const r = await fetchBackOfficeJson(`/api/scanItems/${encodeURIComponent(q)}`, { signal });
  if (r.status === 404) return { found: false };
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Lookup failed'}`);
  const data = r.payload?.data || r.payload || {};
  const history = Array.isArray(data.scanHistory) ? data.scanHistory : [];
  const scanned = data.scanStatus === 'scanned' || history.some((s) => s.result);
  return {
    found: true,
    item: normalizeScanItem(data),
    scanned,
    lastScannedAt: firstPresent(data.lastScannedAt, history[0]?.createdAt, null),
    scanCount: number(firstPresent(data.scanCount, history.filter((s) => s.result).length), 0),
    device: firstPresent(history.find((s) => s.result)?.device?.name, history[0]?.device?.name, ''),
  };
}

export async function loadEventScanItems(eventId, { signal, limit = LIST_PAGE_SIZE, page = 1, searchQuery, searchField } = {}) {
  // `searchQuery` is one named field (name · email · phone · signature), a prefix — the backend's attendee search.
  const search = searchQuery ? `&searchQuery=${encodeURIComponent(searchQuery)}&searchField=${encodeURIComponent(searchField || 'name')}` : '';
  const path = `/api/scanItems/event/${encodeURIComponent(eventId)}?limit=${limit}&page=${page}${search}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Scan items unavailable'}`);
  }
  const rows = list(response.payload);
  return {
    scanItems: rows.map(normalizeScanItem),
    source: path,
    total: number(firstPresent(response.payload?.total, response.payload?.totalItems), rows.length),
    hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false,
    page: number(firstPresent(response.payload?.page, response.payload?.currentPage), page),
    totalPages: number(response.payload?.totalPages, 1),
  };
}

export async function loadEventScanDevices(eventId, { signal } = {}) {
  const path = `/api/scanDevices/event/${encodeURIComponent(eventId)}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Scan devices unavailable'}`);
  }
  const rows = list(response.payload);
  return {
    devices: rows.map(normalizeScanDevice),
    source: path,
    total: rows.length,
  };
}
// Update a scan device: PATCH /api/scanDevices/:id. Backend finds by the param
// id (clean) and updates the given fields (name, role, state, userEmail). Needs
// scanDevice:update permission. role ∈ validate | validate_and_scan | scan.
// Create ONE scan device for an event. POST /api/scanDevices. selectedTicketTypes is
// [{id}] (which tiers this device may scan; empty = all). role ∈ validate|validate_and_scan|scan.
export async function createScanDevice(eventId, { name, role = 'validate_and_scan', selectedTicketTypes = [], userEmail } = {}, { signal } = {}) {
  const body = await writeJson('/api/scanDevices', { body: {
    eventId, name,
    selectedTicketTypes: (selectedTicketTypes || []).map((id) => ({ id })),
    ...(role ? { role } : {}), ...(userEmail ? { userEmail } : {}),
  }, signal });
  return normalizeScanDevice(body?.scanDevice || body?.data || body, 0);
}

// Remove a scan device — its login code stops working. DELETE /api/scanDevices/:id. The only
// revocation this model has (the newer grants revoke the same way); the server records who did it.
export async function deleteScanDevice(id, { signal } = {}) {
  return writeJson(`/api/scanDevices/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// Bulk create scan devices. POST /api/scanDevices/bulk. Provide EITHER names[] OR
// totalScanners + startNum (auto-numbered). Returns the count created.
export async function bulkCreateScanDevices(eventId, { names, totalScanners, startNum = 1, role = 'validate_and_scan', selectedTicketTypes = [], userEmail } = {}, { signal } = {}) {
  const payload = { eventId, selectedTicketTypes: (selectedTicketTypes || []).map((id) => ({ id })), ...(role ? { role } : {}), ...(userEmail ? { userEmail } : {}) };
  if (Array.isArray(names) && names.length) payload.names = names;
  else if (totalScanners) { payload.totalScanners = Number(totalScanners); payload.startNum = Number(startNum) || 1; }
  const body = await writeJson('/api/scanDevices/bulk', { body: payload, signal });
  const created = body?.data || body?.scanDevices || body?.devices || [];
  return { count: Array.isArray(created) ? created.length : (body?.count || (names?.length || totalScanners || 0)), devices: Array.isArray(created) ? created.map(normalizeScanDevice) : [] };
}

// ── Device access (/api/scan/access) — the NEW handheld scanner ──────────────
//
// PARALLEL to the scan-device helpers above, which drive /api/scanDevices (the
// legacy scanner the Flutter app depends on). This surface is the new scanner:
// a "grant" is what lets a handheld admit anyone, keyed on the device's own
// permanent code. The two run side by side over the same ScanDevice table; a
// grant here always carries a deviceCode, which is what tells the rows apart.
//
// admit ⇄ validate_and_scan, check ⇄ validate — the API's role vocabulary. A
// grant is scoped by occurrence · ticket types · add-ons. NAMING NOTHING means
// the whole event; naming anything means exactly that and nothing else. It used
// to be documented here as "empty = all" per dimension, which is what the server
// did until scanAccessRoutes' scopeWhere was fixed — under that rule a gate
// scoped to a voucher also held every ticket, and one scoped to VIP also held
// every voucher.

export async function loadDeviceGrants(eventId, { signal } = {}) {
  const res = await fetchBackOfficeJson(`/api/scan/access?eventId=${encodeURIComponent(eventId)}`, { signal });
  if (!res.ok) throw new Error(`${res.status} ${res.payload?.error || 'Device grants unavailable'}`);
  return Array.isArray(res.payload?.grants) ? res.payload.grants : [];
}

export async function grantDeviceAccess(eventId, { deviceCode, name, role = 'admit', scheduleSlotId = null, ticketTypeIds = [], addOnIds = [] } = {}, { signal } = {}) {
  const body = await writeJson('/api/scan/access', { body: {
    eventId, deviceCode,
    ...(name ? { name } : {}),
    role: role === 'admit' ? 'validate_and_scan' : 'validate',
    scheduleSlotId, allowedTicketTypeIds: ticketTypeIds, allowedAddOnIds: addOnIds,
  }, signal });
  return body?.grant || body;
}

export async function removeDeviceGrant(grantId, { signal } = {}) {
  return writeJson(`/api/scan/access/${encodeURIComponent(grantId)}`, { method: 'DELETE', signal });
}

export async function sendDeviceGrantNote(grantId, text, { signal } = {}) {
  return writeJson(`/api/scan/access/${encodeURIComponent(grantId)}/message`, { body: { text }, signal });
}

// A page of one device's logs, newest first: { rows, hasMore }. The next page starts before `last` (the route's id cursor).
export async function loadDeviceGrantLogs(grantId, { level, last, limit = LIST_PAGE_SIZE, signal } = {}) {
  const q = new URLSearchParams({ limit: String(limit) });
  if (level) q.set('level', level);
  if (last) q.set('before', last.id);
  const res = await fetchBackOfficeJson(`/api/scan/access/${encodeURIComponent(grantId)}/logs?${q}`, { signal });
  if (!res.ok) throw new Error(`${res.status} ${res.payload?.error || 'Device logs unavailable'}`);
  return { rows: Array.isArray(res.payload?.logs) ? res.payload.logs : [], hasMore: Boolean(res.payload?.hasMore) };
}

// The join QR for pairing a roomful of handsets. GET with comma-joined scope in
// the query — this backend's contract (see scanAccessRoutes). Returns a signed
// token that lives 5 minutes; the QR encodes `tkt-join:<token>`.
export async function mintDeviceJoinToken(eventId, { role = 'admit', scheduleSlotId = null, ticketTypeIds = [], addOnIds = [] } = {}, { signal } = {}) {
  const q = new URLSearchParams({ eventId, role: role === 'admit' ? 'validate_and_scan' : 'validate' });
  if (scheduleSlotId) q.set('scheduleSlotId', scheduleSlotId);
  if (ticketTypeIds.length) q.set('allowedTicketTypeIds', ticketTypeIds.join(','));
  if (addOnIds.length) q.set('allowedAddOnIds', addOnIds.join(','));
  const res = await fetchBackOfficeJson(`/api/scan/join-token?${q}`, { signal });
  if (!res.ok) throw new Error(`${res.status} ${res.payload?.error || 'Could not create a pairing code'}`);
  return res.payload;
}

/**
 * Simulator artefacts, hidden from every ticket-type picker.
 *
 * The test harness mints tiers named with sentinel wrappers — `__SIM_MI_…__`,
 * `__PAYTEST_tt-…__`, `__SIM_RAIL_…__`. On this database that is **594 of 603
 * ticket types**, so an operator mapping four sections to their three real tiers
 * scrolls past hundreds of them. Filtered once here rather than in each picker,
 * because it is the same noise wherever ticket types are listed.
 *
 * They are HIDDEN, never deleted, and the count is returned so a surface can say
 * so — silently shrinking a list is how a real tier goes missing and nobody can
 * explain why. The pattern is the harness's own convention (leading and trailing
 * double underscore), which no human-named tier uses.
 */
const isSimulatorTicketType = (t) => /^__[A-Z0-9_]+.*__$/.test(String(t?.tier || t?.name || ''));

export async function loadEventTicketTypes(eventId, { signal } = {}) {
  const adminPath = `/api/ticketTypes/event/${encodeURIComponent(eventId)}`;
  const publicPath = `/api/ticketTypes/public/event/${encodeURIComponent(eventId)}`;
  let response = await fetchBackOfficeJson(adminPath, { signal });
  let source = adminPath;
  let access = 'admin';
  let adminError = null;
  if (!response.ok && response.status === 401) {
    adminError = `${response.status} ${response.payload?.error || 'Unauthorized'}`;
    response = await fetchBackOfficeJson(publicPath, { signal });
    source = publicPath;
    access = 'public';
  }
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Ticket types unavailable'}`);
  }
  const allTickets = list(response.payload).map(normalizeTicketType);
  const tickets = allTickets.filter((t) => !isSimulatorTicketType(t));
  return {
    tickets,
    // THE PICKER LIST AND THE LOOKUP LIST ARE NOT THE SAME LIST.
    //
    // `tickets` is filtered so an operator is never offered a simulator tier to SELL. But an
    // order that was already placed on one still has to be able to say what it was — and the
    // same holds for any tier since deleted or hidden. Using the filtered list as a lookup
    // catalog is what made a real order render "Ticket type 1 · —": the id simply wasn't
    // there to resolve. Enrichment reads `allTickets`; pickers keep reading `tickets`.
    allTickets,
    // What was withheld, so a picker can state it instead of just being short.
    hiddenSimulatorCount: allTickets.length - tickets.length,
    source,
    access,
    adminError,
    total: response.payload?.totalItems || list(response.payload).length,
    hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false,
  };
}

export function enrichOrdersWithTicketTypes(orders = [], ticketTypes = []) {
  const catalog = new Map((ticketTypes || []).map((ticketType) => [String(ticketType.id), ticketType]));
  return (orders || []).map((order) => {
    const selected = Array.isArray(order.raw?.selectedTickets) ? order.raw.selectedTickets : [];
    // THE ORDER'S OWN LINE ITEMS, as a second source of truth for this lookup.
    //
    // The catalog is the EVENT's current ticket types, and it is not guaranteed to contain
    // the tier an order was actually bought at: loadEventTicketTypes filters simulator tiers
    // out entirely, and a tier that has since been deleted or hidden is simply gone. On a
    // catalog miss this function used to emit `Ticket type 1` with price 0 — and because the
    // receipt prefers enrichedTickets over everything else, that placeholder OVERRODE the
    // real `orderTicketTypes` the backend already returns on the order, which carries the
    // tier's actual name, price and currency. Measured live: an order whose tier was absent
    // from the catalog rendered "Ticket type 1 · —" while the very same payload held
    // name "…" and price "35000.00".
    //
    // So the order's own OrderTicketType rows are consulted before falling back to a
    // placeholder. Enrichment must never produce a row weaker than the one already in hand.
    const ownLines = new Map(
      (Array.isArray(order.raw?.orderTicketTypes) ? order.raw.orderTicketTypes : [])
        .filter((row) => row && row.id != null)
        .map((row) => [String(row.id), row]),
    );
    const enrichedTickets = selected.map((ticket, index) => {
      const id = selectedTicketId(ticket);
      const ticketType = catalog.get(id);
      const own = ownLines.get(String(id));
      const count = selectedTicketQuantity(ticket);
      return {
        id: id || `selected-ticket-${index + 1}`,
        name: firstPresent(ticketType?.tier, ticketType?.name, own?.name, ticket?.name, ticket?.ticketTypeName, `Ticket type ${index + 1}`),
        tier: firstPresent(ticketType?.tier, ticketType?.name, own?.name, ticket?.name, ticket?.ticketTypeName, `Ticket type ${index + 1}`),
        price: number(firstPresent(ticketType?.price, own?.price, ticket?.price), 0),
        // The line's own currency — the one its PRICE is in. No `order.currency` tail:
        // that is the settlement currency and describes a different number. Read from
        // whichever source supplied the price, so the two never disagree.
        currency: firstPresent(ticketType?.currency, own?.currency, ticket?.currency),
        OrderTicketType: { count },
        count,
        rawSelection: ticket,
        ticketType: ticketType || own || null,
      };
    });
    if (!enrichedTickets.length) return order;
    // WHAT THE ORDER IS WORTH vs WHAT WAS COLLECTED — two facts, two fields.
    //
    // `amount` used to be `settled || gross`: whenever net_amount was absent this substituted
    // ticket price × count. For a PENDING order that reads fine ("this is what it's worth"),
    // but the substitution has no idea what state the order is in, so it also fired on
    // SUCCESSFUL orders that genuinely collected nothing — a comp/free/press order settled
    // through settleNow() has net_amount NULL by design (no provider reported an amount).
    // Result, seen live: a free press ticket rendering "Paid · UGX 35,000" in the Orders
    // list's TOTAL column, money that was never charged and that no payout will ever contain.
    //
    // It also broke the contract the consumer documents: OrdersInline's hasRealMoney() says
    // "`o.amount` is sourced strictly from net_amount … no fallback to total/amount/price",
    // and decides between a figure and an em-dash on exactly that basis. This enricher runs
    // AFTER normalizeOrder, so it was quietly overruling it.
    //
    // `amount`/`total` now stay the settled money, full stop. The face value is `orderValue`,
    // which readers can show as what an unpaid order is WORTH without claiming it was paid.
    const gross = enrichedTickets.reduce((sum, t) => sum + (number(t.price, 0) * selectedTicketQuantity(t)), 0);
    const settled = number(order.amount, 0);
    const amount = settled;

    // AN AMOUNT AND ITS CURRENCY ARE ONE VALUE. This function used to swap the amount
    // out from under the currency: when net_amount was null it substituted a gross built
    // from TICKET prices, while `order.currency` still described the SETTLEMENT — so a
    // KES-priced order could render its KES gross labelled with a settlement currency it
    // was never charged in. Whichever number we ended up using, the currency must be the
    // one THAT number is denominated in.
    //
    // The ticket currency costs nothing extra: these ticket types are already loaded by
    // the caller (loadTicketTypesForEvents) for the tier names, so this is a read of data
    // in hand, not a new fetch.
    //
    // Only used when every line agrees. A mixed-currency order has no single currency,
    // and picking one would be the same class of guess this whole path exists to remove —
    // it resolves to null, and null means the amount is not shown at all (see fmtMoney).
    // Single pass, no allocation: a Set + map + filter per order costs three throwaway
    // arrays for what is almost always ONE line, and this runs for every row in the list.
    // Measured, 200 runs per point (ms/call, 25 / 100 / 1000 orders):
    //   before this feature existed   0.0175  0.0306  0.1206
    //   with Set+map+filter           0.0159  0.0335  0.1762   <- regressed at scale
    //   this loop                     0.0127  0.0266  0.1084   <- cheaper than before
    let ticketCurrency = null;
    for (let i = 0; i < enrichedTickets.length; i += 1) {
      const lineCurrency = firstPresent(enrichedTickets[i].ticketType?.currency, enrichedTickets[i].rawSelection?.currency);
      if (!lineCurrency) continue;
      if (ticketCurrency && ticketCurrency !== lineCurrency) { ticketCurrency = null; break; } // mixed → no single currency
      ticketCurrency = lineCurrency;
    }
    // The settled money's currency when there is settled money; otherwise the tickets' own,
    // which is what `orderValue` is denominated in.
    const currency = settled ? order.currency : firstPresent(ticketCurrency, order.currency);

    return {
      ...order,
      ticketLines: enrichedTickets,
      tickets: enrichedTickets.reduce((sum, ticket) => sum + selectedTicketQuantity(ticket), 0),
      amount,
      total: amount,
      // Face value of the lines (price x count). NOT money that moved — see above.
      orderValue: gross,
      currency,
      raw: {
        ...order.raw,
        enrichedTickets,
      },
    };
  });
}

export async function loadTicketTypesForEvents(eventIds = [], { signal } = {}) {
  const uniqueEventIds = [...new Set((eventIds || []).filter(Boolean).map(String))];
  if (!uniqueEventIds.length) {
    return {
      byEventId: new Map(),
      errors: [],
      eventCount: 0,
      loadedCount: 0,
      source: null,
    };
  }
  const bulkPath = `/api/ticketTypes/events/bulk?eventIds=${encodeURIComponent(uniqueEventIds.join(','))}`;
  const bulkResponse = await fetchBackOfficeJson(bulkPath, { signal });
  if (bulkResponse.ok) {
    const byEventId = new Map(uniqueEventIds.map((eventId) => [eventId, {
      tickets: [],
      source: bulkPath,
      access: 'admin',
      total: 0,
    }]));
    list(bulkResponse.payload).forEach((row, index) => {
      const eventId = String(firstPresent(row.eventId, row.event?.id, ''));
      if (!eventId) return;
      if (!byEventId.has(eventId)) {
        byEventId.set(eventId, { tickets: [], source: bulkPath, access: 'admin', total: 0 });
      }
      byEventId.get(eventId).tickets.push(normalizeTicketType(row, index));
    });
    byEventId.forEach((result) => {
      result.total = result.tickets.length;
    });
    return {
      byEventId,
      errors: [],
      eventCount: uniqueEventIds.length,
      loadedCount: byEventId.size,
      source: bulkPath,
    };
  }
  const settled = await Promise.allSettled(
    uniqueEventIds.map(async (eventId) => {
      const result = await loadEventTicketTypes(eventId, { signal });
      return [eventId, result];
    }),
  );
  const byEventId = new Map();
  const errors = [];
  settled.forEach((entry, index) => {
    const eventId = uniqueEventIds[index];
    if (entry.status === 'fulfilled') {
      const [resolvedEventId, result] = entry.value;
      byEventId.set(resolvedEventId, result);
    } else {
      errors.push({ eventId, message: entry.reason?.message || 'Ticket types unavailable' });
    }
  });
  return {
    byEventId,
    errors: [{ eventId: 'bulk', message: `${bulkResponse.status} ${bulkResponse.payload?.error || bulkResponse.statusText || 'Bulk ticket types unavailable'}` }, ...errors],
    eventCount: uniqueEventIds.length,
    loadedCount: byEventId.size,
  };
}

export async function loadEventInventoryTotals(eventId, { signal } = {}) {
  const path = `/api/ticketTypes/event/${encodeURIComponent(eventId)}/inventory`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Inventory totals unavailable'}`);
  }
  return {
    totals: response.payload?.totals || {},
    source: path,
  };
}

export async function loadEventAddOns(eventId, { signal } = {}) {
  const path = `/api/addOns/event/${encodeURIComponent(eventId)}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Add-ons unavailable'}`);
  }
  return {
    rows: list(response.payload).map(normalizeAddOn),
    source: path,
  };
}

// --- Inventory holds & allocations -----------------------------------------
// Backed by /api/holds/event/:eventId (HoldController.getHoldsByEvent) which
// returns `Hold.*` rows + an `affectedSeats` count. The Hold model is rich:
// name, holdType, owner, reason/description, seatCount, holdStatus, release
// window (endDate/endTime), releaseRule.
const normalizeHold = (row, index = 0) => {
  const seatCount = number(firstPresent(row.seatCount, row.affectedSeats, 0));
  const allocated = number(firstPresent(row.affectedSeats, 0));
  return {
    ...row,
    id: String(firstPresent(row.id, `hold-${index + 1}`)),
    name: firstPresent(row.name, row.reason, `Hold ${index + 1}`),
    kind: firstPresent(row.holdType, 'Manual admin'),
    category: firstPresent(row.holdType, '—'),
    qty: seatCount,
    allocated,
    remaining: Math.max(seatCount - allocated, 0),
    releaseDate: row.endDate ? formatDateTimeParts(row.endDate, row.endTime) : firstPresent(row.releaseRule, 'Manual'),
    releaseRule: firstPresent(row.releaseRule, 'manual'),
    owner: firstPresent(row.owner, row.createdBy, '—'),
    notes: firstPresent(row.description, row.reason, ''),
    status: firstPresent(row.holdStatus, 'Active'),
    raw: row,
  };
};

export async function loadEventHolds(eventId, { signal, limit = 100 } = {}) {
  if (!eventId) return { rows: [], total: 0, source: null };
  const path = `/api/holds/event/${encodeURIComponent(eventId)}?limit=${limit}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Holds unavailable'}`);
  }
  const rows = list(response.payload).map(normalizeHold);
  // No invented total — same rule as the orders and pass-holder paths. This route sends neither
  // `total` nor a COUNT, so the fallback would have made a page size look like the whole list the
  // first time anyone rendered it.
  const holdsTotal = response.payload?.total;
  return { rows, total: holdsTotal == null ? null : number(holdsTotal, rows.length), hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false, source: path };
}

// Hold mutations — real /api/holds endpoints (HoldController). createHold takes the
// canonical fields; the BE accepts `type` as an alias for `holdType`. Release returns
// the held inventory to public sale; delete removes the hold record entirely.
export async function createHold({ eventId, name, holdType, seatCount, owner, description, releaseRule, endDate, endTime, force } = {}, { signal } = {}) {
  const body = await writeJson('/api/holds', { body: {
    eventId, name, holdType, seatCount: number(seatCount), owner: owner || undefined,
    description: description || undefined, releaseRule: releaseRule || undefined,
    endDate: endDate || undefined, endTime: endTime || undefined, ...(force ? { force: true } : {}),
  }, signal });
  return body?.hold || body?.data || body;
}

export async function releaseHold(holdId, { signal } = {}) {
  return writeJson(`/api/holds/${encodeURIComponent(holdId)}/release`, { method: 'POST', signal });
}

export async function deleteHold(holdId, { signal } = {}) {
  return writeJson(`/api/holds/${encodeURIComponent(holdId)}`, { method: 'DELETE', signal });
}

// (Issued tickets / scan items use the existing loadEventScanItems +
// normalizeScanItem defined earlier in this file — returns { scanItems, total }.)

// --- Add-ons (event add-ons) -----------------------------------------------
// Backed by /api/addOns (tkt-backend addOnRoutes.ts): POST / · PATCH /:id · DELETE /:id.

export async function createAddOn(payload, { signal } = {}) {
  const body = await writeJson('/api/addOns', { body: payload, signal });
  return body?.addOn || body?.data || body;
}

export async function updateAddOn(addOnId, patch, { signal } = {}) {
  const body = await writeJson(`/api/addOns/${encodeURIComponent(addOnId)}`, { method: 'PATCH', body: patch, signal });
  return body?.addOn || body?.data || body;
}

export async function deleteAddOn(addOnId, { signal } = {}) {
  return writeJson(`/api/addOns/${encodeURIComponent(addOnId)}`, { method: 'DELETE', signal });
}

export async function loadEventPromoCodes(eventId, { signal, limit = 80 } = {}) {
  const path = `/api/promoCodes/event/${encodeURIComponent(eventId)}?limit=${limit}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Promo codes unavailable'}`);
  }
  const rows = list(response.payload);
  return {
    rows: rows.map((row, index) => normalizePromotionRule(row, index, 'promo')),
    total: number(firstPresent(response.payload?.totalItems, response.payload?.total), rows.length),
    hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false,
    source: path,
  };
}

export async function loadEventDiscounts(eventId, { signal, limit = 80 } = {}) {
  const path = `/api/discounts/event/${encodeURIComponent(eventId)}?limit=${limit}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Discounts unavailable'}`);
  }
  const rows = list(response.payload);
  return {
    rows: rows.map((row, index) => normalizePromotionRule(row, index, 'discount')),
    total: number(firstPresent(response.payload?.totalItems, response.payload?.total), rows.length),
    hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false,
    source: path,
  };
}

// --- Pricing: FX configs (Sales Setup) ----------------------------------------
// Backed by /api/pricing/fx-configs; links events via eventIds[], org-filtered server-side.
const normalizeFxConfig = (row, index = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `fx-${index + 1}`)),
  name: firstPresent(row.name, `FX config ${index + 1}`),
  baseCurrency: firstPresent(row.baseCurrency, 'UGX'),
  snapshotPolicy: firstPresent(row.snapshotPolicy, 'lock-daily'),
  status: firstPresent(row.status, 'draft'),
  currencies: Array.isArray(row.currencies) ? row.currencies : [],
  eventIds: Array.isArray(row.eventIds) ? row.eventIds : [],
  spread: Number(row.spread) || 0,
  baseRounding: row.baseRounding != null ? Number(row.baseRounding) : null,
  snapshotPolicyRaw: row.snapshotPolicy,
  updatedAt: row.updatedAt || null,
  raw: row,
});

// organizationId is honored server-side ONLY for a platform admin — pass it to target a specific
// org's desk. `scope: 'floor'` (platform-admin only) targets the platform floor: the FxConfig owned
// by NO organization (organizationId IS NULL) — no fake 'platform' id. Omit both for "my own org".
export async function loadFxConfigs(eventId, { signal, organizationId, scope } = {}) {
  const qs = scope === 'floor' ? '&scope=floor' : (organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : '');
  const path = `/api/pricing/fx-configs?limit=100${qs}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || 'FX configs unavailable'}`);
  }
  const all = list(response.payload).map((r, i) => normalizeFxConfig(r, i));
  // Configs that apply to this event: explicitly linked OR org-wide (no eventIds).
  const applicable = eventId ? all.filter((c) => !c.eventIds.length || c.eventIds.includes(eventId)) : all;
  return { rows: applicable, all, source: path };
}

export async function createFxConfig(payload, { signal } = {}) {
  const body = await writeJson('/api/pricing/fx-configs', { body: payload, signal });
  return normalizeFxConfig(body?.data || body?.fxConfig || body, 0);
}

const orgQs = (organizationId) => (organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : '');

export async function updateFxConfig(id, patch, { signal, organizationId } = {}) {
  const body = await writeJson(`/api/pricing/fx-configs/${encodeURIComponent(id)}${orgQs(organizationId)}`, { method: 'PATCH', body: patch, signal });
  return normalizeFxConfig(body?.data || body?.fxConfig || body, 0);
}

// Make this the ONE active table for the org (the backend drafts the rest in a transaction).
// Preview live mid-market rates for the config's currencies, with the spread applied + cleanly rounded.
// Read-only — returns { baseCurrency, spread, source, fetchedAt, rows:[{currency, current, mid, proposed, unavailable}] }.
// Live mid-market rates for ANY base + currency codes (no config id needed — works for a table being
// created). Returns { [CODE]: mid } where mid is "base per 1 foreign unit" (e.g. { USD: 3700 } for base UGX).
export async function loadLiveFxRates(base, codes, { signal } = {}) {
  const list = (Array.isArray(codes) ? codes : String(codes || '').split(',')).map((s) => String(s).trim().toUpperCase()).filter(Boolean);
  if (!base || !list.length) return {};
  const r = await fetchBackOfficeJson(`/api/pricing/fx-configs/live-rates?base=${encodeURIComponent(base)}&codes=${encodeURIComponent(list.join(','))}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Could not fetch live rates'}`);
  return r.payload?.data?.rates || {};
}

// --- Accreditation (per-event categories, access zones, privileges) --------
// Backed by /api/accreditation-categories, /api/access-zones, /api/priviledges
// — all event-scoped. A category bundles access zones + privileges (M2M).
const normalizeAccreditationCategory = (row, index = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `accr-${index + 1}`)),
  name: firstPresent(row.name, `Category ${index + 1}`),
  description: firstPresent(row.description, ''),
  eventId: firstPresent(row.eventId, ''),
  accessZones: (Array.isArray(row.AccessZones) ? row.AccessZones : row.accessZones || []).map((z) => ({ id: z.id, name: z.name })),
  priviledges: (Array.isArray(row.Priviledges) ? row.Priviledges : row.priviledges || []).map((p) => ({ id: p.id, name: p.name })),
  raw: row,
});
const normalizeNamed = (row, index = 0, prefix = 'item') => ({ id: String(firstPresent(row.id, `${prefix}-${index + 1}`)), name: firstPresent(row.name, `${prefix} ${index + 1}`), eventId: firstPresent(row.eventId, ''), raw: row });

export async function loadEventAccreditationCategories(eventId, { signal } = {}) {
  if (!eventId) return { rows: [], source: null };
  const path = `/api/accreditation-categories/event/${encodeURIComponent(eventId)}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Accreditation categories unavailable'}`);
  return { rows: list(response.payload).map(normalizeAccreditationCategory), source: path };
}

export async function loadEventAccessZones(eventId, { signal } = {}) {
  if (!eventId) return { rows: [] };
  const response = await fetchBackOfficeJson(`/api/access-zones/event/${encodeURIComponent(eventId)}`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || 'Access zones unavailable'}`);
  return { rows: list(response.payload).map((r, i) => normalizeNamed(r, i, 'zone')) };
}

export async function loadEventPriviledges(eventId, { signal } = {}) {
  if (!eventId) return { rows: [] };
  const response = await fetchBackOfficeJson(`/api/priviledges/event/${encodeURIComponent(eventId)}`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || 'Privileges unavailable'}`);
  return { rows: list(response.payload).map((r, i) => normalizeNamed(r, i, 'priv')) };
}

export async function createAccreditationCategory({ name, description, eventId, accessZones = [], priviledges = [] }, { signal } = {}) {
  const body = await writeJson('/api/accreditation-categories', { body: { name, description, eventId, accessZones, priviledges }, signal });
  return normalizeAccreditationCategory(body?.data || body?.accreditationCategory || body, 0);
}

export async function updateAccreditationCategory(id, { name, description, accessZones, priviledges }, { signal } = {}) {
  const body = await writeJson(`/api/accreditation-categories/${encodeURIComponent(id)}`, { method: 'PATCH', body: { name, description, accessZones, priviledges }, signal });
  return normalizeAccreditationCategory(body?.data || body?.accreditationCategory || body, 0);
}

export async function deleteAccreditationCategory(id, { signal } = {}) {
  return writeJson(`/api/accreditation-categories/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

export async function createAccessZone({ name, eventId }, { signal } = {}) {
  const body = await writeJson('/api/access-zones', { body: { name, eventId }, signal });
  return normalizeNamed(body?.data || body?.accessZone || body, 0, 'zone');
}

export async function createPriviledge({ name, eventId }, { signal } = {}) {
  const body = await writeJson('/api/priviledges', { body: { name, eventId }, signal });
  return normalizeNamed(body?.data || body?.priviledge || body, 0, 'priv');
}
// Delete an access zone / privilege (clean backend: find-by-id + destroy).
export async function deleteAccessZone(id, { signal } = {}) {
  return writeJson(`/api/access-zones/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
export async function deletePriviledge(id, { signal } = {}) {
  return writeJson(`/api/priviledges/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// The real "accredited persons" directory: PassHolders for an event (joined to
// their accreditation category). Backs the Accreditation tab's people view.
// (/api/pass-holders supports eventId + searchQuery + limit/page; the backend
// resolves the event's categories and filters holders to them.)
/**
 * The RSVP picture for an event — COMPILED BY THE REPORT ENGINE, not a bespoke endpoint.
 *
 * This was a hand-written `/api/pass-holders/rsvp-summary` with one frozen shape: five states,
 * grouped by category, one chart, no export. That is a point where the operator needs an axis —
 * the next question is always "by event", "over time", "only the unreachable ones" — and each
 * would have been another hand-written query drifting from this one.
 *
 * It is now two breakdowns over the `accreditation` source, the same primitive behind Revenue
 * velocity: so the same numbers are also groupable, filterable, windowed, exportable and pinnable
 * to a dashboard, and there is ONE producer of them.
 *
 * `holding_pen: No` excludes rows parked in a not-for-issue category. It is passed as a visible
 * filter rather than buried in the source, so the exclusion can be seen and removed.
 */
// Guests parked in a holding category (void / do not print) are not invited and are left out — a condition the
// guest-list counts accept (services/accreditationFacets) without offering it as a filter.
const NOT_PARKED = { holding_pen: ['No'] };

/**
 * THE RSVP SUMMARY, from the guest list's own counts (GET /api/pass-holders/filters — every filter's values in one
 * statement): the replies and who was sent an invitation, and — under "not invited" — which categories they sit
 * in, biggest gap first. Two reads; the same numbers the guest list's filters show.
 */
export async function loadRsvpSummary(eventId, { signal } = {}) {
  if (!eventId) return { total: {}, byCategory: [] };
  const counts = (list, key) => Object.fromEntries((list.find((f) => f.key === key)?.values || []).map((v) => [v.value, v.count]));
  const [all, notInvited] = await Promise.all([
    loadAccreditationFilters(eventId, { filters: NOT_PARKED, signal }),
    loadAccreditationFilters(eventId, { filters: { ...NOT_PARKED, invited: ['Not invited'] }, signal }).catch(() => []),
  ]);
  const rsvp = counts(all, 'rsvp'); const invited = counts(all, 'invited');
  const categoryTotals = counts(all, 'category'); const gaps = counts(notInvited, 'category');
  const accepted = rsvp.Accepted || 0; const declined = rsvp.Declined || 0;
  return {
    total: {
      guests: Object.values(rsvp).reduce((a, b) => a + b, 0),
      accepted,
      declined,
      // "Invited" counts everyone actually sent to; those who answered are a subset of them.
      askedNoAnswer: Math.max(0, (invited.Invited || 0) - accepted - declined),
      notYetAsked: invited['Not invited'] || 0,
      neverContactable: invited['No contact'] || 0,
    },
    byCategory: Object.entries(gaps).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])
      .map(([category, n]) => ({ category, notYetAsked: n, total: categoryTotals[category] || n })),
  };
}

// Filter values + counts for an event, under the current selection.
export async function loadAccreditationFilters(eventId, { filters = {}, signal } = {}) {
  if (!eventId) return [];
  const qs = new URLSearchParams({ filters: JSON.stringify(filters || {}) });
  const r = await fetchBackOfficeJson(`/api/pass-holders/filters/${encodeURIComponent(eventId)}?${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Could not read filters'}`);
  return list(r.payload?.data || []);
}

export async function loadPassHolders({ eventId, limit = 200, page = 1, search, filters = null, signal } = {}) {
  if (!eventId) return { rows: [], total: 0, source: null };
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('page', String(page));
  params.set('eventId', eventId);
  if (search) params.set('searchQuery', search);
  // {rsvp:['Accepted'], category:['Sponsor']} — OR within a filter, AND across.
  const active = Object.entries(filters || {}).filter(([, v]) => Array.isArray(v) && v.length);
  if (active.length) params.set('filters', JSON.stringify(Object.fromEntries(active)));
  const path = `/api/pass-holders?${params.toString()}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Pass holders unavailable'}`);
  const rows = list(response.payload?.data || []).map((ph, i) => ({
    id: ph.id || `ph-${i}`,
    name: ph.name || '',  // blank stays blank; the renderer decides how it looks
    email: ph.email || '',
    phone: ph.phone || '',
    company: ph.company || '',
    title: ph.title || '',
    prefix: ph.prefix || '',
    category: ph.accreditationCategory?.name || ph.categoryName || '',
    rsvp: ph.rsvp || null,
    rsvpAt: ph.rsvpAt || null,
    categoryId: ph.accreditationCategoryId || null,
    issuedAt: ph.createdAt || null,
    checkedInAt: ph.checkedInAt || null,
    // Null when they have not arrived — see the route. Accreditation says who MAY come; this is
    // the only field that says who did.
    admission: ph.admission || null,
    // The PDF's address is composed by the backend, which owns the bucket, the key prefix and the
    // artwork version — see `withUrls` in passHolderRoutes. Never rebuilt here.
    cardUrl: ph.invitationCardUrl || '',
    raw: ph,
  }));
  // NEVER CLAIM A TOTAL WE DO NOT HAVE. This route runs on the `paginate` engine, which returns
  // {rows, hasMore} and deliberately no COUNT(*) — so `totalItems` is simply absent, and falling
  // back to `rows.length` asserted that the page WAS the whole list. The Accreditation table then
  // read "Showing 25 of 25" for an event with 41 accredited people, and its Load-more button —
  // gated on `rows.length < total` — could never appear, so the other 16 were unreachable and
  // unprintable. Found by importing a guest list and being told a smaller number than I had just
  // imported. `hasMore` is the fact the backend actually sends; total stays null until it is real.
  const total = response.payload?.totalItems;
  return {
    rows,
    total: total == null ? null : number(total, rows.length),
    hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false,
    source: path,
  };
}

/**
 * ONE GUEST, cold — what a shared /accreditation/guest/<id> link resolves.
 *
 * The list read is event-scoped and paginated, so a pasted guest link had nothing to hydrate from
 * unless that person happened to be on page 1. This is the by-id route the invitation page already
 * uses, and it carries what the list cannot: the category's event and organisation, so the page can
 * say WHICH festival this credential is for without a second lookup.
 */
export async function loadPassHolder(id, { signal } = {}) {
  if (!id) return null;
  const response = await fetchBackOfficeJson(`/api/pass-holders/${encodeURIComponent(id)}`, { signal });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Guest unavailable'}`);
  const ph = response.payload?.passHolder;
  if (!ph?.id) return null;
  const cat = ph.accreditationCategory || null;
  return {
    id: ph.id,
    name: ph.name || '—',
    email: ph.email || '',
    phone: ph.phone || '',
    company: ph.company || '',
    title: ph.title || '',
    prefix: ph.prefix || '',
    category: cat?.name || '',
    categoryId: ph.accreditationCategoryId || null,
    rsvp: ph.rsvp || null,
    rsvpAt: ph.rsvpAt || null,
    issuedAt: ph.createdAt || null,
    admission: ph.admission || null,
    eventId: cat?.eventId || cat?.event?.id || null,
    eventTitle: cat?.event?.title || cat?.event?.name || '',
    eventDate: cat?.event?.date || null,
    organizerName: cat?.event?.organization?.name || '',
    photoUrl: ph.profileMedia?.publicUrl || '',
    cardUrl: ph.invitationCardUrl || '',
    raw: ph,
  };
}

// The backend RESOLVES rather than blindly creating (services/guestResolution): issuing someone
// who is already accredited adds the category to the person they already are. Those facts come
// back so the toast can say what actually happened instead of always claiming a new credential.
export async function createPassHolder({ name, email, phone, company, title, prefix, accreditationCategoryId, image }, { signal } = {}) {
  const body = await writeJson('/api/pass-holders', { body: { name, email, phone, company, title, prefix, accreditationCategoryId, ...(image ? { image } : {}) }, signal });
  const holder = body?.passHolder || body;
  return { holder, matchedExisting: Boolean(body?.matchedExisting), matchedOn: body?.matchedOn || null, note: body?.note || null };
}

export async function updatePassHolder(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/pass-holders/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return body?.passHolder || body;
}

export async function deletePassHolder(id, { signal } = {}) {
  return writeJson(`/api/pass-holders/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// Accreditation REQUESTS (applications) — the approval queue that precedes a
// PassHolder. Backed by /api/pass-holder-requests: GET /event/:eventId (list,
// the correct event-scoped handler — the global GET / is broken, returns AddOns),
// POST /public (create/log a request), PATCH /accept|reject/:id (approve→also
// creates the PassHolder, or reject). Status: pending|approved|rejected.
const normalizePassHolderRequest = (row, i = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `req-${i + 1}`)),
  name: firstPresent(row.name, '—'),
  email: firstPresent(row.email, ''),
  phone: firstPresent(row.phone, ''),
  company: firstPresent(row.company, ''),
  title: firstPresent(row.title, ''),
  category: firstPresent(row.accreditationCategory?.name, row.categoryName, ''),
  categoryId: firstPresent(row.accreditationCategoryId, null),
  status: String(firstPresent(row.status, 'pending')).toLowerCase(),
  createdAt: firstPresent(row.createdAt, null),
  raw: row,
});
export async function loadPassHolderRequests(eventId, { signal } = {}) {
  if (!eventId) return [];
  const r = await fetchBackOfficeJson(`/api/pass-holder-requests/event/${encodeURIComponent(eventId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Accreditation requests unavailable'}`);
  return list(r.payload).map(normalizePassHolderRequest);
}
export async function createPassHolderRequest({ name, email, phone, company, title, accreditationCategoryId, eventId } = {}, { signal } = {}) {
  if (!name || !email || !phone) throw new Error('Name, email and phone are required');
  if (!accreditationCategoryId || !eventId) throw new Error('A category and event are required');
  const body = await writeJson('/api/pass-holder-requests/public', { body: { name: name.trim(), email: email.trim(), phone: phone.trim(), ...(company ? { company } : {}), ...(title ? { title } : {}), accreditationCategoryId, eventId }, signal });
  return body?.passHolderApplication || body;
}
export async function acceptPassHolderRequest(id, { signal } = {}) {
  return writeJson(`/api/pass-holder-requests/accept/${encodeURIComponent(id)}`, { method: 'PATCH', body: {}, signal });
}
export async function rejectPassHolderRequest(id, { signal } = {}) {
  return writeJson(`/api/pass-holder-requests/reject/${encodeURIComponent(id)}`, { method: 'PATCH', body: {}, signal });
}

// --- Pass-holder BULK UPLOADS (batch accreditation) -------------------------
// /api/pass-holder-bulk-uploads — a named batch of pass holders submitted for an
// event; an admin accepts (mints them all as PassHolders) or rejects the batch.
const normalizePassHolderBulkUpload = (row, i = 0) => {
  const holders = Array.isArray(row.passHolders) ? row.passHolders : [];
  return {
    ...row,
    id: String(firstPresent(row.id, `bulk-${i + 1}`)),
    name: firstPresent(row.name, `Batch ${i + 1}`),
    status: String(firstPresent(row.status, 'pending')).toLowerCase(),
    eventId: firstPresent(row.eventId, ''),
    holderCount: number(firstPresent(row.passHolderCount, holders.length)),
    holders: holders.map((h) => ({ name: firstPresent(h.name, '—'), email: firstPresent(h.email, ''), company: firstPresent(h.company, '') })),
    createdAt: firstPresent(row.createdAt, null),
    raw: row,
  };
};
export async function loadPassHolderBulkUploads(eventId, { signal } = {}) {
  if (!eventId) return [];
  const r = await fetchBackOfficeJson(`/api/pass-holder-bulk-uploads/event/${encodeURIComponent(eventId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Bulk uploads unavailable'}`);
  return list(r.payload).map(normalizePassHolderBulkUpload);
}
export async function createPassHolderBulkUpload({ name, eventId, passHolders } = {}, { signal } = {}) {
  if (!name || !name.trim()) throw new Error('A batch name is required');
  if (!eventId) throw new Error('An event is required');
  const clean = (passHolders || []).filter((h) => h.name && h.name.trim() && h.accreditationCategoryId);
  if (!clean.length) throw new Error('Add at least one pass holder (name + category)');
  const body = await writeJson('/api/pass-holder-bulk-uploads', { body: {
    name: name.trim(), eventId,
    passHolders: clean.map((h) => ({ name: h.name.trim(), accreditationCategoryId: h.accreditationCategoryId, ...(h.email ? { email: h.email.trim() } : {}), ...(h.phone ? { phone: h.phone.trim() } : {}), ...(h.company ? { company: h.company.trim() } : {}) })),
  }, signal });
  return body?.passHolderBulkUpload || body;
}
export async function acceptPassHolderBulkUpload(id, { signal } = {}) {
  return writeJson(`/api/pass-holder-bulk-uploads/accept/${encodeURIComponent(id)}`, { method: 'PATCH', body: {}, signal });
}
export async function rejectPassHolderBulkUpload(id, { signal } = {}) {
  return writeJson(`/api/pass-holder-bulk-uploads/reject/${encodeURIComponent(id)}`, { method: 'PATCH', body: {}, signal });
}
export async function deletePassHolderBulkUpload(id, { signal } = {}) {
  return writeJson(`/api/pass-holder-bulk-uploads/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// Real badge/credential rendering + export (mounted at /accreditation on the BE).
export const passHolderBadgeUrl = (id) => `${API_BASE}${withOrgScope(`/accreditation/${encodeURIComponent(id)}`)}`;
// One guest's card as the event draws it (its design, the UFF card, or the generic badge) — rendered live by the backend.
export const passHolderCardUrl = (id, eventId) => `${API_BASE}${withOrgScope(`/accreditation/badges/${encodeURIComponent(eventId)}?ids=${encodeURIComponent(id)}`)}`;
// The event's badges in its OWN design (the backend falls back to the generic sheet for an event without one).
// `filters` is the guest list's own selection ({ reachable: ['No contact'] }) — the backend applies the same
// rule the list does (accreditationFacets), so a print is of exactly what the page shows. None: everyone.
export const passHolderEventBadgesUrl = (eventId, filters = null) => {
  const sel = Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => Array.isArray(v) && v.length));
  const q = Object.keys(sel).length ? `?filters=${encodeURIComponent(JSON.stringify(sel))}` : '';
  return `${API_BASE}${withOrgScope(`/accreditation/badges/${encodeURIComponent(eventId)}${q}`)}`;
};
// A LINK THE BROWSER OPENS carries no headers, so it says `orgScope=all` itself, like every console
// GET (withOrgScope). Without it a platform admin was scoped to their active organisation and another
// organisation's guest list read "Event not found" (UFF Awards, prod, 2026-10-06). Public links
// (accreditationApplyUrl) stay clean.
export const passHolderCsvUrl = (eventId, filters = null) => {
  const sel = Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => Array.isArray(v) && v.length));
  const q = Object.keys(sel).length ? `?filters=${encodeURIComponent(JSON.stringify(sel))}` : '';
  return `${API_BASE}${withOrgScope(`/accreditation/event/${encodeURIComponent(eventId)}/export/csv${q}`)}`;
};

/**
 * THE ACCREDITATION REPORT AS A PDF — where the guest list stands, printable.
 *
 * The renderer prints the backend's own report page, exactly as Cmd-P on it would, so the console
 * never owns a second copy of that layout. It carries no session of its own, so the backend mints
 * a short-lived signed link first and THAT is what the renderer is handed. A backend on this
 * machine is out of the renderer's reach, so there the page itself is the link and the operator's
 * own print does the job — the same fallback campaignReportUrl makes.
 *
 * The saved file is named after the url's last segment (the renderer reads it off the path), which
 * is the only reason the event's name is in there: without it every event's report saves as
 * "report.pdf" and a folder of them is unreadable.
 */
export async function accreditationReportPdfUrl(eventId, { filters = null, name = '', signal } = {}) {
  const sel = Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => Array.isArray(v) && v.length));
  const filterQ = Object.keys(sel).length ? `filters=${encodeURIComponent(JSON.stringify(sel))}` : '';
  const host = new URL(API_BASE).host;
  if (/^(localhost|127\.0\.0\.1)(:|$)/.test(host)) {
    return `${API_BASE}${withOrgScope(`/accreditation/event/${encodeURIComponent(eventId)}/report.html${filterQ ? `?${filterQ}` : ''}`)}`;
  }
  const r = await fetchBackOfficeJson(`/accreditation/event/${encodeURIComponent(eventId)}/report-link`, { signal });
  if (!r.ok || !r.payload?.token) throw new Error(r.payload?.error || `${r.status} Could not create a report link`);
  // Dashes, not spaces: the segment travels in a url, and a percent-encoded filename is what the
  // reader would see in their downloads folder.
  const slug = String(name || 'accreditation report').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  const query = [`t=${encodeURIComponent(r.payload.token)}`, filterQ].filter(Boolean).join('&');
  return `${getRendererUrl()}/pdf/${host}/accreditation/event/${encodeURIComponent(eventId)}/report/${slug || 'report'}.pdf?${query}`;
}

// --- Guest list import ------------------------------------------------------
// The template is issued PER EVENT so it carries that event's real category names — see
// tkt-backend/src/routes/guestImportRoutes.ts for why the shape is given rather than inferred.
// THE LINK AN ORGANISER SHARES so people can apply. Public and unauthenticated by design — an
// applicant has no account and should not need one to ask — so it is safe to put in an email, a
// press release or a WhatsApp group, which is exactly how accreditation actually gets advertised.
export const accreditationApplyUrl = (eventId) => `${API_BASE}/accreditation/apply/${encodeURIComponent(eventId)}`;

export const guestImportTemplateUrl = (eventId) => `${API_BASE}${withOrgScope(`/api/guest-import/template/${encodeURIComponent(eventId)}`)}`;

/**
 * Read a filled-in template. Deliberately a small, explicit parser rather than a CSV library:
 * the shape is ours, it is six columns wide, and the only subtlety a real export produces is a
 * quoted field containing a comma ("Nakato, Amina") or a doubled quote inside one.
 *
 * Extra columns are ignored and missing ones come back empty, so a file that has been through
 * Excel — which likes to add a trailing comma and a BOM — still reads.
 */
export function parseGuestCsv(text) {
  const clean = String(text || '').replace(/^﻿/, '').trim();
  if (!clean) return { rows: [], headers: [], problem: 'That file is empty.' };
  // AN .XLSX IS A ZIP, and every zip starts "PK". This is the likeliest thing to happen: the
  // template downloads as .csv, the operator opens it in Excel, and Excel's Save is Save-as-xlsx.
  // Read as text that is binary, so the parser would otherwise produce a row of mojibake and the
  // preview would blame their data. Naming the actual problem is the difference between a
  // ten-second fix and giving up on the importer.
  if (clean.startsWith('PK')) {
    return { rows: [], headers: [], problem: 'That looks like an Excel workbook (.xlsx), not a CSV. In Excel choose File → Save As → CSV, then upload that.' };
  }
  const lines = clean.split(/\r?\n/);
  const splitRow = (line) => {
    const out = []; let cur = ''; let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') { cur += '"'; i += 1; } else quoted = !quoted;
      } else if (ch === ',' && !quoted) { out.push(cur); cur = ''; } else cur += ch;
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };
  const headers = splitRow(lines[0]).map((h) => h.toLowerCase());
  // THE TWO COLUMNS THIS CANNOT WORK WITHOUT. Someone who uploads their own spreadsheet instead
  // of the template gets every row mapped to empty, and the preview then reports "Name is
  // required" five hundred times — technically true, completely useless, and it blames them for
  // the wrong thing. One sentence naming what was found and what is needed is the whole fix.
  const missing = ['name', 'category'].filter((h) => !headers.includes(h));
  if (missing.length) {
    return {
      rows: [], headers,
      problem: `This file isn't the template — it has no ${missing.join(' or ')} column. Its columns are: ${headers.filter(Boolean).join(', ') || '(none)'}. Download the template and copy your list into it.`,
    };
  }
  const at = (cells, name) => {
    const i = headers.indexOf(name);
    return i >= 0 ? (cells[i] || '') : '';
  };
  const rows = lines.slice(1)
    .filter((l) => l.trim() && l.replace(/[,\s]/g, ''))   // a blank or comma-only line is not a person
    .map(splitRow)
    .map((cells) => ({
      name: at(cells, 'name'),
      email: at(cells, 'email'),
      phone: at(cells, 'phone'),
      organisation: at(cells, 'organisation') || at(cells, 'organization'),
      title: at(cells, 'title'),
      category: at(cells, 'category'),
    }));
  return { rows, headers, problem: rows.length ? null : 'That file has a header row but no people under it.' };
}

/**
 * Rows PASTED from a spreadsheet — the template's six columns copied straight out of Excel or Google
 * Sheets instead of saved as a file. A spreadsheet puts a TAB between cells, and quotes a cell only
 * when it holds a tab, a line break or a quote. If the header row was copied too, columns are found
 * by name; if only the people were selected, they are in the template's own order. Each row keeps
 * `line` — where it sits in what was pasted — so a problem names the row the operator can see.
 */
const GUEST_TEMPLATE_COLUMNS = ['name', 'email', 'phone', 'organisation', 'title', 'category'];
export function parseGuestPaste(text) {
  const src = String(text || '').replace(/^﻿/, '').replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  if (!src.trim()) return { rows: [], problem: 'Nothing was pasted.' };
  const sep = src.includes('\t') ? '\t' : ',';
  const table = []; let row = []; let cur = ''; let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cur += '"'; i += 1; } else if (ch === '"') quoted = false; else cur += ch;
    } else if (ch === '"' && !cur) quoted = true;
    else if (ch === sep) { row.push(cur); cur = ''; }
    else if (ch === '\n') { row.push(cur); table.push(row); row = []; cur = ''; }
    else cur += ch;
  }
  row.push(cur); table.push(row);
  const cells = table.map((r) => r.map((c) => c.trim()));
  const first = cells[0].map((c) => c.toLowerCase().replace('organization', 'organisation'));
  const hasHeader = first.includes('name') && first.includes('category');
  const headers = hasHeader ? first : GUEST_TEMPLATE_COLUMNS;
  const rows = (hasHeader ? cells.slice(1) : cells)
    .map((c, i) => ({ c, line: i + (hasHeader ? 2 : 1) }))
    .filter(({ c }) => c.some(Boolean))                                   // an empty row is not a person
    .map(({ c, line }) => ({ line, ...Object.fromEntries(GUEST_TEMPLATE_COLUMNS.map((k) => [k, headers.indexOf(k) >= 0 ? (c[headers.indexOf(k)] || '') : ''])) }));
  return { rows, hasHeader, problem: rows.length ? null : 'That paste has a header row but no people under it.' };
}

/** Check (dryRun) or perform. Same endpoint, same code path — the preview cannot drift. */
export async function importGuestList({ eventId, rows, dryRun = true } = {}, { signal } = {}) {
  return writeJson('/api/guest-import', { body: { eventId, rows, dryRun }, signal });
}

// Event-scoped audit trail (Inventory → Audit log sub-tab). Backed by
// /api/audit-logs?entityType=event&entityId=:id (auditLogService.getEntityLogs).
export async function loadEventAuditLog(eventId, { signal, limit = 100, page = 1, system = null } = {}) {
  if (!eventId) return { rows: [], source: null, hasMore: false };
  // Everything that happened ON the event — a comp on it, a price change on its ticket type, a
  // promo code — not only edits to the event row itself (the backend's eventId, see auditCapture).
  // `system` narrows it to one part of the event: its seating, its door access.
  // `page` + hasMore (the route's look-ahead, no count) for a list with Load more.
  const path = `/api/audit-logs?eventId=${encodeURIComponent(eventId)}&limit=${limit}&offset=${(page - 1) * limit}&includeTotal=false${system ? `&system=${encodeURIComponent(system)}` : ''}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Audit log unavailable'}`);
  }
  const rows = list(response.payload?.logs || response.payload).map(normalizeAuditLog);
  return { rows, source: path, hasMore: Boolean(response.payload?.hasMore) };
}

// The acts whose subject is one record — a payout's approval, rejection, fulfilment — as whole
// acts (who, what, why), not flattened per field like loadEntityAuditLog below.
export async function loadSubjectActs(entityType, entityId, { signal, limit = 50, page = 1 } = {}) {
  if (!entityType || !entityId) return { rows: [], hasMore: false };
  const path = `/api/audit-logs?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&limit=${limit}&offset=${(page - 1) * limit}&includeTotal=false`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Audit log unavailable'}`);
  return { rows: list(response.payload?.logs || response.payload).map(normalizeAuditLog), hasMore: Boolean(response.payload?.hasMore) };
}

// Generic entity audit trail from the WIDER AuditLog. Backed by
// /api/audit-logs?entityType=<t>&entityId=<id> — flattens each log's
// oldValues/newValues to one before→after row per changed field.
export async function loadEntityAuditLog(entityType, entityId, { signal, limit = 100, page = 1 } = {}) {
  if (!entityType || !entityId) return { rows: [], source: null, hasMore: false };
  const path = `/api/audit-logs?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&limit=${limit}&offset=${(page - 1) * limit}&includeTotal=false`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Audit log unavailable'}`);
  }
  const logs = list(response.payload?.logs || response.payload);
  const rows = [];
  logs.forEach((log, li) => {
    const base = normalizeAuditLog(log, li);
    const oldV = log.oldValues || {};
    const newV = log.newValues || {};
    const keys = Array.from(new Set([...Object.keys(oldV || {}), ...Object.keys(newV || {})]));
    if (!keys.length) {
      // An act that set no field (a gate removed, a hold released) says what it did in its details.
      rows.push({ id: `${base.id}-0`, ts: base.ts, action: base.action, actor: base.actor, field: base.summary || '—', before: undefined, after: undefined });
    } else {
      keys.forEach((k, ki) => rows.push({
        id: `${base.id}-${ki}`, ts: base.ts, action: base.action, actor: base.actor,
        field: k, before: oldV ? oldV[k] : undefined, after: newV ? newV[k] : undefined,
      }));
    }
  });
  return { rows, source: path, hasMore: Boolean(response.payload?.hasMore) };
}

export async function loadEventSubscriptions(eventId, { signal, page = 1, limit = LIST_PAGE_SIZE, q = '', status = '' } = {}) {
  // Searched and filtered on the SERVER: an event's list runs to tens of thousands, so narrowing
  // the loaded page would only ever search the newest 100 people.
  const path = `/api/subscriptions/event/${encodeURIComponent(eventId)}?page=${page}&limit=${limit}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}${status ? `&status=${encodeURIComponent(status)}` : ''}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Event subscriptions request failed'}`);
  }
  // The body is `{ data: { hasMore, page, data: [...] } }` — hasMore lives on the inner object.
  const body = response.payload?.data && typeof response.payload.data === 'object' ? response.payload.data : response.payload;
  return {
    rows: list(response.payload).map(normalizeSubscription),
    hasMore: Boolean(body?.hasMore),
    source: path,
  };
}

/**
 * WHO FOLLOWS THIS ORGANIZER — not one of their events, the organizer themselves.
 *
 * `Subscription` carries exactly one of `eventId` or `organizationId`: "tell me about THIS event"
 * versus "tell me about this organizer's next thing". The back-office only ever read the first,
 * so the second was invisible — 42 real followers on prod, all of them unreachable from any
 * screen, while the Subscriptions page states plainly that it is scoped per event.
 *
 * Same normalizer and same shape as the event reader, because a follower is a follower; only the
 * thing being followed differs.
 */
export async function loadOrganizationSubscriptions(organizationId, { signal, limit = LIST_PAGE_SIZE, page = 1 } = {}) {
  const path = `/api/subscriptions/organization/${encodeURIComponent(organizationId)}?limit=${limit}&page=${page}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Organizer subscribers request failed'}`);
  }
  // { success, data: { data: rows, hasMore } } — the route's look-ahead; no total.
  const body = response.payload?.data && typeof response.payload.data === 'object' ? response.payload.data : response.payload;
  return { rows: list(response.payload).map(normalizeSubscription), hasMore: Boolean(body?.hasMore), source: path };
}

export async function createEventSubscription(eventId, { email, phone, channels = ['email'], preferences = {}, metadata = {} } = {}, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const body = {
    eventId,
    ...(email ? { email: email.trim() } : {}),
    ...(phone ? { phone: phone.trim() } : {}),
    reminderChannels: channels,
    preferences: {
      eventUpdates: true,
      promotions: false,
      partnerOffers: false,
      reminderFrequency: '1d_before',
      ...preferences,
    },
    metadata: {
      source: 'back-office',
      ...metadata,
    },
  };
  const response = await fetch(`${API_BASE}/api/subscriptions`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const result = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok || result?.success === false) {
    throw new Error(result?.message || result?.error || `${response.status} ${response.statusText || 'Subscription create failed'}`);
  }
  return normalizeSubscription(result.data || result.subscription || result);
}

export async function updateEventSubscriptionStatus(subscriptionId, status, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const response = await fetch(`${API_BASE}/api/subscriptions/${encodeURIComponent(subscriptionId)}/status`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok || body?.success === false) {
    throw new Error(body?.message || body?.error || `${response.status} ${response.statusText || 'Subscription status update failed'}`);
  }
  return normalizeSubscription(body.data || body.subscription || body);
}

export async function deleteEventSubscription(subscriptionId, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const response = await fetch(`${API_BASE}/api/subscriptions/${encodeURIComponent(subscriptionId)}`, {
    method: 'DELETE',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok || body?.success === false) {
    throw new Error(body?.message || body?.error || `${response.status} ${response.statusText || 'Subscription delete failed'}`);
  }
  return true;
}

export async function loadWebhookLogs({ signal, limit = 25, beforeId, status, provider, search, startDate, endDate } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    includeTotal: 'false',
  });
  if (beforeId) params.set('beforeId', String(beforeId));
  if (status && status !== 'all') params.set('status', status);
  if (provider && provider !== 'all') params.set('provider', provider);
  if (search?.trim()) params.set('search', search.trim());
  // A window makes the payload search an Index Cond on createdAt instead of a walk of the whole log.
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const path = `/api/webhooklogs?${params.toString()}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Webhook logs unavailable'}`);
  }
  const rows = list(response.payload).map(normalizeWebhookLog);
  return {
    rows,
    pagination: response.payload?.pagination || {},
    source: path,
  };
}

export async function loadEventRequestPage({ page = 1, limit = LIST_PAGE_SIZE, status = null, from = null, signal } = {}) {
  // `status` (e.g. 'new' = awaiting review) and `from` (recent window) filter at
  // the DB, so a caller showing the pending queue fetches just those rows.
  const path = `/api/event-chat-requests?limit=${limit}&page=${page}&includeTotal=false${status ? `&status=${encodeURIComponent(status)}` : ''}${from ? `&from=${encodeURIComponent(from)}` : ''}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Event requests request failed'}`);
  }
  const pagination = response.payload?.data?.pagination || response.payload?.pagination || {};
  const rows = list(response.payload).map(normalizeEventRequest);
  return {
    rows,
    total: number(pagination.total, rows.length),
    page: number(pagination.page, 1),
    totalPages: number(pagination.totalPages, pagination.hasMore ? page + 1 : page),
    hasMore: Boolean(pagination.hasMore) || number(pagination.totalPages, 1) > number(pagination.page, 1),
    source: path,
  };
}

export async function loadAuditLogPage({ page = 1, limit = LIST_PAGE_SIZE, system = null, userId = null, eventId = null, q = '', signal } = {}) {
  const offset = (page - 1) * limit;
  // `system`, `userId`, `eventId` and the search `q` are filtered by the server, so the list is complete rather than a
  // filter over one loaded page.
  const path = `/api/audit-logs?limit=${limit}&offset=${offset}&includeTotal=false${system ? `&system=${encodeURIComponent(system)}` : ''}${userId ? `&userId=${encodeURIComponent(userId)}` : ''}${eventId ? `&eventId=${encodeURIComponent(eventId)}` : ''}${String(q || '').trim() ? `&q=${encodeURIComponent(String(q).trim())}` : ''}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Audit logs request failed'}`);
  }
  const rows = list(response.payload?.logs || response.payload).map(normalizeAuditLog);
  return {
    rows,
    total: number(response.payload?.total, rows.length),
    page: number(response.payload?.page, page),
    limit,
    hasMore: Boolean(response.payload?.hasMore),
    source: path,
  };
}

// The systems an audit entry can belong to, with their counts over the last 30 days — the labels
// come from the backend's ownership map, so the console never keeps a second copy of the list.
// The systems and the people who acted in the last 30 days, with counts — one read for both filters.
export async function loadAuditDirectory({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/audit-logs/activity?days=30', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Audit systems unavailable'}`);
  return { systems: list(r.payload?.systems || []), actors: list(r.payload?.actors || []) };
}

// Streams the audit log as a CSV file from the backend. Returns the blob
// and the suggested filename. The caller triggers the browser download.
export async function exportAuditLogs({ filters = {}, signal } = {}) {
  const params = new URLSearchParams();
  if (filters.entityType) params.set('entityType', filters.entityType);
  if (filters.entityId) params.set('entityId', filters.entityId);
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.source) params.set('source', filters.source);
  if (filters.system) params.set('system', filters.system);
  if (filters.eventId) params.set('eventId', filters.eventId);
  if (String(filters.q || '').trim()) params.set('q', String(filters.q).trim());
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  const query = params.toString();
  const path = `/api/audit-logs/export${query ? `?${query}` : ''}`;
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    signal,
    credentials: 'include',
    headers: { Accept: 'text/csv', 'x-org-scope': 'all' },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `${response.status} ${response.statusText || 'Audit log export failed'}`);
  }
  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition') || '';
  const match = contentDisposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
  return { blob, filename };
}

// Helper that triggers a browser download for a Blob with a filename.
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    anchor.remove();
  }, 0);
};

export async function createEventRequest(payload, { signal } = {}) {
  const response = await fetch(`${API_BASE}/api/event-chat-requests`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok || body?.success === false) {
    throw new Error(body?.error || body?.message || `${response.status} ${response.statusText || 'Event request creation failed'}`);
  }
  return normalizeEventRequest(body.data || body.eventRequest || body.request || body);
}

// Who decided is stamped by the server from the session (it used to receive 'Back office' from
// here); the reason travels with the decision itself, so the act carries it.
export async function updateEventRequestStatus(requestId, status, { reason, signal } = {}) {
  const response = await fetch(`${API_BASE}/api/event-chat-requests/${encodeURIComponent(requestId)}/status`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json', ...reasonHeader(reason) },
    body: JSON.stringify({ status }),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = Array.isArray(body?.errors) ? body.errors.join(', ') : body?.error || body?.message || `${response.status} ${response.statusText || 'Request status update failed'}`;
    throw new Error(message);
  }
  return normalizeEventRequest(body.data || body.eventRequest || body.request || body);
}

export async function addEventRequestNote(requestId, note, { signal } = {}) {
  const response = await fetch(`${API_BASE}/api/event-chat-requests/${encodeURIComponent(requestId)}/notes`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = Array.isArray(body?.errors) ? body.errors.join(', ') : body?.error || body?.message || `${response.status} ${response.statusText || 'Request note failed'}`;
    throw new Error(message);
  }
  return normalizeEventRequest(body.data || body.eventRequest || body.request || body);
}

export async function loadOrderDetail(orderId, { signal } = {}) {
  const response = await fetchBackOfficeJson(`/api/orders/${encodeURIComponent(orderId)}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Order detail request failed'}`);
  }
  const row = unwrap(response.payload);
  return normalizeOrder(row && typeof row === 'object' && !Array.isArray(row) ? row : response.payload);
}

// Correct the attendee/ticket-holder name on a single ticket (scanItem). Real
// support op — fix a typo'd buyer name, or set the recipient on a comp ticket.
// Contract mirrors tkt-frontend attendees-table: PATCH /api/scanItems/:id with
// { firstName, lastName } (accepts the scanItem id OR its signature). The handler
// validates {firstName?, lastName?} and updates in place.
export async function updateScanItemNames(scanItemId, { firstName, lastName } = {}, { signal } = {}) {
  if (!scanItemId) throw new Error('A scan item id is required');
  if (!firstName || !firstName.trim()) throw new Error('First name is required');
  return writeJson(`/api/scanItems/${encodeURIComponent(scanItemId)}`, {
    method: 'PATCH',
    body: { firstName: firstName.trim(), lastName: (lastName || '').trim() },
    signal,
  });
}

// Cross-page cache of the most recent UNFILTERED page-1 orders. The dashboard
// already fetches recent orders on landing; the Orders page seeds its first
// paint from this instantly (then refreshes in the background) instead of
// waiting on a fresh cold round-trip. "Use what's already loaded."
let _recentOrders = null; // { rows, total, page, totalPages, hasMore, at }
// NO `includeTotal`. Ever.
//
// The param is not accepted here, so no caller can turn a list read into a COUNT. On the
// backend `includeTotal` runs `db.Order.count({ where })` — a full scan of a 1.5M-row table
// with the same predicate as the page — purely to print "of N". The house pagination
// primitive is a `limit+1` look-ahead returning `hasMore`; a grand total is never worth a
// second full-table pass, and a param that CAN be true will eventually be set true.
/**
 * PER-STATUS TOTALS for one event's Orders tabs — the numbers on the badges.
 *
 * A separate call rather than a field on the list response, because the two change on different
 * clocks: the list refetches on every page and tab, while these move only when orders do. Fetching
 * them once per event keeps the aggregate off the paging path entirely.
 *
 * This is NOT `includeTotal` in disguise (see the note on loadOrderPage — that is a COUNT over a
 * 1.5M-row table to print one number, and it is forbidden here). The backend answers with ONE
 * grouped aggregate scoped to a single event and served by the eventId index, returning all six
 * numbers in one pass. Measured on BrakaLand (944 orders): ~220-310ms, cheaper than a single page
 * of the list beside it.
 *
 * Returns null rather than throwing: badges are an enhancement, and a list that renders without
 * them beats a page that fails because a count did.
 */
export async function loadOrderStatusCounts({ eventId, includeAbandoned = false, dateFrom, dateTo, dateBasis, currency, signal } = {}) {
  if (!eventId) return null;
  const params = new URLSearchParams();
  if (includeAbandoned) params.set('includeAbandoned', 'true');
  // The badges must be under the SAME window as the rows. A count of all time above a list bounded
  // to seven days is the original defect wearing a different hat.
  if (dateFrom) params.set('from', dateFrom);
  if (dateTo) params.set('to', dateTo);
  if (dateBasis) params.set('dateBasis', dateBasis);
  if (currency) params.set('currency', currency);
  try {
    const response = await fetchBackOfficeJson(
      `/api/orders/event/${encodeURIComponent(eventId)}/status-counts?${params.toString()}`,
      { signal },
    );
    if (!response.ok) return null;
    const p = response.payload || {};
    return {
      all: number(p.all, 0),
      paid: number(p.paid, 0),
      reserved: number(p.reserved, 0),
      failed: number(p.failed, 0),
      manual: number(p.manual, 0),
      refunded: number(p.refunded, 0),
      comp: number(p.comp, 0),
      // HOW MANY ROWS THE DEFAULT EXCLUDED. `all` counts everything except abandoned checkouts
      // (pending, with no email and no phone), which on a busy event is most of the table — 1,144
      // of this one's 1,738. Dropping the field here would leave "171 paid of 594" unable to say
      // what 594 is, and 171/594 ≈ 29% reads as the checkout conversion when it is really ≈ 10%.
      abandoned: number(p.abandoned, 0),
      // The backend's word for the Cancelled / expired bucket. `failed` above has no backend
      // counterpart and reads 0; this is the number.
      closed: number(p.closed, 0),
      // Per TAB, as each tab's list filters (by status alone, so a comp is in Paid too), under the
      // list's own exclusions. What the tab badges read; the cascade above is for the summary cards.
      tabs: p.tabs ? { paid: number(p.tabs.paid, 0), reserved: number(p.tabs.reserved, 0), closed: number(p.tabs.closed, 0) } : null,
    };
  } catch {
    return null;
  }
}

export async function loadOrderPage({ eventId, page = 1, limit = LIST_PAGE_SIZE, includeStats = false, searchQuery, searchField, dateFrom, dateTo, dateBasis, scheduleSlotId, ticketType, status, orderType, partPaid, overpaid, plans, currency: currencyFilter, sortBy, paymentMethod, channel, includeAbandoned = false, signal } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
  });
  params.set('includeTotal', 'false'); // always — see the note on this function
  if (!eventId && !includeStats) params.set('includeStats', 'false');
  if (eventId) params.set('includeTicketTypes', 'false');
  // Server-side filtering — the backend matches searchQuery against
  // firstName/lastName/email/phone/orderId/id and bounds by createdAt. This
  // makes search find ANY order (not just the loaded page) and lets a date
  // window bound the scan, instead of filtering the 80 already-loaded rows.
  if (searchQuery && searchQuery.trim()) {
    params.set('searchQuery', searchQuery.trim());
    // WHICH field to match against. Without it the server ORs across name/email/phone/orderId,
    // which no single index can answer — measured at 5.6s and a full scan of 1.5M orders, and
    // that is what took the app down. Named, the same search is an index lookup.
    params.set('searchField', searchField || 'name');
  }
  // SEND WHICHEVER BOUND EXISTS — `&&` here meant no preset ever filtered anything.
  //
  // Every relative preset (Today, 7d, 30d, 90d, this month, this year) resolves to a
  // LOWER bound with `to: null` on purpose — see lib/dateRange: bounding `to` to now
  // would hide future-dated records. So `dateFrom && dateTo` was false for all of them,
  // no from/to was ever put on the query string, and the list quietly returned every
  // order. Only a hand-picked custom range — the one case that sets both — ever filtered.
  // Selecting "Today" and getting the full history is exactly that bug, not a timezone.
  if (dateFrom) params.set('from', dateFrom);
  if (dateTo) params.set('to', dateTo);
  // WHICH date the window bounds — 'activity' (settled, else created) keeps unpaid orders visible.
  if (dateBasis) params.set('dateBasis', dateBasis);
  // The money's currency. The Sales pulse shows one currency at a time; without this the ledger
  // beneath it mixed them, so the strip and the list could never agree on a count.
  if (currencyFilter) params.set('currency', currencyFilter);
  if (scheduleSlotId) params.set('scheduleSlotId', scheduleSlotId); // narrow to one showtime
  if (ticketType) params.set('ticketType', ticketType); // orders holding that tier (event list only)
  // Status tab drives a SERVER filter (not an in-memory slice of the loaded page) — so e.g.
  // the "Paid" tab fetches ALL paid orders even when none are on the freshest page, and sorts
  // them by order_date. `status` is the backend order.status ('successful','failed','pending').
  if (status) params.set('status', status);
  // Complimentary is not a STATUS — a comp'd order is legitimately status:'successful' too. It is
  // `orderType`, the real classification field, which the backend already filters on and this
  // function simply never sent.
  if (orderType) params.set('orderType', orderType);
  if (partPaid) params.set('partPaid', partPaid); // pending orders already holding money (the Part paid tab)
  if (overpaid) params.set('overpaid', overpaid); // paid orders holding more than their total (the Overpaid tab)
  if (plans) params.set('plans', plans);          // 'hide' leaves installment and split plans out
  if (sortBy) params.set('sortBy', sortBy);
  // The rail the money came in on. A REAL backend filter (`where.paymentMethod = {[Op.in]:
  // csv}`) that nothing was ever sending — the console filtered it in the browser, over the
  // loaded page only, against a set of options that could never match (see OrdersInline).
  if (paymentMethod) params.set('paymentMethod', paymentMethod);
  // Where the order was made (purchase_channel) — indexed in both of the list's orders.
  if (channel) params.set('channel', channel);
  // The backend hides ABANDONED CHECKOUTS by default — orders still `pending` with
  // neither an email nor a phone, i.e. someone opened checkout and walked away
  // leaving no way to be reached. On prod that is ~75% of the Order table and not
  // one of those rows carries money. But the filter is AND-ed with the search terms
  // too, so "I searched for an order and it isn't there" is a real operator moment.
  // This opt-in is the escape hatch. See tkt-backend/src/utils/abandonedCheckouts.ts.
  if (includeAbandoned) params.set('includeAbandoned', 'true');
  const path = eventId
    ? `/api/orders/event/${encodeURIComponent(eventId)}?${params.toString()}`
    : `/api/orders/all?${params.toString()}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Orders request failed'}`);
  }
  const rows = list(response.payload).map(normalizeOrder);
  const hasMore = Boolean(response.payload?.hasMore);
  const total = firstPresent(response.payload?.total, response.payload?.totalItems);
  const result = {
    rows,
    source: path,
    // NULL when unknown — never invented. This used to be `rows.length + (hasMore ? 1 : 0)`,
    // which renders as "Showing 25 of 26": a denominator that is not a count of anything, just
    // "there is at least one more". Now that no caller can ask for a COUNT, a total is simply
    // not a fact we have, and consumers say so rather than printing a fabricated one.
    total: total == null ? null : number(total, rows.length),
    page: number(firstPresent(response.payload?.page, response.payload?.currentPage), 1),
    totalPages: response.payload?.totalPages == null
      ? page + (hasMore ? 1 : 0)
      : response.payload.totalPages,
    hasMore,
  };
  // Cache the canonical unfiltered first page for instant reuse elsewhere.
  // `includeAbandoned` is a DIFFERENT row set, not a widening of the same one —
  // caching it here would seed every other surface with pending contactless shells.
  if (!eventId && !searchQuery && !dateFrom && !includeAbandoned && page === 1 && rows.length) {
    _recentOrders = { ...result, at: Date.now() };
  }
  return result;
}

/* THE RAW ORDERS CSV DUMP IS GONE FROM THIS CONSOLE — but the ROUTE it called is not dead.
 *
 * `exportEventOrdersCsv` and `emailEventOrdersCsv` used to POST to /api/orders/export/csv and
 * /api/orders/export/fin: a `Order.findAll({raw:true})` dump of 63 table columns that ignored
 * whichever columns the operator had ticked. Every orders export here now goes through
 * `requestEntityExport` (the report engine), which runs the real compiled query, honours the
 * picked columns, records an ExportJob and can be scheduled or emailed. Both wrappers had no
 * callers left — only a code comment and the auto-derived surface census mention them — so they
 * are removed rather than left as a second way to do the same thing badly.
 *
 * DO NOT delete the backend routes. `/api/orders/export/csv` is still called by a live Export
 * button in tkt-frontend's seller dashboard
 * (app/dashboard/products/[product]/orders/_components/orders-table.tsx), and mtn-moments uses
 * the sibling /api/orders/export/fin. Retiring the route would break a shipped seller-facing
 * feature; retiring these two wrappers only removes the console's path back to it. */

// Browser-side helper: turn CSV text into a download. Pure DOM, no network.
export function downloadCsv(csv, fileName = 'export.csv') {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Build a CSV from an in-memory array of row objects and download it for real.
// Generalist replacement for the many `toast('X exported')` fake-success buttons:
// the data is already loaded client-side, so a real export needs no backend.
// `columns` optionally restricts/orders fields as [{ key, label }] or ['key', …];
// otherwise it unions the scalar keys across rows. Returns the CSV string (for tests).
/** A row's value at a column key — `order.orderId` reaches into the row's `order`, the same dotted
 *  path the report engine uses, so a row keeps its model shape all the way to the file. */
export const valueAt = (row, key) => String(key).split('.').reduce((o, k) => (o == null ? o : o[k]), row);

export function rowsToCsv(rows, columns = null) {
  const list = Array.isArray(rows) ? rows : [];
  const esc = (v) => {
    if (v == null) return '';
    if (typeof v === 'object') v = JSON.stringify(v);
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  let cols;
  if (columns && columns.length) {
    cols = columns.map((c) => (typeof c === 'string' ? { key: c, label: c } : { key: c.key || c.id, label: c.label || c.key || c.id }));
  } else {
    const keys = [...new Set(list.flatMap((r) => Object.keys(r || {})))]
      .filter((k) => { const v = list.find((r) => r && r[k] != null)?.[k]; return v == null || typeof v !== 'object' || Array.isArray(v); });
    cols = keys.map((k) => ({ key: k, label: k }));
  }
  const header = cols.map((c) => esc(c.label)).join(',');
  const body = list.map((r) => cols.map((c) => esc(valueAt(r, c.key))).join(',')).join('\n');
  return body ? `${header}\n${body}` : header;
}

// One-call real export: build CSV from rows and trigger the download. Toasts the
// honest truth (real row count) or an honest "nothing to export" when empty.
export function exportRowsToCsv(rows, { fileName = 'export.csv', columns = null, label = 'rows' } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) { toast('Nothing to export', 'info', `No ${label} in the current view`); return null; }
  const csv = rowsToCsv(list, columns);
  downloadCsv(csv, fileName);
  toast('Export downloaded', 'success', `${list.length.toLocaleString()} ${label} · ${fileName}`);
  return csv;
}

// --- Per-event checkout / registration forms (/api/order-forms) -------------
// An OrderForm defines the data captured at checkout for an event: the built-in
// buyer fields (firstName/lastName/email/phone, each enabled/required) plus an
// ordered list of custom fields (text/select/checkbox/…). Responses are the
// orders whose customFormResponses are populated. The event id IS the
// eventPageId these key on.
const BUILTIN_FIELD_DEFAULTS = {
  firstName: { enabled: true, required: true, order: 1 },
  lastName: { enabled: true, required: true, order: 2 },
  email: { enabled: true, required: true, order: 3 },
  phone: { enabled: false, required: false, order: 4 },
};
const normalizeOrderForm = (row, i = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `order-form-${i + 1}`)),
  eventId: firstPresent(row.eventPageId, row.eventId, ''),
  eventTitle: firstPresent(row.eventPage?.title, ''),
  title: firstPresent(row.formTitle, ''),
  description: firstPresent(row.formDescription, ''),
  submitButtonText: firstPresent(row.submitButtonText, 'Complete Order'),
  isActive: row.isActive !== false,
  firstName: row.firstName || BUILTIN_FIELD_DEFAULTS.firstName,
  lastName: row.lastName || BUILTIN_FIELD_DEFAULTS.lastName,
  email: row.email || BUILTIN_FIELD_DEFAULTS.email,
  phone: row.phone || BUILTIN_FIELD_DEFAULTS.phone,
  customFields: Array.isArray(row.customFields) ? row.customFields : [],
  ticketTypeIds: Array.isArray(row.ticketTypeIds) ? row.ticketTypeIds : [],
  createdAt: firstPresent(row.createdAt, null),
  raw: row,
});

/** Every form the event has had — active or not, any tier — for labelling answers already given (an order's). */
export async function loadEventFormQuestions(eventId, { signal } = {}) {
  if (!eventId) return {};
  const r = await fetchBackOfficeJson(`/api/order-forms/event/${encodeURIComponent(eventId)}?all=1`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Checkout forms unavailable'}`);
  return Object.fromEntries((r.payload?.orderForms || []).flatMap((f) => f.customFields || []).map((q) => [String(q.id), q.label || q.name || null]).filter(([, l]) => l));
}

export async function loadEventOrderForms(eventId, { signal } = {}) {
  if (!eventId) return [];
  // Use the EVENT-scoped endpoint, NOT the list (`GET /api/order-forms`). The
  // list inner-joins eventPage.organizationId = session.activeOrganizationId, so
  // a platform super-admin viewing an event in another org gets nothing. The
  // /event/:id endpoint keys on eventPageId directly (org-agnostic) and returns
  // the active form (or null).
  const r = await fetchBackOfficeJson(`/api/order-forms/event/${encodeURIComponent(eventId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Checkout form unavailable'}`);
  const f = r.payload?.orderForm;
  return f ? [normalizeOrderForm(f)] : [];
}

// Shape the FE form-builder state into the backend create/update body.
function buildOrderFormBody({ title, description, submitButtonText, isActive, customFields, firstName, lastName, email, phone, ticketTypeIds } = {}) {
  const body = {};
  if (title !== undefined) body.formTitle = title;
  if (description !== undefined) body.formDescription = description;
  if (submitButtonText !== undefined) body.submitButtonText = submitButtonText || 'Complete Order';
  if (isActive !== undefined) body.isActive = isActive;
  ['firstName', 'lastName', 'email', 'phone'].forEach((k) => {
    const v = { firstName, lastName, email, phone }[k];
    if (v !== undefined) body[k] = v;
  });
  // A set, sent whole: what the operator just saw IS the scope. Empty array = all tiers.
  if (ticketTypeIds !== undefined) body.ticketTypeIds = ticketTypeIds;
  if (customFields !== undefined) {
    body.customFields = (customFields || []).map((f, idx) => ({
      id: f.id || `field-${idx + 1}`,
      type: f.type || 'short_text',
      label: f.label || `Field ${idx + 1}`,
      required: !!f.required,
      ...(f.placeholder ? { placeholder: f.placeholder } : {}),
      ...(f.description ? { description: f.description } : {}),
      ...(Array.isArray(f.options) && f.options.length ? { options: f.options } : {}),
      // Conditional display. Dropped here, the editor above would be decorative: the operator
      // sets "ask only if…", saves, and the question is asked of everyone anyway.
      ...(Array.isArray(f.conditions) && f.conditions.length
        ? { conditions: f.conditions, showIf: f.showIf !== false }
        : {}),
      order: f.order ?? idx + 1,
    }));
  }
  return body;
}

export async function createEventOrderForm(eventId, input = {}, { signal } = {}) {
  if (!eventId) throw new Error('An event is required');
  const body = buildOrderFormBody({ submitButtonText: 'Complete Order', isActive: true, customFields: [], ...input });
  const res = await writeJson(`/api/order-forms/event/${encodeURIComponent(eventId)}`, { body, signal });
  return normalizeOrderForm(res?.orderForm || res);
}

export async function updateOrderForm(id, patch = {}, { signal } = {}) {
  const body = buildOrderFormBody(patch);
  const res = await writeJson(`/api/order-forms/${encodeURIComponent(id)}`, { method: 'PUT', body, signal });
  return normalizeOrderForm(res?.orderForm || res);
}

export async function deleteOrderForm(id, { signal } = {}) {
  return writeJson(`/api/order-forms/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// A page of responses, newest first: { rows, hasMore } (the route's look-ahead; it counts nothing).
export async function loadOrderFormResponses(id, { page = 1, limit = LIST_PAGE_SIZE, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/order-forms/${encodeURIComponent(id)}/responses?limit=${limit}&page=${page}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Responses unavailable'}`);
  return { rows: r.payload?.responses || [], hasMore: Boolean(r.payload?.pagination?.hasMore) };
}

// --- Image upload (Cloudflare R2 via presigned PUT) -------------------------
// Mirrors tkt-frontend's logoUploader: ask the BE for a presigned URL, then PUT
// the raw file straight to R2, then use the returned publicUrl. The R2 bucket
// CORS allows direct browser PUT (verified from this origin).
let _cachedUploadUserId = null;
async function uploadUserId({ signal } = {}) {
  if (_cachedUploadUserId) return _cachedUploadUserId;
  const s = await loadSession({ signal });
  _cachedUploadUserId = s?.user?.id || null;
  return _cachedUploadUserId;
}

// POST /api/upload/presigned-url → { uploadUrl, publicUrl, key, media }.
export async function getUploadUrl(file, { signal } = {}) {
  if (!file) throw new Error('No file provided');
  const userId = await uploadUserId({ signal });
  if (!userId) throw new Error('Not signed in — cannot upload');
  return writeJson('/api/upload/presigned-url', {
    body: { userId, fileName: file.name || 'upload', contentType: file.type || 'application/octet-stream', size: file.size },
    signal,
  });
}

/* ── BIG UPLOADS (multipart) ──────────────────────────────────────────────────────────────────
   getUploadUrl above is one presigned PUT holding the entire file — fine for a poster, useless for
   a multi-gigabyte archive. These four wrap the backend's multipart routes; the bytes still go
   browser → R2 directly, never through our server.

   `completeBigUpload` deliberately sends NO ETags: the backend reads the parts back from R2 itself.
   The browser cannot reliably read an ETag off a cross-origin PUT unless the bucket's CORS exposes
   that header, and when it doesn't the failure lands at the final step, after every byte is
   already uploaded and paid for. */
export async function startBigUpload(file, { signal } = {}) {
  if (!file) throw new Error('No file provided');
  const userId = await uploadUserId({ signal });
  if (!userId) throw new Error('Not signed in — cannot upload');
  return writeJson('/api/upload/multipart/create', {
    body: { userId, fileName: file.name || 'upload.bin', contentType: file.type || 'application/octet-stream' },
    signal,
  });
}

export async function signBigUploadParts({ key, uploadId, partNumbers }, { signal } = {}) {
  const body = await writeJson('/api/upload/multipart/sign', { body: { key, uploadId, partNumbers }, signal });
  return body?.urls || [];
}

export async function completeBigUpload({ key, uploadId, expectedParts }, { signal } = {}) {
  return writeJson('/api/upload/multipart/complete', { body: { key, uploadId, expectedParts }, signal });
}

/** Abandoned multipart uploads keep their parts in the bucket and keep BILLING, invisibly — they
 *  do not appear in a normal object listing. Every cancel/failure path must call this. */
export async function abortBigUpload({ key, uploadId }, { signal } = {}) {
  return writeJson('/api/upload/multipart/abort', { body: { key, uploadId }, signal });
}

// Full upload: presign → PUT bytes to R2 → return { url, key }. `url` is the
// permanent public URL to store/render. Validates type + size up front.
/**
 * A PROOF OF PAYMENT — a screenshot or the bank's PDF — through the same presigned R2 upload images
 * use. Images and PDFs only; the URL it returns is what a payout records as its proof.
 */
export async function uploadProofFile(file, { signal, maxBytes = 10 * 1024 * 1024 } = {}) {
  if (!file) throw new Error('No file selected');
  if (!/^image\//.test(file.type || '') && file.type !== 'application/pdf') throw new Error('Choose an image or a PDF');
  if (file.size > maxBytes) throw new Error(`File must be under ${Math.round(maxBytes / 1024 / 1024)}MB`);
  const presign = await getUploadUrl(file, { signal });
  if (!presign?.uploadUrl || !presign?.publicUrl) throw new Error('Upload service did not return a URL');
  const put = await fetch(presign.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file, signal });
  if (!put.ok) throw new Error(`Upload failed (${put.status}) — the storage rejected the file`);
  return { url: presign.publicUrl, key: presign.key, name: file.name };
}

export async function uploadImage(file, { signal, maxBytes = 8 * 1024 * 1024 } = {}) {
  if (!file) throw new Error('No file selected');
  if (!/^image\//.test(file.type || '')) throw new Error('Please choose an image file (PNG, JPG, WebP…)');
  if (file.size > maxBytes) throw new Error(`Image must be under ${Math.round(maxBytes / 1024 / 1024)}MB`);
  const presign = await getUploadUrl(file, { signal });
  const uploadUrl = presign?.uploadUrl;
  const publicUrl = presign?.publicUrl;
  if (!uploadUrl || !publicUrl) throw new Error('Upload service did not return a URL');
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file, signal });
  if (!put.ok) throw new Error(`Upload failed (${put.status}) — the storage rejected the file`);
  // ITS SHAPE, measured here — the only place that holds the bytes (they go straight to storage).
  // As displayed (EXIF rotation applied), so a portrait phone photo reads portrait. Readers need
  // it before the pixels arrive; a failure just leaves it unmeasured.
  const size = await createImageBitmap(file, { imageOrientation: 'from-image' })
    .then((b) => { const s = { width: b.width, height: b.height }; b.close?.(); return s; })
    .catch(() => null);
  return { url: publicUrl, key: presign.key, ...(size || {}) };
}

// --- Feedback (screenshot + comment → /api/v1/feedback) -----------------------
// Submits feedback with an optional screenshot. The screenshot can be a base64
// data URL (the backend uploads it to R2) or a pre-uploaded R2 URL.

export async function submitFeedback({ message, screenshot, screenshotUrl, category, pageUrl, metadata, email, name } = {}, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch(() => {});
  const response = await fetch(`${API_BASE}/api/v1/feedback`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, ...(screenshot ? { screenshot } : {}), ...(screenshotUrl ? { screenshotUrl } : {}), ...(category ? { category } : {}), ...(pageUrl ? { pageUrl } : {}), ...(metadata ? { metadata } : {}), ...(email ? { email } : {}), ...(name ? { name } : {}) }),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message2 = Array.isArray(body?.errors) ? body.errors.join(', ') : body?.error || `${response.status} ${response.statusText || 'Feedback submission failed'}`;
    throw new Error(message2);
  }
  return body?.data || body;
}

export async function loadFeedbackList({ page = 1, limit = LIST_PAGE_SIZE, status, signal } = {}) {
  await ensureDevAuthSession({ signal }).catch(() => {});
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) qs.set('status', status);
  const response = await fetchBackOfficeJson(`/api/v1/feedback?${qs}`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText || 'Feedback list failed'}`);
  const payload = response.payload;
  return { rows: payload?.data || payload?.rows || [], hasMore: payload?.hasMore ?? false };
}

export async function updateFeedbackStatus(id, status, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch(() => {});
  const response = await fetch(`${API_BASE}/api/v1/feedback/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) throw new Error(body?.error || `${response.status} ${response.statusText}`);
  return body?.data || body;
}

// A CUSTOMER IS A DIMENSION OF ORDERS (backend utils/customerKey): a phone, an email or an account id,
// each its own exact slice — never a prefix search, never merged. These two read one key.

/** The header facts of one customer key: paid per currency, orders by status, tickets, the other keys seen. */
/** The period a customer read covers: an ISO instant, or 'all'. Absent, the backend reads the last 30 days. */
const sinceQuery = (since) => (since ? `since=${encodeURIComponent(since)}` : '');
const withQuery = (path, ...parts) => { const q = parts.filter(Boolean).join('&'); return q ? `${path}?${q}` : path; };

/** One customer key's figures over a period (`since`: an ISO instant or 'all'; absent = the last 30 days). */
export async function loadCustomer(key, { since, signal } = {}) {
  const response = await fetchBackOfficeJson(withQuery(`/api/users/customers/${encodeURIComponent(key)}`, sinceQuery(since)), { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Customer request failed`);
  return response.payload;
}

/** One support case as the support page holds it — with its facts (the order it is about) — for the drawer. */
export async function loadSupportTicket(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/support-escalations/${encodeURIComponent(id)}`, { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} Case unavailable`);
  return ticketFromEscalation(normalizeEscalation(r.payload?.data || {}));
}

/** One customer key's tickets, order by order — each paid order with its ticket count and up to 100 tickets. */
export async function loadCustomerTickets(key, { page = 1, since, signal } = {}) {
  const r = await fetchBackOfficeJson(withQuery(`/api/users/customers/${encodeURIComponent(key)}/tickets`, `page=${page}`, sinceQuery(since)), { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} Tickets request failed`);
  return { rows: r.payload?.data || [], hasMore: Boolean(r.payload?.hasMore) };
}

/** One customer key's timeline, a page of the period's orders at a time (`before`: where the previous page ended) —
 *  orders, charges, refunds, unapplied money, scans, requests; on the first page, cases and WhatsApp — and the
 *  WhatsApp id to write to. The page derives what needs a person and the support panel from these rows. */
export async function loadCustomerActivity(key, { since, before, signal } = {}) {
  const r = await fetchBackOfficeJson(withQuery(`/api/users/customers/${encodeURIComponent(key)}/activity`, sinceQuery(since), before && `before=${encodeURIComponent(before)}`), { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} Activity request failed`);
  return { rows: r.payload?.data || [], chatId: r.payload?.chatId || null, more: Boolean(r.payload?.more), before: r.payload?.before || null };
}


/** Every terms acceptance on one customer key, as recorded at checkout (backend utils/termsAcceptance). */
export async function loadCustomerTerms(key, { page = 1, since, signal } = {}) {
  const r = await fetchBackOfficeJson(withQuery(`/api/users/customers/${encodeURIComponent(key)}/terms`, `page=${page}`, sinceQuery(since)), { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} Terms request failed`);
  return { rows: r.payload?.data || [], hasMore: Boolean(r.payload?.hasMore) };
}

/** Everything held on one customer key, saved as the JSON file the backend writes — not parsed and re-printed here. */
export async function downloadCustomerExport(key) {
  const res = await fetch(`${API_BASE}${withOrgScope(`/api/users/customers/${encodeURIComponent(key)}/export`)}`, { credentials: 'include' });
  if (!res.ok) { const msg = await res.json().catch(() => null); throw new Error(msg?.error || `${res.status} Export failed`); }
  const blob = await res.blob();
  saveBlob(blob, `customer-${String(key).replace(/[^A-Za-z0-9@.+-]+/g, '_')}.json`);
  return { bytes: blob.size };
}

/** One customer key's orders in the period (`since`: an ISO instant, or 'all'/absent for any time), newest first, a page at a time. */
export async function loadCustomerOrders(key, { page = 1, limit = 25, status, since, signal } = {}) {
  const params = new URLSearchParams({ customer: key, limit: String(limit), page: String(page), includeTotal: 'false', includeStats: 'false' });
  if (status) params.set('status', status);
  if (since && since !== 'all') params.set('from', since);
  const response = await fetchBackOfficeJson(`/api/orders/all?${params.toString()}`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Customer orders request failed'}`);
  return { rows: list(response.payload).map(normalizeOrder), hasMore: Boolean(response.payload?.hasMore) };
}

// The transactions ledger — REAL PaymentAttempt rows (one per charge on a rail), not orders.
// GET /api/transactions joins each attempt to its order/customer/event and org-scopes through
// the order, so an installment order surfaces as its N slices and a retried payment as its N
// attempts — the money movements an operator actually reconciles.
export async function loadTransactionPage({ eventId, page = 1, limit = PAGE_LIMIT, status, surplus, provider, currency, searchQuery, searchField, from, to, signal } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (eventId) params.set('eventId', eventId);
  if (status) params.set('status', status);
  // A fact about the accumulator, not a status: references that took more than they asked for.
  if (surplus) params.set('surplus', 'true');
  if (provider) params.set('provider', provider);
  if (currency && currency !== 'all') params.set('currency', currency);
  if (searchQuery) {
    params.set('searchQuery', searchQuery);
    // WHICH column the term is matched against. Without it the server ORed across the attempt's
    // reference/gateway ref AND the order's code/email/phone/name — a predicate spanning two
    // tables, which no index can serve, so the plan was "scan one whole table and probe the other
    // for every row". Named, it is a single index lookup. Defaults server-side to `reference`.
    params.set('searchField', searchField || 'reference');
  }
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const response = await fetchBackOfficeJson(`/api/transactions?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Transactions request failed'}`);
  }
  const payload = response.payload || {};
  const rows = (payload.rows || []).map(normalizeTransactionFromAttempt);
  const hasMore = payload.hasMore ?? payload.pagination?.hasMore ?? false;
  return {
    rows,
    // NULL when the server didn't count — never `rows.length`, which reads as "that's all of
    // them" at the exact moment there are more. The ledger only counts when its WHERE clause is
    // index-narrowed (a search, the surplus worklist, one order): unfiltered, COUNT(*) over
    // PaymentAttempt JOIN Order is ~30µs per attempt and heads for 45s at 1.5M rows. Unknown is
    // the honest answer, and `hasMore` is what actually drives paging.
    total: payload.total ?? null,
    hasMore,
    page: payload.page ?? page,
    totalPages: payload.totalPages ?? (page + (hasMore ? 1 : 0)),
    source: 'live',
  };
}

/**
 * ONE transaction = ONE PaymentAttempt, plus the payment story it belongs to.
 *
 * A transaction detail page has to answer a question the attempt alone cannot: "is this the
 * whole payment, or one slice of it?" On mookh, 10 of 34 orders (29%) have more than one
 * attempt, and one order has three SUCCESSFUL ones — 167 + 167 + 166 KES, an installment
 * plan settling to 500. Rendering only the clicked attempt hides two thirds of the money;
 * rendering the ORDER (what this page used to do) hides that there were three movements at
 * all. So the unit is the attempt, shown in the sequence of its siblings.
 *
 * Both reads use filters GET /api/transactions already supports (?id=, ?orderId=) — no new
 * endpoint. The second call is skipped when the caller already knows the order uuid, which
 * is the normal path (clicked from the ledger), so that path costs ONE request.
 */
export async function loadTransactionDetail(attemptId, { orderUuid = null, signal } = {}) {
  let knownOrderUuid = orderUuid;
  let targetRow = null;

  if (!knownOrderUuid) {
    // Cold entry (deep link / refresh): resolve the attempt by its own id first.
    const one = await fetchBackOfficeJson(`/api/transactions?id=${encodeURIComponent(attemptId)}&limit=1&includePayload=true`, { signal });
    if (!one.ok) throw new Error(`${one.status} ${one.statusText || 'Transaction request failed'}`);
    targetRow = (one.payload?.rows || [])[0] || null;
    if (!targetRow) return null;
    knownOrderUuid = targetRow.orderId;
  }

  const all = await fetchBackOfficeJson(`/api/transactions?orderId=${encodeURIComponent(knownOrderUuid)}&limit=100&includePayload=true`, { signal });
  if (!all.ok) throw new Error(`${all.status} ${all.statusText || 'Transaction request failed'}`);

  // Oldest first: this is a SEQUENCE (slice 1, 2, 3 / retry 1, 2), and the endpoint returns
  // newest-first for the ledger. Sort on the raw timestamp, not the formatted display string.
  const siblings = (all.payload?.rows || [])
    .slice()
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
    .map(normalizeTransactionFromAttempt);

  const attempt = siblings.find((r) => r.id === attemptId)
    || (targetRow ? normalizeTransactionFromAttempt(targetRow) : null);
  if (!attempt) return null;

  return { attempt, siblings, orderUuid: knownOrderUuid };
}

/**
 * ONE attempt, nothing else — for the route resolver on a cold deep link.
 *
 * The resolver only needs enough to label the route and hand the page a real starting object
 * (crucially its `orderId`, so the page's own read is a single request). It deliberately does
 * NOT pull siblings or the provider payload: doing the full read here made a cold open cost
 * three requests, because the page then read everything again.
 */
/**
 * Every real attempt on an order, oldest first — for order→transaction navigation.
 * Replaces synthesising a fake transaction out of the order (transactionFromOrder), which
 * invented a settlement state, a recon status and an id, and made the transaction page open
 * on an ORDER uuid. An order with no attempts returns [] and the caller says so honestly.
 */
export async function loadAttemptsForOrder(orderUuid, { signal } = {}) {
  if (!orderUuid) return [];
  const res = await fetchBackOfficeJson(`/api/transactions?orderId=${encodeURIComponent(orderUuid)}&limit=100`, { signal });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText || 'Transactions request failed'}`);
  return (res.payload?.rows || [])
    .slice()
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
    .map(normalizeTransactionFromAttempt);
}

export async function loadVenues({ page, limit, searchQuery, signal } = {}) {
  // searchQuery hits the backend (server-side name/city ILIKE) so the caller can reach
  // ANY of the ~700 venues, not just the first page. Without a page it still returns a
  // flat array (the picker's expectation), just narrowed to the search.
  const params = new URLSearchParams();
  // The venues page also shows each venue's sections, seat plans and events (tkt-backend ?include=counts).
  if (page != null) { params.set('page', String(page)); params.set('limit', String(limit || 50)); params.set('include', 'counts'); }
  const q = (searchQuery || '').trim();
  if (q) { params.set('searchQuery', q); if (!params.has('limit')) params.set('limit', String(limit || 100)); }
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchBackOfficeJson(`${ENDPOINTS.venues}${qs}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Venues request failed'}`);
  }
  const rows = list(response.payload).map(normalizeVenue);
  if (page != null) {
    // Paginated call — return {rows, total, hasMore} for the venues page
    return { rows, total: response.payload?.total ?? rows.length, hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false };
  }
  // Unpaginated call — return flat array for backward compat
  return rows;
}

// Venue create/update. Backed by /api/venues (VenueController). venueSchema
// requires { name, capacity:int>0 }; address/city/country + entrances optional.
export async function createVenue({ name, capacity, address, city, country, entrances, venueType, description, svgLayoutUrl } = {}, { signal } = {}) {
  // `venueType` and `description` are REAL columns and venueSchema accepts both — the old
  // comment here said otherwise and stashed them in `metadata` instead, which is why
  // Venue.venueType is NULL on every row ever created from this console. They only went into
  // the jsonb because the picker offered slugs the enum doesn't have; the vocabulary is now
  // the enum itself (constants/venues.js), so the value can go where it belongs.
  const body = await writeJson('/api/venues', { body: {
    name, capacity: Math.max(1, Math.round(Number(capacity) || 1)),
    ...(address ? { address } : {}), ...(city ? { city } : {}), ...(country ? { country } : {}),
    ...(venueType ? { venueType } : {}), ...(description ? { description } : {}),
    ...(Array.isArray(entrances) && entrances.length ? { entrances } : {}),
    // The venue's floor plan. A REAL column on Venue that had no writer until
    // tables needed something to stand on — the image itself is uploaded to R2
    // by <ImageUpload> first, so this only ever carries the resulting key.
    ...(svgLayoutUrl ? { svgLayoutUrl } : {}),
  }, signal });
  return normalizeVenue(body?.venue || body?.data || body);
}

export async function updateVenue(venueId, patch, { signal } = {}) {
  const body = await writeJson(`/api/venues/${encodeURIComponent(venueId)}`, { method: 'PATCH', body: patch, signal });
  return normalizeVenue(body?.venue || body?.data || body);
}
// Bare destroy on the backend (no cascade) — a venue referenced by events or
// seating will FK-fail (surfaced as an error). Safe for unused venues.
export async function deleteVenue(venueId, { signal } = {}) {
  return writeJson(`/api/venues/${encodeURIComponent(venueId)}`, { method: 'DELETE', signal });
}

export async function loadOrganizerDirectory({ limit = LIST_PAGE_SIZE, page = 1, search, signal } = {}) {
  // Prefer real organizations (/api/organizations) — their ids resolve against
  // /:id/details. The organization-profiles seed is orphaned (profiles share a
  // bogus organizationId that 404s), so it's only a fallback.
  //
  // Paginated like every other list: a page at a time + server-side search
  // (searchQuery) + load-more via hasMore. No COUNT(*), no load-everything.
  const params = new URLSearchParams({ limit: String(limit), page: String(page), includeTotal: 'false' });
  if (search) params.set('searchQuery', search);
  const directoryPath = `/api/organizations?${params.toString()}`;
  const response = await fetchBackOfficeJson(directoryPath, { signal });
  if (response.ok) {
    const orgRows = list(response.payload).map(normalizeOrg);
    const hasMore = response.payload?.hasMore ?? (orgRows.length >= limit);
    if (orgRows.length || page > 1 || search) {
      return { rows: orgRows, hasMore, source: directoryPath, mode: 'direct', message: null };
    }
  }

  const profilesResponse = await fetchBackOfficeJson(ENDPOINTS.organizationProfiles, { signal });
  if (profilesResponse.ok) {
    const profileRows = list(profilesResponse.payload).map(normalizeOrganizationProfile);
    if (profileRows.length) {
      return {
        rows: profileRows,
        source: ENDPOINTS.organizationProfiles,
        mode: 'profiles',
        message: null,
      };
    }
  }

  if (response.status !== 401 && profilesResponse.status !== 404) {
    throw new Error(`${response.status} ${response.statusText || 'Organizations request failed'}`);
  }

  const fallback = await fetchBackOfficeJson(ENDPOINTS.eventPagesFallback, { signal });
  if (!fallback.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Organizations unauthorized'}; event-derived fallback failed with ${fallback.status}`);
  }

  return {
    rows: deriveOrganizationsFromEvents(list(fallback.payload)),
    source: ENDPOINTS.eventPagesFallback,
    mode: 'event-derived',
    message: `${response.status} ${response.payload?.error || 'Organizations endpoint is super-admin gated'}`,
  };
}

export async function createOrganizationProfile(payload, { signal } = {}) {
  await ensureDevAuthSession({ signal }).catch((error) => {
    console.warn('Local backend dev auth unavailable', error);
  });
  const response = await fetch(`${API_BASE}${ENDPOINTS.organizationProfiles}`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (response.headers.get('content-type') || '').includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = Array.isArray(body?.errors) ? body.errors.join(', ') : body?.error || body?.message || `${response.status} ${response.statusText || 'Organization profile creation failed'}`;
    throw new Error(message);
  }
  return body?.profile || body?.organizationProfile || body?.order || body?.data || body;
}

// Paged through to the end rather than capped at one window — see fetchAllPages. Still a plain
// array to every caller.
export async function loadTournaments({ signal } = {}) {
  const { rows, response, ok } = await fetchAllPages(
    (page) => `${ENDPOINTS.tournaments}&page=${page}`, { signal });
  if (!ok) throw new Error(`${response.status} ${response.statusText || 'Tournaments request failed'}`);
  return rows.map(normalizeTournament);
}

export async function loadTeams({ signal } = {}) {
  const { rows, response, ok } = await fetchAllPages(
    (page) => `${ENDPOINTS.teams}&page=${page}`, { signal });
  if (!ok) throw new Error(`${response.status} ${response.statusText || 'Teams request failed'}`);
  return rows.map(normalizeTeam);
}

// --- Sports: tournament / team / season CRUD + linking ---------------------
// Backed by /api/sports/{tournaments,teams,seasons}. Full CRUD + season↔team
// linking (POST/DELETE /seasons/:id/teams). tkt-frontend's repository pages.

export async function createTournament({ name, description, type = 'tournament', sportType = 'football', frequency = 'annual', isActive = true, logo } = {}, { signal } = {}) {
  // logo is JSON NOT NULL, frequency is NOT NULL — always send defaults.
  const body = await writeJson('/api/sports/tournaments', { body: { name, ...(description ? { description } : {}), type, sportType, frequency, isActive, logo: logo || {} }, signal });
  return body?.tournament || body?.data || body;
}
export async function updateTournament(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/sports/tournaments/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return body?.tournament || body?.data || body;
}
// A venue's fixtures (SportEvent rows), each with its tournament and how many event pages are its own.
// Fixtures without an event page appear nowhere else in the console, so this is where they are seen.
export async function loadVenueFixtures(venueId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/sports/events?venueId=${encodeURIComponent(venueId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Fixtures unavailable'}`);
  return list(r.payload?.data);
}
/** Delete one fixture (refused while an event page is its own; its games go with it). */
export const deleteFixture = (id, { signal } = {}) => writeJson(`/api/sports/events/${encodeURIComponent(id)}`, { method: 'DELETE', signal });

export async function deleteTournament(id, { signal } = {}) {
  return writeJson(`/api/sports/tournaments/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
export async function loadTournamentTeams(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/sports/tournaments/${encodeURIComponent(id)}/teams`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Tournament teams unavailable'}`);
  return list(r.payload);
}
// ─── Fixtures (Game) ────────────────────────────────────────────────────────
//
// A Game is created during event setup in the merchant UI and, until now, never read back or
// edited anywhere: the console had no fixture surface, and editing an event's details does not
// touch the Game row. So a fixture created with the sides the wrong way round — the event page
// titled "Mogadishu City Club vs Kitara FC" whose game called Kitara the home side — could not be
// corrected by anyone.

/** The fixture(s) attached to a sport event, with both teams resolved. */
export async function loadGamesForSportEvent(sportEventId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/games?sportEventId=${encodeURIComponent(sportEventId)}&limit=50`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Fixture unavailable'}`);
  return list(r.payload);
}

/** Change a fixture. Send BOTH team ids together to swap sides — the endpoint rejects a state
 *  where home and away are the same team, which two separate writes would pass through. */
export async function updateGame(gameId, patch, { signal } = {}) {
  return writeJson(`/api/games/${encodeURIComponent(gameId)}`, { method: 'PATCH', body: patch, signal });
}

/** Add a fixture under a sport event — a double header is two of these. `gameTime` is "HH:mm". */
export async function createGame({ sportEventId, homeTeamId, awayTeamId, gameTime }, { signal } = {}) {
  return writeJson('/api/games', { method: 'POST', body: { sportEventId, homeTeamId, awayTeamId, gameTime }, signal });
}

export async function deleteGame(gameId, { signal } = {}) {
  return writeJson(`/api/games/${encodeURIComponent(gameId)}`, { method: 'DELETE', signal });
}

// The fixtures under a tournament, as sellable events. The tab used to render MOCK.events behind a
// diagnostics flag, so with the flag off it showed an empty table under a subtitle that counted
// real ones. `gross` comes back null — not computed rather than zero; see the endpoint's note.
export async function loadTournamentEvents(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/sports/tournaments/${encodeURIComponent(id)}/events`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Tournament events unavailable'}`);
  return list(r.payload);
}

// Real venues used by this tournament — DERIVED from its fixtures' real EventPage.venueId
// (there's no stored Tournament↔Venue link in the schema; venue is chosen per-fixture).
// See tournamentRoutes.ts's `/:tournamentId/venues` for the full reasoning.
export async function loadTournamentVenues(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/sports/tournaments/${encodeURIComponent(id)}/venues`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Tournament venues unavailable'}`);
  return list(r.payload);
}

// The tournaments a team plays in — the mirror of loadTournamentTeams, and the reason a team page
// could show "0 tournaments" for a team that was linked: the link was always in TournamentTeam,
// but only the tournament end had a route to read it from. Rows are the JOIN records, so the same
// tournament appears once per season the team played in it; `season` is what says which edition.
export async function loadTeamTournaments(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/sports/teams/${encodeURIComponent(id)}/tournaments`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Team tournaments unavailable'}`);
  return list(r.payload);
}

// A team's fixtures — DERIVED from Game.homeTeamId/awayTeamId, not a stored Team↔Event link
// (there isn't one, and shouldn't be: the fixture IS the connection). Each row carries both teams
// and the fixture's EventPage, which is the thing with a title, a date and tickets.
export async function loadTeamEvents(id, { limit, signal } = {}) {
  const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  const r = await fetchBackOfficeJson(`/api/sports/teams/${encodeURIComponent(id)}/events${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Team events unavailable'}`);
  return list(r.payload);
}

export async function createTeam({ name, shortName, city, country, status, logo } = {}, { signal } = {}) {
  const body = await writeJson('/api/sports/teams', { body: { name, shortName: shortName || name, logo: logo || {}, ...(city ? { city } : {}), ...(country ? { country } : {}), ...(status ? { status } : {}) }, signal });
  return body?.team || body?.data || body;
}
export async function updateTeam(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/sports/teams/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return body?.team || body?.data || body;
}
export async function deleteTeam(id, { signal } = {}) {
  return writeJson(`/api/sports/teams/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// --- Season passes & season ticket types (sports monetization) --------------
// Backed by /api/season-passes (CRUD, status draft|published|archived; a pass is
// scoped to a season + team + org) and /api/season-ticket-types?seasonPassId=
// (CRUD + toggle-status). Contract mirrors tkt-frontend admin/season-passes.
const SEASON_PASS_STATUSES = ['draft', 'published', 'archived'];
const normalizeSeasonPass = (row, index = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `pass-${index + 1}`)),
  name: firstPresent(row.name, row.team?.name ? `${row.team.name} Season Pass` : 'Season pass'),
  description: firstPresent(row.description, ''),
  status: SEASON_PASS_STATUSES.includes(row.status) ? row.status : 'draft',
  seasonId: firstPresent(row.seasonId, row.season?.id, ''),
  seasonName: firstPresent(row.season?.name, ''),
  teamId: firstPresent(row.teamId, row.team?.id, ''),
  teamName: firstPresent(row.team?.name, '—'),
  teamShort: firstPresent(row.team?.shortName, ''),
  teamLogo: (row.team?.logo && (row.team.logo.url || row.team.logo)) || null,
  organizationId: firstPresent(row.organizationId, ''),
  ticketTypeCount: number(firstPresent(row.ticketTypeCount, row.seasonTicketTypeCount, row.ticketTypesCount)),
  holderCount: number(firstPresent(row.holderCount, row.holdersCount, row.passHolderCount)),
  createdAt: firstPresent(row.createdAt, null),
  raw: row,
});
const normalizeSeasonTicketType = (row, index = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `stt-${index + 1}`)),
  name: firstPresent(row.name, `Tier ${index + 1}`),
  description: firstPresent(row.description, ''),
  price: number(firstPresent(row.price, row.amount)),
  currency: firstPresent(row.currency, 'UGX'),
  status: row.status === 'published' ? 'published' : 'draft',
  seasonPassId: firstPresent(row.seasonPassId, row.season_pass_id, ''),
  sold: number(firstPresent(row.successfulOrderCount, row.sold)),
  raw: row,
});

export async function loadSeasonPasses({ signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/season-passes?limit=${PAGE_LIMIT}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Season passes unavailable'}`);
  return list(r.payload).map(normalizeSeasonPass);
}
export async function createSeasonPass({ seasonId, teamId, name, description, organizationId } = {}, { signal } = {}) {
  if (!seasonId || !name) throw new Error('Season and name are required');
  const body = await writeJson('/api/season-passes', { body: { seasonId, ...(teamId ? { teamId } : {}), name: name.trim(), ...(description ? { description } : {}), ...(organizationId ? { organizationId } : {}) }, signal });
  return normalizeSeasonPass(body?.data || body?.seasonPass || body, 0);
}
export async function updateSeasonPass(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/season-passes/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return normalizeSeasonPass(body?.data || body?.seasonPass || body, 0);
}
export async function deleteSeasonPass(id, { signal } = {}) {
  return writeJson(`/api/season-passes/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
export async function loadSeasonPassStats(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/season-passes/${encodeURIComponent(id)}/stats`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Pass stats unavailable'}`);
  return r.payload?.data || r.payload || {};
}

export async function loadSeasonTicketTypes(seasonPassId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/season-ticket-types?seasonPassId=${encodeURIComponent(seasonPassId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Season ticket types unavailable'}`);
  return list(r.payload).map(normalizeSeasonTicketType);
}
export async function createSeasonTicketType({ seasonPassId, seasonId, name, description, price, currency = 'UGX', status = 'draft' } = {}, { signal } = {}) {
  // Backend createSchema REQUIRES seasonId (the season UUID); seasonPassId is optional.
  if (!seasonId || !name) throw new Error('seasonId and name are required');
  const body = await writeJson('/api/season-ticket-types', { body: { seasonId, ...(seasonPassId ? { seasonPassId } : {}), name: name.trim(), ...(description ? { description } : {}), price: number(price), currency, status }, signal });
  return normalizeSeasonTicketType(body?.data || body?.seasonTicketType || body, 0);
}
export async function updateSeasonTicketType(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/season-ticket-types/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return normalizeSeasonTicketType(body?.data || body?.seasonTicketType || body, 0);
}
export async function deleteSeasonTicketType(id, { signal } = {}) {
  return writeJson(`/api/season-ticket-types/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
export async function toggleSeasonTicketTypeStatus(id, { signal } = {}) {
  const body = await writeJson(`/api/season-ticket-types/${encodeURIComponent(id)}/toggle-status`, { method: 'PATCH', body: {}, signal });
  return body?.seasonTicketType || body?.data || body;
}

// Season pass → events (which events the pass grants access to) + holders.
const normalizePassEvent = (row, i = 0) => ({
  ...row,
  id: String(firstPresent(row.id, row.eventId, `pe-${i + 1}`)),
  eventId: firstPresent(row.eventId, row.event?.id, row.id, ''),
  title: firstPresent(row.title, row.event?.title, row.name, 'Event'),
  date: firstPresent(row.date, row.event?.date, row.startDate, null),
  status: firstPresent(row.status, 'active'),
});
// The holders endpoint groups ScanItems by signature and returns firstName/
// lastName/email/phone + totalScanItems & usedCount (NOT a name or status).
// Revoke marks every unused ScanItem usedAt=now, so usedCount reaching
// totalScanItems is the real "revoked/consumed" signal (events are future-dated,
// so a holder rarely has all entries used except by revocation).
const normalizePassHolder = (row, i = 0) => {
  const total = number(firstPresent(row.totalScanItems, row.total, 0));
  const used = number(firstPresent(row.usedCount, 0));
  const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
  return {
    ...row,
    signature: firstPresent(row.signature, row.id, `holder-${i + 1}`),
    name: fullName || firstPresent(row.name, row.fullName, row.user?.name, '—'),
    email: firstPresent(row.email, row.user?.email, ''),
    phone: firstPresent(row.phone, row.user?.phone, ''),
    status: total > 0 && used >= total ? 'revoked' : 'active',
    events: total,
    used,
    createdAt: firstPresent(row.createdAt, null),
  };
};
export async function loadSeasonPassEvents(passId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/season-passes/${encodeURIComponent(passId)}/events`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Pass events unavailable'}`);
  return list(r.payload).map(normalizePassEvent);
}
export async function addEventToSeasonPass(passId, eventId, { signal } = {}) {
  if (!eventId) throw new Error('eventId is required');
  return writeJson(`/api/season-passes/${encodeURIComponent(passId)}/events`, { body: { eventId }, signal });
}
export async function removeEventFromSeasonPass(passId, eventId, { signal } = {}) {
  return writeJson(`/api/season-passes/${encodeURIComponent(passId)}/events/${encodeURIComponent(eventId)}`, { method: 'DELETE', signal });
}
// Remove is a soft-exclude (status='excluded'); this flips it back to active.
export async function reincludeSeasonPassEvent(passId, eventId, { signal } = {}) {
  return writeJson(`/api/season-passes/${encodeURIComponent(passId)}/events/${encodeURIComponent(eventId)}`, { method: 'PATCH', body: { status: 'active' }, signal });
}
export async function syncSeasonPassEvents(passId, { signal } = {}) {
  return writeJson(`/api/season-passes/${encodeURIComponent(passId)}/sync-events`, { body: {}, signal });
}
export async function loadSeasonPassHolders(passId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/season-passes/${encodeURIComponent(passId)}/holders`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Pass holders unavailable'}`);
  return list(r.payload).map(normalizePassHolder);
}
export async function revokeSeasonPassHolder(passId, signature, { signal } = {}) {
  return writeJson(`/api/season-passes/${encodeURIComponent(passId)}/holders/${encodeURIComponent(signature)}/revoke`, { method: 'PATCH', body: {}, signal });
}
// Issue a complimentary season pass (comp a VIP/sponsor) — mints a real holder
// with no payment via POST /:id/comp (server creates a successful comp order +
// runs the season fulfillment processor → ScanItems = holder).
export async function issueComplimentarySeasonPass(passId, { seasonTicketTypeId, firstName, lastName, email, phone, count = 1 } = {}, { signal } = {}) {
  if (!seasonTicketTypeId) throw new Error('A tier (season ticket type) is required');
  if (!firstName || !email || !phone) throw new Error('Recipient name, email and phone are required');
  return writeJson(`/api/season-passes/${encodeURIComponent(passId)}/comp`, { body: { seasonTicketTypeId, firstName, lastName, email, phone, count }, signal });
}

// Issue complimentary EVENT tickets (comp a press/sponsor/guest) — no payment.
// POST /api/orders/complimentary creates a successful orderType:'complimentary'
// order + runs the fulfillment processor → real ScanItems (tickets).
export async function createComplimentaryOrder({ eventId, ticketTypeId, quantity = 1, sectionId, selectedTickets: lines, firstName, lastName, email, phone, slotId, reason, note } = {}, { signal } = {}) {
  if (!eventId) throw new Error('eventId is required');
  // ONE COMP PATH, TWO SHAPES OF SELECTION. The event page comps a single tier; the manual-order
  // modal can comp several at once. Both are the same act, so both come here — the caller passes
  // either `selectedTickets` or a `ticketTypeId`/`quantity` pair, and the endpoint (which has
  // always taken an array) receives one shape.
  // WHERE IN THE VENUE, when the caller says. A ticket line already carries `sectionId`/`rowId`
  // all the way through fulfilment — `reserveNewSeats` groups by them and asks
  // SeatReservationService for seats in that section — so naming a section is how an order gets
  // seats there, and therefore the gate that section is mapped to. This mapper dropped both, so
  // every comp took whatever seat came up and its gate was luck. Same shape the buy side sends.
  const tickets = Array.isArray(lines) && lines.length
    ? lines.map((line) => ({
        id: line.id, count: Number(line.count) || 1,
        ...(line.sectionId ? { sectionId: line.sectionId } : {}),
        ...(line.rowId ? { rowId: line.rowId } : {}),
        ...(slotId ? { slotId } : {}), ...(line.slotId ? { slotId: line.slotId } : {}),
      }))
    : ticketTypeId
      ? [{ id: ticketTypeId, count: Number(quantity) || 1, ...(sectionId ? { sectionId } : {}), ...(slotId ? { slotId } : {}) }]
      : [];
  if (!tickets.length) throw new Error('A ticket type is required');
  if (!firstName || !email || !phone) throw new Error('Recipient name, email and phone are required');
  return writeJson('/api/orders/complimentary', { body: {
    eventId,
    // slotId threads onto the ticket line → OrderTicketType.scheduleSlotId →
    // ScanItem.scheduleSlotId, so a comp can target a specific occurrence.
    selectedTickets: tickets,
    firstName: firstName.trim(), ...(lastName ? { lastName: lastName.trim() } : {}),
    email: email.trim(), phone: phone.trim(),
    isBuyerRecipient: true,
    // Audit context (why the comp was issued) — persisted to order.misc by the BE.
    ...(reason ? { reason } : {}), ...(note ? { note } : {}),
  }, signal });
}

// --- Products & merchandise (Product → variants/inventory) ------------------
// Backed by /api/products (CRUD) + /api/products/variants/:productId (list/create)
// & /api/products/variants/:variantId (update/delete). productType physical|digital.
const imgUrl = (arr) => (Array.isArray(arr) && arr[0] && (arr[0].url || arr[0])) || null;
const normalizeProduct = (row, i = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `product-${i + 1}`)),
  name: firstPresent(row.name, `Product ${i + 1}`),
  price: number(firstPresent(row.price, row.amount)),
  currency: firstPresent(row.currency, 'UGX'),
  description: firstPresent(row.description, ''),
  productType: firstPresent(row.productType, 'physical'),
  season: firstPresent(row.season, ''),
  image: imgUrl(row.coverImage) || imgUrl(row.gallery),
  variantCount: number(firstPresent(row.variantCount, Array.isArray(row.variants) ? row.variants.length : 0)),
  createdAt: firstPresent(row.createdAt, null),
  raw: row,
});
const normalizeProductVariant = (row, i = 0) => {
  // variantAttributes -> attributeValue {name} -> attribute {name}, e.g. Size: M
  const options = (Array.isArray(row.variantAttributes) ? row.variantAttributes : [])
    .map((va) => ({
      attribute: firstPresent(va.attributeValue?.attribute?.name, ''),
      value: firstPresent(va.attributeValue?.name, ''),
    }))
    .filter((o) => o.value);
  return {
    ...row,
    id: String(firstPresent(row.id, `variant-${i + 1}`)),
    sku: firstPresent(row.sku, `SKU-${i + 1}`),
    price: number(firstPresent(row.price, row.amount)),
    stock: number(firstPresent(row.stock, row.quantity, row.inventory)),
    options,
    attributes: Array.isArray(row.attributes) ? row.attributes : [],
    raw: row,
  };
};
export async function loadProducts({ signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/products?limit=${PAGE_LIMIT}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Products unavailable'}`);
  return list(r.payload).map(normalizeProduct);
}
export async function createProduct({ name, price, currency = 'UGX', description, productType = 'physical', season, coverImage } = {}, { signal } = {}) {
  if (!name || price == null) throw new Error('Name and price are required');
  const body = await writeJson('/api/products', { body: { name: name.trim(), price: number(price), currency, productType, ...(description ? { description } : {}), ...(season ? { season } : {}), ...(Array.isArray(coverImage) && coverImage.length ? { coverImage } : {}) }, signal });
  return normalizeProduct(body?.data || body?.product || body, 0);
}
export async function updateProduct(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/products/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return normalizeProduct(body?.data || body?.product || body, 0);
}
export async function deleteProduct(id, { signal } = {}) {
  return writeJson(`/api/products/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
export async function loadProductVariants(productId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/products/variants/${encodeURIComponent(productId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Variants unavailable'}`);
  return list(r.payload).map(normalizeProductVariant);
}
export async function createProductVariant(productId, { sku, price, stock, attributes = [] } = {}, { signal } = {}) {
  if (!sku) throw new Error('SKU is required');
  return writeJson(`/api/products/variants/${encodeURIComponent(productId)}`, { body: { sku: sku.trim(), price: number(price), stock: number(stock), attributes }, signal });
}
export async function updateProductVariant(variantId, patch, { signal } = {}) {
  return writeJson(`/api/products/variants/${encodeURIComponent(variantId)}`, { method: 'PATCH', body: patch, signal });
}
export async function deleteProductVariant(variantId, { signal } = {}) {
  return writeJson(`/api/products/variants/${encodeURIComponent(variantId)}`, { method: 'DELETE', signal });
}
// Product attributes (options) — e.g. "Size" with values S/M/L. A variant is a
// combination of one value per attribute (ProductVariantAttributes link).
const normalizeProductAttribute = (row, i = 0) => ({
  ...row,
  id: firstPresent(row.id, `attr-${i + 1}`),
  name: firstPresent(row.name, 'Attribute'),
  values: (Array.isArray(row.values) ? row.values : []).map((v, j) => ({ id: firstPresent(v.id, `val-${j + 1}`), name: firstPresent(v.name, '') })).filter((v) => v.name),
});
export async function loadProductAttributes(productId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/products/attributes/${encodeURIComponent(productId)}?limit=100`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Attributes unavailable'}`);
  return list(r.payload).map(normalizeProductAttribute);
}
export async function createProductAttribute(productId, { name, values } = {}, { signal } = {}) {
  if (!name || !name.trim()) throw new Error('Attribute name is required');
  const cleanValues = (values || []).map((v) => ({ name: (typeof v === 'string' ? v : v?.name || '').trim() })).filter((v) => v.name);
  if (!cleanValues.length) throw new Error('At least one value is required');
  return writeJson(`/api/products/attributes/${encodeURIComponent(productId)}`, { body: { name: name.trim(), productId, values: cleanValues }, signal });
}
// PATCH renames and (optionally) replaces values. Replacing values an existing
// variant references will 500 (FK) — surfaced as an error to the operator.
export async function updateProductAttribute(attributeId, { name, values } = {}, { signal } = {}) {
  const body = {};
  if (name && name.trim()) body.name = name.trim();
  if (values) {
    body.values = values.map((v) => ({ ...(v?.id ? { id: v.id } : {}), name: (typeof v === 'string' ? v : v?.name || '').trim() })).filter((v) => v.name);
  }
  return writeJson(`/api/products/attributes/${encodeURIComponent(attributeId)}`, { method: 'PATCH', body, signal });
}
export async function deleteProductAttribute(attributeId, { signal } = {}) {
  return writeJson(`/api/products/attributes/${encodeURIComponent(attributeId)}`, { method: 'DELETE', signal });
}

export async function loadSeasons({ tournamentId, signal } = {}) {
  const base = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}&limit=80` : '?limit=80';
  const { rows, response: r, ok } = await fetchAllPages(
    (page) => `/api/sports/seasons${base}&page=${page}`, { signal });
  if (!ok) throw new Error(`${r.status} ${r.payload?.error || 'Seasons unavailable'}`);
  return rows.map((s) => ({
    id: String(firstPresent(s.id, '')), name: firstPresent(s.name, 'Season'),
    tournamentId: firstPresent(s.tournamentId, ''), startDate: firstPresent(s.startDate, null), endDate: firstPresent(s.endDate, null),
    isActive: s.isActive !== false, teamCount: number(firstPresent(s.teamCount, s.teams?.length)), raw: s,
  }));
}
export async function createSeason({ tournamentId, name, startDate, endDate, isActive = true } = {}, { signal } = {}) {
  const body = await writeJson('/api/sports/seasons', { body: { tournamentId, name, startDate, endDate, isActive }, signal });
  return body?.season || body?.data || body;
}
export async function deleteSeason(id, { signal } = {}) {
  return writeJson(`/api/sports/seasons/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}
// Teams that can still be added to a season.
//
// RETURNS THE COUNT, NOT JUST THE ROWS, and that is the whole point of the shape. The endpoint used
// to answer 50 rows and report `totalAvailable` as the length of that page, so a picker holding 50
// of 51 teams had no way to know one was missing — and the modal filters the array it was handed,
// so searching could not reach the missing team either. It was invisible from both ends.
//
// `hasMore` is now the server's own answer about the set, so a caller can say "showing 500 of 812"
// and send the search term to the server instead of quietly presenting a slice as everything.
export async function loadSeasonAvailableTeams(seasonId, { searchQuery, limit, signal } = {}) {
  const qs = [
    searchQuery?.trim() ? `searchQuery=${encodeURIComponent(searchQuery.trim())}` : '',
    limit ? `limit=${encodeURIComponent(limit)}` : '',
  ].filter(Boolean).join('&');
  const r = await fetchBackOfficeJson(`/api/sports/seasons/${encodeURIComponent(seasonId)}/available-teams${qs ? `?${qs}` : ''}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Available teams unavailable'}`);
  const rows = list(r.payload).map(normalizeTeam);
  return {
    rows,
    // Falls back to the row count only for a backend that predates the fix, where it is the same
    // lie as before but at least not a crash.
    total: Number.isFinite(Number(r.payload?.totalAvailable)) ? Number(r.payload.totalAvailable) : rows.length,
    hasMore: Boolean(r.payload?.hasMore),
  };
}
export async function loadSeasonTeams(seasonId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/sports/seasons/${encodeURIComponent(seasonId)}/teams`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Season teams unavailable'}`);
  // Rows are TournamentTeam join records: { id (join id), teamId, status, team:{...} }.
  // Flatten the nested team and use teamId as the id — unlink/PATCH target /teams/:teamId.
  return list(r.payload).map((row, i) => {
    const t = normalizeTeam({ ...(row.team || {}), id: row.teamId || row.team?.id, status: row.status }, i);
    t.tournamentTeamId = row.id;
    return t;
  });
}
export async function linkTeamsToSeason(seasonId, teamIds, { signal } = {}) {
  return writeJson(`/api/sports/seasons/${encodeURIComponent(seasonId)}/teams`, { body: { teamIds: Array.isArray(teamIds) ? teamIds : [teamIds] }, signal });
}
export async function unlinkTeamFromSeason(seasonId, teamId, { signal } = {}) {
  return writeJson(`/api/sports/seasons/${encodeURIComponent(seasonId)}/teams/${encodeURIComponent(teamId)}`, { method: 'DELETE', signal });
}

/**
 * ONE PAYOUT, BY ID — what a shared /payouts/<id> link resolves.
 *
 * The detail route used to resolve by fetching the payout LIST and looking for the id in it, so a
 * link to anything past the newest page opened on "No payout matches this link" for a payout that
 * plainly exists. The list endpoint has always accepted `payoutId` (it is in its query schema); the
 * handler simply never applied it, so this is the same call, now actually filtered — no new route.
 */
export async function loadPayouts({ organizationId, eventId = null, limit = null, status = null, from = null, to = null, signal } = {}) {
  // status (comma-separated) + date window + eventId are applied at the DB, so callers
  // that only need a specific slice (e.g. one event's payout) fetch just those rows.
  let base = limit ? `/api/payouts/admin?limit=${limit}&includeTotal=false` : ENDPOINTS.payouts;
  if (status) base += `&status=${encodeURIComponent(status)}`;
  if (eventId) base += `&eventId=${encodeURIComponent(eventId)}`;
  if (from) base += `&from=${encodeURIComponent(from)}`;
  if (to) base += `&to=${encodeURIComponent(to)}`;
  const path = organizationId
    ? `${base}${base.includes('?') ? '&' : '?'}organizationId=${encodeURIComponent(organizationId)}`
    : base;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Payouts request failed'}`);
  }
  return list(response.payload).map(normalizePayout);
}

export async function loadPaymentAccounts({ signal, organizationId } = {}) {
  const path = organizationId
    ? `/api/payment-accounts?organizationId=${encodeURIComponent(organizationId)}&limit=${PAGE_LIMIT}`
    : ENDPOINTS.paymentAccounts;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Payment accounts request failed'}`);
  }
  return list(response.payload).map(normalizePaymentAccount);
}

// Build the backend payload from flat form fields (mirrors tkt-frontend's
// payment-account-sheet): the bank/account/currency/status pack into accountDetails.
// `name` is the operator's nickname ("Equity UGX Settlement"); the REAL holder
// (accountName), SWIFT/BIC, branch and bank code ride inside accountDetails so a
// payout can show who the money goes to instead of the nickname.
function buildPaymentAccountPayload({ name, description, type, bankName, accountNumber, accountName, swift, branch, bankCode, currency, status, isDefault, organizationId }) {
  return {
    name,
    ...(description ? { description } : {}),
    type, // bank_account | mobile_money | crypto
    accountDetails: {
      bankName: bankName || '',
      accountNumber: accountNumber || '',
      ...(accountName ? { accountName } : {}),
      ...(accountName ? { accountHolder: accountName } : {}),
      ...(swift ? { swift } : {}),
      ...(swift ? { bic: swift } : {}),
      ...(branch ? { branch } : {}),
      ...(bankCode ? { bankCode } : {}),
      currency: currency || null,
      status: status || 'active',
    },
    isDefault: !!isDefault,
    organizationId,
  };
}

// POST /api/payment-accounts — add a payout account FOR an organization (the back
// office is super-admin, so organizationId is explicit). Real mutation.
export async function createPaymentAccount(input, { signal } = {}) {
  const body = await writeJson('/api/payment-accounts', { body: buildPaymentAccountPayload(input), signal });
  return normalizePaymentAccount(body?.data || body?.paymentAccount || body);
}

export async function updatePaymentAccount(id, patch, { signal } = {}) {
  // PATCH accepts a partial of the same shape; pack accountDetails if any detail field given.
  // WARNING: the backend applies PATCH with a wholesale `update()` — an accountDetails
  // object here REPLACES the stored JSONB, it does not merge. Never send a partial
  // details object unless every key was read first; the only current caller toggles
  // isDefault (no details), which is safe.
  const hasDetails = ['bankName', 'accountNumber', 'accountName', 'swift', 'branch', 'bankCode', 'currency', 'status'].some((k) => patch[k] !== undefined);
  const body = {
    ...(patch.name !== undefined ? { name: patch.name } : {}),
    ...(patch.description !== undefined ? { description: patch.description } : {}),
    ...(patch.type !== undefined ? { type: patch.type } : {}),
    ...(patch.isDefault !== undefined ? { isDefault: !!patch.isDefault } : {}),
    ...(patch.organizationId !== undefined ? { organizationId: patch.organizationId } : {}),
    ...(hasDetails ? { accountDetails: { bankName: patch.bankName, accountNumber: patch.accountNumber, accountName: patch.accountName, swift: patch.swift, branch: patch.branch, bankCode: patch.bankCode, currency: patch.currency, status: patch.status } } : {}),
  };
  const res = await writeJson(`/api/payment-accounts/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal });
  return normalizePaymentAccount(res?.data || res?.paymentAccount || res);
}

/**
 * Fill in what an account is missing (its number, provider, holder) ON TOP of what it already holds.
 * The backend's PATCH replaces accountDetails wholesale, so the stored details are the base and
 * only non-empty fields from `fill` are laid over them.
 */
export async function completePaymentAccount(account, fill, { signal } = {}) {
  const base = { ...(account?.raw?.accountDetails || {}) };
  for (const [k, v] of Object.entries(fill || {})) if (String(v ?? '').trim()) base[k] = String(v).trim();
  const res = await writeJson(`/api/payment-accounts/${encodeURIComponent(account.id)}`, { method: 'PATCH', body: { accountDetails: base }, signal });
  return normalizePaymentAccount(res?.data || res?.paymentAccount || res);
}

export async function deletePaymentAccount(id, { signal } = {}) {
  return writeJson(`/api/payment-accounts/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// A payout route's reason: `{ error }`, or `{ errors: [...] }` — its validation answers a list (messages or zod issues).
const payoutErrorOf = (payload) => payload?.error || payload?.message
  || (Array.isArray(payload?.errors) ? payload.errors.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean).join('; ') : '');

async function mutatePayout(path, body, { signal } = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payoutErrorOf(payload) || `${response.status} ${response.statusText || 'Payout mutation failed'}`);
  }
  return normalizePayout(payload?.payout || payload?.data || payload);
}

async function patchPayout(path, body, { signal } = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payoutErrorOf(payload) || `${response.status} ${response.statusText || 'Payout update failed'}`);
  }
  return normalizePayout(payload?.payout || payload?.data || payload);
}

async function deletePayoutRequest(path, body, { signal } = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payoutErrorOf(payload) || `${response.status} ${response.statusText || 'Payout delete failed'}`);
  }
  return payload;
}

export function approvePayout(payout, { signal } = {}) {
  return mutatePayout(payoutEndpoint('/approve'), { payoutId: payout.id, amount: number(payout.amount) }, { signal });
}

export function fulfillPayout(payout, transactionId = '', { signal, proofUrl } = {}) {
  return mutatePayout(payoutEndpoint('/fulfill'), { payoutId: payout.id, transactionId: transactionId || undefined, proofUrl: proofUrl || undefined }, { signal });
}

// Attach (or replace) the proof of payment on a payout already paid — with its reference if missing.
export function attachPayoutProof(payout, { proofUrl, transactionId } = {}, { signal } = {}) {
  return mutatePayout(payoutEndpoint('/proof'), { payoutId: payout.id, proofUrl, transactionId: transactionId || undefined }, { signal });
}

/**
 * Record payouts that have ALREADY been made — one event or many — straight to paid, with when,
 * the transfer reference and a proof (backend POST /record-paid; all or nothing, per-line errors).
 */
export async function recordPayoutsPaid({ items, transactionId, proofUrl, paidAt, note, beyondOwed = false }, { signal } = {}) {
  const response = await fetch(`${API_BASE}${payoutEndpoint('/record-paid')}`, {
    method: 'POST', signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, transactionId: transactionId || undefined, proofUrl: proofUrl || undefined, paidAt: paidAt || undefined, note: note || undefined, ...(beyondOwed ? { beyondOwed: true } : {}) }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error(payload?.error || (payload?.errors || []).join(' · ') || `${response.status} ${response.statusText || 'Recording failed'}`);
    err.failures = payload?.failures || [];
    // BEYOND_OWED: more than the event owes — recordable only when the operator confirms it was really sent.
    err.code = payload?.code || null;
    throw err;
  }
  window.dispatchEvent(new CustomEvent(WRITE_EVENT));
  return (payload?.payouts || []).map(normalizePayout);
}

export function rejectPayout(payout, reason = '', { signal } = {}) {
  return mutatePayout(payoutEndpoint('/reject'), { payoutId: payout.id, reason: reason || undefined }, { signal });
}

export function updatePayout(payoutId, changes, { signal } = {}) {
  return patchPayout(payoutEndpoint(`?payoutId=${encodeURIComponent(payoutId)}`), changes, { signal });
}

export function deletePayout(payoutId, { signal } = {}) {
  return deletePayoutRequest(payoutEndpoint(`?payoutId=${encodeURIComponent(payoutId)}`), { payoutId }, { signal });
}

export function createPayout({ eventId, paymentAccountId, amount, description, currency = 'UGX', paymentMethod, reference, scheduledDate }, { signal } = {}) {
  return mutatePayout(payoutEndpoint(''), {
    eventId,
    paymentAccountId,
    amount: number(amount),
    description,
    currency,
    paymentMethod,
    reference,
    scheduledDate,
  }, { signal });
}

export async function loadPayoutStatement(payoutId, { signal } = {}) {
  const response = await fetchBackOfficeJson(payoutEndpoint(`/${encodeURIComponent(payoutId)}/statement`), { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Payout statement request failed'}`);
  }
  return unwrap(response.payload)?.data || unwrap(response.payload);
}

export async function loadPayoutFinanceStats({ from = null, to = null, allTime = false, eventId = null, fresh = false, signal } = {}) {
  // The backend bounds providerTotals + eventTotals by `from`/`to` (default
  // ~1 month when neither is sent). Callers can pass an explicit from/to window
  // (the dashboard's time switch does this so the collected headline AND the
  // reconciling event breakdown move together), or allTime to reach back far.
  // Same shape as tkt-frontend's useFinanceStats.
  //
  // One way to say a window: an absolute bound from lib/dateRange. The `windowDays` shorthand
  // removed from here built its own `Date.now() - days * 86400000` — a rolling N×24h reach, not
  // a day boundary — so the same preset meant two things depending on which argument was used.
  let path = ENDPOINTS.payoutFinanceStats;
  const params = new URLSearchParams();
  if (allTime) {
    params.set('from', '2015-01-01');
  } else if (from) {
    params.set('from', from);
    if (to) params.set('to', to);
  }
  if (eventId) params.set('eventId', eventId); // one event's money, as paid
  if (fresh) params.set('fresh', '1');           // Refresh: recompute, not the 5-minute copy
  const qs = params.toString();
  if (qs) path += `?${qs}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Payout finance stats request failed'}`);
  }
  return unwrap(response.payload);
}

/**
 * AN EVENT'S MONEY POSITION, from finance-stats — one derivation for every page that states it
 * (the event's Finance tab, a payout awaiting approval). Collected is net per currency across
 * providers; paid out is fulfilled payouts, which carry NO currency, so "left" is only derivable
 * when the event collected in exactly one currency (otherwise it is null, not a sum of unlike units).
 */
export const eventMoneyPosition = (data) => {
  const buckets = {};
  for (const totals of Object.values(data?.providerBreakdown || {})) {
    for (const [cur, amt] of Object.entries(totals || {})) if (Number(amt) > 0) buckets[cur] = (buckets[cur] || 0) + Number(amt);
  }
  // What went back to buyers, per currency — the backend's own figure beside what was owed (settlementLines.refunded),
  // subtracted here, where both are shown.
  const refundedBy = {};
  for (const l of data?.settlementLines || []) if (Number(l.refunded) > 0) refundedBy[l.currency] = (refundedBy[l.currency] || 0) + Number(l.refunded);
  // The event's balance deductions (those not on a payout), per currency — they come off what is left.
  const deductedBy = {};
  for (const d of data?.deductions || []) if (!d.payoutId && Number(d.amount) > 0) deductedBy[d.currency] = (deductedBy[d.currency] || 0) + Number(d.amount);
  const currencies = Object.keys(buckets);
  const currency = currencies.length === 1 ? currencies[0] : null;
  const paidOut = Number(data?.totalPayoutSoFar || 0);
  const refunded = currency ? refundedBy[currency] || 0 : null;
  const deducted = currency ? deductedBy[currency] || 0 : null;
  // Not clamped: below zero is money the organiser was paid that went back to buyers since — owed back, and shown so.
  return { buckets, refundedBy, deductedBy, currencies, currency, paidOut, collected: currency ? buckets[currency] : null, refunded, deducted,
    left: currency ? buckets[currency] - refunded - deducted - paidOut : null };
};

export async function loadEventFinanceStats(eventId, { signal } = {}) {
  const path = `/api/payouts/events/${encodeURIComponent(eventId)}/finance-stats`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Event finance stats request failed'}`);
  }
  return {
    ...unwrap(response.payload),
    source: path,
  };
}

export const SETTLEMENT_PAGE_EVENTS = 25;
export async function loadPayoutSettlements({ signal, page = 1, organizationId = null, eventId = null, limit = null } = {}) {
  let path = ENDPOINTS.payoutSettlements;
  if (limit) path = path.replace(/limit=\d+/, `limit=${limit}`);
  if (page > 1) path += `&page=${page}`;
  if (organizationId) path += `&organizationId=${encodeURIComponent(organizationId)}`;
  if (eventId) path += `&eventId=${encodeURIComponent(eventId)}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Payout settlements request failed'}`);
  }
  return list(response.payload).map((row) => ({ ...row, state: settlementStateOf(row) }));
}

/** Treasury for one period [from, to) (instants) — receipts by method/provider/currency/what the order is now, what
 *  the touched orders still owe or hold, unplaced money, refunds asked and payouts sent. Scope: eventId, method, provider. */
export async function loadTreasury({ from, to, eventId = null, network = null, provider = null, signal } = {}) {
  const p = new URLSearchParams({ from, to });
  if (eventId) p.set('eventId', eventId);
  if (network) p.set('network', network);
  if (provider) p.set('provider', provider);
  const response = await fetchBackOfficeJson(`/api/payouts/treasury?${p}`, { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Treasury request failed`);
  return response.payload?.data;
}

/** Treasury's exceptions — open items where money is not where it should be (overpaid, voided holding money, unplaced,
 *  refunds to send) that arose in [from, to), newest first, 25 a page (`limit` asks for fewer). Scope: kind, eventId, currency. */
export async function loadTreasuryExceptions({ page = 1, limit = null, kind = null, eventId = null, currency = null, from, to, totals = false, signal } = {}) {
  const p = new URLSearchParams({ page: String(page), from, to });
  if (limit) p.set('limit', String(limit));
  if (totals) p.set('totals', '1'); // what each kind holds in the window, whole: [{ kind, currency, amount, count }]
  if (kind) p.set('kind', kind);
  if (eventId) p.set('eventId', eventId);
  if (currency) p.set('currency', currency);
  const response = await fetchBackOfficeJson(`/api/payouts/treasury/exceptions?${p}`, { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Exceptions request failed`);
  return { rows: response.payload?.data?.rows || [], hasMore: !!response.payload?.hasMore, meta: { totals: response.payload?.data?.totals || null } };
}

/** What each organizer is owed, one row per (organizer, currency) — 25 organizers a page, most recently active
 *  first; `q` searches organizer names on the server. Rows: { organizerId, organizerName, currency, events, gross,
 *  owed, paidOut, approved, stillOwed, free, requests, overpaid }. */
export async function loadOrganizerBalances({ page = 1, q = '', organizationId = null, signal } = {}) {
  const p = new URLSearchParams({ page: String(page) });
  if (q.trim()) p.set('q', q.trim());
  if (organizationId) p.set('organizationId', organizationId);
  const response = await fetchBackOfficeJson(`/api/payouts/organizer-balances?${p}`, { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Organizer balances request failed`);
  return { rows: response.payload?.data || [], hasMore: !!response.payload?.hasMore };
}

/** An organizer's ticket sales since `from` (an ISO instant), per currency: [{ currency, orders, gross }]. The header's
 *  window — bounded on the server by (event, paid date), so it costs the window's orders, never the organizer's history. */
export async function loadOrganizerSales(organizationId, { from, signal } = {}) {
  const p = new URLSearchParams({ organizationId, from });
  const response = await fetchBackOfficeJson(`/api/payouts/organizer-sales?${p}`, { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Organizer sales request failed`);
  return response.payload?.data || [];
}

/** An organizer's statement for one period — `from` / `to` are local days (YYYY-MM-DD), [from, to); absent = open.
 *  The server builds it (events, position, the balance brought forward, the period's lines) in this browser's zone. */
export async function loadOrganizerStatementPeriod(organizationId, { from = null, to = null, signal } = {}) {
  const p = new URLSearchParams({ organizationId, tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' });
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  const response = await fetchBackOfficeJson(`/api/payouts/organizer-statement?${p}`, { signal });
  if (!response.ok) throw new Error(response.payload?.error || `${response.status} Statement request failed`);
  return response.payload?.data;
}

// Builds of the backend before 2026-09-30 name states nothing produces (awaiting_recon, draft,
// disputed). Read those by the same facts the backend now uses; a current backend passes through.
const LEGACY_SETTLEMENT_STATES = new Set(['awaiting_recon', 'draft', 'disputed', 'on_hold', 'under_investigation']);
const settlementStateOf = (row) => {
  if (!LEGACY_SETTLEMENT_STATES.has(row.state)) return row.state;
  if (Number(row.overpaid) > 0) return 'overpaid';
  if (row.requestedPayoutId) return 'awaiting_approval'; // "disputed" hid a waiting request behind an old rejection
  if (Number(row.paidOut) > 0) return Number(row.outstanding) > 0 ? 'partially_paid' : 'paid';
  return Number(row.netPayable) > 0 ? 'unpaid' : 'no_sales';
};

// --- Wallets (BE: /api/wallets) -------------------------------------------
// Returns three grouped lists (platform, rails, orgs) computed from real
// Order and Payout data on the BE. The "platform" bucket math is:
//   operating = gross_sales − platform_fees − disbursed − refunded
//   fee float = 4.5% of successful orders (the platform commission)
//   reserve   = 5% of gross (rolling reserve against chargebacks)
//   refund    = refunded amount held in float
// "rails" are grouped by Order.paymentMethod, "orgs" by Payout.organizationId.

// `window` bounds the (expensive) rail/collections scan: 'all' computes
// everything ever (~6s — opt-in), otherwise we send a recent from-date so the
// page loads fast (~400ms). Default 30d is set by the caller.
export async function loadWallets({ from = null, allTime = false, signal } = {}) {
  let path = ENDPOINTS.wallets;
  if (allTime) {
    path += '?all=true';
  } else if (from) {
    // An absolute, UTC-day-anchored instant from lib/dateRange.windowFrom — the only way a
    // window is expressed here. See loadPayoutFinanceStats for the `windowDays` this replaced.
    path += `?from=${encodeURIComponent(from)}`;
  }
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Wallets request failed'}`);
  }
  return {
    platform: list(response.payload?.platform || []),
    rails:    list(response.payload?.rails || []),
    orgs:     list(response.payload?.orgs || []),
  };
}

export async function loadWalletLedger({ walletId, page = 1, from = null, signal } = {}) {
  if (!walletId) return { walletId: null, movements: [], hasMore: false };
  // 25 a page, newest first, within the page's window (`from`; none = all time); `hasMore` says whether another page exists.
  const params = new URLSearchParams({ page: String(page) });
  if (from) params.set('from', from);
  const response = await fetchBackOfficeJson(`${ENDPOINTS.wallets}/${encodeURIComponent(walletId)}/ledger?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Wallet ledger request failed'}`);
  }
  return {
    walletId: response.payload?.walletId || walletId,
    movements: list(response.payload?.movements || []),
    hasMore: !!response.payload?.hasMore,
  };
}

// --- Box offices -----------------------------------------------------------
// Backed by /api/boxOffices (tkt-backend src/routes/boxOfficeRoutes.ts).
// The list is org-scoped server-side (via event ownership). A box office is
// event-scoped: create requires { name, email, eventId, selectedTicketTypes }.
// Live shift metrics (cash, sold-today) are not yet aggregated by the list
// endpoint, so they default to 0 — the row is truthful rather than seeded.

const normalizeBoxOffice = (row, index = 0) => {
  const paused = Boolean(row.paused);
  // CLOSED once its own expiry or its event has passed (a one-day event counts through its day) — every box office
  // that was not paused read "Open", including 11 of prod's 12, which belong to events already over.
  const dayAfter = (v) => { const t = v ? Date.parse(v) : NaN; return Number.isFinite(t) ? t + 86_400_000 : null; };
  const closesAt = [dayAfter(row.expiryDate), dayAfter(row.event?.date)].filter((t) => t != null);
  const closed = closesAt.length > 0 && Math.min(...closesAt) < Date.now();
  const location = parseMaybeJson(row.location, {});
  return {
    ...row,
    id: String(firstPresent(row.id, `box-office-${index + 1}`)),
    name: firstPresent(row.name, `Box office ${index + 1}`),
    email: firstPresent(row.email, ''),
    phone: firstPresent(row.phone, ''),
    venue: firstPresent(location.name, location.venue, location.address, '—'),
    event: String(firstPresent(row.eventId, row.event?.id, '')),
    eventName: firstPresent(row.event?.title, row.eventName, '—'),
    eventStatus: firstPresent(row.event?.status, ''),
    status: paused ? 'Suspended' : closed ? 'Closed' : 'Open',
    paused,
    agents: Array.isArray(row.operators) ? row.operators : [],
    soldToday: number(firstPresent(row.soldToday, row.stats?.soldToday)),
    cashCollected: number(firstPresent(row.cashCollected, row.stats?.cashCollected)),
    cardCollected: number(firstPresent(row.cardCollected, row.stats?.cardCollected)),
    momoCollected: number(firstPresent(row.momoCollected, row.stats?.momoCollected)),
    refunds: number(firstPresent(row.refunds, row.stats?.refunds)),
    pendingRecon: number(firstPresent(row.pendingRecon, row.stats?.pendingRecon)),
    cashLimit: number(row.cashLimit),
    dailyLimit: number(row.dailyLimit),
    // Detail-page shape the backend box office doesn't carry yet — default so
    // the detail view renders truthfully empty rather than crashing on .map().
    allowedTiers: Array.isArray(row.allowedTiers) ? row.allowedTiers : (Array.isArray(row.ticketTypes) ? row.ticketTypes.map((t) => t?.TicketType?.name || t?.name).filter(Boolean) : []),
    allowedPayments: Array.isArray(row.allowedPayments) ? row.allowedPayments : [],
    openedAt: firstPresent(row.openedAt, ''),
    openingHours: firstPresent(location.openingHours, row.openingHours, ''),
    ticketTypes: Array.isArray(row.ticketTypes) ? row.ticketTypes : [],
    created: formatDate(row.createdAt),
    backendBacked: Boolean(row.id),
    raw: row,
  };
};

export async function loadBoxOffices({ limit = PAGE_LIMIT, page = 1, search, organizationOf = null, signal } = {}) {
  const params = new URLSearchParams({ limit: String(limit), page: String(page) });
  if (search) params.set('searchQuery', search);
  if (organizationOf) params.set('organizationOf', organizationOf); // the other outlets of this outlet's organisation
  const response = await fetchBackOfficeJson(`/api/boxOffices?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText || 'Box offices request failed'}`);
  }
  return {
    boxOffices: list(response.payload?.data || response.payload).map(normalizeBoxOffice),
    total: number(response.payload?.totalItems, list(response.payload?.data || response.payload).length),
    hasMore: response.payload?.hasMore ?? response.payload?.pagination?.hasMore ?? false,
  };
}

export async function createBoxOffice({ name, email, phone, eventId, location, expiryDate, selectedTicketTypes = [] }, { signal } = {}) {
  const body = {
    name,
    email,
    ...(phone ? { phone } : {}),
    eventId,
    location: location ?? null,
    ...(expiryDate ? { expiryDate } : {}),
    selectedTicketTypes,
  };
  const response = await fetch(`${API_BASE}/api/boxOffices`, {
    method: 'POST', signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = Array.isArray(payload?.errors) ? payload.errors.join(', ') : payload?.error || `${response.status} Failed to create box office`;
    throw new Error(message);
  }
  return payload?.boxOffice || payload;
}

// --- Box-office operators (the real "sales agents") ------------------------
// Each box office has operators (/api/boxOffices/:id/operators). loadAgents
// aggregates operators across all box offices into one directory.

const normalizeOperator = (op, boxOffice) => ({
  ...op,
  id: String(firstPresent(op.id, op.operatorId, '')),
  name: firstPresent(op.name, 'Operator'),
  role: firstPresent(op.role, 'Operator'),
  status: titleCase(firstPresent(op.status, 'active')),
  source: firstPresent(op.source, 'external'),
  contact: firstPresent(op.contact, ''),
  contactType: firstPresent(op.contactType, ''),
  pausedReason: firstPresent(op.pausedReason, ''),
  blockedReason: firstPresent(op.blockedReason, ''),
  lastActiveAt: firstPresent(op.lastActiveAt, null),
  boxOfficeId: firstPresent(op.boxOfficeId, boxOffice?.id, ''),
  // Alias used by older AgentDetail mock chrome (boxOffice vs boxOfficeId).
  boxOffice: firstPresent(op.boxOfficeId, boxOffice?.id, ''),
  boxOfficeName: firstPresent(boxOffice?.name, op.boxOfficeName, '—'),
  joined: formatDate(firstPresent(op.createdAt, op.joinedAt)),
  // Detail used to invent sales/refunds; keep zeros so UI never shows mock numbers.
  todaySales: 0,
  lifetimeSales: 0,
  lifetimeRevenue: 0,
  refundsToday: 0,
  failedToday: 0,
  cashHeld: 0,
  momoToday: 0,
  cardToday: 0,
  restrictions: 0,
  currentShift: '—',
  supervisor: '—',
  raw: op,
});

export async function loadBoxOfficeOperators(boxOfficeId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/operators`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Operators unavailable'}`);
  return list(r.payload);
}

// The REAL ticket types a box office sells, with per-type quantity. Backed by
// GET /api/boxOffices/ticketTypes/:id (BoxOfficeTicketType ⋈ TicketType). This
// is the genuine "what this counter sells" — set at create — not the old
// hardcoded category chips.
export async function loadBoxOfficeTicketTypes(boxOfficeId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/boxOffices/ticketTypes/${encodeURIComponent(boxOfficeId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Ticket types unavailable'}`);
  return list(r.payload).map((row, i) => {
    const tt = row.TicketType || row.ticketType || row.ticketTypeData || {};
    return {
      id: String(firstPresent(row.id, row.ticketTypeId, tt.id, `bott-${i + 1}`)),
      ticketTypeId: firstPresent(row.ticketTypeId, tt.id, ''),
      name: firstPresent(tt.name, tt.title, row.name, 'Ticket type'),
      price: number(firstPresent(tt.price, tt.amount, tt.cost)),
      currency: firstPresent(tt.currency, tt.cur, 'UGX'),
      quantity: number(firstPresent(row.quantity, row.qty, row.allocation)),
      raw: row,
    };
  });
}

// Replace a box office's ticket-type allocation. Backed by the real
// PATCH /api/boxOffices/:id/ticket-types (replaces BoxOfficeTicketType rows;
// quantity 0 drops a type). `ticketTypes` = [{ ticketTypeId, quantity }].
export async function saveBoxOfficeTicketTypes(boxOfficeId, ticketTypes = [], { signal } = {}) {
  const body = await writeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/ticket-types`, {
    method: 'PATCH',
    body: { ticketTypes: ticketTypes.map((t) => ({ ticketTypeId: String(t.ticketTypeId), quantity: Number(t.quantity) || 0 })) },
    signal,
  });
  return body;
}

// Box-office shift history. Backed by GET /api/boxOffices/:id/sessions
// (BoxOfficeSession: operator, openedAt/closedAt, status, ticketsSold, revenue).
const normalizeBoxOfficeSession = (row, index = 0) => {
  const fmt = (v) => { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
  const opened = fmt(row.openedAt);
  const closed = fmt(row.closedAt);
  return {
    ...row,
    id: String(firstPresent(row.id, `session-${index + 1}`)),
    operator: firstPresent(row.operatorName, row.operator?.name, 'Operator'),
    openedAt: opened ? opened.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—',
    closedAt: closed ? closed.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null,
    status: firstPresent(row.status, 'closed'),
    ticketsSold: number(row.ticketsSold),
    revenue: number(row.revenue),
    openingFloat: row.openingFloat != null || row.floatAmount != null || row.opening_float != null || row.cashFloat != null
      ? number(firstPresent(row.openingFloat, row.floatAmount, row.opening_float, row.cashFloat)) : null,
    paymentMethods: Array.isArray(row.paymentMethods) ? row.paymentMethods : [],
    raw: row,
  };
};

export async function loadBoxOfficeSessions(boxOfficeId, { signal, limit = 50, operatorId = null } = {}) {
  if (!boxOfficeId) return { rows: [], total: 0 };
  const r = await fetchBackOfficeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/sessions?limit=${limit}${operatorId ? `&operatorId=${encodeURIComponent(operatorId)}` : ''}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Sessions unavailable'}`);
  const rows = list(r.payload).map(normalizeBoxOfficeSession);
  // Box-office sessions run on `paginate` too — no COUNT, so no total to report.
  const sessTotal = firstPresent(r.payload?.totalItems, r.payload?.total);
  return { rows, total: sessTotal == null ? null : number(sessTotal, rows.length), hasMore: r.payload?.hasMore ?? r.payload?.pagination?.hasMore ?? false };
}
// Open a shift: POST /:id/sessions {operatorName?, paymentMethods?, notes?} → active session.
// The backend stores NO cash float (no such column) — don't fabricate one.
export async function openBoxOfficeSession(boxOfficeId, { operatorName, paymentMethods, notes } = {}, { signal } = {}) {
  const body = {};
  if (operatorName) body.operatorName = operatorName;
  if (Array.isArray(paymentMethods) && paymentMethods.length) body.paymentMethods = paymentMethods;
  if (notes) body.notes = notes;
  const r = await writeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/sessions`, { body, signal });
  return normalizeBoxOfficeSession(r?.session || r?.data || r, 0);
}
// Close a shift: PATCH /:id/sessions/:sessionId {status:'closed', ticketsSold?, revenue?, notes?}.
export async function closeBoxOfficeSession(boxOfficeId, sessionId, { ticketsSold, revenue, notes } = {}, { signal } = {}) {
  const body = { status: 'closed' };
  if (ticketsSold != null && ticketsSold !== '') body.ticketsSold = number(ticketsSold);
  if (revenue != null && revenue !== '') body.revenue = number(revenue);
  if (notes) body.notes = notes;
  const r = await writeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/sessions/${encodeURIComponent(sessionId)}`, { method: 'PATCH', body, signal });
  return normalizeBoxOfficeSession(r?.session || r?.boxSession || r?.data || r, 0);
}

// Aggregate operators across every box office → flat agent directory.
export async function loadAgents({ signal } = {}) {
  const { boxOffices } = await loadBoxOffices({ limit: 80, signal });
  const results = await Promise.allSettled(
    boxOffices.map((bo) => loadBoxOfficeOperators(bo.id, { signal }).then((ops) => ops.map((op) => normalizeOperator(op, bo))))
  );
  const agents = results.filter((x) => x.status === 'fulfilled').flatMap((x) => x.value);
  return { agents, boxOffices };
}

export async function createOperator(boxOfficeId, { name, pin, role = 'Operator', source = 'external', contact, contactType } = {}, { signal } = {}) {
  const body = await writeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/operators`, { body: { name, pin, role, source, ...(contact ? { contact, contactType: contactType || (/@/.test(contact) ? 'email' : 'phone') } : {}) }, signal });
  return body?.operator || body?.data || body;
}

export async function updateOperator(boxOfficeId, operatorId, patch, { signal } = {}) {
  const body = await writeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/operators/${encodeURIComponent(operatorId)}`, { method: 'PATCH', body: patch, signal });
  return body?.operator || body?.data || body;
}

export async function deleteOperator(boxOfficeId, operatorId, { signal } = {}) {
  return writeJson(`/api/boxOffices/${encodeURIComponent(boxOfficeId)}/operators/${encodeURIComponent(operatorId)}`, { method: 'DELETE', signal });
}

export async function setBoxOfficePaused(boxOfficeId, { paused, reason, signal } = {}) {
  const response = await fetch(`${API_BASE}/api/boxOffices/${encodeURIComponent(boxOfficeId)}/pause`, {
    method: 'PATCH', signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? null, ...(typeof paused === 'boolean' ? { paused } : {}) }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || `${response.status} Failed to toggle box office`);
  }
  return payload;
}

export async function deleteBoxOffice(boxOfficeId, { signal } = {}) {
  const response = await fetch(`${API_BASE}/api/boxOffices/${encodeURIComponent(boxOfficeId)}`, {
    method: 'DELETE', signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.error || `${response.status} Failed to delete box office`);
  }
  return true;
}

// --- Design & Comms (designs, message templates, campaigns) ---------------
// Backed by /api/designs, /api/message-templates, /api/campaigns
// (tkt-backend). All org-scoped (resolveOrgScope → admins see all orgs).

// THE one place this app turns a request body into JSON — which makes it the one place the
// date contract can be satisfied for every writer at once. See lib/absoluteInstant.
export const writeJson = async (path, { method = 'POST', body, signal, reason } = {}) => {
  const payloadBody = withAbsoluteInstants(body);
  const response = await fetch(`${API_BASE}${withReaderClock(path)}`, {
    method, signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', ...(body ? { 'Content-Type': 'application/json' } : {}), ...reasonHeader(reason) },
    body: body ? JSON.stringify(payloadBody) : undefined,
  });
  const payload = (response.headers.get('content-type') || '').includes('application/json')
    ? await response.json().catch(() => null) : await response.text();
  if (!response.ok) {
    const message = Array.isArray(payload?.errors) ? payload.errors.join(', ') : (payload?.error || payload?.message || `${response.status} request failed`);
    throw new Error(message);
  }
  return payload;
};

// --- Platform config (SCALAR Settings editors only) ------------------------
// Real persistence for the platform-wide SCALAR settings — refund policy,
// branding, general, delivery defaults, QR rules, access-control defaults.
// These are plain values the platform reads; a keyed blob store is the right
// home. NOT for providers/domains — those are first-class entities with their
// own real models (see the delivery-provider / sender-domain helpers below).
// Backed by /api/platform-config (super-admin). loadPlatformConfig returns a
// { key: value } map for a scope; savePlatformConfig upserts one key's blob.
export async function loadPlatformConfig(scope = 'platform', { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/platform-config?scope=${encodeURIComponent(scope)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Platform config unavailable'}`);
  if (r.payload?.deployment) _deployment = r.payload.deployment;
  return r.payload?.data || {};
}

/**
 * WHAT THE BACKEND THIS CONSOLE IS TALKING TO ACTUALLY IS — its tenant and the currency any row
 * it creates will default to (its `DEFAULT_CURRENCY`).
 *
 * `BRAND` answers the same question from the HOSTNAME, which is right in production and wrong
 * everywhere else: a console on localhost, or on one tenant's domain pointed at another tenant's
 * API, brands itself TKT and therefore calls the money UGX. That reached an operator as a price
 * field labelled "PRICE (UGX)" while creating a Kenyan club's first ticket tier — a row the
 * backend would then have stored as KES. A currency is a fact about the server that writes it.
 *
 * Resolved once and cached: it cannot change under a running console (the backend URL is fixed at
 * load). Until it resolves, `deploymentCurrency()` returns the brand's value, so nothing waits on
 * a round-trip to render a label — it just stops being a guess as soon as the answer arrives.
 */
let _deployment = null;
let _deploymentInFlight = null;
export function deploymentCurrency() {
  return _deployment?.defaultCurrency || BRAND.defaultCurrency;
}
export function resolveDeployment() {
  if (_deployment) return Promise.resolve(_deployment);
  // One flight, shared — every caller awaits the same request rather than each firing its own.
  _deploymentInFlight = _deploymentInFlight || loadPlatformConfig()
    .then(() => _deployment)
    .catch(() => null)
    .finally(() => { _deploymentInFlight = null; });
  return _deploymentInFlight;
}

// The REAL better-auth roles + permission matrix + live per-role membership counts.
// Backed by /api/admin/roles (serializes src/utils/permissions.ts). Authoritative,
// read-only — roles are defined in code, not editable at runtime.
export async function loadRolesMatrix({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/admin/roles', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Roles unavailable'}`);
  return r.payload?.data || { resources: [], roles: [], totalMembers: 0 };
}

export async function savePlatformConfig(key, value, { scope = 'platform', signal, reason } = {}) {
  const payload = await writeJson(`/api/platform-config/${encodeURIComponent(key)}`, {
    method: 'PUT', body: { value, scope }, signal, reason,
  });
  return payload?.data?.value ?? value;
}

// Brands — the unified communication identity (sender + design family + reach), matched to
// an order by organizer or deployment. The one operator-facing model for "how we talk to people".
export async function loadBrands({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/routing/brands', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'brands unavailable'}`);
  return r.payload?.data || { brands: [], configured: false };
}
// The REAL mapping: every actual organizer → the brand its buyers receive on THIS env (the
// backend's own process.env.LAND). Returns { rows, env } — env is what this backend deploys as.
// Drop this backend's in-memory brand cache so it re-reads comm-brands from the DB.
export async function reloadBrandConfig({ signal } = {}) {
  return writeJson('/api/routing/reload', { body: {}, signal });
}
// The human-lens view: real events → what each buyer actually gets (category-driven ticket/email
// design + sender + channels), on THIS env. { rows, env }.
export async function loadEventAssignments({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/routing/event-assignments', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'event assignments unavailable'}`);
  return { rows: r.payload?.data || [], env: r.payload?.env || null };
}

// The component ids a brand's designs may point at (ticket / email), for the design pickers.
export async function loadDesignCatalog({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/routing/registry', { signal });
  const d = r.payload?.data || {};
  // Keep the LABEL and GROUP the registry already sends. Mapping straight to `t.id` threw them
  // away, so every design picker in the console showed raw ids — and a new template like
  // `sports-venue-ticket` arrived with its display name ("Sports Ticket (crests from fixture)")
  // already registered and simply never shown, leaving the operator to guess which id it was.
  const shape = (list) =>
    (list || []).map((t) => ({
      id: t.id,
      label: t.display?.label || t.id,
      group: t.display?.group || null,
    }));
  return { ticket: shape(d.ticket), email: shape(d.email) };
}
export async function saveBrands(brands, { signal } = {}) {
  // Stored one row per brand in the comm_brands table (PUT replaces the set: upsert each, delete removed).
  return writeJson('/api/routing/brands', { method: 'PUT', body: { brands }, signal });
}

// --- Delivery / message providers (REAL entity) ----------------------------
// Platform-global WhatsApp/SMS/Email/Push providers the real notification path
// actually sends through. Backed by the DeliveryProvider model via
// /api/sender-compliance/providers (super-admin → platform scope). Secrets are
// write-only: the API never returns apiKey/apiSecret, only hasApiKey/hasApiSecret
// flags so the UI can show "configured" without ever holding the secret.
const CHANNEL_LABEL = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email', push: 'Push' };
const PROVIDER_DOT = { active: 'Healthy', degraded: 'Degraded', inactive: 'Offline' };
const normalizeDeliveryProvider = (r) => ({
  ...r,
  id: String(r.id),
  name: firstPresent(r.name, 'Untitled provider'),
  type: r.type,
  channel: CHANNEL_LABEL[r.type] || titleCase(r.type || ''),
  region: firstPresent(r.region, 'Global'),
  status: firstPresent(r.status, 'inactive'),
  dot: PROVIDER_DOT[r.status] || 'Offline',
  dailyLimit: number(r.dailyLimit, 0),
  hourlyLimit: number(r.hourlyLimit, 0),
  usedToday: number(r.usedToday, 0),
  avgLatencyMs: number(r.avgLatencyMs, 0),
  failureRatePct: number(r.failureRatePct, 0),
  priority: number(r.priority, 1),
  isDefault: !!r.isDefault,
  hasApiKey: !!r.hasApiKey,
  hasApiSecret: !!r.hasApiSecret,
  lastVerifiedAt: r.lastVerifiedAt || null,
  raw: r,
});

// The platform's real channel sender identities configured at deployment (WhatsApp
// phone-number ID, SMS sender, email from-address). Read-only & secret-safe.
export async function loadChannelEnv({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/sender-compliance/channel-env', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Channel config unavailable'}`);
  return r.payload?.data || {};
}

export async function loadDeliveryProviders({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/sender-compliance/providers?limit=100', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Providers unavailable'}`);
  return { rows: list(r.payload?.data).map(normalizeDeliveryProvider), total: number(r.payload?.totalItems, 0) };
}

export async function createDeliveryProvider(input, { signal } = {}) {
  const body = await writeJson('/api/sender-compliance/providers', { body: input, signal });
  return body?.provider ? normalizeDeliveryProvider(body.provider) : null;
}

export async function updateDeliveryProvider(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/sender-compliance/providers/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return body?.provider ? normalizeDeliveryProvider(body.provider) : null;
}

export async function deleteDeliveryProvider(id, { signal } = {}) {
  return writeJson(`/api/sender-compliance/providers/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

export async function testDeliveryProvider(id, { signal } = {}) {
  return writeJson(`/api/sender-compliance/providers/${encodeURIComponent(id)}/test`, { body: {}, signal });
}

export async function setDefaultDeliveryProvider(id, { signal } = {}) {
  return writeJson(`/api/sender-compliance/providers/${encodeURIComponent(id)}/set-default`, { body: {}, signal });
}

// --- Email sender domains (REAL entity) ------------------------------------
// Platform-global verified sending domains, backed by the SenderDomain model via
// /api/sender-compliance/domains (super-admin → platform scope). The real email
// path sends from these; DNS verify/set-default/remove are real mutations.
const DOMAIN_DOT = { verified: 'Healthy', 'partially-verified': 'Degraded', pending: 'Degraded', blocked: 'Offline' };
const normalizeSenderDomain = (r) => ({
  ...r,
  id: String(r.id),
  domain: r.domain,
  fromName: firstPresent(r.fromName, ''),
  replyTo: firstPresent(r.replyTo, ''),
  displayName: firstPresent(r.displayName, ''),
  channel: firstPresent(r.channel, 'email'),
  sendingStatus: firstPresent(r.sendingStatus, 'pending'),
  dot: DOMAIN_DOT[r.sendingStatus] || 'Degraded',
  isDefault: !!r.isDefault,
  healthScore: number(r.healthScore, 0),
  spf: r.spf || null,
  dkim: r.dkim || null,
  dmarc: r.dmarc || null,
  createdAt: r.createdAt || null,
  raw: r,
});

// Send a one-off email through the platform's transactional sender (POST /api/send/email
// → sendEmail). The FROM is the ENVIRONMENT's verified sender domain — we pass
// `senderdomain` so the backend's from-address matches the active brand/env (TKT →
// courier@ticket.ug, Moments → tickets@moments.ug, Fanpass → tickets@fanpass.ke), not a
// compliance-list pick. Returns the send result (incl. `filtered:true` if policy-blocked).
export async function sendAdminEmail({ email, subject, html, senderdomain, fromname, signal } = {}) {
  const res = await fetch(`${API_BASE}/api/send/email`, {
    method: 'POST', credentials: 'include', signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, subject, html, senderdomain, fromname }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.errors?.[0] || payload?.error || `${res.status} email send failed`);
  return payload;
}

// Resolve an M-Pesa hash → MSISDN via the backend proxy (the worker has no CORS).
export async function lookupMpesaHash(hash, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/utils/mpesa-hash-lookup?hash=${encodeURIComponent(String(hash).trim())}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Lookup failed'}`);
  return r.payload; // { ok, msisdn } | { ok, raw }
}

// Would this address receive mail? Loft's prober (email-check.loft.ug) through the backend proxy —
// syntax, MX, an SMTP conversation, flags — returned as the prober answered it.
export const checkEmailAddress = (email, { signal } = {}) => writeJson('/api/utils/email-check', { body: { email }, signal });

export async function loadSenderDomains({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/sender-compliance/domains?limit=100', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Sender domains unavailable'}`);
  return { rows: list(r.payload?.data).map(normalizeSenderDomain), total: number(r.payload?.totalItems, 0) };
}

export async function createSenderDomain(input, { signal } = {}) {
  const body = await writeJson('/api/sender-compliance/domains', { body: input, signal });
  return body?.domain ? normalizeSenderDomain(body.domain) : null;
}

export async function verifySenderDomain(id, { signal } = {}) {
  const body = await writeJson(`/api/sender-compliance/domains/${encodeURIComponent(id)}/verify`, { body: {}, signal });
  return body?.domain ? normalizeSenderDomain(body.domain) : null;
}

export async function setDefaultSenderDomain(id, { signal } = {}) {
  return writeJson(`/api/sender-compliance/domains/${encodeURIComponent(id)}/set-default`, { body: {}, signal });
}

export async function deleteSenderDomain(id, { signal } = {}) {
  return writeJson(`/api/sender-compliance/domains/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// --- Payment rails — OBSERVE-ONLY config + health --------------------------
// The rails (MTN MoMo, Airtel, M-Pesa, Flutterwave, Azam, Paynow, Mookh,
// Pesapal) are code/env integrations that move real money — NOT browser-editable.
// This surface reports only config status (which required env vars are present —
// booleans, never values) and a real, safe reachability probe of the gateway
// endpoint. Backed by /api/admin/payment-rails (super-admin).
export async function loadPaymentRails({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/admin/payment-rails', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Payment rails unavailable'}`);
  return list(r.payload?.data).map((x) => ({
    id: x.id,
    name: x.name,
    region: firstPresent(x.region, '—'),
    methods: Array.isArray(x.methods) ? x.methods : [],
    requiredEnv: Array.isArray(x.requiredEnv) ? x.requiredEnv : [],
    missingEnv: Array.isArray(x.missingEnv) ? x.missingEnv : [],
    configured: !!x.configured,
    probeHost: x.probeHost || '',
  }));
}

// Real, safe liveness probe — does not move money or authenticate. Returns
// { ok, reachable, httpStatus, latencyMs } or { ok:false, reason, missingEnv }.
export async function testPaymentRail(id, { signal } = {}) {
  return writeJson(`/api/admin/payment-rails/${encodeURIComponent(id)}/test`, { body: {}, signal });
}

// --- Commission & transaction fee ----------------------------------------------
// TWO RATES, IN THE EVENT'S METADATA (`commission`, `transactionFee`; fractions: 0.05 = 5%):
//   commission      what the platform keeps of the organiser's money — stamped on each order at
//                   birth (fwData.commission → net_amount)
//   transactionFee  what the buyer pays on top, per gateway charge — never the organiser's money
// An organiser's metadata holds the same two keys: what its NEW events are created with. Absent
// anywhere = this platform's rate. Changing either applies to orders and charges from then on.

/** The rates an event or organiser states — undefined where it states none. */
export const ratesOf = (row) => {
  const m = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const r = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? undefined : Number(v));
  return { commission: r(m.commission), transactionFee: r(m.transactionFee) };
};
/** This platform's own transaction fee — what applies where nobody states one. */
export function deploymentTransactionFee() {
  return Number.isFinite(Number(_deployment?.transactionFee)) ? Number(_deployment.transactionFee) : null;
}

/** This platform's own commission rate — what an organiser with no terms of its own gets. */
export function deploymentCommission() {
  return Number.isFinite(Number(_deployment?.commission)) ? Number(_deployment.commission) : null;
}

/** Set an event's rates — the ordinary event PATCH (which merges metadata, and takes these two
 *  keys from platform admins only). null = back to the platform's rate. Returns the event as saved. */
export async function setEventRates(eventId, { commission, transactionFee } = {}, { signal } = {}) {
  if (!eventId) throw new Error('eventId is required');
  const metadata = {};
  if (commission !== undefined) metadata.commission = commission;
  if (transactionFee !== undefined) metadata.transactionFee = transactionFee;
  const res = await writeJson(`/api/event-pages/${encodeURIComponent(eventId)}`, { method: 'PATCH', body: { metadata }, signal });
  return res?.eventPage || res?.data || res || {};
}


// NOTE the destructured allowlist: a field the caller passes but this signature doesn't
// name is silently dropped here, with no error anywhere — the POST succeeds, the console
// shows the campaign, and the value simply never existed. That is how templateName/
// templateComponents went missing on the first run of the WhatsApp template picker: the
// review screen said `jambazz_early_bird_expires7`, the row in the database said null.
// If you add a field to the campaign wizard, add it here too.
export async function createCampaign({
  name, channel, eventId, subject, content, audience, scope = 'global',
  templateName, templateLanguage, templateComponents, emailSettings, metadata, whatsapp,
}, { signal } = {}) {
  const payload = await writeJson('/api/campaigns', { body: {
    name, channel, ...(eventId ? { eventId } : {}),
    ...(subject ? { subject } : {}), ...(content ? { content } : {}),
    // The three columns the WhatsApp blast executor reads. For WhatsApp these ARE the
    // message (Meta takes no free text outside the 24h window), so a dropped templateName
    // isn't a missing detail — it's a campaign that cannot send.
    ...(templateName ? { templateName } : {}),
    ...(templateLanguage ? { templateLanguage } : {}),
    ...(Array.isArray(templateComponents) ? { templateComponents } : {}),
    // emailSettings carries the per-recipient RENDERER (e.g. the UFF invitation, which builds a
    // different body per guest from their own pass). Dropping it here made a campaign that looked
    // configured send a generic blast instead — the personalisation silently gone.
    ...(emailSettings ? { emailSettings } : {}),
    ...(metadata ? { metadata } : {}),
    // A WRITTEN WhatsApp message (Meta Direct Send): { mode:'direct', body, buttonText, save, label, branchOf }.
    // The backend derives the template name and refuses it for audiences with no transaction.
    ...(whatsapp ? { whatsapp } : {}),
    ...(audience ? { audience } : {}), scope,
  }, signal });
  return payload?.data || payload?.campaign || payload;
}

// Saved Direct Send messages (newest first) — what the composer offers to start from.
export async function loadDirectSendMessages({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/campaigns/direct-send/messages', { signal });
  if (!r.ok) return [];
  return Array.isArray(r.payload?.data) ? r.payload.data : [];
}

// Send ONE rendered message to the operator's own phone, through the path a blast uses. Throws
// Meta's own reason — e.g. "Direct Send isn't enabled for this account" — so it doubles as the
// eligibility check before anything goes to customers.
export async function sendDirectSendTest({ to, eventId, content, sample, save, label }, { signal } = {}) {
  return writeJson('/api/campaigns/direct-send/test', { body: { to, eventId, content, sample, save, label }, signal });
}

/** One person's orders, newest first — what a Direct Send to them can be about (campaignRoutes /direct-send/person). */
export async function loadDirectSendPerson(to, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/campaigns/direct-send/person?to=${encodeURIComponent(to)}`, { signal });
  if (!r.ok) throw new Error(r.payload?.error || 'Could not load their orders');
  return Array.isArray(r.payload?.data) ? r.payload.data : [];
}

/** A saved wording (kept as a template) sent to one person, filled from one of their orders (campaignRoutes /direct-send/to-one). */
export async function sendDirectSendToOne({ to, orderId, content, label, startedFrom }, { signal } = {}) {
  return writeJson('/api/campaigns/direct-send/to-one', { body: { to, orderId, content, label, startedFrom }, signal });
}

// Real recipient count for a draft audience, before sending — so the wizard shows a
// true size (not a fabricated one). audienceSources mirror the backend resolver:
// [{ type:'event', sourceId, audienceType:'buyers'|'subscribers'|'all' } | { type:'tag', sourceId }].
// `content` ({ body, buttonText }) is the wording being composed, if any: the preview then counts
// out the people who already received these exact words, as the send will.
export async function previewCampaignAudience(audienceSources, { channel, content, batchSize, includeRecent, eventId, signal } = {}) {
  // The campaign's own settings, as the send will read them: people per click, and whether people
  // another campaign reached this week are included anyway.
  const metadata = { ...(batchSize ? { batchSize } : {}), ...(includeRecent ? { enableBlastDedup: false } : {}) };
  const payload = await writeJson('/api/campaigns/audience/preview', { body: { audienceSources, channel, ...(eventId ? { eventId } : {}), ...(content ? { content } : {}), ...(Object.keys(metadata).length ? { metadata } : {}) }, signal });
  return payload?.data || { total: 0, perSource: [], exact: false };
}

// CONTACT LISTS — a list of people an organiser brought (a promoter's numbers, a past event's guests),
// kept as a reusable audience. Newest first, the 200 most recent (GET /api/contact-lists is bounded).
export async function loadContactLists({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/contact-lists', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Contact lists unavailable'}`);
  return Array.isArray(r.payload?.lists) ? r.payload.lists : [];
}
// A CSV becomes a list: headers name the phone/name/email columns (else column one is the phone). The
// list belongs to the uploader's organisation, or the organiser of `eventId`.
export async function importContactListCsv(file, { listName, eventId, signal } = {}) {
  const form = new FormData();
  form.append('file', file, file.name || 'contacts.csv');
  if (listName) form.append('listName', listName);
  if (eventId) form.append('eventId', eventId);
  const response = await fetch(`${API_BASE}${withOrgScope('/api/contact-lists/import-csv')}`, {
    method: 'POST', body: form, signal, credentials: 'include', headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `${response.status} upload failed`);
  return payload.list;
}

// Real subscriber tags (the saved audience segments) with live counts.
export async function loadAudienceTags({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/campaigns/summary/tag-counts', { signal });
  if (!r.ok) return [];
  return Array.isArray(r.payload?.tags) ? r.payload.tags : [];
}

export async function executeCampaign(id, { signal } = {}) {
  return writeJson(`/api/campaigns/${encodeURIComponent(id)}/execute`, { body: {}, signal });
}
// Lifecycle control for a running blast — pause/resume/stop. The console could
// START a campaign (execute) but never halt one; stopping a mis-targeted blast
// was impossible. (POST /api/campaigns/:id/{pause,resume,stop}.)
export async function pauseCampaign(id, { signal } = {}) {
  return writeJson(`/api/campaigns/${encodeURIComponent(id)}/pause`, { body: {}, signal });
}
export async function resumeCampaign(id, { signal } = {}) {
  return writeJson(`/api/campaigns/${encodeURIComponent(id)}/resume`, { body: {}, signal });
}
export async function stopCampaign(id, { signal } = {}) {
  return writeJson(`/api/campaigns/${encodeURIComponent(id)}/stop`, { body: {}, signal });
}
// Re-send to the recipients a finished run never delivered to. Already-delivered
// recipients are untouched server-side, and the audience is NOT re-resolved — so this
// can neither double-message anyone nor quietly widen who is being messaged.
export async function retryCampaign(id, { signal } = {}) {
  return writeJson(`/api/campaigns/${encodeURIComponent(id)}/retry`, { body: {}, signal });
}
// Everything a campaign detail view needs, from ONE endpoint the backend already had and
// nothing consumed: live stats, the per-recipient failures WITH the provider's own error
// text, and the successes with their Meta message ids. Without this a failed blast told
// an operator nothing but the word "failed".
/**
 * The campaign's send report, as a PDF the operator can hand to somebody.
 *
 * A URL rather than a fetch: the browser's own download machinery handles the stream, the filename
 * and the save dialog, and pulling 200KB into JS to re-offer it as a blob would only add ways to
 * fail. Session cookies ride along because it is a same-site navigation.
 */
// The send report AS A PDF: the public renderer prints the backend's report page (…/report.html, public by the
// campaign's uuid, so the renderer needs no credential). Linking the backend's `report.pdf` itself handed people the
// HTML page — the backend serves the page under both names. A backend on this machine is out of the renderer's reach,
// so there the page is the link, printable from the browser.
export const campaignReportUrl = (id) => {
  const path = `/api/campaigns/${encodeURIComponent(id)}/report.html`, host = new URL(API_BASE).host;
  return /^(localhost|127\.0\.0\.1)(:|$)/.test(host) ? `${API_BASE}${path}` : `${getRendererUrl()}/pdf/${host}${path}`;
};

export async function loadCampaignProgress(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/campaigns/${encodeURIComponent(id)}/progress`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Campaign progress unavailable'}`);
  const p = r.payload || {};
  return {
    campaign: p.campaign || null,
    stats: p.stats || { total: 0, sent: 0, failed: 0, pending: 0, skipped: 0 },
    progressPercent: number(p.progressPercent, 0),
    estimatedMinutesRemaining: p.estimatedMinutesRemaining ?? null,
    failures: Array.isArray(p.recentFailures) ? p.recentFailures : [],
    successes: Array.isArray(p.recentSuccesses) ? p.recentSuccesses : [],
    // The mail service's own verdict, not ours. Dropped here it would never reach the UI, and the
    // operator would keep watching a "sent" counter that cannot tell them anything arrived.
    delivery: p.delivery || null,
    haltReason: p.haltReason || null,
    ratePerMinute: p.ratePerMinute ?? null,
    etaBasis: p.etaBasis || null,
  };
}

// --- Support inbox (real WhatsApp chat) -------------------------------------
// Conversations are phone-keyed WhatsApp threads. supportInfo.mode = chatbot|human:
// the AI handles chatbot mode and auto-escalates; an agent can TAKE OVER (set human)
// and reply. The conversations-list SQL returns flat dotted keys ("latestMessage.x").
// WhatsApp message content is stored as the raw webhook/send payload (JSON) OR
// plain text. Derive a clean human preview + a kind for any of those shapes.
export const deriveWhatsAppContent = (raw) => {
  let display = typeof raw === 'string' ? raw : '';
  let kind = 'text';
  if (typeof display === 'string' && display.trim().startsWith('{')) {
    try {
      const obj = JSON.parse(display);
      const type = obj.type || (obj.template ? 'template' : obj.interactive ? 'interactive' : obj.text ? 'text' : null);
      // Legacy quick-reply button tap — reply text in button.{text,payload} (Meta's shape)
      // or context.{text,payload} (some stored messages). Key on the reply-object FIELDS,
      // not a bare `obj.button` (which is a plain STRING in some outbound template sends),
      // so an outbound template-with-buttons is never mislabeled a reply. Was falling
      // through to the raw JSON string as the list preview.
      const buttonReplyText = obj.button?.text || obj.button?.payload || obj.context?.payload || obj.context?.text;
      if (type === 'button' || buttonReplyText) {
        kind = 'button';
        display = buttonReplyText || 'Button reply';
      } else if (type === 'template') {
        kind = 'template';
        const name = obj.template?.name ? String(obj.template.name).replace(/_/g, ' ') : 'template';
        const bodyParam = obj.template?.components?.find((c) => c.type === 'body')?.parameters?.[0]?.text;
        display = `📄 ${name}${bodyParam ? ` · ${bodyParam}` : ''}`;
      } else if (type === 'interactive') {
        kind = 'interactive';
        const it = obj.interactive || obj;
        display = it.button_reply?.title || it.list_reply?.title || it.body?.text || it.body || 'Interactive message';
      } else if (type === 'text') {
        display = typeof obj.text === 'string' ? obj.text : (obj.text?.body || '');
      } else if (type === 'image') { kind = 'image'; display = obj.image?.caption || '📷 Photo'; }
      else if (type === 'document') { kind = 'document'; display = `📎 ${obj.document?.filename || 'Document'}`; }
      else if (type === 'audio') { kind = 'audio'; display = '🎤 Voice message'; }
      else if (type === 'video') { kind = 'video'; display = obj.video?.caption || '🎬 Video'; }
      else if (type === 'location') { kind = 'location'; display = '📍 Location'; }
      else if (obj.text?.body) { display = obj.text.body; }
    } catch { /* keep raw text */ }
  }
  return { display, kind };
};

const normalizeConversation = (row, i = 0) => {
  const contact = String(firstPresent(row.contact, row.phoneNumber, row.id, `convo-${i + 1}`));
  return {
    ...row,
    contact,
    // The customer's real WhatsApp display name (captured off the inbound
    // webhook's contacts[].profile.name — see WhatsAppService), not a mirror
    // of the phone number. Only present once that customer has messaged
    // since this capture shipped; falls back to the number honestly.
    name: firstPresent(row.name, row.contactName, contact),
    screenName: row.contactName || null,
    lastContent: deriveWhatsAppContent(firstPresent(row['latestMessage.content'], row.latestMessage?.content, '')).display,
    lastDirection: String(firstPresent(row['latestMessage.direction'], row.latestMessage?.direction, '')).toUpperCase(),
    lastType: firstPresent(row['latestMessage.messageType'], row.latestMessage?.messageType, 'text'),
    lastStatus: String(firstPresent(row['latestMessage.status'], row.latestMessage?.status, '')).toLowerCase(),
    lastAt: firstPresent(row['latestMessage.timestamp'], row.lastInteraction, row.latestMessage?.timestamp, null),
    unread: number(firstPresent(row.unreadCount, 0)),
    messageCount: number(firstPresent(row.messageCount, 0)),
    // Meta only allows freeform text replies within 24h of the CUSTOMER's last
    // message — not the last message either way. A conversation whose customer
    // never replied (lastInboundAt null) or replied >24h ago is session-closed:
    // only a pre-approved template can reach them.
    lastInboundAt: firstPresent(row.lastInboundAt, null),
    raw: row,
  };
};

// Meta's WhatsApp customer-service window: freeform text is only deliverable
// within 24h of the customer's own last inbound message.
const SESSION_WINDOW_HOURS = 24;
export const whatsAppSessionState = (lastInboundAt) => {
  if (!lastInboundAt) return { open: false, hoursSince: null };
  const hoursSince = (Date.now() - new Date(lastInboundAt).getTime()) / 36e5;
  return { open: hoursSince < SESSION_WINDOW_HOURS && hoursSince >= 0, hoursSince };
};

// A media attachment can arrive two ways: INBOUND media only ever has a Meta
// `id` (needs our media proxy, since Meta's own URL is ephemeral + requires
// our access token — no browser can hit it directly); a link we send OUTBOUND
// (e.g. a ticket PDF on our own domain) is already a plain public URL. Either
// is "real" — just resolve to whichever the payload actually has.
// tkt-frontend already has a real, working answer for this (lib/whatsapp-formatter.ts) —
// a legacy PHP endpoint at ticket.ug that resolves a Meta media id to the actual bytes.
// There is no reason to make our own backend re-implement that Meta round-trip: it's
// already solved, already deployed, and pointing straight at it means OUR server never
// buffers a single byte of someone's photo/video through itself just to redraw it.
const mediaSrc = (media) => {
  if (!media) return null;
  if (media.id) return `${getWhatsAppMediaProxyUrl()}/replies/mediaget.php?id=${encodeURIComponent(media.id)}&download${media.mime_type ? `&mime=${encodeURIComponent(media.mime_type)}` : ''}`;
  if (media.link) return media.link;
  return null;
};

// Message content can be plain text OR JSON (template/interactive/media/
// location/contacts/order/system/reaction). `metadata` is the raw Meta
// payload (parsed, not stringified) — the only place the type-specific
// fields a rich thread needs (media id, coordinates, contact cards, button
// replies) actually live. `display`/`kind` remain the safe text fallback for
// whatever this doesn't specifically render.
const normalizeChatMessage = (m, i = 0) => {
  const { display, kind } = deriveWhatsAppContent(firstPresent(m.content, ''));
  const dir = String(firstPresent(m.direction, '')).toUpperCase();
  const meta = (m.metadata && typeof m.metadata === 'object') ? m.metadata : {};
  const type = String(firstPresent(meta.type, m.messageType, kind));

  const rich = { type };
  if (['image', 'video', 'audio', 'document', 'sticker'].includes(type)) {
    const media = meta[type] || {};
    rich.mediaSrc = mediaSrc(media);
    rich.filename = media.filename || null;
    rich.caption = media.caption || null;
    rich.mimeType = media.mime_type || null;
  } else if (type === 'location' && meta.location) {
    rich.location = meta.location;
  } else if (type === 'contacts' && Array.isArray(meta.contacts)) {
    rich.contacts = meta.contacts;
  } else if (type === 'interactive' && meta.interactive) {
    rich.interactive = meta.interactive;
  } else if (type === 'order' && meta.order) {
    rich.order = meta.order;
  } else if (type === 'button') {
    // Legacy quick-reply button tap: Meta stores the reply in button.{text,payload};
    // some persisted messages carry it under context.{text,payload}. Prefer the parsed
    // metadata (reliable) over the derived text. Rendered by whatsappMessageRender's
    // 'button' case; without this the raw content JSON leaked into the bubble.
    rich.buttonText = meta.button?.text || meta.button?.payload || meta.context?.text || meta.context?.payload || null;
  } else if (type === 'reaction' && meta.reaction) {
    rich.reactionEmoji = meta.reaction.emoji || null;
  } else if (type === 'system' && meta.system) {
    rich.systemBody = meta.system.body || 'System update';
  }

  return {
    ...m,
    id: String(firstPresent(m.id, m.messageId, `msg-${i + 1}`)),
    fromCustomer: dir === 'INBOUND',
    direction: dir,
    display,
    kind,
    ...rich,
    status: String(firstPresent(m.status, '')).toLowerCase(),
    at: firstPresent(m.timestamp, m.createdAt, null),
    // metadata.source: 'dashboard' = a human agent sent it; 'bot' = the AI.
    source: m.metadata?.source || (dir === 'OUTBOUND' ? 'bot' : null),
    // Whichever end ISN'T us — only meaningful in the cross-contact "house"
    // firehose view (see loadWhatsAppConfig), where a single thread spans
    // many different real customers and each bubble needs to say whose.
    counterpart: dir === 'OUTBOUND' ? firstPresent(m.to, null) : firstPresent(m.from, null),
    raw: m,
  };
};

/**
 * A page of this week's conversations, newest first — `{ rows, hasMore }`. One row more than the page is asked for:
 * its presence is `hasMore` (the route returns a bare array other apps read, so no field is added to it). `q` finds
 * within the week on the server — a number prefix, or a name; `contacts` asks for exactly those (taken over).
 */
export async function loadConversationPage({ page = 1, q = '', contacts = null, signal } = {}) {
  const p = new URLSearchParams({ offset: String((page - 1) * LIST_PAGE_SIZE), limit: String(LIST_PAGE_SIZE + 1) });
  if (q) p.set('q', q);
  if (contacts) p.set('contacts', contacts.join(','));
  const r = await fetchBackOfficeJson(`/api/v1/whatsapp/conversations?${p}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Conversations unavailable'}`);
  const rows = (Array.isArray(r.payload) ? r.payload : (r.payload?.data || r.payload?.conversations || [])).map(normalizeConversation);
  return { rows: rows.slice(0, LIST_PAGE_SIZE), hasMore: rows.length > LIST_PAGE_SIZE };
}

export async function loadSupportConversations({ limit = 30, offset = 0, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v1/whatsapp/conversations?offset=${offset}&limit=${limit}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Conversations unavailable'}`);
  const rows = Array.isArray(r.payload) ? r.payload : (r.payload?.data || r.payload?.conversations || []);
  return rows.map(normalizeConversation);
}

// Contacts a human agent currently has (SupportMode.HUMAN) — the same real fact the thread
// composer's "AI takeover" gate reads per-contact, for every conversation in the list at once.
export async function loadHumanModeContacts({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/v1/whatsapp/human-mode', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Human-mode contacts unavailable'}`);
  return Array.isArray(r.payload) ? r.payload : [];
}

// `before` walks BACKWARDS through the conversation (keyset cursor on createdAt,
// from a previous call's `oldestAt`); `limit` widens the window. The thread used to
// be a hidden 30-message wall with no way past it — these are the axis.
export async function loadSupportThread(contact, { signal, before, limit } = {}) {
  const qs = new URLSearchParams();
  if (before) qs.set('before', typeof before === 'string' ? before : new Date(before).toISOString());
  if (limit) qs.set('limit', String(limit));
  const suffix = qs.toString() ? `?${qs}` : '';
  const r = await fetchBackOfficeJson(`/api/chat/conversation/${encodeURIComponent(contact)}${suffix}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Conversation unavailable'}`);
  return {
    // Whether an OLDER page exists, and the cursor to ask for it. Straight from the
    // engine's look-ahead — never inferred from "did we get a full page".
    hasMore: Boolean(r.payload?.hasMore),
    oldestAt: r.payload?.oldestAt || null,
    // The backend returns newest-first (its own pagination order). A thread
    // reads top-to-bottom oldest→newest like every real chat UI, and
    // "scroll to bottom" is only correct — lands on the LATEST message —
    // once this is reversed. Left as the backend's order, the newest message
    // (and the auto-scroll) silently landed on the OLDEST one instead.
    messages: (r.payload?.messages || []).map(normalizeChatMessage).reverse(),
    mode: firstPresent(r.payload?.supportInfo?.mode, 'chatbot'),
    isHumanMode: Boolean(r.payload?.supportInfo?.isHumanMode),
    ttl: r.payload?.supportInfo?.ttl ?? null,
    // The real operator holding this conversation (name/email/since) — null in
    // AI mode, or if it somehow predates this capture. Not a fabricated label.
    agent: r.payload?.supportInfo?.agent || null,
  };
}

// Switch a conversation between the AI chatbot and a human agent (Redis-backed,
// 1h TTL on human). 'human' = take over; 'chatbot' = hand back to the AI.
export async function setSupportMode(contact, mode, { signal } = {}) {
  const body = await writeJson(`/api/chat/${encodeURIComponent(contact)}/support-mode`, { method: 'PATCH', body: { mode }, signal });
  return body?.data || body;
}

// Send an agent reply. POST /api/v1/whatsapp/messages/text dispatches the WhatsApp
// message AND auto-flips the conversation to human mode (server-side).
export async function sendSupportMessage(contact, text, { signal } = {}) {
  if (!text || !text.trim()) throw new Error('Message is empty');
  return writeJson('/api/v1/whatsapp/messages/text', { body: { to: contact, text: text.trim() }, signal });
}

/** WhatsApp takes an image as JPEG or PNG, up to 5 MB, fetched by Meta from a public URL. */
export const WHATSAPP_IMAGE_TYPES = ['image/jpeg', 'image/png'];
export const WHATSAPP_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** An image into a chat, with the message as its caption: the file goes to public storage (uploadImage) and WhatsApp
 *  fetches it from there. A reply like a text one — it takes the chat from the assistant. */
export async function sendSupportImage(contact, file, caption = '', { signal } = {}) {
  if (!file) throw new Error('No image chosen');
  if (!WHATSAPP_IMAGE_TYPES.includes(file.type)) throw new Error('WhatsApp sends JPEG or PNG images');
  if (file.size > WHATSAPP_IMAGE_MAX_BYTES) throw new Error('WhatsApp sends images up to 5 MB');
  const { url } = await uploadImage(file, { signal, maxBytes: WHATSAPP_IMAGE_MAX_BYTES });
  const text = String(caption || '').trim();
  return writeJson('/api/v1/whatsapp/messages/image', { body: { to: contact, mediaUrl: url, ...(text ? { caption: text } : {}) }, signal });
}

// The template list is a small constant (see KNOWN_TEMPLATES in
// whatsappRoutes.ts) — templates change when someone gets a new one approved
// by Meta, not per request, so there's no query behind this at all. The real
// payload to edit/resend is fetched separately, once, only for whichever
// template the operator actually picks (loadWhatsAppTemplateSample).
const normalizeTemplate = (t, i = 0) => ({
  ...t,
  name: firstPresent(t.name, `template-${i + 1}`),
  languageCode: firstPresent(t.languageCode, 'en'),
});

// The business's own WhatsApp identity — lets the Chat page pin a special
// row (real name for "the house": opening a thread against this exact
// number hits the backend's isMe branch, which drops contact filtering and
// returns the most recent activity across every conversation, not one).
// Brand name resolved server-side (resolveBrand) — never hardcoded, since
// it's deployment-specific (TKT/Moments/FanPass/...).
export async function loadWhatsAppConfig({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/v1/whatsapp/config', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Config unavailable'}`);
  return {
    phoneNumberId: r.payload?.phoneNumberId || null,
    displayPhoneNumber: r.payload?.displayPhoneNumber || null,
    brandName: r.payload?.brandName || 'the platform',
  };
}

export async function loadWhatsAppTemplates({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/v1/whatsapp/templates', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Templates unavailable'}`);
  const rows = Array.isArray(r.payload) ? r.payload : (r.payload?.data || []);
  return rows.map(normalizeTemplate);
}

// Mail templates — the Firebase `handlebar` collection an email campaign's body comes from
// (routes/mailTemplateRoutes). `variables` = what a campaign send fills, so a template can be
// checked against it. Platform admins only: one collection serves every brand.
export async function loadMailTemplates({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/mail-templates', { signal });
  if (!r.ok) throw new Error(r.status === 403 ? 'Campaign emails are shared by every brand, so only a platform admin can see them.' : `${r.status} ${r.payload?.error || 'Campaign emails unavailable'}`);
  return { templates: r.payload?.templates || [], variables: r.payload?.variables || [], sender: r.payload?.sender || null };
}
export async function loadMailTemplate(name, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/mail-templates/${encodeURIComponent(name)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Template unavailable'}`);
  return r.payload;
}
export async function saveMailTemplate(name, { subject, html }, { signal } = {}) {
  return writeJson(`/api/mail-templates/${encodeURIComponent(name)}`, { method: 'PUT', body: { subject, html }, signal });
}

/** Send an email campaign's template to a few named addresses, as the send would. Returns the mail ids. */
export async function sendCampaignEmailTest({ templateName, to, fromName, eventId }, { signal } = {}) {
  return writeJson('/api/campaigns/email-test', { body: { templateName, to, fromName: fromName || undefined, eventId: eventId || undefined }, signal });
}
/** The mail service's record of one test message: PENDING | PROCESSING | SUCCESS | ERROR. */
export async function loadEmailTestStatus(mailId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/campaigns/email-test/${encodeURIComponent(mailId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Status unavailable'}`);
  return r.payload;
}

// The real last-sent payload for ONE template — a single indexed row, fetched
// only once an operator actually picks that template (not upfront for all).
export async function loadWhatsAppTemplateSample(name, languageCode = 'en', { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v1/whatsapp/templates/${encodeURIComponent(name)}/sample?lang=${encodeURIComponent(languageCode)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Template sample unavailable'}`);
  return {
    components: Array.isArray(r.payload?.sampleComponents) ? r.payload.sampleComponents : [],
    lastUsedAt: r.payload?.lastUsedAt || null,
  };
}

/** The structured messages the bot sends — `events`: the events on sale (the menu's list); `tickets`: one event's
 *  tier picker, by slug (the assistant's). Built and sent by the backend's own builders (whatsappRoutes
 *  /messages/structured), so a tap enters the same checkout. Puts the conversation in human mode, like a reply. */
export async function sendWhatsAppStructured(to, { kind, slug }, { signal } = {}) {
  if (!to || !kind) throw new Error('Recipient and message are required');
  return writeJson('/api/v1/whatsapp/messages/structured', { body: { to, kind, ...(slug ? { slug } : {}) }, signal });
}

/** The assistant answers the chat now — the bot's own turn on what the customer last said, reading the whole
 *  conversation including the operator's messages (whatsappRoutes /messages/ai-reply). The chat stays in human mode. */
export async function askWhatsAppAiReply(to, { signal } = {}) {
  if (!to) throw new Error('Recipient is required');
  return writeJson('/api/v1/whatsapp/messages/ai-reply', { body: { to }, signal });
}

export async function sendWhatsAppTemplate(to, { templateName, languageCode, components }, { signal } = {}) {
  if (!to || !templateName) throw new Error('Recipient and template are required');
  return writeJson('/api/v1/whatsapp/messages/template', {
    body: { to, templateName, languageCode: languageCode || 'en', components: components || [] },
    signal,
  });
}

// --- Support escalations (AI-flagged issue triage) --------------------------
// The triage queue: chats the AI/user flagged for a human, with the reason +
// auto-derived category + linked order/event. This is the spine of the console.
const ESC_CAT_TONE = { payment: 'amber', refund: 'red', ticket: 'blue', complaint: 'red', account: 'violet', event_info: 'neutral', other: 'neutral' };
const ESC_STATUS_TONE = { open: 'red', in_progress: 'amber', resolved: 'green', closed: 'neutral' };
const normalizeEscalation = (e, i = 0) => ({
  ...e,
  id: String(firstPresent(e.id, `esc-${i + 1}`)),
  contact: firstPresent(e.contact, ''),
  // Where the person is reachable: a case from the help form is 'web', not WhatsApp.
  channel: firstPresent(e.channel, 'whatsapp'),
  source: firstPresent(e.source, 'ai'),
  reason: firstPresent(e.reason, ''),
  category: firstPresent(e.category, 'other'),
  categoryTone: ESC_CAT_TONE[e.category] || 'neutral',
  priority: firstPresent(e.priority, 'normal'),
  status: firstPresent(e.status, 'open'),
  statusTone: ESC_STATUS_TONE[e.status] || 'neutral',
  customerName: firstPresent(e.customerName, ''),
  customerEmail: firstPresent(e.customerEmail, ''),
  orderId: firstPresent(e.orderId, null),
  eventId: firstPresent(e.eventId, null),
  assignedTo: firstPresent(e.assignedTo, null),
  assignedToName: firstPresent(e.assignedToName, ''),
  notes: Array.isArray(e.metadata?.notes) ? e.metadata.notes : [],
  reasonHistory: Array.isArray(e.metadata?.reasonHistory) ? e.metadata.reasonHistory : [],
  tags: Array.isArray(e.metadata?.tags) ? e.metadata.tags : [],
  escalatedTo: e.metadata?.escalatedTo || null,   // { team, at, by } | null
  snoozedUntil: e.metadata?.snoozedUntil || null,
  createdAt: firstPresent(e.createdAt, null),
  resolvedAt: firstPresent(e.resolvedAt, null),
  // What is TRUE of the case, read by the backend (supportEscalationRoutes caseFacts): what they
  // said, whether a person has answered, and what their order's payment actually did.
  facts: e.facts ? {
    said: e.facts.said ? deriveWhatsAppContent(e.facts.said).display : null,
    saidAt: e.facts.saidAt || null,
    lastInboundAt: e.facts.lastInboundAt || null,
    answeredAt: e.facts.answeredAt || null,
    answeredBy: e.facts.answeredBy || null,
    answer: e.facts.answer ? deriveWhatsAppContent(e.facts.answer).display : null,
    order: e.facts.orderCode ? {
      code: e.facts.orderCode, id: e.facts.orderUuid, status: e.facts.orderStatus, event: e.facts.orderEvent,
      eventId: e.facts.orderEventId, issued: number(e.facts.issued, 0), attempts: number(e.facts.attempts, 0),
      lastAttempt: e.facts.lastAttempt || null, given: Boolean(e.facts.orderGiven),
    } : null,
  } : null,
  raw: e,
});

// Newest first, a page at a time: `before` is the previous page's `nextBefore` (backend cursor).
export async function loadEscalations({ status, category, search, contact, before, page = 1, limit = LIST_PAGE_SIZE, signal } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (before) params.set('before', before);
  if (status) params.set('status', status);
  if (category && category !== 'all') params.set('category', category);
  if (search) params.set('search', search);
  if (contact) params.set('contact', contact);
  const r = await fetchBackOfficeJson(`/api/support-escalations?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Escalations unavailable'}`);
  return {
    rows: (r.payload?.data || []).map(normalizeEscalation),
    openCount: number(r.payload?.openCount, 0),
    // Over EVERY open case, counted by the server — never the loaded page's.
    waitingCount: r.payload?.waitingCount != null ? number(r.payload.waitingCount, 0) : null,
    answeredCount: r.payload?.answeredCount != null ? number(r.payload.answeredCount, 0) : null,
    total: number(r.payload?.pagination?.total, (r.payload?.data || []).length),
    hasMore: r.payload?.hasMore ?? r.payload?.pagination?.hasMore ?? false,
    nextBefore: r.payload?.pagination?.nextBefore || null,
  };
}
export async function updateEscalation(id, patch, { signal } = {}) {
  const body = await writeJson(`/api/support-escalations/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return normalizeEscalation(body?.data || body);
}
// --- Admin notes (AdminComment) ---------------------------------------------
// ONE surface for operator annotations, keyed by the pair the model is indexed on:
// GET|POST /api/admin/comments/:entityType/:entityId, entityType ∈ organization|user|event.
// The model always allowed all three; only the organization path had a route, which is why the
// event note composer had nowhere to POST and said "Note added to timeline" having written nothing.
export async function loadAdminNotes(entityType, entityId, { signal } = {}) {
  if (!entityType || !entityId) return [];
  const r = await fetchBackOfficeJson(
    `/api/admin/comments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    { signal },
  );
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Notes unavailable'}`);
  return r.payload?.data || [];
}

export async function createAdminNote(entityType, entityId, comment, { signal } = {}) {
  const body = await writeJson(
    `/api/admin/comments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    { body: { comment }, signal },
  );
  return body?.data || body;
}

export async function addEscalationNote(id, note, { signal } = {}) {
  const body = await writeJson(`/api/support-escalations/${encodeURIComponent(id)}/notes`, { body: { note }, signal });
  return normalizeEscalation(body?.data || body);
}
export async function createEscalation(input, { signal } = {}) {
  const body = await writeJson('/api/support-escalations', { body: input, signal });
  return normalizeEscalation(body?.data || body);
}
// Flattened recent support activity across all escalations — the audit trail.

// --- ISSUES (delivery/payment incidents = the remediate queue) ---------------
// Payment incidents: failed Flutterwave webhooks (WebhookLog). The real failure
// reason lives in payload.data.processor_response.
const normalizePaymentIncident = (row) => {
  const d = row.payload?.data || {};
  const cust = d.customer || {};
  return {
    ...row,
    id: String(row.id),
    kind: 'payment',
    at: firstPresent(row.createdAt, d.created_at, null),
    customer: firstPresent(cust.name, ''),
    email: firstPresent(cust.email, ''),
    phone: firstPresent(cust.phone_number, cust.phone, ''),
    amount: number(d.amount, 0),
    currency: firstPresent(d.currency, 'UGX'),
    // OUR refusal, when we refused it: the gateway's own words on such a row are "successful" / "Request successfully
    // processed" — the buyer paid — and the reason that matters is why we did not settle it (row.error).
    refused: row.status === 'error',
    reason: row.status === 'error' && row.error ? row.error : firstPresent(d.processor_response, d.status, 'Payment failed'),
    status: row.status === 'error' ? 'paid, not settled' : firstPresent(d.status, 'failed'),
    deliveries: number(row.deliveries, 1),
    deliveriesAtLeast: Boolean(row.deliveriesAtLeast), // counted over the deliveries read for this page — a floor
    orderRef: firstPresent(d.tx_ref, ''),
    raw: row,
  };
};
// WhatsApp incidents: failed/undeliverable message webhooks.
const normalizeWhatsappIncident = (row) => {
  let st = {};
  try { st = row.payload.entry[0].changes[0].value.statuses[0] || {}; } catch { /* */ }
  const err = (st.errors && st.errors[0]) || {};
  return {
    ...row,
    id: String(row.id),
    kind: 'whatsapp',
    at: firstPresent(row.createdAt, st.timestamp ? new Date(Number(st.timestamp) * 1000).toISOString() : null, null),
    phone: firstPresent(st.recipient_id, ''),
    customer: '',
    reason: firstPresent(err.error_data?.details, err.title, st.status, 'Message undeliverable'),
    errorCode: firstPresent(err.code, ''),
    status: firstPresent(st.status, 'failed'),
    raw: row,
  };
};
// Email errors: Firestore mail docs with delivery.state = ERROR.
const normalizeEmailError = (row) => ({
  ...row,
  id: String(row.id),
  kind: 'email',
  at: firstPresent(row.startTime, row.timestamp, null),
  email: firstPresent(Array.isArray(row.to) ? row.to[0] : row.to, ''),
  customer: '',
  reason: firstPresent(row.error, row.errorType, 'Delivery error'),
  // Which message it was — a ticket and a receipt fail the same way and matter differently.
  subject: firstPresent(row.subject, ''),
  // The same message to the same address went out on another send: the buyer has it.
  alreadyDelivered: row.alreadyDelivered === true,
  orderRef: firstPresent(row.tid, ''),
  status: firstPresent(row.state, 'ERROR'),
  raw: row,
});

/**
 * ABUSE SIGNALS — the detector's findings, what has already been marked, and what to do next.
 *
 * GET /api/incidents/abuse returns all three in one response on purpose: on 2026-10-08 an
 * automated run placed 1,325 orders against two events in five hours and every fact about it —
 * which address, which event, what to block, what not to delete — lived in one person's head for
 * the afternoon. The next one will be found by somebody else, so the runbook travels with the
 * findings rather than sitting in a document nobody opens mid-incident.
 *
 * `findings` is empty for anyone who is not a platform admin, and that is the backend's call, not
 * a bug to work around here: the detector compares one buyer against every other buyer's
 * behaviour, so showing it to one organiser would show them another organiser's buyers. `scope`
 * says which answer you got.
 */
export async function loadAbuseSignals({ hours = 24, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/incidents/abuse?hours=${hours}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Abuse signals unavailable'}`);
  const p = r.payload || {};
  return {
    scope: p.scope || 'organisation',
    windowHours: number(p.windowHours, hours),
    findings: list(p.findings),
    marked: list(p.marked).map((m) => ({
      eventId: m.eventId,
      // The TITLE, falling back to the id. A table of bare uuids is a table nobody can read: the
      // first version shipped one and the only way to learn which event it meant was to paste the
      // id somewhere else.
      eventTitle: m.eventTitle || m.eventId,
      orders: number(m.orders, 0),
      identities: number(m.identities, 0),
      // WHO. A count answers nothing — the question after an incident is handled is "who was it",
      // and the page had stopped being able to say.
      identityNames: list(m.identityNames),
      lastMarkedAt: m.lastMarkedAt || null,
    })),
    // The hourly shape. This was missing — the client was written before the backend grew a
    // series and never went back for it, so the chart and every tile above it read zero while the
    // findings underneath reported 1,322 orders. A page that contradicts itself in two places is
    // worse than a page with no chart.
    series: list(p.series).map((d) => ({
      t: d.t,
      orders: number(d.orders, 0),
      paid: number(d.paid, 0),
      flagged: number(d.flagged, 0),
      attempts: number(d.attempts, 0),
      ok: number(d.ok, 0),
    })),
    // A rule that threw is NOT a rule that found nothing. Carried through so the panel can say so
    // — a quiet detector reads as an all-clear, which is the one thing it must never do.
    errors: list(p.errors),
    runbook: p.runbook || null,
    ms: number(p.ms, 0),
    // The catalogue the page draws its checklist from, so "five rules ran" is a list it read
    // rather than a sentence typed into the UI that nobody updates when a sixth lands.
    rules: list(p.rules).map((r) => ({ id: r.id, label: r.label })),
    // What is defending checkout right now, straight from the values in force.
    defences: list(p.defences).map((d) => ({
      id: d.id, label: d.label, value: d.value, note: d.note, covers: list(d.covers),
    })),
    // What never reached us — blocked at the edge. `configured: false` until a token is set, and
    // the panel says so rather than showing an empty table that reads like "nothing was blocked".
    edge: {
      configured: p.edge?.configured === true,
      reason: p.edge?.reason || null,
      sinceHours: number(p.edge?.sinceHours, 24),
      totalBlocked: number(p.edge?.totalBlocked, 0),
      events: list(p.edge?.events).map((e) => ({
        zone: e.zone, action: e.action, ip: e.ip, asn: e.asn,
        country: e.country, path: e.path, count: number(e.count, 0),
        // WHICH rule refused it. The service already fetched this and the client threw it away,
        // which left the panel unable to answer the first question it provokes: is this something
        // we wrote, or Cloudflare's own baseline catching background noise? Those call for
        // completely different responses.
        ruleId: e.ruleId || '',
      })),
      errors: list(p.edge?.errors),
    },
  };
}

/**
 * RATE LIMITS. `limits`: every limiter in force and its numbers. `rows`: every live counter — who,
 * against which limit, how close, when it resets — read from Redis on demand, blocked first.
 */
export async function loadRateLimits({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/incidents/rate-limits', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Rate limits unavailable'}`);
  const p = r.payload || {};
  return { total: number(p.total, 0), blocked: number(p.blocked, 0), truncated: !!p.truncated, limits: list(p.limits), rows: list(p.rows) };
}

/** Lift one counter — a genuine buyer who hit a limit. */
export const releaseRateLimit = (key) => writeJson('/api/incidents/rate-limits', { method: 'DELETE', body: { key } });

/**
 * ONE EVENT, in full. The marked table answers "which events were hit"; this answers what
 * happened to one of them — who, over what window, with which numbers, how much of the event is
 * real, and whether anything has arrived since the marking.
 */
/**
 * THE ATTACK HISTORY — every detection the backend recorded, whether or not anybody acted on it.
 *
 * Until the backend grew `AbuseIncident` this could only be reconstructed from orders somebody
 * had MARKED, which made the history "the attacks we got round to marking". On 2026-10-09 one
 * identity ran for over two hours before anyone marked it and would have been absent entirely.
 */
export async function loadAbuseIncidents({ limit = 100, status = null, signal } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (status) qs.set('status', status);
  const r = await fetchBackOfficeJson(`/api/incidents/incidents?${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Incidents unavailable'}`);
  return list(r.payload?.incidents).map((i) => ({
    key: i.key,
    subject: i.subject || '',
    severity: i.severity || 'critical',
    status: i.status || 'open',
    detectedAt: i.detectedAt || null,
    lastDetectedAt: i.lastDetectedAt || null,
    firstSeen: i.firstSeen || null,
    lastSeen: i.lastSeen || null,
    orders: number(i.orders, 0),
    phones: number(i.phones, 0),
    billed: number(i.billed, 0),
    // How long the ORDERS ran, not how long we watched. Null when a single-tick incident gave us
    // no span to measure, which a "0s" would misrepresent as instantaneous.
    ranForSeconds: i.ranForSeconds == null ? null : number(i.ranForSeconds, 0),
    signals: list(i.signals),
    evidence: i.evidence && typeof i.evidence === 'object' ? i.evidence : {},
    markedAt: i.markedAt || null,
    markedBy: i.markedBy || null,
    markedOrders: i.markedOrders == null ? null : number(i.markedOrders, 0),
  }));
}

/**
 * WHO WAS REFUSED, AND BY WHICH CONTROL. Bounded in Redis — the last few hundred refusals and an
 * hourly tally — so this is a 7-day window by construction, not a full history.
 */
export async function loadRefusals({ limit = 100, hours = 24, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/incidents/refusals?limit=${limit}&hours=${hours}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Refusals unavailable'}`);
  const p = r.payload || {};
  return {
    recent: list(p.recent).map((x) => ({
      at: x.at || null,
      control: x.control || 'unknown',
      reason: x.reason || '',
      bucket: x.bucket || null,
      ip: x.ip || null,
      email: x.email || null,
      phone: x.phone || null,
      detail: x.detail || null,
    })),
    // [{ hour: "2026-10-09T12", counts: { "number-run:numbers walked in sequence": "4" } }]
    counts: list(p.counts).map((h) => ({ hour: h.hour, counts: h.counts && typeof h.counts === 'object' ? h.counts : {} })),
  };
}

export async function loadAbuseEvent(eventId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/incidents/abuse/event/${encodeURIComponent(eventId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Event detail unavailable'}`);
  const p = r.payload || {};
  const t = p.totals || {};
  return {
    event: p.event || {},
    totals: {
      marked: number(t.marked, 0),
      real: number(t.real, 0),
      realPaid: number(t.realPaid, 0),
      realPending: number(t.realPending, 0),
      fabricatedBillings: number(t.fabricatedBillings, 0),
      firstMarkedOrder: t.firstMarkedOrder || null,
      lastMarkedOrder: t.lastMarkedOrder || null,
    },
    identities: list(p.identities),
    attempts: list(p.attempts),
    unmarkedRecentPending: {
      count: number(p.unmarkedRecentPending?.count, 0),
      last: p.unmarkedRecentPending?.last || null,
    },
  };
}

/**
 * ONE check, on demand. The page walks the catalogue calling this so it can name the rule it is
 * on and time it — a full scan is ~12s and a spinner for twelve seconds is indistinguishable
 * from a hang.
 */
export async function runAbuseRule(ruleId, { hours = 24, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/incidents/abuse?hours=${hours}&rule=${encodeURIComponent(ruleId)}`, { signal, timeoutMs: 60000 });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Check failed'}`);
  return {
    rule: r.payload?.rule || ruleId,
    ms: number(r.payload?.ms, 0),
    findings: list(r.payload?.findings),
    errors: list(r.payload?.errors),
  };
}

export async function loadIncidents(kind, { page = 1, limit = LIST_PAGE_SIZE, signal } = {}) {
  const path = kind === 'payment' ? '/api/incidents/payment'
    : kind === 'whatsapp' ? '/api/incidents/whatsapp'
    : '/api/incidents/email-errors';
  const r = await fetchBackOfficeJson(`${path}?limit=${limit}&offset=${(page - 1) * limit}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Incidents unavailable'}`);
  const norm = kind === 'payment' ? normalizePaymentIncident : kind === 'whatsapp' ? normalizeWhatsappIncident : normalizeEmailError;
  return {
    rows: (r.payload?.data || []).map(norm),
    total: number(r.payload?.pagination?.total, (r.payload?.data || []).length),
    hasMore: r.payload?.hasMore ?? r.payload?.pagination?.hasMore ?? false,
    // Mail for EVERY brand lives in one Firestore collection, so the backend returns only the rows
    // whose sender domain is this deployment's and says how many it held back. Carried through so
    // the panel can say so — a list that just got shorter with no explanation is the silent cap.
    withheld: number(r.payload?.pagination?.withheld, 0),
  };
}

/**
 * RESEND FAILED DELIVERY EMAILS — one row, or every row matching what the operator is looking at.
 *
 * A retry is a single field flip on the Firebase Trigger-Email doc (`delivery.state = 'RETRY'`), so
 * the message, recipient and attachment are the ones already recorded — nothing re-renders and no
 * order is re-read. That is why bulk is safe to offer: these failures arrive in BURSTS (the pass
 * renderer serving every ticket PDF goes down, and every ticket email in that window fails at once),
 * and clicking two hundred rows to recover from one outage is not an action, it is a punishment.
 *
 * `errorContains` scopes a bulk retry to the error the operator has filtered to, so "retry all" is
 * never broader than what is on screen.
 */
/** SEND ANY LOGGED EMAIL AGAIN — a fresh copy of the stored message, to its address or `to` another. */
export async function resendMail(docId, { to = null, signal } = {}) {
  return writeJson(`/api/incidents/mail/${encodeURIComponent(docId)}/resend`, { body: to ? { to } : {}, signal });
}

/** RESEND ONE SETTLED PAYMENT'S RECEIPT — to the address it goes to, or (an operator of the order) `email`. */
export async function resendPaymentReceipt(attemptId, { email = null, signal } = {}) {
  return writeJson(`/api/send/receipt/${encodeURIComponent(attemptId)}`, { body: email ? { email } : {}, signal });
}

/** CORRECT THE BUYER'S EMAIL AND/OR PHONE on an order (and on the buyer's own tickets that carried the old one);
 *  `send` delivers the tickets to what was corrected, on the channels the order's brand uses for it. */
export async function correctOrderContact(orderId, { email, phone, send = false } = {}, { signal } = {}) {
  return writeJson(`/api/orders/${encodeURIComponent(orderId)}/contact`, { method: 'PATCH', body: { ...(email ? { email } : {}), ...(phone ? { phone } : {}), send }, signal });
}

export async function retryEmailIncidents({ ids, all, errorContains, days, dryRun, signal } = {}) {
  const body = await writeJson('/api/incidents/email-errors/retry', {
    body: { ...(ids?.length ? { ids } : {}), ...(all ? { all: true } : {}), ...(errorContains ? { errorContains } : {}), ...(days ? { days } : {}), ...(dryRun ? { dryRun: true } : {}) },
    signal,
  });
  return {
    retried: number(body?.retried, 0), batches: number(body?.batches, 0), skipped: number(body?.skipped, 0),
    // dry run: what a bulk resend WOULD send, and what it leaves out because the buyer already has it
    owed: number(body?.owed, 0), alreadyDelivered: number(body?.alreadyDelivered, 0), days: number(body?.days, 0),
  };
}

// --- UNPLACED MONEY (payment_dead_letters) -----------------------------------
// Money a gateway confirmed that no order has taken. Each row ends once: applied to an order,
// refunded, or closed with a reason — the backend's conditional update decides who acts.
export async function loadUnplacedMoney({ state = 'open', page = 1, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/unplaced-money?state=${state}&page=${page}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Unplaced money unavailable'}`);
  return { rows: r.payload?.data || [], hasMore: !!r.payload?.hasMore, page, open: r.payload?.open, gateways: r.payload?.gateways || [] };
}
export const fileUnplacedMoney = (body) => writeJson('/api/unplaced-money', { body });
export const refundUnplacedMoney = (id, { refundTransactionId, note } = {}) => writeJson(`/api/unplaced-money/${id}/refunded`, { body: { refundTransactionId, note } });
export const closeUnplacedMoney = (id, note) => writeJson(`/api/unplaced-money/${id}/close`, { body: { note } });

// --- NEEDS HELP (customers with failed/pending orders, last 7 days) ----------
const normalizeNeedsHelp = (c, i = 0) => {
  const statuses = c.statuses || {};
  const failed = number(statuses.failed, 0);
  const pending = number(statuses.pending, 0) + number(statuses.expired, 0) + number(statuses.cancelled, 0);
  return {
    ...c,
    id: String(firstPresent(c.email, c.phone, `nh-${i + 1}`)),
    name: firstPresent(c.name, c.email, c.phone, 'Customer'),
    email: firstPresent(c.email, ''),
    phone: firstPresent(c.phone, ''),
    orderCount: number(c.orderCount, (c.orders || []).length),
    lastAttempt: firstPresent(c.lastAttempt, null),
    failed,
    pending,
    statuses,
    orders: (c.orders || []).map((o) => ({
      id: String(o.id),
      status: titleCase(o.status || ''),
      amount: number(o.amount, 0),
      currency: firstPresent(o.currency, 'UGX'),
      // Same shape as the promo bug above: this may arrive as an event row
      // rather than a title, depending on what /api/needs-help includes.
      event: firstPresent(textLabel(o.event), 'Event'),
      eventId: firstPresent(o.eventId, ''),
      at: firstPresent(o.createdAt, o.updatedAt, null),
    })),
  };
};
export async function loadNeedsHelp({ page = 1, limit = 25, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/needs-help?page=${page}&limit=${limit}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Needs-help unavailable'}`);
  return {
    rows: (r.payload?.data || []).map(normalizeNeedsHelp),
    total: number(r.payload?.pagination?.total, (r.payload?.data || []).length),
    hasMore: r.payload?.hasMore ?? r.payload?.pagination?.hasMore ?? false,
  };
}

// Map a real escalation → the rich "ticket" shape the Support queue + drawer
// render. The escalation IS the ticket; the conversation is loaded on open.
const ESC_PRIORITY_TO_P = { urgent: 'p1', high: 'p2', normal: 'p3', low: 'p4' };
const ESC_STATE_TO_TICKET = { open: 'open', in_progress: 'waiting', resolved: 'resolved', closed: 'closed' };
const ESC_SLA_TARGET = { p1: 15, p2: 60, p3: 240, p4: 1440 };
export function ticketFromEscalation(e) {
  const ageMin = e.createdAt ? Math.max(0, Math.floor((Date.now() - new Date(e.createdAt).getTime()) / 60000)) : 0;
  const priority = ESC_PRIORITY_TO_P[e.priority] || 'p3';
  const isOpen = e.status === 'open' || e.status === 'in_progress';
  return {
    id: e.id,
    subject: e.reason || 'Support request',
    customer: e.customerName || e.contact,
    customerHandle: e.contact,
    // The case's contact is the person's (a phone E.164, or an email). WhatsApp names the same phone by its
    // digits — the thread, live messages, replies and the support mode are all keyed on that.
    whatsappId: /^\+\d{6,15}$/.test(String(e.contact || '')) ? String(e.contact).slice(1) : null,
    customerEmail: e.customerEmail || '',
    channel: e.channel || 'whatsapp',
    priority,
    state: ESC_STATE_TO_TICKET[e.status] || 'open',
    assigned: e.assignedTo || null,
    assignedName: e.assignedToName || null,
    linkedOrder: e.orderId || null,
    linkedEvent: e.eventId || null,
    linkedTxn: null,
    // Real triage tags (metadata.tags) if set, else seed from category.
    tags: (e.tags && e.tags.length) ? e.tags : [e.category].filter(Boolean),
    escalatedTo: e.escalatedTo || null,
    snoozedUntil: e.snoozedUntil || null,
    snoozed: Boolean(e.snoozedUntil && new Date(e.snoozedUntil).getTime() > Date.now()),
    opened: e.createdAt,
    ageMin,
    slaLeftMin: isOpen ? (ESC_SLA_TARGET[priority] - ageMin) : null,
    msgs: 0,
    internal: (e.notes || []).length,
    source: e.source,
    category: e.category,
    status: e.status,
    notes: e.notes,
    reasonHistory: e.reasonHistory,
    facts: e.facts,
    raw: e.raw,
  };
}
export async function loadSupportTickets({ status = 'open,in_progress', before, signal } = {}) {
  const { rows, openCount, waitingCount, answeredCount, nextBefore } = await loadEscalations({ status, before, signal });
  return { tickets: rows.map(ticketFromEscalation), openCount, waitingCount, answeredCount, nextBefore };
}

// Support AGENTS = TKT operators (super_admin / finance / support / admin).
// Anyone on the team can be assigned a ticket. load = their active assignments.
const AGENT_ROLES = new Set(['super_admin', 'finance', 'support', 'admin', 'owner']);
const AGENT_ROLE_LABEL = { super_admin: 'Operator', finance: 'Finance', support: 'Support agent', admin: 'Account admin', owner: 'Owner', cashier: 'Box office' };
export async function loadSupportAgents({ signal } = {}) {
  // The team only, asked by role at the backend (it matches any role a user holds — "user,admin" is an admin).
  const { users } = await listUsersOperator({ role: [...AGENT_ROLES].join(','), limit: 100, page: 1, sortBy: 'updatedAt', sort: 'desc', signal });
  return users.map((u) => {
    const rawRole = String(u.role || '').split(',').find((r) => AGENT_ROLES.has(r.trim()))?.trim() || u.role;
    return {
      id: u.id,
      name: u.name || u.email || u.id,
      email: u.email || '',
      role: AGENT_ROLE_LABEL[rawRole] || 'Operator',
      rawRole,
      banned: !!u.banned,
    };
  });
}

// Fast EXACT-phone order lookup (support drawer). The general order search is an
// ILIKE full-scan (~20s on 1.5M rows); this hits an exact-match endpoint (~160ms).
export async function loadOrdersByPhone(phone, { signal } = {}) {
  if (!phone) return { orders: [] };
  const r = await fetchBackOfficeJson(`/api/support-escalations/orders/by-phone?phone=${encodeURIComponent(phone)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Order lookup failed'}`);
  const orders = (r.payload?.data || []).map((o) => ({
    id: o.id,
    code: o.code || null,
    event: o.event,
    eventId: o.eventId,
    total: number(o.total, 0),
    currency: o.currency || null,
    status: o.status,
    orderType: o.orderType,
    created: o.at ? formatDate(o.at) : '',
    at: o.at || null,
    // The money across the order's attempts, as settlement recorded it (backend by-phone).
    attempts: number(o.attempts, 0),
    received: number(o.received, 0),
    credited: number(o.credited, 0),
    fees: number(o.fees, 0),
    amountPaid: number(o.amountPaid, 0),
    // The latest attempt's outcome ({ status, reason, detail }), so orderStatusWord reads "Failed" as the lists do.
    lastAttempt: o.lastAttempt || null,
  }));
  return { orders };
}

// Resolve a WhatsApp contact (phone) → real customer context: their orders,
// what they spent in the house window (CONTEXT_WINDOW — figures are bounded by time, like the customer
// page; `window` says which), and how many prior support tickets they've opened. Used to fill the
// ticket drawer's Customer + Linked panels without fabrication.
const CONTEXT_WINDOW = '30d';
export async function loadCustomerContext(contact, { signal } = {}) {
  const phone = String(contact || '').trim();
  if (!phone) return { customer: null, orders: [], tickets: [], priorTickets: 0, moreTickets: false, window: CONTEXT_WINDOW, since: null };
  const contextSince = resolveDatePreset(CONTEXT_WINDOW).from;
  const [factsRes, orderRes, escRes] = await Promise.allSettled([
    // WHO THIS NUMBER IS, as every customer surface reads it — the customer key's facts (backend
    // GET /users/customers/:key), scoped to the actor, per currency.
    loadCustomer(phone, { since: contextSince, signal }),
    loadOrdersByPhone(phone, { signal }),                          // the ten most recent, and the money each took
    // The few most recent cases, SHOWN — not fifty read to be counted. The route returns no total
    // (only hasMore), so "how many" is what came back, and "5+" when there are more.
    loadEscalations({ contact: phone, limit: 5, signal }),
  ]);
  const orders = orderRes.status === 'fulfilled' ? orderRes.value.orders : [];
  const f = factsRes.status === 'fulfilled' ? factsRes.value : null;
  const tickets = escRes.status === 'fulfilled' ? escRes.value.rows : [];
  const priorTickets = tickets.length;
  const moreTickets = escRes.status === 'fulfilled' && Boolean(escRes.value.hasMore);
  // A customer who paid in more than one currency has more than one lifetime spend; the headline is
  // the largest real one, never a blend, and `spendByCurrency` carries them all.
  const spendByCurrency = (f?.paid || []).map((m) => ({ currency: m.currency, spend: m.amount }));
  // A customer exists when the number has ANY order (firstOrderAt, any time); the counts and spend are the window's.
  const customer = f && f.firstOrderAt ? {
    key: f.key,
    name: f.name || '',
    email: f.emails?.[0]?.value || null,
    orders: f.orders,
    paid: f.byStatus?.successful || 0,
    spend: spendByCurrency[0]?.spend || 0,
    currency: spendByCurrency[0]?.currency || null,
    spendByCurrency,
    firstOrderAt: f.firstOrderAt,
    lastOrderAt: f.lastOrderAt,
  } : null;
  return { customer, orders, tickets, priorTickets, moreTickets, window: CONTEXT_WINDOW, since: contextSince };
}
/** How many support cases this number has opened, as far as loadCustomerContext read: "5+" when
 *  there are more than it fetched. `exceptId` leaves out the case on screen — a ticket drawer that
 *  counted its own ticket told every first-time complainant they had one prior ticket. */
export const priorTicketsOf = (ctx, exceptId) => {
  const n = (ctx?.tickets || []).filter((t) => t.id !== exceptId).length;
  return `${n}${ctx?.moreTickets ? '+' : ''}`;
};
// Claim a ticket: assign to the agent + move to in_progress (+ caller can take over
// the chat separately via setSupportMode). Returns the ticket-shaped record.
export async function claimEscalation(id, { userId, userName } = {}, { signal } = {}) {
  const updated = await updateEscalation(id, { assignedTo: userId || null, assignedToName: userName || null, status: 'in_progress' }, { signal });
  return ticketFromEscalation(updated);
}

export async function createDesign({ name, artifactType = 'ticket', status = 'draft', eventIds = [], contentBlocks, themeConfig, scope, qrMode }, { signal } = {}) {
  const payload = await writeJson('/api/designs', { body: { name, artifactType, status, eventIds,
    ...(contentBlocks && typeof contentBlocks === 'object' ? { contentBlocks } : {}),
    ...(themeConfig && typeof themeConfig === 'object' ? { themeConfig } : {}),
    ...(scope ? { scope } : {}), ...(qrMode ? { qrMode } : {}) } , signal });
  return payload?.design || payload?.data || payload;
}

export async function deleteDesign(id, { signal } = {}) {
  return writeJson(`/api/designs/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// Real partial-update of a design. PATCH /api/designs/:id (designBodySchema.partial()
// + eventIds[] → DesignEventPage links). The styling fields ARE part of the Design
// model (contentBlocks = which fields print on the ticket; themeConfig = brand
// colors/font; qrMode) and the Mustache render honors them — they were just never
// sent. Now they persist so an operator's design choices reach the real artifact.
export async function updateDesignArtifact(id, { name, eventIds, contentBlocks, themeConfig, qrMode, status, scope, artifactType, badge } = {}, { signal } = {}) {
  const body = {};
  if (name != null) body.name = name;
  if (Array.isArray(eventIds)) body.eventIds = eventIds;
  if (contentBlocks && typeof contentBlocks === 'object') body.contentBlocks = contentBlocks;
  if (themeConfig && typeof themeConfig === 'object') body.themeConfig = themeConfig;
  if (qrMode) body.qrMode = qrMode;
  if (status) body.status = status;
  if (scope) body.scope = scope;
  if (artifactType) body.artifactType = artifactType;
  // Accreditation badge config — top-level Design fields (which fields print on the
  // badge, default role, logos). The Mustache render + passHolderDesignRoutes honor them.
  if (badge && typeof badge === 'object') {
    for (const k of ['showFirstName', 'showLastName', 'showRole', 'showProfileImage', 'role', 'primaryLogoUrl', 'secondaryLogoUrl', 'template']) {
      if (badge[k] !== undefined) body[k] = badge[k];
    }
  }
  const payload = await writeJson(`/api/designs/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal });
  return payload?.design || payload?.data || payload;
}

// --- Real API keys (better-auth apiKey plugin) -----------------------------
// GET  /api/auth/api-key/list   → [{ id, name, start, prefix, enabled, createdAt,
//                                    lastRequest, requestCount, expiresAt, ... }]
// POST /api/auth/api-key/create { name } → full key incl. `key` (secret, shown ONCE)
// POST /api/auth/api-key/delete { keyId } → { success }
const normalizeApiKey = (k) => ({
  ...k,
  id: k.id,
  name: firstPresent(k.name, 'API key'),
  masked: `${k.prefix || ''}${k.start || ''}${k.start ? '…' : '••••••'}`,
  scope: firstPresent(k.metadata?.scope, 'Full'),
  status: k.enabled === false ? 'Revoked' : 'Active',
  created: k.createdAt ? formatDate(k.createdAt) : '—',
  lastUsed: k.lastRequest ? formatDate(k.lastRequest) : 'never',
  requests: number(k.requestCount),
  expires: k.expiresAt ? formatDate(k.expiresAt) : 'never',
});

export async function loadApiKeys({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/auth/api-key/list', { signal, skipDevAuth: true });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error?.message || r.payload?.error || 'API keys unavailable'}`);
  return (Array.isArray(r.payload) ? r.payload : (r.payload?.data || [])).map(normalizeApiKey);
}

// System error log (GET /api/system-errors) — backend service/error telemetry.
const normalizeSystemError = (e, i = 0) => ({
  ...e,
  id: String(firstPresent(e.id, `err-${i + 1}`)),
  at: firstPresent(e.timestamp, e.created_at, e.createdAt, null),
  service: firstPresent(e.service, '—'),
  errorType: firstPresent(e.errorType, e.error_type, e.error_code, e.errorCode, ''),
  message: firstPresent(e.message, ''),
  severity: String(firstPresent(e.severity, 'ERROR')).toUpperCase(),
  status: String(firstPresent(e.status, 'NEW')).toUpperCase(),
  environment: firstPresent(e.environment, ''),
  raw: e,
});
export async function loadSystemErrors({ severity, status, limit = 50, page = 1, signal } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (severity && severity !== 'all') params.set('severity', severity);
  if (status && status !== 'all') params.set('status', status);
  const r = await fetchBackOfficeJson(`/api/system-errors?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'System errors unavailable'}`);
  return {
    rows: (r.payload?.data || []).map(normalizeSystemError),
    total: number(r.payload?.pagination?.total, (r.payload?.data || []).length),
    hasMore: r.payload?.hasMore ?? r.payload?.pagination?.hasMore ?? false,
  };
}

export async function createApiKey({ name } = {}, { signal } = {}) {
  const body = await writeJson('/api/auth/api-key/create', { body: { name }, signal });
  // Returns the full key object incl. the secret `key` (shown once).
  return { ...normalizeApiKey(body), secret: body?.key };
}

export async function deleteApiKey(keyId, { signal } = {}) {
  return writeJson('/api/auth/api-key/delete', { body: { keyId }, signal });
}

export async function createMessageTemplate({ name, type = 'custom', channel = 'email', content }, { signal } = {}) {
  // Backend enums: channel ∈ email|whatsapp|in-app|thermal-sub; type ∈
  // ticket-confirmation|order-confirmation|reminder|promotional|accreditation|
  // cancellation|refund|custom. Content (when given) goes in metadata.
  const allowedChannels = ['email', 'whatsapp', 'in-app', 'thermal-sub'];
  const allowedTypes = ['ticket-confirmation', 'order-confirmation', 'reminder', 'promotional', 'accreditation', 'cancellation', 'refund', 'custom'];
  const body = {
    name,
    channel: allowedChannels.includes(channel) ? channel : 'email',
    type: allowedTypes.includes(type) ? type : 'custom',
    ...(content ? { metadata: { content } } : {}),
  };
  const payload = await writeJson('/api/message-templates', { body, signal });
  return payload?.data || payload?.template || payload;
}

// Persist edits to a stored message template. Content lives in metadata.content
// (matching createMessageTemplate) — the backend has no top-level content column;
// real content homes are metadata / languageVersions[] / emailBlocks[] (JSONB).
// NOTE: these templates are a STORED LIBRARY — no send/preview/campaign path renders
// from them yet (verified 2026-06-18: MessageTemplate is referenced only by its own
// CRUD). So persisting edits is real, but it does not change the email a buyer receives.
export async function updateMessageTemplate(id, { name, content, subject } = {}, { signal } = {}) {
  const body = {
    ...(name ? { name } : {}),
    ...((content !== undefined || subject !== undefined)
      ? { metadata: { ...(content !== undefined ? { content } : {}), ...(subject !== undefined ? { subject } : {}) } }
      : {}),
  };
  const payload = await writeJson(`/api/message-templates/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal });
  return payload?.data || payload?.template || payload;
}

export async function deleteMessageTemplate(id, { signal } = {}) {
  return writeJson(`/api/message-templates/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// --- Access control integrations -------------------------------------------
// Backed by /api/access-control-integrations (full CRUD + sync + test).
// Create requires name, eventId, acBaseUrl (HTTPS), acIntegrationId, apiKey.

// One integration row, shaped for the AccessControlPage table + detail drawer.
// (Also used by the domain hydration.) Only what the backend actually holds: the provider,
// "direction", gate and category lists this used to carry were invented defaults — the
// integration pushes TKT's gate roster (tickets + accreditation badges) to ONE access-control
// system, and access control posts scans back. There was never a second direction to choose.
export function normalizeAccessControlIntegration(item, index = 0) {
  const run = item.latestRun ? normalizeAcRun(item.latestRun) : null;
  return {
    ...item,
    ...normalizeSimpleNamed(item, index, 'integration'),
    eventId: String(firstPresent(item.eventId, item.event?.id, '—')),
    eventName: firstPresent(item.event?.title, item.eventName, '—'),
    endpoint: firstPresent(item.acBaseUrl, '—'),
    callbackUrl: firstPresent(item.callbackUrl, null),
    acIntegrationId: firstPresent(item.acIntegrationId, ''),
    status: firstPresent(item.status, 'active'),
    autoSync: item.autoSync !== false,
    // 'incremental' = send what changed; 'full' = send the complete roster every time.
    syncMode: item.syncMode === 'full' ? 'full' : 'incremental',
    gatesOpenAt: firstPresent(item.gatesOpenAt, null),
    entryClosesAt: firstPresent(item.entryClosesAt, null),
    nextSyncAt: firstPresent(item.nextSyncAt, null),
    autoSyncCompletedAt: firstPresent(item.autoSyncCompletedAt, null),
    lastSyncedAt: firstPresent(item.lastSyncedAt, null),
    lastSync: run?.startedAt ? formatTime(run.startedAt) : 'never synced',
    latestRun: run,
    health: acHealth(item.status, run),
    raw: item,
  };
}

// HEALTH IS WHAT LAST HAPPENED, not what an operator set. `status` only says whether the
// integration is switched on; whether it WORKS is the newest run's outcome. The old mapping
// read paused as "Degraded" and never looked at a run, so an integration failing every push
// showed Healthy.
export function acHealth(status, run) {
  const s = String(status || '').toLowerCase();
  if (s === 'disabled') return 'Disabled';
  if (s === 'paused') return 'Paused';
  if (!run) return 'Never synced';
  if (run.status === 'failed') return 'Failing';
  if (run.status === 'partial') return 'Degraded';
  if (run.status === 'pending' || run.status === 'accepted') return 'Syncing';
  return 'Healthy';
}

// One sync run. `sent` splits what the run carried into tickets and accreditation badges —
// both travel in the same run because access control's reconcile withdraws whatever a run leaves out.
export function normalizeAcRun(row) {
  const sent = row.response?.sent || null;
  return {
    ...row,
    id: String(firstPresent(row.id, '')),
    status: String(firstPresent(row.status, 'unknown')),
    mode: firstPresent(row.mode, null),
    trigger: String(firstPresent(row.trigger, 'manual')),
    sentTotal: number(firstPresent(row.ticketsSent, 0)),
    sentTickets: sent ? number(sent.tickets) : null,
    sentAccreditations: sent ? number(sent.accreditations) : null,
    created: row.createdRecords == null ? null : number(row.createdRecords),
    updated: row.updatedRecords == null ? null : number(row.updatedRecords),
    errors: row.errorCount == null ? null : number(row.errorCount),
    warnings: row.response?.warnings == null ? null : number(row.response.warnings),
    batches: number(firstPresent(row.response?.totalBatches, row.acChunkCount, 0)),
    error: firstPresent(row.error, null),
    startedAt: firstPresent(row.startedAt, row.createdAt, null),
    completedAt: firstPresent(row.completedAt, null),
  };
}

// The REAL integrations list (replaces the page's MOCK_AC_INTEGRATIONS_V2).
export async function loadAccessControlIntegrations({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/access-control-integrations?limit=80&includeTotal=false', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Integrations unavailable'}`);
  return list(r.payload).map(normalizeAccessControlIntegration);
}

export async function createAccessControlIntegration({ name, eventId, acBaseUrl, acIntegrationId, apiKey, syncMode = 'incremental', autoSync = true }, { signal } = {}) {
  const payload = await writeJson('/api/access-control-integrations', { body: {
    name, eventId, acBaseUrl, acIntegrationId, apiKey, syncMode, autoSync,
  }, signal });
  return payload?.data || payload?.integration || payload;
}

export async function updateAccessControlIntegration(id, patch, { signal } = {}) {
  return writeJson(`/api/access-control-integrations/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
}

export async function deleteAccessControlIntegration(id, { signal } = {}) {
  return writeJson(`/api/access-control-integrations/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// mode: undefined = the integration's own setting; 'reconcile' = send the COMPLETE roster, the only
// run that withdraws a refunded ticket or a removed badge at the gate.
export async function syncAccessControlIntegration(id, { signal, mode } = {}) {
  return writeJson(`/api/access-control-integrations/${encodeURIComponent(id)}/sync`, { body: mode ? { mode } : {}, signal });
}

// What this integration puts on the gate: eligible tickets by type, accreditation badges by
// category — counted by the backend from the same rules the sync sends by.
export async function loadAcRoster(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/access-control-integrations/${encodeURIComponent(id)}/roster`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Roster unavailable'}`);
  return r.payload?.data || { tickets: { total: 0, byType: [] }, accreditations: { total: 0, byCategory: [] } };
}

export async function testAccessControlIntegration(id, { signal } = {}) {
  return writeJson(`/api/access-control-integrations/${encodeURIComponent(id)}/test`, { method: 'POST', body: {}, signal });
}

// Rotate the callback secret (revokes the old one). POST /:id/rotate-callback-secret.
// Returns the NEW secret — shown once, because it has to be typed into access control and the
// backend stores it encrypted. Discarding it (as callers used to) left nobody able to set it.
export async function rotateAccessControlSecret(id, { signal } = {}) {
  const payload = await writeJson(`/api/access-control-integrations/${encodeURIComponent(id)}/rotate-callback-secret`, { body: {}, signal });
  return payload?.data?.callbackSecret || null;
}

// Pause/resume/disable an integration via PATCH status (active|paused|disabled).
export async function setAccessControlIntegrationStatus(id, status, { signal } = {}) {
  return writeJson(`/api/access-control-integrations/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status }, signal });
}

// --- Design & Comms: direct loaders (reliable; no singleton indirection) ----

export async function loadCampaignsList({ signal, limit = PAGE_LIMIT, eventId } = {}) {
  return (await loadCampaignPage({ signal, limit, eventId })).rows;
}

/** A page of campaigns, newest first — `{ rows, hasMore }` (the route's look-ahead). */
export async function loadCampaignPage({ signal, limit = LIST_PAGE_SIZE, page = 1, eventId } = {}) {
  const r = await fetchBackOfficeJson(`/api/campaigns?limit=${limit}&page=${page}${eventId ? `&eventId=${encodeURIComponent(eventId)}` : ''}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Campaigns unavailable'}`);
  return { rows: list(r.payload).map(normalizeCampaign), hasMore: Boolean(r.payload?.hasMore ?? r.payload?.pagination?.hasMore) };
}

function normalizeCampaign(c, i) {
  const stats = c.stats || {};
  const src = Array.isArray(c.audience?.audienceSources) ? c.audience.audienceSources[0] : null;
  return {
    id: String(firstPresent(c.id, `campaign-${i + 1}`)),
    name: firstPresent(c.name, c.subject, `Campaign ${i + 1}`),
    channel: titleCase(firstPresent(c.channel, 'email')),
    audience: firstPresent(src?.sourceName, c.scope === 'event' ? 'Event audience' : 'All customers'),
    // After a batch, `_totalResolved` is everyone sent SO FAR — the audience is that plus who is still waiting.
    audienceSize: number(firstPresent(c.audience?._remaining != null ? Number(c.audience._totalResolved || 0) + Number(c.audience._remaining) : null, c.audience?._totalResolved, stats.total, src?.count)),
    // The next click: who is still waiting and how many it sends (recorded by the last send).
    remaining: c.audience?._remaining ?? null,
    batchSize: c.audience?._batchSize ?? c.metadata?.batchSize ?? null,
    sent: number(firstPresent(stats.sent, c.progress?.sent)),
    opened: number(firstPresent(stats.read, stats.delivered)),
    clicks: number(firstPresent(stats.clicked, 0)),
    delivered: number(stats.delivered),
    failed: number(firstPresent(stats.failed, c.progress?.failed)),
    status: titleCase(firstPresent(c.status, 'draft')),
    // Measured over a recent window by the list endpoint — null while nothing is moving, so the
    // row can say "working it out" instead of inventing a duration.
    minutesRemaining: c.minutesRemaining ?? null,
    ratePerMinute: c.ratePerMinute ?? null,
    when: formatDate(firstPresent(c.schedule?.scheduledTime, c.startedAt, c.createdAt)),
    by: firstPresent(c.createdBy, 'Backend'),
    raw: c,
  };
}

// Merge real ticket /api/designs + /api/message-templates into the one "designs" list.
export async function loadDesignArtifacts({ signal, limit = PAGE_LIMIT } = {}) {
  const [dRes, tRes] = await Promise.allSettled([
    fetchBackOfficeJson(`/api/designs?limit=${limit}`, { signal }),
    fetchBackOfficeJson(`/api/message-templates?limit=${limit}`, { signal }),
  ]);
  const dOk = dRes.status === 'fulfilled' && dRes.value.ok;
  const tOk = tRes.status === 'fulfilled' && tRes.value.ok;
  // If BOTH endpoints failed (e.g. a dev-auth race on cold load), surface an
  // error so the page shows a Retry instead of a silent, misleading "empty".
  if (!dOk && !tOk) {
    const dErr = dRes.status === 'rejected' ? dRes.reason?.message : `${dRes.value?.status} ${dRes.value?.payload?.error || ''}`;
    const tErr = tRes.status === 'rejected' ? tRes.reason?.message : `${tRes.value?.status} ${tRes.value?.payload?.error || ''}`;
    throw new Error(`Designs unavailable (designs: ${dErr || 'failed'} · templates: ${tErr || 'failed'})`);
  }
  const designs = dOk ? list(dRes.value.payload).map((d, i) => ({
    id: String(firstPresent(d.id, `design-${i + 1}`)),
    name: firstPresent(d.name, d.title, `Design ${i + 1}`),
    type: titleCase(firstPresent(d.artifactType, d.type, 'Ticket design')),
    channel: firstPresent(d.channel, 'PDF / Wallet'),
    linked: firstPresent(d.event?.title, d.linked, 'Global'),
    linkedId: String(firstPresent(d.eventId, '—')),
    v: firstPresent(d.version, '1'), version: firstPresent(d.version, '1'),
    sends: number(firstPresent(d.sends, d.usageCount)),
    edited: formatDate(firstPresent(d.updatedAt, d.createdAt)),
    by: firstPresent(d.updatedBy, d.createdBy, 'Backend'),
    status: titleCase(firstPresent(d.status, 'Live')),
    archived: d.archived === true,
    // The REAL design config the backend stores + the Mustache render honors. These
    // were dropped on read before, so the customizer couldn't show saved state.
    artifactType: firstPresent(d.artifactType, 'ticket'),
    scope: firstPresent(d.scope, 'global'),
    // WHICH TEMPLATE THIS DESIGN CUSTOMISES. Dropped on read, so a design that HAD one arrived in
    // the console without it: the email editor reported "no template recorded" and the detail
    // modal fell back to the hand-drawn preview card, both on a row whose `template` column reads
    // `payment-receipt`. It is the key the ticket path uses for its DesignTemplate too.
    template: firstPresent(d.template, null),
    contentBlocks: (d.contentBlocks && typeof d.contentBlocks === 'object') ? d.contentBlocks : null,
    themeConfig: (d.themeConfig && typeof d.themeConfig === 'object') ? d.themeConfig : null,
    qrMode: firstPresent(d.qrMode, 'dynamic'),
    // Accreditation badge config (top-level Design fields) so the badge editor can show saved state.
    badge: {
      showFirstName: d.showFirstName !== false, showLastName: d.showLastName !== false,
      showRole: d.showRole !== false, showProfileImage: d.showProfileImage !== false,
      role: d.role || '', primaryLogoUrl: d.primaryLogoUrl || '', secondaryLogoUrl: d.secondaryLogoUrl || '',
      template: d.template || 'default',
    },
    kind: 'design', raw: d,
  })) : [];
  const templates = tRes.status === 'fulfilled' && tRes.value.ok ? list(tRes.value.payload).map((t, i) => ({
    id: String(firstPresent(t.id, `template-${i + 1}`)),
    name: firstPresent(t.name, t.title, t.key, `Template ${i + 1}`),
    type: titleCase(firstPresent(t.type, t.channel, 'Message')),
    channel: titleCase(firstPresent(t.channel, t.provider, 'Email')),
    linked: firstPresent(t.event?.title, t.linked, 'Global'),
    linkedId: String(firstPresent(t.eventId, '—')),
    v: firstPresent(t.version, '1'), version: firstPresent(t.version, '1'),
    sends: number(firstPresent(t.sends, t.sentCount)),
    edited: formatDate(firstPresent(t.updatedAt, t.createdAt)),
    by: firstPresent(t.updatedBy, t.createdBy, 'Backend'),
    // Real backend status (draft/published/archived); never default to "Live" — a
    // stored template that nothing sends from is not live mail.
    status: titleCase(firstPresent(t.status, 'Draft')),
    archived: t.archived === true || t.status === 'archived',
    // Surface saved content so the editor seeds real text (content homes: metadata
    // then languageVersions[0]). text.body is what the simple editor reads/writes.
    subject: firstPresent(t.metadata?.subject, t.languageVersions?.[0]?.subject, ''),
    content: firstPresent(t.metadata?.content, t.languageVersions?.[0]?.content, ''),
    text: { body: firstPresent(t.metadata?.content, t.languageVersions?.[0]?.content, '') },
    kind: 'template', raw: t,
  })) : [];
  return [...designs, ...templates];
}

// --- Dashboard OTP-gated order actions -------------------------------------
// Admin order deletion is gated behind a 6-digit OTP emailed to the admin.
// 1) POST /api/dashboard-otp/send {action} → emails OTP. 2) POST the action
// with an X-OTP header. (Manual-confirm also needs a biometric passkey, so it
// stays out of scope here.)

export async function deleteOrderDashboard(orderId, otp, { signal, reason } = {}) {
  const response = await fetch(`${API_BASE}/api/orders/dashboarddelete`, {
    method: 'POST', signal, credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json', 'X-OTP': String(otp), ...reasonHeader(reason) },
    body: JSON.stringify({ orderId }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error(data?.error || data?.message || `${response.status} Order delete failed`);
    err.payload = data; throw err;
  }
  return data?.data || data;
}

// --- Order delivery (resend tickets) ---------------------------------------
// Real: POST /api/send/ticket|whatsapp|sms/:orderId. Body email/phone optional
// (backend falls back to the order's stored contact when omitted).

export async function resendOrderTicketEmail(orderId, { email, signal } = {}) {
  return writeJson(`/api/send/ticket/${encodeURIComponent(orderId)}`, { body: email ? { email } : {}, signal });
}
export async function resendOrderTicketWhatsapp(orderId, { phone, signal } = {}) {
  return writeJson(`/api/send/whatsapp/${encodeURIComponent(orderId)}`, { body: phone ? { phone } : {}, signal });
}
export async function resendOrderTicketSms(orderId, { phone, signal } = {}) {
  return writeJson(`/api/send/sms/${encodeURIComponent(orderId)}`, { body: phone ? { phone } : {}, signal });
}

// One sent message, whichever surface reads it (an order's delivery panel, the email log): the
// backend maps the mail doc once (incidentRoutes emailRecord) and this is its one reading.
const normalizeDeliveryRecord = (r) => ({
  ...r,
  id: r.id,
  channel: r.channel || 'email', // 'email' | 'whatsapp' | 'sms' — SMS rows are failed sends only; a sent SMS is recorded nowhere
  sentAt: r.timestamp || null,
  from: r.from || '',
  to: r.to || '',
  cc: r.cc || '',
  replyTo: r.replyTo || '',
  subject: r.subject || '',
  // What the message actually was, and whether its body can be shown:
  //  'stored'   — the HTML is in the mail doc, fetched on demand by loadDeliveredEmailBody
  //  'template' — no HTML was kept; the body routes render the template with the send's data
  //  'inline'   — WhatsApp text, already here in bodyText
  bodySource: r.bodySource || 'none',
  bodyText: r.bodyText || '',
  template: r.template || null,
  attachments: list(r.attachments).map((a) => ({ filename: a?.filename || '', path: a?.path || '' })),
  state: String(r.delivery?.state || '').toUpperCase(), // SUCCESS | PROCESSING | ERROR | PENDING | RETRY | READ
  startTime: r.delivery?.startTime || null,
  endTime: r.delivery?.endTime || null,
  error: r.delivery?.error || '',
  errorType: r.delivery?.errorType || '',
  // The provider's own receipt — the SMTP message-id and which recipients the mail
  // server actually took vs bounced. This is the hardest evidence a send exists.
  attempts: Number.isFinite(r.delivery?.attempts) ? r.delivery.attempts : null,
  messageId: r.delivery?.messageId || '',
  accepted: list(r.delivery?.accepted),
  rejected: list(r.delivery?.rejected),
  pending: list(r.delivery?.pending),
  providerResponse: r.delivery?.response || '',
  // A failed send whose same message to the same address was accepted by another send (email log).
  anotherCopyAccepted: r.anotherCopyAccepted === true,
  // What it was about — the email log resolves the order / guest / campaign / event by id.
  about: r.about || null,
});

// --- Email delivery status --------------------------------------------------
// Real: GET /api/incidents/delivery-status/:orderUUID. Reads the Firebase "Trigger
// Email" `mail` collection where tid == order UUID, so it reflects ACTUAL email
// delivery outcome (state SUCCESS/PROCESSING/ERROR/PENDING + start/end times +
// error/errorType), not just "we tried to send". Keys on the order UUID (the human
// ref 404s). 404 = no email records for this order (never emailed, or too old to be
// in the collection) — a normal state, not an error. Returns [] on 404 so callers
// distinguish "no records" from a real failure (which throws).
export async function loadOrderDeliveryStatus(orderUuid, { signal } = {}) {
  if (!orderUuid) return { records: [], found: false };
  const response = await fetchBackOfficeJson(`/api/incidents/delivery-status/${encodeURIComponent(orderUuid)}`, { signal });
  if (response.status === 404) return { records: [], found: false };
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Delivery status unavailable'}`);
  const rows = list(response.payload?.data).map(normalizeDeliveryRecord);
  return { records: rows, found: rows.length > 0 };
}

// The rendered HTML of one sent email, on request — kept off the list read because it's
// 10–26KB per message. Scoped by orderUuid on the backend, so a doc id alone can't be
// used to read another order's mail.
export async function loadDeliveredEmailBody(orderUuid, docId, { signal } = {}) {
  const response = await fetchBackOfficeJson(
    `/api/incidents/delivery-status/${encodeURIComponent(orderUuid)}/message/${encodeURIComponent(docId)}/body`,
    { signal },
  );
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Email body unavailable'}`);
  const p = response.payload || {};
  return {
    subject: p.subject || '',
    html: p.html || '',
    text: p.text || '',
    bodySource: p.bodySource || 'none',
    template: p.template || null,
    note: p.note || '',
  };
}

/**
 * THE EMAIL LOG — every email this deployment sent to an address (exact), for an order code, or for
 * an order / guest id. GET /api/incidents/mail?q=&state=&after= is one indexed Firestore query:
 * newest first, 50 a page, `next` the cursor for the page after. The first page carries the counts
 * (Firestore aggregation) by state — NONE is mail the service never picked up — and `since`, where
 * the log begins (mail is only in it from when its brand was stamped on it).
 */
export async function searchMail(q, { by = '', state = '', after = '', signal } = {}) {
  // `by` — what the term is: an order code, an email address, or an order / guest id (MAIL_SEARCH_FIELDS).
  const qs = new URLSearchParams({ q, ...(by ? { by } : {}), ...(state ? { state } : {}), ...(after ? { after } : {}) });
  const r = await fetchBackOfficeJson(`/api/incidents/mail?${qs}`, { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} Email search unavailable`);
  const p = r.payload || {};
  return {
    by: p.by || '', records: list(p.data).map(normalizeDeliveryRecord), next: p.next || null,
    total: p.total == null ? null : number(p.total, 0), states: p.states || null, since: p.since || null, note: p.note || '',
  };
}

/** One email's body by its mail-doc id — the email log's reader (stored HTML, or the template rendered). */
export async function loadMailBody(docId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/incidents/mail/${encodeURIComponent(docId)}/body`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Email body unavailable'}`);
  const p = r.payload || {};
  return { subject: p.subject || '', html: p.html || '', text: p.text || '', bodySource: p.bodySource || 'none', template: p.template || null, note: p.note || '' };
}

// --- Ticket action requests (maker-checker: transfer / void / force_complete) --
// Real: POST /api/ticket-action-requests. Requires {action, orderId, reason}.
// Payload shape varies by action (transfer needs newOwner* fields).
// Then POST /api/ticket-action-requests/:id/approve to execute (super-admin).

export async function createTicketActionRequest({ action, orderId, reason, agentNotes, payload }, { signal } = {}) {
  return writeJson('/api/ticket-action-requests', { body: { action, orderId, reason, ...(agentNotes && { agentNotes }), ...(payload && { payload }) }, signal });
}

// The checker side of the maker-checker loop: the approval queue. A maker (e.g.
// the inventory drawer "Void" button) files a request via createTicketActionRequest;
// a super-admin reviews it here. Approving EXECUTES the action against the order
// (void→cancelled, force_complete→successful, transfer→reassign buyer).
export const TICKET_ACTION_LABEL = { void: 'Void order', force_complete: 'Force complete', transfer: 'Transfer ownership' };
const TICKET_ACTION_STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

function normalizeTicketActionRequest(r = {}) {
  return {
    ...r,
    id: r.id,
    action: r.action,
    actionLabel: TICKET_ACTION_LABEL[r.action] || titleCase(String(r.action || ''), 'Action'),
    orderId: r.orderId,
    routeId: r.orderId, // so a row can deep-link to the order via the entity cache
    orderRef: firstPresent(r.orderRef, r.orderId, ''),
    requestedBy: r.requestedBy,
    requestedByName: firstPresent(r.requestedByName, 'Unknown'),
    reason: firstPresent(r.reason, ''),
    agentNotes: firstPresent(r.agentNotes, ''),
    payload: r.payload || null,
    status: firstPresent(r.status, 'pending'),
    statusLabel: TICKET_ACTION_STATUS_LABEL[r.status] || titleCase(String(r.status || ''), 'Pending'),
    reviewedBy: r.reviewedBy,
    reviewedByName: firstPresent(r.reviewedByName, ''),
    reviewedAt: r.reviewedAt,
    reviewNotes: firstPresent(r.reviewNotes, ''),
    executionResult: r.executionResult || null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

export async function loadTicketActionRequests({ status, action, search, from = null, page = 1, limit = 50, signal } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (from) params.set('from', from);
  if (status && status !== 'all') params.set('status', status);
  if (action && action !== 'all') params.set('action', action);
  if (search) params.set('search', search);
  const r = await fetchBackOfficeJson(`/api/ticket-action-requests?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Approvals queue unavailable'}`);
  const rows = (r.payload?.data || []).map(normalizeTicketActionRequest);
  // NULL WHEN UNKNOWN — same rule the orders path already states. `/api/ticket-action-requests`
  // runs on the `paginate` engine, which never issues a COUNT, so `total` and `totalPages` are
  // simply absent. Defaulting them to `rows.length` and `1` produced two live lies: the Orders
  // page's Approvals badge asks with `limit: 1` and read `total`, so it displayed "1" whether one
  // approval was pending or thirty; and the approvals queue reported one page however many there
  // were. A count nobody has must read as a count nobody has.
  const total = r.payload?.total ?? r.payload?.totalItems;
  return {
    rows,
    total: total == null ? null : number(total, rows.length),
    hasMore: r.payload?.hasMore ?? r.payload?.pagination?.hasMore ?? false,
    page: number(r.payload?.page, page),
    totalPages: r.payload?.totalPages == null ? null : number(r.payload.totalPages, 1),
  };
}

/**
 * ORDERS WE HAVE BEEN PAID FOR AND HAVE NOT DELIVERED, while the event is still ahead.
 *
 * The console's other queues are decisions we owe an operator. This is the one thing we owe a
 * BUYER, and it is the only queue here whose deadline is set by someone else — the doors open
 * whether or not anybody looked. So it sorts by event date, soonest first.
 *
 * `fullyPaid` is decided by the backend using the platform's ONE completion rule (isPaid, with its
 * per-currency tolerance), not re-derived here from paid/total. Two answers to "has this been paid"
 * is exactly how an order ends up stranded in the first place.
 */
export async function loadUnfulfilledPaidOrders({ limit = 25, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/orders/unfulfilled?limit=${encodeURIComponent(limit)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Unfulfilled orders unavailable'}`);
  const rows = (r.payload?.rows || []).map((row) => ({
    ...row,
    paid: number(row.paid),
    total: row.total == null ? null : number(row.total),
    refundState: row.refundState ?? null,
    // What the buyer would show at the gate, in the order the operator would say it.
    // Whitespace collapsed: "Ernest" + trailing spaces + "Nareeba" is one name, and the double
    // gap it rendered with reads as a data glitch in the middle of a person's name.
    who: String(firstPresent(row.name, row.email, row.phone, 'Buyer')).replace(/\s+/g, ' ').trim(),
  }));
  // No `total` invented from `rows.length` — the endpoint deliberately issues no COUNT.
  return { rows, hasMore: r.payload?.hasMore ?? false };
}

export async function approveTicketActionRequest(id, { reviewNotes, signal } = {}) {
  const p = await writeJson(`/api/ticket-action-requests/${encodeURIComponent(id)}/approve`, { body: reviewNotes ? { reviewNotes } : {}, signal });
  return normalizeTicketActionRequest(p?.request || {});
}

export async function rejectTicketActionRequest(id, { reviewNotes, signal } = {}) {
  const p = await writeJson(`/api/ticket-action-requests/${encodeURIComponent(id)}/reject`, { body: reviewNotes ? { reviewNotes } : {}, signal });
  return normalizeTicketActionRequest(p?.request || {});
}

// Generate a real hosted-checkout payment link for an unpaid order. Mirrors the
// tkt-frontend buyer flow (GET /api/orders/:id/payment-link/pesapal → {paymentLink}).
// Non-destructive (returns a URL; doesn't charge). Operator uses it to recover a
// stuck/reserved/failed order by resending the link to the customer.
export async function generateOrderPaymentLink(orderId, { provider = 'pesapal', signal } = {}) {
  const suffix = provider === 'pesapal' ? '/pesapal' : '';
  const r = await fetchBackOfficeJson(`/api/orders/${encodeURIComponent(orderId)}/payment-link${suffix}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.errors?.[0] || r.payload?.error || 'Could not generate payment link'}`);
  const link = firstPresent(r.payload?.paymentLink, r.payload?.link, r.payload?.url, r.payload?.data?.paymentLink, '');
  if (!link) throw new Error(r.payload?.errors?.[0] || 'No payment link returned (order may already be paid)');
  return { paymentLink: link, raw: r.payload };
}

// --- Sales analytics report (REAL) -----------------------------------------
// Backed by GET /api/reports/sales — real aggregates over the Order table,
// scoped platform-wide for the super-admin (resolveOrgScope) or to an event.
// Returns { kpis, revenueByDay[], paymentMethods[], ticketsByEvent[], heatmap[],
// velocityByBucket[] }. Throws on failure (never silently empty).
/**
 * Ask a buyer again for a payment that never landed. Sends ONLY the attempt id — amount, rail,
 * currency and phone are resolved server-side from the attempt and its order, because a browser
 * that could name a phone or an amount could push an arbitrary sum at an arbitrary handset.
 *
 * What comes back is rail-dependent by design (see RailDef.reengage on the backend):
 *   mode 'push' — a NEW attempt was created and a fresh prompt fired. Re-using the old reference
 *                 would have returned 200 while no handset rang (MTN treats it as idempotent).
 *   mode 'link' — the gateway's hosted checkout is still valid; the link is returned for
 *                 re-delivery and nothing was charged again.
 */
/** ASK THIS ONE PAYMENT'S GATEWAY where it stands, and settle it if it has (POST /api/payment-attempts/:id/refresh —
 *  the same engine as every callback). Returns { attempt, order }: the charge after the answer, the order's money state. */
export async function recheckPaymentAttempt(attemptId, { signal } = {}) {
  return writeJson(`/api/payment-attempts/${encodeURIComponent(attemptId)}/refresh`, { signal });
}

export async function repromptPaymentAttempt(attemptId, { signal } = {}) {
  const response = await fetch(`${API_BASE}/api/payment-attempts/${encodeURIComponent(attemptId)}/reprompt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    signal,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || `${response.status} ${response.statusText || 'Re-prompt failed'}`);
  return body;
}

export async function loadSalesReport({ period = '30d', eventId, signal } = {}) {
  const params = new URLSearchParams({ period });
  if (eventId) params.set('eventId', eventId);
  const response = await fetchBackOfficeJson(`/api/reports/sales?${params.toString()}`, { signal });
  if (!response.ok) throw new Error(`${response.status} ${response.payload?.error || 'Sales report unavailable'}`);
  const data = response.payload || {};
  const k = data?.kpis || {};
  return {
    currency: data?.currency || null,
    revenueByCurrency: Array.isArray(data?.revenueByCurrency)
      ? data.revenueByCurrency.map((r) => ({ currency: r.currency || null, gross: number(r.gross), net: number(r.net), platform: number(r.platform), orders: number(r.orders), tickets: number(r.tickets), refunds: number(r.refunds) }))
      : [],
    kpis: {
      grossRevenue: number(k.grossRevenue),
      netRevenue: number(k.netRevenue),
      // Platform revenue = commission kept = gross − net (matches dashboard).
      platformRevenue: Math.max(0, number(k.grossRevenue) - number(k.netRevenue)),
      orderCount: number(k.orderCount),
      ticketCount: number(k.ticketCount),
      refundCount: number(k.refundCount),
    },
    // `currency` MUST survive this mapping. The backend groups revenue by (day, currency); a
    // normalizer that drops the currency hands the panel rows it cannot tell apart, which is how
    // a per-currency series silently becomes a blended one again — and it renders perfectly while
    // doing it. Same reason tickets/refunds are carried on revenueByCurrency above.
    revenueByDay: Array.isArray(data?.revenueByDay) ? data.revenueByDay.map((r) => ({ date: r.date, currency: r.currency || null, revenue: number(r.revenue), orders: number(r.orders) })) : [],
    paymentMethods: Array.isArray(data?.paymentMethods) ? data.paymentMethods.map((r) => ({ method: r.method || 'unknown', orderCount: number(r.orderCount), revenue: number(r.revenue), currency: r.currency || null })) : [],
    ticketsByEvent: Array.isArray(data?.ticketsByEvent) ? data.ticketsByEvent.map((r) => ({ eventId: r.eventId || r.event_id || null, name: r.name || 'Untitled', tickets: number(r.tickets), revenue: number(r.revenue), currency: r.currency || null })) : [],
    heatmap: Array.isArray(data?.heatmap) ? data.heatmap.map((r) => ({ day: number(r.day), hour: number(r.hour), value: number(r.value) })) : [],
  };
}

// --- Report Definitions CRUD ------------------------------------------------
export async function loadReportDefinitions({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/reports', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load reports'}`);
  // Strip TSV — exports are CSV/JSON only; no surface should offer or list it.
  return (r.payload?.data || []).map((rep) => Array.isArray(rep?.formats)
    ? { ...rep, formats: rep.formats.filter((f) => String(f).toUpperCase() !== 'TSV') }
    : rep);
}

export async function loadReportDefinition(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/reports/${encodeURIComponent(id)}`, { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} report unavailable`);
  return r.payload?.data;
}

export async function createReportDefinition(body, { signal } = {}) {
  const r = await writeJson('/api/reports', { body, signal });
  return r?.data || r;
}

export async function updateReportDefinition(id, patch, { signal } = {}) {
  const r = await writeJson(`/api/reports/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return r?.data || r;
}

export async function deleteReportDefinition(id, { signal } = {}) {
  return writeJson(`/api/reports/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// --- Report history: runs and exports ---------------------------------------
/**
 * The runs or the export files, filtered WHERE THEY LIVE: `reportId` and `eventId` narrow the list
 * on the server, `total` and `byState` count everything the filter matches, and `nextCursor` pages
 * it. One loader for both, because they are two views of the same activity and every screen that
 * lists them asks the same question — "for this report, for this event?". Filtering a held page in
 * the browser answered it wrongly: the list was paged, so counts and lists were partial, and the
 * Export Center was the latest 50 files on the platform and nothing older.
 *
 * Each item carries `scopeLabel` (what it was for, from its stored question) and the person who
 * made it (`actor` on a run, `generatedBy` on an export).
 */
export async function loadReportHistory(kind, { cursor, limit = LIST_PAGE_SIZE, reportId, eventId, signal } = {}) {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  if (cursor) qs.set('cursor', cursor);
  if (reportId) qs.set('reportId', reportId);
  if (eventId) qs.set('eventId', eventId);
  const path = kind === 'exports' ? '/api/reports/exports/list' : '/api/reports/runs/list';
  const r = await fetchBackOfficeJson(`${path}?${qs.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || `Failed to load ${kind}`}`);
  const items = r.payload?.data || [];
  // total / byState / counts cover the window from `countsSince` (the server bounds them; history grows forever).
  return { items, nextCursor: r.payload?.nextCursor || null, total: r.payload?.total ?? items.length, byState: r.payload?.byState || {}, counts: r.payload?.counts || {}, countsSince: r.payload?.countsSince || null };
}

// Keyset (cursor) pagination — ReportRuns grows forever (every execution,
// scheduled or ad-hoc), so an OFFSET page can silently skip or repeat rows
// under concurrent inserts while an operator scrolls back through history.
// `nextCursor` continues from the exact (createdAt, id) of the last row
// already seen; pass it back to fetch the next page. Omit it for page one.
export async function loadReportRuns({ cursor, limit = 50, signal } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const r = await fetchBackOfficeJson(`/api/reports/runs/list?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load runs'}`);
  return { runs: r.payload?.data || [], nextCursor: r.payload?.nextCursor || null };
}

export async function loadRunResult(runId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/reports/runs/${encodeURIComponent(runId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load run result'}`);
  return r.payload?.data || {};
}

export async function executeReport(id, body = {}, { signal } = {}) {
  const r = await writeJson(`/api/reports/${encodeURIComponent(id)}/run`, { body, signal });
  return r?.data || r;
}

// --- Report Schedules -------------------------------------------------------
export async function loadReportSchedules({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/reports/schedules/list', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load schedules'}`);
  return r.payload?.data || [];
}

export async function createReportSchedule(body, { signal } = {}) {
  const r = await writeJson('/api/reports/schedules', { body, signal });
  return r?.data || r;
}

export async function updateReportSchedule(id, patch, { signal } = {}) {
  const r = await writeJson(`/api/reports/schedules/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, signal });
  return r?.data || r;
}

/** Run a schedule now: its report over the period since it last ran, filed and emailed as scheduled. */
export async function runReportScheduleNow(id, { signal } = {}) {
  const r = await writeJson(`/api/reports/schedules/${encodeURIComponent(id)}/run`, { body: {}, signal });
  return r?.data || r;
}

export async function deleteReportSchedule(id, { signal } = {}) {
  return writeJson(`/api/reports/schedules/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// --- Refunds ----------------------------------------------------------------
//
// The money-OUT ledger, mirroring /api/transactions. A refund is anchored to a PAYMENT ATTEMPT,
// never to an order: you cannot return money in general, you return a specific payment, and the
// rail needs that payment's own id to do it.
//
// TWO THINGS THE BACKEND DECIDES AND THIS MUST NOT SECOND-GUESS:
//   · `canComplete` / `canCancel` per row. A refund the rail is still chewing on may not be
//     resolved by hand at all; one we never got a verdict for may only be cancelled. Render those
//     flags — do not re-derive them from `status`, or the console will offer a button the service
//     refuses and the two rules will drift.
//   · `needsManualAction` on a request. Most rails have no automated refund API, so a 200 means
//     "recorded, now go and send the money" rather than "done". The HTTP code cannot say that.

/** A page of the refund ledger, newest first; `last` (the last row shown) is where the next page starts. */
export async function loadRefundPage({ status, orderId, eventId, owedCurrency, last, limit = LIST_PAGE_SIZE, signal } = {}) {
  const p = new URLSearchParams();
  if (status) p.set('status', status);
  if (orderId) p.set('orderId', orderId);
  if (eventId) p.set('eventId', eventId);
  if (owedCurrency) p.set('owedCurrency', owedCurrency);
  if (last) { p.set('beforeAt', last.createdAt); p.set('beforeId', last.id); }
  p.set('limit', String(limit));
  const r = await fetchBackOfficeJson(`/api/refunds?${p.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load refunds'}`);
  return { rows: r.payload?.data || [], hasMore: !!r.payload?.hasMore };
}
/** One order's refunds — all of them (an order carries a handful; the route caps at 100). */
export const loadRefunds = async ({ orderId, signal } = {}) => (await loadRefundPage({ orderId, limit: 100, signal })).rows;

/** `amount` omitted means everything still refundable on that payment — which is what an operator
 *  means by "refund this payment", and the backend owns that arithmetic. */
// `sentOutside` ({ externalRef? }): already returned another way — recorded as done, nothing sent.
export async function requestRefund({ reference, amount, reason, sentOutside }, { signal } = {}) {
  return writeJson('/api/refunds', { body: { reference, ...(amount != null ? { amount } : {}), reason, ...(sentOutside ? { sentOutside } : {}) }, signal });
}

export async function resolveRefund(id, { outcome, externalRef, note }, { signal } = {}) {
  return writeJson(`/api/refunds/${encodeURIComponent(id)}/resolve`, {
    body: { outcome, ...(externalRef ? { externalRef } : {}), ...(note ? { note } : {}) },
    signal,
  });
}

// --- Saved Views ------------------------------------------------------------
// `reportId` narrows to one surface's views (Payment Plans passes its own). Omitted, every board
// the org can see comes back — which is what Reports wants, so its call is unchanged.
export async function loadSavedViews({ reportId, signal } = {}) {
  const qs = reportId ? `?reportId=${encodeURIComponent(reportId)}` : '';
  const r = await fetchBackOfficeJson(`/api/reports/views/list${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load views'}`);
  return r.payload?.data || [];
}

export async function createSavedView(body, { signal } = {}) {
  const r = await writeJson('/api/reports/views', { body, signal });
  return r?.data || r;
}

export async function updateSavedView(id, body, { signal } = {}) {
  const r = await writeJson(`/api/reports/views/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal });
  return r?.data || r;
}

export async function deleteSavedView(id, { signal } = {}) {
  return writeJson(`/api/reports/views/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

// --- Export Jobs -------------------------------------------------------------
export async function loadExportJobs({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/reports/exports/list', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load exports'}`);
  return r.payload?.data || [];
}

// Real, shape-appropriate chart data for a report (trend/bars/heat/aging/funnel/
// table) — read-only, no run record created. Drives the report preview's varied
// visualizations instead of one generic bar chart.
export async function loadReportVisualization(reportId, { days = 30, dateFrom, dateTo, rowLimit, groupBy, chartType, filters, scope, allEvents, measures, currency, refresh, runId, signal } = {}) {
  const body = dateFrom || dateTo ? { dateFrom, dateTo } : { days };
  if (rowLimit) body.rowLimit = rowLimit;
  if (groupBy) body.groupBy = groupBy;
  if (chartType) body.chartType = chartType;
  if (Array.isArray(filters)) body.filters = filters;
  if (scope) body.scope = scope;
  if (allEvents) body.allEvents = true;
  if (Array.isArray(measures) && measures.length) body.measures = measures;
  // WHICH BOOK. The engine will not add UGX to KES, so a money report is scoped to one
  // currency; absent, it picks the busiest in scope and reports which on the frame.
  if (currency) body.currency = currency;
  // TRUE REFRESH — recompute closed periods rather than reading the kept ones.
  if (refresh) body.refresh = true;
  // Minted by the CALLER, before the request — a cancel that needs the response first is not a
  // cancel. The engine tags its SQL with this so the statement is findable in pg_stat_activity.
  if (runId) body.runId = runId;
  const r = await writeJson(`/api/reports/${encodeURIComponent(reportId)}/visualize`, { body, signal });
  return r?.data || r;
}

// "…compared to what?" — the same report against the previous period, the same window a
// year ago, or another EVENT. The event case is not a window shift: the backend moves both
// sides onto the days-to-event axis, because two events share no calendar days and
// overlaying their calendars answers nothing.
export async function compareReport(reportId, compareTo, { days = 30, dateFrom, dateTo, eventId, allEvents, scope, signal } = {}) {
  const body = { compareTo, ...(dateFrom || dateTo ? { dateFrom, dateTo } : { days }) };
  if (eventId) body.eventId = eventId;
  if (allEvents) body.allEvents = true;
  if (scope) body.scope = scope;
  const r = await writeJson(`/api/reports/${encodeURIComponent(reportId)}/compare`, { body, signal });
  return r?.data || r;
}

// Run N queries and combine them — the composite/formula path (conversion rates,
// per-attendee figures, anything that is a function of more than one query).
export async function runReportComposite(spec, { signal } = {}) {
  const r = await writeJson('/api/reports/composite', { body: spec, signal });
  return r?.data || r;
}

// The expression grammar, functions and null rules — served by the backend so the
// builder's help text and the parser cannot drift apart.
export async function loadExpressionHelp({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/reports/expressions', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load expression help'}`);
  return r.payload?.data || null;
}

// The dimensions a user can group/scope by for a report (its navigable axes).
export async function loadReportDimensions(reportId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/reports/${encodeURIComponent(reportId)}/dimensions`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load dimensions'}`);
  return r.payload?.data || { groupBy: [], scope: [] };
}

// The real data sources the engine supports (for the create wizard's source picker).
export async function loadReportSources({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/reports/sources', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load sources'}`);
  return r.payload?.data || [];
}

// What each source MEANS — what one row is, and which date places it in a period. One flight per
// session: it describes the engine, which does not change while the console is open.
let _semantics = null;
export function loadReportSemantics() {
  _semantics ||= fetchBackOfficeJson('/api/reports/semantics').then((r) => {
    if (!r.ok) { _semantics = null; throw new Error(`${r.status} ${r.payload?.error || 'Failed to load source meanings'}`); }
    return r.payload?.data || { sources: [] };
  });
  return _semantics;
}

// The real dimensions / measurable columns / filterable columns for a SOURCE —
// drives the create wizard off the engine's schema (no saved report needed).
export async function loadSourceDimensions(source, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/reports/dimensions?source=${encodeURIComponent(source)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load dimensions'}`);
  return r.payload?.data || { groupBy: [], scope: [], measureCols: [], filterCols: [] };
}

// Render a DRAFT report definition (unsaved) so the wizard previews the real result.
// Default window is a SHORT recent slice, never all-time — preview must stay snappy.
// `cache: true` opts into the backend stale-while-revalidate cache — for glanceable
// inline cards (sparklines, health ribbons, vitals) where second-level recency isn't
// the point. First load computes; later loads return instantly + refresh in background.
// Interactive/precise report views leave it off and always run live.
/**
 * ASK — any question in one shape (see the backend's services/ask/compile). The vocabulary is static: every
 * table, its columns and links, and the one-to-many paths that can be measured. An answer carries what it read.
 */
export async function loadAskVocabulary({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/ask/vocabulary', { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} vocabulary unavailable`);
  return r.payload;
}
/** Ask a question. `runId` is minted by the caller BEFORE asking, so a running question can be cancelled. */
export async function askQuestion(question, { runId, signal } = {}) {
  return writeJson('/api/ask', { body: { ...question, ...(runId ? { runId } : {}) }, signal });
}

/** The answer to a question as a file, every row (up to 200,000) — asked again server-side, streamed, not stored. */
export async function downloadAnswer({ question, name = 'answer', format = 'CSV' } = {}) {
  const res = await fetch(`${API_BASE}${withOrgScope('/api/ask/export')}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*' },
    body: JSON.stringify({ question, name, format }),
  });
  if (!res.ok) { const msg = await res.json().catch(() => null); throw new Error(msg?.error || `${res.status} download failed`); }
  const blob = await res.blob();
  saveBlob(blob, `${String(name).replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'answer'}.${exportExtFor(format)}`);
  return { rows: Number(res.headers.get('x-export-rows')) || 0, truncated: res.headers.get('x-truncated') === 'true' };
}

/**
 * A question's rows as a file in the Export Center — asked again server-side for every row (up to 200,000),
 * stored (so it can be emailed and downloaded again), and downloaded now unless `download: false`.
 */
/**
 * MAKE A REPORT'S FILE AGAIN — for a run's Download and the Export Center's "Make again".
 *
 * These used to re-run POST /api/reports/:id/export, the old engine, which reads a report's old spec and not its
 * question — so a report that is a question came back as whatever its leftover spec (source defaulting to
 * orders) produced. A report with a question is asked again here: the recorded window becomes the question's
 * window, a recorded event answers its event prompt (any other prompt is left open: every value), and the file
 * lands in the Export Center like every question export. A report with no question is made the old way.
 */
export async function remakeReportFile({ reportId, params = {}, format = 'CSV', name } = {}) {
  const report = reportId && !String(reportId).startsWith('ent:') ? await loadReportDefinition(reportId).catch(() => null) : null;
  const q = report?.question;
  if (!q) return downloadReportExport(reportId, { format, params, filename: name });
  const isPrompt = (c) => c?.value == null && c?.op !== 'null' && c?.op !== 'notnull';
  const where = (q.where || []).flatMap((c) => (!isPrompt(c) ? [c]
    : /(^|\.)event$/.test(c.path) && params.eventId ? [{ ...c, value: params.eventId }] : []));
  const path = q.window?.path ? { path: q.window.path } : {};
  const window = params.dateFrom ? { ...path, from: params.dateFrom, ...(params.dateTo ? { to: params.dateTo } : {}) }
    : Number(params.days) > 0 ? { ...path, days: Number(params.days) } : q.window;
  const { window: _w, ...rest } = q; // eslint-disable-line no-unused-vars
  return exportQuestion({ question: { ...rest, where, ...(window ? { window } : {}) }, name: name || report.name, format, reportId });
}

export async function exportQuestion({ question, name = 'export', format = 'XLSX', download = true, reportId = null } = {}) {
  const res = await fetch(`${API_BASE}${withOrgScope('/api/ask/export')}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*' },
    // `reportId` files it under its report (that report's Files tab); without one it is filed under its table.
    body: JSON.stringify({ question, name, format: String(format).toUpperCase(), store: true, download, ...(reportId ? { reportId } : {}) }),
  });
  if (!res.ok) { const msg = await res.json().catch(() => null); throw new Error(msg?.error || `${res.status} export failed`); }
  if (!download) { const j = await res.json(); return { rows: j.rows, exportId: j.exportId, truncated: !!j.truncated }; }
  const blob = await res.blob();
  saveBlob(blob, `${String(name).replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'export'}.${exportExtFor(format)}`);
  return { rows: Number(res.headers.get('x-export-rows')) || 0, exportId: res.headers.get('x-export-id'), truncated: res.headers.get('x-truncated') === 'true' };
}

export async function previewReportDraft(draft, { days = 90, dateFrom, dateTo, signal, cache = false } = {}) {
  const spec = { ...draft, ...(dateFrom || dateTo ? { dateFrom, dateTo } : { days }) };
  if (cache) {
    // GET twin ending in /cached → caught by the Cloudflare `*/cached` cache rule + browser.
    const q = encodeURIComponent(JSON.stringify(spec));
    const r = await fetchBackOfficeJson(`/api/reports/preview/cached?q=${q}`, { signal });
    if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Preview failed'}`);
    return r.payload?.data ?? r.payload;
  }
  // Interactive/precise views run live (POST, never edge-cached).
  const r = await writeJson('/api/reports/preview', { body: spec, signal });
  return r?.data || r;
}

// (Provider reconciliation now routes through the generic engine export —
// requestEntityExport({source:'transactions', columns, filters:[rail, network, state]}) —
// not a bespoke endpoint. The `network` telco is a derived report-engine column.)

// Distinct real VALUES of a filter column (state, channel, rail…) with counts, so
// the builder offers them instead of error-prone free-text. Keyed by source+col so
// it works in the wizard BEFORE a report is saved. Returns [{value, count}].
export async function loadFieldValues(source, col, { days = 90, signal } = {}) {
  if (!source || !col) return [];
  const r = await fetchBackOfficeJson(`/api/reports/field-values?source=${encodeURIComponent(source)}&col=${encodeURIComponent(col)}&days=${days}`, { signal });
  if (!r.ok) return [];
  return r.payload?.data || [];
}

// The values to scope to for a given dimension (events, slots, tiers…).
export async function loadScopeValues(reportId, dim, { days = 365, signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/reports/${encodeURIComponent(reportId)}/scope-values?dim=${encodeURIComponent(dim)}&days=${days}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Failed to load scope values'}`);
  return r.payload?.data || [];
}

// Real report export: runs the report's compiled query on the backend, streams
// back a file (CSV/JSON), and triggers a browser download. Also records an
// ExportJob server-side (so the Export Center shows it). Replaces toast-only.
/**
 * The extension a file of this format is saved under.
 *
 * Every export helper here used to decide `json ? 'json' : 'csv'`, so the moment the server could
 * produce a real workbook it would have been saved as `report.csv` — a zip with the wrong name,
 * which Excel refuses and WPS opens as garbage. The format names the file; nothing else does.
 */
const EXPORT_EXT = { xlsx: 'xlsx', csv: 'csv', tsv: 'tsv', json: 'json' };
export const exportExtFor = (format) => EXPORT_EXT[String(format || 'csv').toLowerCase()] || 'csv';

/**
 * Save a response body AS BYTES. `downloadCsv` reads text, which is fine for CSV and silently
 * corrupts anything binary — an .xlsx is a zip, and a zip round-tripped through a string is no
 * longer a zip. Every export download goes through here.
 */
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * A report scope as the engine's request fields. `{ dim, value }` for one value — an event as
 * `eventId` (the engine's established door, which also lifts the date window), anything else as a
 * scope path — or `{ all: true }` for platform-wide. `null` asks for the report's own default.
 */
export function reportScopeBody(scope) {
  if (!scope) return {};
  if (scope.all) return { allEvents: true };
  if (scope.value == null || scope.value === '') return {};
  return scope.dim === 'event' ? { eventId: scope.value } : { scope: [{ dim: scope.dim, value: scope.value }] };
}

// `scope` — see reportScopeBody. This sent only `{ format, days }`, so a download from a sheet
// scoped to one event came back as whichever event the report picks by default.
//
// `params` — a RUN's stored request (`run.params`: days, eventId, scope, allEvents, filters,
// groupBy). Downloading a run used to mean saving the 100-row sample its response carries, so a
// 2,208-line statement arrived as a 100-row file. With `params` the file is the export endpoint's
// full result for exactly the question the run asked.
//
// `runId` — set when the download comes from a run, so the server names the file and the Export
// Center row "report · event · run" rather than the report alone.
// `save: false` — produce and store the file without saving it in this browser (a share link).
export async function downloadReportExport(reportId, { format = 'XLSX', days = 30, dateFrom, dateTo, filename = 'report', scope = null, params = null, runId = null, save = true } = {}) {
  const p = params || {};
  // A calendar period travels as its range; a rolling one as its day count (lib/reportPeriod).
  const range = dateFrom || dateTo ? { dateFrom, dateTo } : null;
  const fromRun = params ? {
    ...(p.dateFrom || p.dateTo ? { dateFrom: p.dateFrom, dateTo: p.dateTo } : { days: typeof p.days === 'number' ? p.days : days }),
    ...(p.eventId ? { eventId: p.eventId } : {}),
    ...(p.scope ? { scope: p.scope } : {}),
    ...(p.allEvents ? { allEvents: true } : {}),
    ...(Array.isArray(p.filters) ? { filters: p.filters } : {}),
    ...(p.groupBy ? { groupBy: p.groupBy } : {}),
  } : { ...(range || { days }), ...reportScopeBody(scope) };
  const res = await fetch(`${API_BASE}${withReaderClock(`/api/reports/${encodeURIComponent(reportId)}/export`)}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*', 'x-org-scope': 'all' },
    body: JSON.stringify({ format, ...fromRun, ...(runId ? { runId } : {}) }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => null);
    throw new Error(msg?.error || `${res.status} export failed`);
  }
  const blob = await res.blob();
  // THE SERVER'S NAME FOR THE FILE. It knows which event the export actually answered for (it
  // applied any default scope itself) and names the file after it; the name built here from the
  // report alone saved every event's statement under the same filename.
  const served = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') || '')?.[1];
  const safe = String(filename).replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'report';
  if (save) saveBlob(blob, served || `${safe}.${exportExtFor(format)}`);
  return { bytes: blob.size, rows: res.headers.get('x-export-rows'), exportId: res.headers.get('x-export-id'), truncatedAt: Number(res.headers.get('x-export-truncated')) || null };
}

// Download a STORED export artifact by its job id — serves the same point-in-time
// bytes from R2 (not a re-run). Works for both saved-report and entity (ent:*) jobs.
export async function downloadExportArtifact(exportId, { fileName } = {}) {
  // withOrgScope: the export history is the platform's (reportRoutes platformOnly), like every other read here.
  const res = await fetch(`${API_BASE}${withOrgScope(`/api/reports/exports/${encodeURIComponent(exportId)}/download`)}`, { credentials: 'include' });
  if (!res.ok) {
    const msg = await res.json().catch(() => null);
    throw new Error(msg?.error || `${res.status} download failed`);
  }
  const blob = await res.blob();
  // Honor the server's filename, else fall back to a caller-supplied name.
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^"]+)"?/i.exec(cd);
  const name = m?.[1] || fileName || `${exportId}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  return { bytes: blob.size, fileName: name };
}

// A real, time-limited signed URL to the stored artifact — for "copy share link".
export async function shareExportArtifact(exportId, { expiresIn = 86400 } = {}) {
  const r = await fetchBackOfficeJson(`/api/reports/exports/${encodeURIComponent(exportId)}/share?expiresIn=${expiresIn}`);
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} could not create share link`);
  return r.payload?.url;
}

// Email a real download link (presigned R2 URL) for a stored artifact to recipients,
// via the platform mail queue. Returns { sent, filtered, recipients }.
export async function emailExportArtifact(exportId, recipients) {
  const r = await writeJson(`/api/reports/exports/${encodeURIComponent(exportId)}/email`, { body: { recipients } });
  return r;
}

// The engine's real column list for a source (key + label), so an export column picker
// reflects exactly what the server can return — no offering a column the export drops.
// Returns [] on any failure (the modal falls back to a full-row export).
export async function loadSourceColumns(source) {
  try {
    const r = await fetchBackOfficeJson(`/api/reports/dimensions?source=${encodeURIComponent(source)}`);
    if (!r.ok) return [];
    const cols = r.payload?.data?.filterCols || r.payload?.filterCols || [];
    // `exportDefault` is the source's own answer to "which columns does an export start with" —
    // carried through so the picker pre-ticks those instead of all 29. Absent → undefined, and
    // the picker keeps ticking everything, as it always has for sources that do not declare one.
    return Array.isArray(cols)
      ? cols.filter(c => c?.key).map(c => ({ id: c.key, key: c.key, label: c.label || c.key, ...(typeof c.exportDefault === 'boolean' ? { exportDefault: c.exportDefault } : {}) }))
      : [];
  } catch { return []; }
}

/**
 * Rows the browser already holds, written as a file by the SERVER's serializer.
 *
 * For exports that are of what is on screen (a filtered page, a search) — re-querying could not
 * reproduce them. They were built here as CSV and so could never be a workbook; now they go through
 * the same writer as every other export. Send numbers as numbers and text as text: the server
 * types each column from the JSON, so a phone kept as a string is never written as a number.
 */
export async function exportRowsFile({ name = 'export', columns, rows, format = 'XLSX' } = {}) {
  const res = await fetch(`${API_BASE}/api/reports/export/rows`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*' },
    body: JSON.stringify({ name, columns, rows, format }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => null);
    throw new Error(msg?.error || `${res.status} export failed`);
  }
  const blob = await res.blob();
  const safe = String(name).replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'export';
  saveBlob(blob, `${safe}.${exportExtFor(format)}`);
  return { bytes: blob.size, rows: Number(res.headers.get('x-export-rows')) || rows.length };
}

// GENERIC entity export — the ONE real export path: POST a draft {source, filters,
// columns} to the report engine, which runs the FULL query server-side, streams the
// file, and records an ExportJob (visible in the Reports Export Center). This is how
// every list surface should export — not a client-side CSV of the loaded page.
// Returns { rows, exportId, fileName } after triggering the browser download.
/**
 * `download: false` is for the EMAIL delivery path. The export still runs and still stores its
 * artifact (that is what gets emailed) — it just does not also drop a file in the operator's
 * Downloads folder, which is not what they asked for when they chose "email me a link".
 */
export async function requestEntityExport({ source, filters = [], columns = null, columnLabels = null, format = 'XLSX', days = 0, eventId = null, scope = null, allEvents, from = null, to = null, name = null, includeAbandoned = false, hideAbandoned, download = true } = {}) {
  if (!source) throw new Error('source is required for a real export');
  // The orders list's "Abandoned checkouts: hidden" chip (constants/orders), as the export dialog
  // passes it: kept → hideAbandoned true, taken off → false, which means include them.
  if (hideAbandoned === false) includeAbandoned = true;
  const res = await fetch(`${API_BASE}/api/reports/export`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*', 'x-org-scope': 'all' },
    body: JSON.stringify({ source, filters, ...(columns ? { columns } : {}), ...(columnLabels && Object.keys(columnLabels).length ? { columnLabels } : {}), format, days, ...(eventId ? { eventId } : {}), ...(scope ? { scope } : {}), ...(allEvents != null ? { allEvents } : {}), ...(from ? { dateFrom: from } : {}), ...(to ? { dateTo: to } : {}), ...(name ? { name } : {}), ...(includeAbandoned ? { includeAbandoned: 'true' } : {}) }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => null);
    throw new Error(msg?.error || `${res.status} export failed`);
  }
  const blob = await res.blob();
  // Named by what it is — "kitara-fc-vs-mogadishu-city-orders.xlsx" — not by the engine's source
  // key. A file called `orders.csv` in somebody's Downloads says nothing about which event.
  // Saved as BYTES: reading the blob as text is fine for CSV and turns a workbook (a zip) to rubbish.
  const fileName = `${String(name || source).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.${exportExtFor(format)}`;
  if (download) saveBlob(blob, fileName);
  return { rows: res.headers.get('x-export-rows'), exportId: res.headers.get('x-export-id'), fileName };
}

// --- Organizer (organization) real detail ----------------------------------
// /api/organizations/:id/details → organization, members, invitations, events, stats.


// --- Organization KYC / verification (BE: /api/admin/verification + /api/media) ---
// Real admin review of org business verification. Documents reuse the Media (R2)
// upload plumbing; this is the review workflow (list/detail/approve/reject).
const KYC_STATUS_LABEL = { pending: 'Pending', submitted: 'Submitted', verified: 'Verified', rejected: 'Rejected' };

export async function loadKycOrganizations({ status, page = 1, limit = LIST_PAGE_SIZE, search, signal } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status && status !== 'all') params.set('status', status);
  if (search) params.set('search', search);
  const r = await fetchBackOfficeJson(`/api/admin/verification/organizations?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'KYC list unavailable'}`);
  const rows = (r.payload?.data || []).map((o) => ({
    id: o.id,
    org: firstPresent(o.name, 'Organization'),
    slug: firstPresent(o.slug, ''),
    logo: firstPresent(o.logo, ''),
    status: firstPresent(o.verification_status, 'pending'),
    statusLabel: KYC_STATUS_LABEL[o.verification_status] || 'Pending',
    comment: firstPresent(o.verification_comment, ''),
    documents: number(o.document_count),
    createdAt: o.createdAt,
  }));
  // No total: counting every organization by status is a whole-table read. A page, and whether there is more.
  return { rows, hasMore: !!r.payload?.pagination?.hasMore, page: number(r.payload?.pagination?.page, page) };
}

/**
 * THE REVIEW QUEUE BY STAGE (services/merchantReview on the backend): one stage of it, a page at a
 * time, each row with its progress, what is missing and its reviewer, as the backend sends them
 * (modules/compliance/kycModel kycQueueRow reads them). `counts` asks for every stage's count too: a
 * merchant registry is hundreds of rows, counted in one grouped read.
 */
export async function loadKycQueue({ stage = 'open', page = 1, limit = LIST_PAGE_SIZE, search, counts = false, signal } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (stage && stage !== 'all') params.set('stage', stage);
  if (search) params.set('search', search);
  if (counts) params.set('counts', '1');
  const r = await fetchBackOfficeJson(`/api/admin/verification/organizations?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'KYC queue unavailable'}`);
  return {
    rows: r.payload?.data || [],
    hasMore: !!r.payload?.pagination?.hasMore,
    page: number(r.payload?.pagination?.page, page),
    counts: r.payload?.counts || null,
  };
}

export async function loadKycOrganizationDetail(orgId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/admin/verification/organizations/${encodeURIComponent(orgId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'KYC detail unavailable'}`);
  const org = r.payload?.organization || {};
  const meta = parseMaybeJson(org.metadata);
  const metaText = (v) => (typeof v === 'string' ? v.trim() : '');
  return {
    org: {
      id: org.id, name: firstPresent(org.name, 'Organization'), slug: org.slug, logo: org.logo,
      status: firstPresent(org.verification_status, 'pending'), comment: firstPresent(org.verification_comment, ''),
      bio: org.bio || '', website: org.website || '', createdAt: org.createdAt,
      // What the organizer CLAIMS — the certificate below is checked against these, so a drawer
      // showing the document without them gives the reviewer nothing to compare it to.
      // tkt-new-ui saves `regNo` (with legalName, taxId, address); older pages saved
      // `businessRegistrationNumber`. Read in the order tkt-new-ui reads them, strings only as it does.
      registrationNumber: metaText(meta.regNo) || metaText(meta.businessRegistrationNumber),
      legalName: metaText(meta.legalName),
      taxId: metaText(meta.taxId),
      address: metaText(meta.address),
      compliant: meta.isCompliant || '',
    },
    documents: (r.payload?.documents || []).map((d) => ({
      id: d.uuid, name: firstPresent(d.file_name, 'document'), type: d.document_type || 'other',
      mime: d.content_type, size: number(d.size),
      // The backend redirects this to a signed link that expires in minutes; the file's public
      // address is not handed out. A plain link works because the session cookie is sent on it.
      url: d.uuid ? `${API_BASE}/api/admin/verification/documents/${encodeURIComponent(d.uuid)}/file` : '',
      reviewStatus: firstPresent(d.review_status, 'pending'), adminComment: d.admin_comment || '',
      uploadStatus: d.status, uploadedBy: d.uploaded_by, createdAt: d.created_at,
      // Set when a reviewer approves a document that expires (an ID, a tax certificate).
      expiresAt: d.expires_at || null, reviewedBy: d.reviewed_by_name || null, reviewedAt: d.reviewed_at || null,
    })),
    comments: (r.payload?.comments || []).map((c) => ({ id: c.id, comment: c.comment, by: c.admin_user_name, at: c.created_at })),
    // The review itself (services/merchantReview): its stage and reviewer, what was asked for, each
    // requirement for the legal form with its state, the reviewer's checks, what still blocks verifying,
    // the progress bar's stage and the history. Absent from an older backend: the drawer then shows
    // only what it showed before.
    review: r.payload?.review || null,
    requirements: r.payload?.requirements || [],
    checklist: r.payload?.checklist || [],
    blockers: r.payload?.blockers || [],
    progress: r.payload?.progress || null,
    missing: r.payload?.missing || [],
    steps: r.payload?.steps || [],
    settlement: r.payload?.settlement || null,
    contacts: r.payload?.contacts || null,
    business: r.payload?.business || null,
  };
}

// ── The review's decisions (POST/PUT/PATCH /api/admin/verification/…, rules in services/merchantReview).
// Each is refused with the server's own reason when the case is not ready for it (409), which the
// caller shows as it is.
const kycCase = (orgId) => `/api/admin/verification/organizations/${encodeURIComponent(orgId)}`;
/** Take the case: you are its reviewer ("In review"). `force` takes it over from another reviewer. */
export const takeKycCase = (orgId, { force = false } = {}) => writeJson(`${kycCase(orgId)}/take`, { body: { force } });
/** Put it back in the queue, nobody on it. */
export const releaseKycCase = (orgId) => writeJson(`${kycCase(orgId)}/release`, { body: {} });
/** Send it back to the merchant: what to change, item by item ([{ item, note? }]), and the message they read. */
export const requestKycChanges = (orgId, { items, message }) => writeJson(`${kycCase(orgId)}/request-changes`, { body: { items, message } });
/** Verify: refused with the first thing still missing until every requirement and check holds. */
export const verifyKycCase = (orgId, { note } = {}) => writeJson(`${kycCase(orgId)}/verify`, { body: { note: note || undefined } });
/** Confirm (or clear) one of the reviewer's checks. */
export const setKycCheck = (orgId, key, { done, note } = {}) =>
  writeJson(`${kycCase(orgId)}/checks/${encodeURIComponent(key)}`, { method: 'PUT', body: { done: done !== false, note: note || undefined } });
/**
 * CHECK A PAYOUT ACCOUNT (PUT /api/admin/merchants/:orgId/payout-accounts/:id/verification): a bank
 * account against the bank letter on file ({ method: 'bank_letter', documentId }), a mobile money
 * number by declaring whose name it is in ({ method: 'declaration', declaredHolder, relationship }),
 * or clear the check ({ status: 'unverified' }). A merchant is active once its account is checked.
 */
export const setPayoutAccountCheck = (orgId, accountId, body) =>
  writeJson(`/api/admin/merchants/${encodeURIComponent(orgId)}/payout-accounts/${encodeURIComponent(accountId)}/verification`, { method: 'PUT', body });

// ── A merchant's contacts (/api/admin/merchants/:orgId/contacts, services/merchantContacts): people at
// the merchant the platform can reach, exactly one primary, each with responsibilities. Merchant
// reviewers only. `team` is its own people on the platform (never platform staff): who a contact can
// be picked from, and who stands in as the primary until a contact is added.
const merchantAdmin = (orgId) => `/api/admin/merchants/${encodeURIComponent(orgId)}`;
export async function loadMerchantContacts(orgId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`${merchantAdmin(orgId)}/contacts`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Contacts unavailable'}`);
  return r.payload?.data || [];
}
export async function loadMerchantTeam(orgId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`${merchantAdmin(orgId)}/team`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Team unavailable'}`);
  return r.payload?.data || [];
}
export const addMerchantContact = (orgId, body) => writeJson(`${merchantAdmin(orgId)}/contacts`, { body });
export const updateMerchantContact = (orgId, contactId, body) => writeJson(`${merchantAdmin(orgId)}/contacts/${encodeURIComponent(contactId)}`, { method: 'PATCH', body });
/** Removing the primary names who takes over (`newPrimaryId`), unless it is the last contact. */
export const removeMerchantContact = (orgId, contactId, newPrimaryId) =>
  writeJson(`${merchantAdmin(orgId)}/contacts/${encodeURIComponent(contactId)}`, { method: 'DELETE', body: { newPrimaryId: newPrimaryId || undefined } });

/** Approve a document (with the date it expires, when it does), reject it with the reason the merchant sees, or set it back. */
export const reviewKycCaseDocument = (mediaId, { status, reason, expiresAt } = {}) =>
  writeJson(`/api/admin/verification/documents/${encodeURIComponent(mediaId)}/review`, {
    method: 'PATCH', body: { status, ...(reason ? { reason } : {}), ...(expiresAt ? { expiresAt } : {}) },
  });

/**
 * A REVIEWER FILES A DOCUMENT FOR AN ORGANIZATION — one that came by email, was collected in person,
 * or that the checklist doesn't name. `documentType` is a KYC_DOC_LABEL id or the document's own
 * name ("Board resolution"). Presign under that organization → PUT the bytes to R2 → confirm; it
 * then sits beside the organizer's own uploads, pending review like them.
 */
export async function uploadKycDocument(orgId, file, documentType, { signal, maxBytes = 15 * 1024 * 1024 } = {}) {
  if (!file) throw new Error('No file selected');
  if (file.size > maxBytes) throw new Error(`File must be under ${Math.round(maxBytes / 1024 / 1024)}MB`);
  const mimeType = file.type || 'application/octet-stream';
  const presign = await writeJson(`/api/admin/verification/organizations/${encodeURIComponent(orgId)}/documents`, {
    body: { filename: file.name || 'document', mimeType, size: file.size, documentType }, signal,
  });
  if (!presign?.uploadUrl || !presign?.mediaId) throw new Error('Upload service did not return a URL');
  const put = await fetch(presign.uploadUrl, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: file, signal });
  if (!put.ok) throw new Error(`Upload failed (${put.status}) — the storage rejected the file`);
  return writeJson(`/api/admin/verification/documents/${encodeURIComponent(presign.mediaId)}/confirm`, { body: {}, signal });
}

export async function addKycComment(orgId, comment, { signal } = {}) {
  return writeJson(`/api/admin/verification/organizations/${encodeURIComponent(orgId)}/comments`, { body: { comment }, signal });
}

// ─── Funding — the platform review queue (organizer funding applications) ───────────
// The organizer applies from their dashboard; the platform reviews here, approves + sets
// the advance amount (which becomes a standing debit recovered from their event payouts —
// computeOrgWallet derives it, no ledger table), or rejects with a note.
export async function loadFundingApplications({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/funding/applications/admin', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Funding queue unavailable'}`);
  const rows = (r.payload?.data || []).map((a) => ({
    id: a.id,
    org: firstPresent(a.organizationName, 'Organization'),
    orgId: a.organizationId,
    eventId: firstPresent(a.eventId, ''),
    amount: number(a.amount),
    currency: firstPresent(a.currency, 'UGX'),
    purpose: firstPresent(a.purpose, ''),
    status: firstPresent(a.status, 'requested'),
    comment: firstPresent(a.reviewerComment, ''),
    createdAt: a.createdAt,
  }));
  return { rows, total: rows.length };
}

// The credit picture an admin needs to decide a funding advance — the org's track record,
// current wallet, outstanding exposure, recovery source, and a recommended amount. Derived
// server-side from computeOrgWallet + sales (the source of truth), not assembled client-side.
export async function loadFundingReview(id, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/funding/applications/${encodeURIComponent(id)}/review`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Credit review unavailable'}`);
  return r.payload?.data || null;
}

export async function reviewFundingApplication(id, { status, amount, reviewerComment } = {}, { signal } = {}) {
  return writeJson(`/api/funding/applications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: {
      ...(status !== undefined ? { status } : {}),
      ...(amount !== undefined ? { amount } : {}),
      ...(reviewerComment !== undefined ? { reviewerComment } : {}),
    },
    signal,
  });
}

// Org metadata is stored inconsistently: sometimes a real object, sometimes a
// JSON string, sometimes null. Normalize to an object so consumers (Profile tab)
// can read website/email/phone/socials/bio uniformly.
function parseOrgMetadata(meta) {
  if (!meta) return null;
  if (typeof meta === 'string') { try { return JSON.parse(meta); } catch { return null; } }
  return typeof meta === 'object' ? meta : null;
}

export async function loadOrganizerDetails(orgId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/organizations/${encodeURIComponent(orgId)}/details`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Organizer details unavailable'}`);
  const d = r.payload?.data || r.payload || {};
  const org = d.organization || {};
  return {
    id: firstPresent(org.id, orgId), name: (firstPresent(org.name, 'Organizer') || '').trim(),
    slug: firstPresent(org.slug, ''), logo: firstPresent(org.logo, ''), createdAt: firstPresent(org.createdAt, null),
    metadata: parseOrgMetadata(org.metadata),
    members: (d.members || []).map((m) => ({
      id: firstPresent(m.id, m.userId), userId: m.userId, name: firstPresent(m.name, m.email, 'Member'),
      email: firstPresent(m.email, ''), role: firstPresent(m.role, 'member'), image: firstPresent(m.image, ''),
      // The member's phone — carried because an organization's usable contact details ARE its
      // people. The org.metadata contact fields are empty on every organization on the platform
      // (0 of 209 have metadata.phone), so this is the only number an operator can actually call.
      phone: firstPresent(m.phoneNumber, m.phone, ''),
      phoneVerified: m.phoneNumberVerified === true,
      emailVerified: m.emailVerified === true, joinedAt: firstPresent(m.joinedAt, null),
      // EVENT-SCOPED MEMBERSHIP. null = the whole organisation (everyone, until an event-scoped
      // invite is sent). Surfaced because a confined member looks identical to a full-access one
      // in every list — and "who can see the payouts" is exactly the question this console exists
      // to answer. An access restriction nobody can see is an access restriction nobody audits.
      eventId: firstPresent(m.eventId, null),
      eventTitle: firstPresent(m.eventTitle, null),
    })),
    invitations: (d.invitations || []).map((i) => ({ id: i.id, email: firstPresent(i.email, ''), role: firstPresent(i.role, 'member'), status: firstPresent(i.status, 'pending'), expiresAt: firstPresent(i.expiresAt, null),
      // Carried on the invitation too, so the scope is visible while it is still pending — the
      // window in which it can be cancelled, which is the only window in which it is cheap to fix.
      eventId: firstPresent(i.eventId, null), eventTitle: firstPresent(i.eventTitle, null) })),
    events: (d.events || []).map((e) => ({ id: e.id, title: firstPresent(e.title, e.name, 'Event'), slug: firstPresent(e.slug, ''), status: firstPresent(e.status, ''), startDate: firstPresent(e.date, e.startDate, e.eventDate, null) })), // the event's own date (`date` on /details); never when it was created
    stats: d.stats || {},
  };
}

// --- Team management (the operator OWNS who belongs to an org) ---------------
// The org detail shows members; making them real means the operator can add,
// re-role, and remove them. Backed by /api/organizations/{add,update,remove}-member
// (the super-admin endpoints that act on an arbitrary org), with email→userId
// resolved via the users directory for "add existing user".

// Look up real platform users by NAME or EMAIL (to add one to an org). Uses
// the better-auth admin listUsers endpoint — the only platform-wide user
// search. The custom /api/users route is org-scoped (it scopes to
// session.activeOrganizationId members), which would only ever return users
// already in the active org — defeating the "find someone to invite" use case.
// Better-auth's admin endpoint requires the caller to have `user.role`
// containing "admin"; that's bridged for SUPER_ADMIN_EMAILS users by the
// databaseHooks in tkt-backend/src/utils/auth.ts + the 20260706 migration.
//
// Searches BOTH fields in parallel and de-dupes by id, because the better-auth
// admin endpoint only accepts ONE searchField per call. The modal's prompt is
// "Search name or email" so the operator types whatever they remember — typing
// "thimba" should find "Eric <thimba@mookh.africa>" via the email field, and
// typing "Eric" should find the same row via the name field. A single-field
// search misses half the cases. Name-matches sort first (likely intent), then
// email-matches, capped at 8 rows total.
export async function searchUsersByEmail(query, { signal } = {}) {
  const q = (query || '').trim();
  if (q.length < 2) return [];
  const params = (field) => new URLSearchParams({
    searchField: field,
    searchValue: q,
    limit: '8',
    sortBy: 'name',
    sortDirection: 'asc',
  });
  const headers = { Accept: 'application/json', 'x-org-scope': 'all' };
  const fetchField = (field) => fetch(`${API_BASE}${ADMIN_BASE}/list-users?${params(field).toString()}`, {
    method: 'GET', signal, credentials: 'include', headers,
  }).then(async (r) => {
    if (!r.ok) {
      const body = await r.text().catch(() => '');
      throw new Error(`${r.status} ${body || r.statusText || 'User lookup failed'}`);
    }
    const payload = await r.json().catch(() => null);
    return payload?.users || [];
  });
  // Fan out, surface the first error, and merge by id. Better-auth's
  // contains-match handles partials ("thimba" → "thimba@mookh.africa") so we
  // don't need a `searchOperator` override.
  const [byName, byEmail] = await Promise.all([
    fetchField('name').catch((e) => { throw e; }),
    fetchField('email').catch((e) => { throw e; }),
  ]);
  const seen = new Set();
  const merged = [];
  // Name-matches first (operator's more likely intent), then email-matches
  // that aren't already in the name set.
  for (const u of [...byName, ...byEmail]) {
    if (!u?.id || seen.has(u.id)) continue;
    seen.add(u.id);
    merged.push(u);
  }
  return merged.slice(0, 8).map((u) => ({
    id: u.id,
    name: firstPresent(u.name, u.email, 'User'),
    email: firstPresent(u.email, ''),
    image: firstPresent(u.image, ''),
    emailVerified: u.emailVerified === true,
  }));
}

export async function addOrganizationMember({ organizationId, userId, role = 'member' }, { signal } = {}) {
  if (!organizationId || !userId) throw new Error('organizationId and userId are required');
  return writeJson('/api/organizations/add-member', { body: { organizationId, userId, role }, signal });
}

// Invite someone WITHOUT a platform account yet to join an organization, via the
// SAME better-auth SDK call tkt-frontend's admin invite flow uses
// (authClient.organization.inviteMember) — not a custom backend route. This works
// for an arbitrary org here (not just the caller's own) because a platform admin is a
// real `admin`-role member of every org, so the SDK's own "caller must already be a
// member" check is satisfied for real, not bypassed.
//
// THAT WAS ASSERTED HERE AS FACT AND WAS FALSE. It credited auth.ts's
// afterCreateOrganization hook, which only covered orgs created after the hook existed.
// Measured on prod 2026-09-05: the signed-in super admin was a member of 50 of 112
// organizations, so this call was FAILING on 62 of them with
// USER_IS_NOT_A_MEMBER_OF_THE_ORGANIZATION — inviting a colleague to an organizer simply
// did not work on most of the platform, and nothing said so.
// The invariant is now maintained and asserted server-side
// (services/platformAdminMembership + platformAdminIsMemberOfEveryOrg.check.ts). This
// call is correct exactly as far as that check is green.
//
// TWO KINDS (tkt-backend utils/auth inviteLinkOriginFor): an invitation to the organizer, whose email
// link opens its organizer dashboard (dashboard.<domain>), or, with `platformScope: 'all'`, an
// invitation to the back office itself: platform staff, a member of every organization at `role` on
// acceptance, accepting here (components/AcceptInvitationPage). Only a super-admin may send the
// second; the backend refuses it from anyone else.
export async function inviteOrganizationMember({ organizationId, email, role = 'member', platformScope } = {}) {
  const e = (email || '').trim();
  if (!organizationId || !/\S+@\S+\.\S+/.test(e)) throw new Error('organizationId and a valid email are required');
  const { data, error } = await authClient.organization.inviteMember({ email: e, role, organizationId, ...(platformScope === 'all' ? { platformScope: 'all' } : {}) });
  if (error) throw new Error(error.message || error.statusText || `${error.status || ''} could not invite member`.trim());
  return data;
}

// Create a REAL platform user — EXACTLY the tkt-frontend contract: the better-auth client helper
// authClient.admin.createUser (lib/authClient.js), which calls better-auth's /admin/create-user.
// No invitation/email is sent; the account exists immediately and the person signs in with an email
// OTP. `password` is a required field of that API, so we set a strong random one they never use.
// `role` is the platform role: 'user' or 'admin'. Only a real better-auth admin may call this.
export async function createPlatformUser({ name, email, role = 'user' } = {}) {
  const e = (email || '').trim();
  if (!name?.trim() || !/\S+@\S+\.\S+/.test(e)) throw new Error('name and a valid email are required');
  // Math.random() is not cryptographically secure — predictable in some engines, and this is a
  // real account's password (never TYPED by the person, who signs in via email OTP instead, but
  // still a live credential better-auth would accept for password sign-in if ever exercised).
  // crypto.getRandomValues is the Web Crypto API, available in every browser this app runs in.
  const randomB64 = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16)))).replace(/[+/=]/g, '');
  const password = `Tkt-${randomB64.slice(0, 10)}-${randomB64.slice(10, 16).toUpperCase()}9!`;
  const { data, error } = await authClient.admin.createUser({ email: e, name: name.trim(), password, role });
  // A 401 here is a dead session, not a permissions problem (better-auth answers a real
  // admin who lacks the permission with 403). authClient's global onError already raised
  // the "Session expired" toast — nothing call-site-specific to do.
  if (error) throw new Error(error.message || error.statusText || `${error.status || ''} could not create user`.trim());
  return data?.user || data;
}

/**
 * CREATE AN ORGANIZATION — better-auth's own SDK, exactly like every other auth operation here.
 *
 * `authClient.organization.create` + the existing `addOrganizationMember`. No bespoke backend
 * route: this used to POST to a hand-written `/api/organizations` that re-implemented, worse,
 * things better-auth already does — the super-admin gate (auth.ts's `allowUserToCreateOrganization`
 * enforces it on the plugin's own endpoint), the owner's Member row, and `afterCreateOrganization`.
 *
 * The reason that route existed was a requirement nobody asked for. Its comment argued
 * `organization.create` was "wrong for a console creating one FOR someone" because it makes the
 * CALLER the owner — but the caller is a platform super admin, who is added to every organization
 * anyway, so caller-as-owner was never a problem to solve. The organizer is then added as an
 * `owner` member in their own right, which is one ordinary call and the shape better-auth is built
 * around. Two owners is a normal state, not a workaround.
 *
 * SLUGS ARE PROBED, NOT ASSUMED. Two real organizations may legitimately share a name and the
 * second still has to exist, so `checkSlug` (better-auth's own) picks a free one first rather than
 * letting the create fail on a unique constraint.
 */
export async function createOrganization({ name, slug, logo, ownerUserId, ownerEmail, metadata } = {}, { signal } = {}) {
  if (!name?.trim()) throw new Error('An organization name is required');
  // NO OWNER IS NOT NO OWNER. better-auth's `organization.create` makes the CALLER an owner
  // member of what it creates, and auth.ts's afterCreateOrganization hook adds every super
  // admin as an admin on top — so the organization this mints is always administrable. The
  // `ownerUserId` below is for an owner who is SOMEONE ELSE: the organizer themselves, once
  // the platform knows their email. This used to throw without one, which made "the club gave
  // us a name but not yet an address" unrecordable — and an organizer you cannot write down
  // is one somebody keeps in a WhatsApp thread instead.

  const base = (slug || name).toLowerCase().normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 100) || 'organization';
  let chosen = base;
  for (let n = 2; n <= 50; n++) {
    // A TAKEN SLUG COMES BACK AS AN ERROR, NOT `status: false`. better-auth's check-slug returns
    // `{ status: true }` when free and THROWS `BAD_REQUEST "slug is taken"` otherwise (verified in
    // plugins/organization/routes/crud-org), which the client surfaces as `{ data: null, error }`.
    // So "free" is the only case with data — do not rewrite this as `if (!data.status)`.
    const { data } = await authClient.organization.checkSlug({ slug: chosen });
    if (data?.status) break;
    chosen = `${base}-${n}`;
    if (n === 50) throw new Error('Could not find a free slug — pass one explicitly');
  }

  const { data, error } = await authClient.organization.create({
    name: name.trim(),
    slug: chosen,
    ...(logo?.trim() ? { logo: logo.trim() } : {}),
    ...(metadata ? { metadata } : {}),
  });
  if (error) throw new Error(error.message || error.statusText || `${error.status || ''} could not create organization`.trim());
  const organization = data?.organization || data;
  const organizationId = organization?.id;
  if (!organizationId) throw new Error('The organization was created but returned no id');

  // THE ORGANIZER OWNS THEIR OWN BUSINESS TOO. Not fatal if it fails — the organization exists and
  // is administrable by platform staff either way, so this reports rather than unwinds a real row.
  if (ownerUserId) {
    try {
      await addOrganizationMember({ organizationId, userId: ownerUserId, role: 'owner' }, { signal });
    } catch (e) {
      organization.ownerAddError = e?.message || 'could not add the organizer as an owner';
    }
  }
  return organization;
}

/**
 * PROVISION AN ORGANIZER — the whole act, in order, reporting what it actually did.
 *
 * TWO DOORS, ONE ACT. An organizer arrives either through an approved event request or through the
 * "Onboard organizer" wizard, and both need the same three things to become true: the person has an
 * account, an organization exists, and THEY own it. Onboarding left the organization owned by the
 * admin who clicked and the organizer off it entirely — its own server comment admits the plan was
 * to "invite the real primary contact onto it afterward", which nothing ever prompted. The problem
 * was never that staff had access (they are admin members of every org by design); it was that the
 * person whose business it is had none. One function, so neither door can drift from the other.
 *
 * "Approve" used to mean `status = 'resolved'` and nothing else. But an event request is somebody
 * saying "I want to run an event with you", and the doc for the public form states the premise
 * plainly: "The person filling it in has, by definition, never had an account here — that is what
 * they are asking for." So approving it and creating nothing leaves the actual work — an account,
 * an organization, their brand — to be re-typed by hand somewhere else, from details the form
 * never captured.
 *
 * TWO WRITES THAT CAN EACH ALREADY BE DONE. An operator approves, the org create fails, they fix
 * the name and approve again — and a second account for the same person must not appear. So the
 * user step is find-or-create (by email, the identity better-auth itself keys on), and the caller
 * is told which of the two happened rather than being left to guess from a success toast.
 *
 * IT RETURNS A REPORT, NOT A BOOLEAN. Each step says what it did, so a partial run — account made,
 * organization refused — is legible instead of being flattened into "failed" over a person who now
 * exists.
 */
export async function provisionOrganizer({ name, email, organizationName, organizationLogo, metadata, requestId } = {}) {
  const e = (email || '').trim();
  if (!/\S+@\S+\.\S+/.test(e)) throw new Error('A valid email is required to create the account');
  if (!organizationName?.trim()) throw new Error('An organization name is required');

  // FIND FIRST. `listUsers` is the console's existing reader; an exact email match is the same
  // identity better-auth enforces uniqueness on, so this cannot create a duplicate account.
  let user = null;
  let userCreated = false;
  try {
    // `searchField`/`searchValue` is this reader's real contract, and it returns `{ users }`,
    // not `{ rows }` — better-auth's own shape, kept verbatim by listUsers.
    const found = await listUsers({ searchField: 'email', searchValue: e, limit: 5 });
    user = (found?.users || []).find((u) => String(u.email || '').toLowerCase() === e.toLowerCase()) || null;
  } catch { /* a failed lookup must not block the create; the create is itself unique-constrained */ }
  if (!user) {
    user = await createPlatformUser({ name: name || e, email: e, role: 'user' });
    userCreated = true;
  }
  const ownerUserId = user?.id || user?.userId || null;
  if (!ownerUserId) throw new Error('The account was resolved but returned no id — cannot own an organization');

  const organization = await createOrganization({
    name: organizationName,
    logo: organizationLogo || undefined,
    ownerUserId,
    metadata: metadata || undefined,
  });

  /**
   * AND TELL THEM. Until this existed, accepting a request built the account and the organization
   * and informed the organizer of neither — verified by driving the console on a live tenant:
   * user, organization and owner membership all created, and zero rows in MessageLog.
   *
   * Sent from here because this is the one function that means "an organizer now exists"; the
   * organization itself is created by better-auth's client, which has no server-side moment to
   * hang it on. The backend owns WHICH dashboard to name (services/organizerWelcome) — TKT's
   * panel.tkt.ug is stated, others derive from the brand's domain.
   *
   * Non-fatal, and reported rather than swallowed: the account is real whether or not the email
   * left, so a mail failure must not read as a provisioning failure — but the caller is told, so
   * nobody assumes a welcome went out that did not.
   */
  let welcome = { sent: false, reason: 'not attempted' };
  try {
    welcome = await writeJson('/api/organizations/welcome', {
      // `requestId` becomes the mail doc's tid — which is what lets the request page read this
      // email's delivery status and HTML back through the existing delivery-status endpoint.
      body: { email: e, name: name || null, organizationName, requestId: requestId || null },
    });
  } catch (err) {
    welcome = { sent: false, reason: err?.message || 'welcome email request failed' };
  }

  return { user, userCreated, organization, welcome };
}

// Update an organization's profile (name/slug/logo/metadata). Uses our direct,
// super-admin-gated PATCH /api/organizations/:id — NOT better-auth's
// organization.update, which requires the caller to be a MEMBER of the org
// (USER_IS_NOT_A_MEMBER_OF_THE_ORGANIZATION) and so can't edit an arbitrary
// organizer from a platform console. logo is a plain string URL.
export async function updateOrganization({ organizationId, data } = {}, { signal } = {}) {
  if (!organizationId) throw new Error('organizationId is required');
  const clean = {};
  ['name', 'slug', 'logo', 'metadata'].forEach((k) => { if (data?.[k] !== undefined) clean[k] = data[k]; });
  const body = await writeJson(`/api/organizations/${encodeURIComponent(organizationId)}`, { method: 'PATCH', body: clean, signal });
  return body?.organization || body;
}

export async function updateOrganizationMemberRole({ organizationId, memberId, role }, { signal } = {}) {
  if (!organizationId || !memberId || !role) throw new Error('organizationId, memberId and role are required');
  return writeJson('/api/organizations/update-member-role', { body: { organizationId, memberId, role }, signal });
}

export async function removeOrganizationMember({ organizationId, memberId }, { signal } = {}) {
  if (!organizationId || !memberId) throw new Error('organizationId and memberId are required');
  return writeJson('/api/organizations/remove-member', { body: { organizationId, memberId }, signal });
}

// Hand the organization to another member. ONE call, not two, because promoting and demoting
// separately leaves a moment with two owners or none depending on the order they land in — the
// backend does both in one transaction. It is also the only way out of the last-owner lock: the
// role dropdown and Remove are disabled on a sole owner precisely so the org cannot be stranded,
// and that guard is a dead end until somebody else can be made owner.
//
// `previousOwner` says what happens to the outgoing one: 'admin' (the default, and the reversible
// answer), 'member', or 'remove'. Banning the account is deliberately NOT here — that is
// platform-wide and lives on Users and Customers, where its blast radius is visible.
export async function transferOrganizationOwnership(
  { organizationId, toMemberId, previousOwner = 'admin' }, { signal } = {},
) {
  if (!organizationId || !toMemberId) throw new Error('organizationId and toMemberId are required');
  return writeJson('/api/organizations/transfer-ownership', {
    body: { organizationId, toMemberId, previousOwner }, signal,
  });
}

// --- Seat-level inventory (venue-booking) ----------------------------------
// /api/venue-booking/events/:eventId/venue-summary → venue + sections;
// /api/venue-booking/sections/:sectionId?eventId= → rows + (assigned) seats.

export async function loadVenueSummary(eventId, { slotId, signal } = {}) {
  const qs = slotId ? `?slotId=${encodeURIComponent(slotId)}` : '';
  const r = await fetchBackOfficeJson(`/api/venue-booking/events/${encodeURIComponent(eventId)}/venue-summary${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Venue inventory unavailable'}`);
  const v = r.payload?.data || r.payload || {};
  return {
    id: v.id, name: firstPresent(v.name, 'Venue'),
    capacity: number(v.capacity), sold: number(v.sold), available: number(v.available), reserved: number(v.reserved),
    sections: (v.sections || []).map((s) => ({
      id: s.id, name: firstPresent(s.name, 'Section'),
      capacity: number(s.capacity), sold: number(s.sold), available: number(s.available), reserved: number(s.reserved),
      // Tickets for this section already through a gate — ScanItems.usedAt.
      admitted: number(s.admitted),
      fillPercentage: number(s.fillPercentage), color: s.color || null,
      hasRows: Array.isArray(s.rows) && s.rows.length > 0,
      rows: Array.isArray(s.rows) ? s.rows : [],
      perRow: Array.isArray(s.rows) && s.rows.length > 0 ? Math.max(...s.rows.map(r => Number(r.capacity) || 0)) : 0,
      entranceName: s.entranceName || s.entrance?.name || null,
      ticketType: s.ticketType || s.ticketTypeName || null,
    })),
    entrances: (v.entrances || []).map((e) => ({ id: e.id, name: firstPresent(e.name, 'Entrance'), type: e.type })),
  };
}

// Real per-seat status for ONE section, row-paginated (bounded — never the whole
// stadium): /api/venue-booking/sections/:id/status → rows → seats with live
// status. Feeds the seat grid a slice of rows at a time.
/**
 * MOVE A HOLDER TO ANOTHER SEAT — one call, one transaction, EventSeatReservation only.
 *
 * The grid already knows everything this needs: each seat arrives with its status, its orderId
 * and its ticketTypeId, straight off the reservation rows. So the caller does not look anything
 * up to decide what is legal — same ticketTypeId, destination available — and the backend
 * re-asserts both under a row lock, because a UI check is a courtesy and not a guarantee.
 */
/**
 * WHERE COULD N PEOPLE SIT TOGETHER — the read primitive.
 *
 * `freeing` names seats the caller is about to give up, so they count as free: a party holding
 * I-4 and I-5 can be seated at I-3/4/5 by moving ONE person, and a search over strictly-available
 * seats cannot see that run at all. Each run comes back as every window of n with `keeps` — how
 * many of those seats the party already occupies.
 *
 * `entranceId` holds the search to one gate. Their pass already names a door, and a pass sitting
 * in someone's Wallet does not update, so crossing gates has to be a decision rather than a result.
 */
export async function findSeatsTogether({ eventId, n, ticketTypeId, entranceId, slotId, freeing = [], signal } = {}) {
  const qs = new URLSearchParams({ n: String(n), ticketTypeId });
  if (entranceId) qs.set('entranceId', entranceId);
  if (slotId) qs.set('slotId', slotId);
  if (freeing.length) qs.set('freeing', freeing.join(','));
  const r = await fetchBackOfficeJson(`/api/v2/seat-manager/event/${encodeURIComponent(eventId)}/contiguous?${qs}`, { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} could not look for seats together`);
  return r.payload;
}

/**
 * SEAT A PARTY TOGETHER — composed from the two primitives, not a third endpoint.
 *
 *   find where they fit  →  pick the window that moves fewest of them  →  move them, atomically
 *
 * Nothing here decides what is legal. Same tier, gate present, destination free and all-or-nothing
 * belong to the reassign primitive and are enforced there, once, for every caller.
 */
export async function seatPartyTogether({ eventId, seats, entranceId, slotId, reason, dryRun = false } = {}) {
  const ours = seats.map((s) => s.seatId);
  const found = await findSeatsTogether({
    eventId, n: seats.length, ticketTypeId: seats[0].ticketTypeId,
    entranceId, slotId, freeing: ours,
  });
  const windows = (found.data || []).flatMap((run) => (run.windows || []).map((w) => ({ ...w, run })));
  if (!windows.length) return { found: false };

  // Fewest movers first, then the smallest run — so three people take a 4-seat pocket instead of
  // carving the middle out of a block a larger group will need.
  windows.sort((a, b) => (b.keeps - a.keeps) || (a.run.runLength - b.run.runLength));
  const best = windows[0];

  const target = new Set(best.seatIds);
  const movers = seats.filter((s) => !target.has(s.seatId));
  const free = best.seatIds.filter((id) => !ours.includes(id));
  const moves = movers.map((m, i) => ({ fromSeatId: m.seatId, toSeatId: free[i] }));

  const plan = {
    section: best.run.section, row: best.run.row, seats: best.seats,
    moving: moves.length, staying: seats.length - moves.length,
  };
  if (!moves.length) return { found: true, alreadyTogether: true, plan };
  if (dryRun) return { found: true, plan, moves };

  const result = await reassignSeat({ eventId, moves, slotId, reason: reason || 'Seated together on request' });
  return { found: true, plan, moved: result?.moved ?? moves.length };
}

/**
 * SET THE GATE A SECTION'S HOLDERS WALK TO, for THIS event.
 *
 * It is a scoped edit to the event's ONE seat mapping (`EventSeatingPlan.config.sections[]`),
 * applied to the seats in the same transaction — not a separate way to write a gate. Writing the
 * seats alone would be reverted by the next mapping save; writing the mapping alone would tell
 * nobody. From the seats, `trg_scanitem_gate_follows_seat` carries the gate onto every ticket
 * already issued, so the people affected are told without a second write.
 *
 * `sectionIds` are PHYSICAL Section ids — the same ones this panel already uses to load a
 * section's seats. The plan-side ids the venue seat-manager holds reach no reservation at all.
 */
export async function setEventSectionGate({ eventId, sectionIds, gateId, slotId, reason } = {}) {
  return writeJson(`/api/v2/seat-manager/event/${encodeURIComponent(eventId)}/section-gate`, {
    body: { sectionIds, gateId, slotId: slotId || null, reason: reason || 'Gate set from the seat map' },
  });
}

export async function reassignSeat({ eventId, fromSeatId, toSeatId, moves, slotId, reason, reasonKey } = {}) {
  return writeJson(`/api/v2/seat-manager/event/${encodeURIComponent(eventId)}/reassign`, {
    // One move is an array of one — the endpoint has a single shape, so this does too.
    body: {
      moves: moves || [{ fromSeatId, toSeatId }],
      slotId: slotId || null,
      reason: reason || 'Reassigned from the seat grid',
      // WHY, IN A FORM THAT CAN BE COUNTED. `auditMove` has always accepted `reasonKey` alongside
      // the free-text reason and nothing has ever sent one, so the only record of why anybody was
      // moved is a sentence. A key is what makes "how often are we moving people off obstructed
      // seats in 104?" a question the audit can answer rather than one you grep for.
      ...(reasonKey ? { reasonKey } : {}),
    },
  });
}

export async function loadSectionSeatStatus(sectionId, eventId, { rowOffset = 0, rowLimit = 20, slotId, signal } = {}) {
  const slotQs = slotId ? `&slotId=${encodeURIComponent(slotId)}` : '';
  const r = await fetchBackOfficeJson(`/api/venue-booking/sections/${encodeURIComponent(sectionId)}/status?eventId=${encodeURIComponent(eventId)}&rowOffset=${rowOffset}&rowLimit=${rowLimit}${slotQs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Seat status unavailable'}`);
  const p = r.payload || {};
  const rows = Object.values(p.rows || {}).map((row) => ({
    id: firstPresent(row.id, row.name),
    name: firstPresent(row.name, 'Row'),
    seats: (row.seats || [])
      .map((s) => ({ id: s.id, number: number(s.number), status: String(s.status || 'available').toLowerCase(), orderId: s.orderId || null, ticketTypeId: s.ticketTypeId || null }))
      .sort((a, b) => a.number - b.number),
  })).sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  // The section's gate FOR THIS EVENT, off its own seats — not the venue's default, which is
  // empty on real data and made a section with a gate read as having none.
  return { sectionId, name: firstPresent(p.name, 'Section'), entranceId: p.entranceId || null, rows, rowOffset, rowLimit, hasMore: Boolean(p.hasMore) };
}

// THE TICKET ON A SEAT — the credential, not the purchase.
//
// The seat drawer could reach the ORDER and never the ticket, which are different things: an order
// is a purchase, a ticket is what admits one person, and only the ticket carries the signature a
// scanner reads. `GET /api/venue-booking/seats/:seatId/ticket` is an exact lookup against the
// unique (seatId, eventId) index, asked once when a seat is opened rather than joined onto the
// section grid, which loads hundreds of seats nobody is inspecting.
//
// Returns null when nothing holds the seat. That is an ANSWER — an unsold seat has no ticket — so
// it is not an error, and the caller renders it as a state.
export async function loadSeatTicket(seatId, eventId, { signal } = {}) {
  if (!seatId || !eventId) return null;
  const r = await fetchBackOfficeJson(`/api/venue-booking/seats/${encodeURIComponent(seatId)}/ticket?eventId=${encodeURIComponent(eventId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Ticket unavailable'}`);
  const t = r.payload?.ticket;
  if (!t) return null;
  return {
    id: t.id || null,
    // The string the QR encodes and the gate resolves. Printed under a code, it identifies
    // something scannable — which the seat's own uuid does not.
    signature: t.signature || null,
    holder: t.holder || null,
    contact: [t.email, t.phone].filter(Boolean).join(' · ') || null,
    orderId: t.orderId || null,
    type: t.type || null,
    scanned: Boolean(t.scanned),
    usedAt: t.usedAt ? formatTime(t.usedAt) : null,
    issuedAt: t.issuedAt ? formatTime(t.issuedAt) : null,
    // Built by the backend, which is the only process that knows which deployment it is.
    verifyUrl: t.verifyUrl || null,
  };
}

// --- Reserved-seating config flow (REAL, mirrors tkt-frontend) --------------
// The real chain: venue → seating-template (sections/rows) → link to event →
// map each section to ticketType+entrance(gate)+capacity → generate the
// EventSeatReservation rows → read inventory. Endpoints + shapes are documented
// in memory `backoffice-venue-seating-flow`. The seat-config POST schema requires
// every section to carry ticketTypeId AND entranceId.

const SEAT_SECTION_TYPES = ['seating', 'standing', 'table', 'vip', 'other'];
const normalizeTemplateSection = (s, i = 0) => ({
  ...s,
  id: s.id,
  name: firstPresent(s.name, `Section ${i + 1}`),
  description: firstPresent(s.description, ''),
  type: SEAT_SECTION_TYPES.includes(s.type) ? s.type : 'seating',
  capacity: number(s.capacity),
  color: firstPresent(s.color, '#1F2937'),
  ticketTypeId: s.ticketTypeId || null,
  entranceId: s.entranceId || null,
  rows: Array.isArray(s.rows) ? s.rows.map((r, j) => ({
    id: r.id, name: firstPresent(r.name, `Row ${j + 1}`), capacity: number(r.capacity), ticketTypeId: r.ticketTypeId || null,
  })) : [],
});

// GET /api/seating-templates?venueId= → { success, data: [SeatingTemplate] }.
// Each template carries `sections` (and a `template.sections` mirror).
export async function loadSeatingTemplates(venueId, { signal } = {}) {
  if (!venueId) return [];
  const r = await fetchBackOfficeJson(`/api/seating-templates?venueId=${encodeURIComponent(venueId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Seating templates unavailable'}`);
  const rows = r.payload?.data || (Array.isArray(r.payload) ? r.payload : []);
  return rows.map((t) => {
    const sections = (Array.isArray(t.sections) && t.sections.length ? t.sections : t.template?.sections) || [];
    return {
      id: t.id, venueId: t.venueId, name: firstPresent(t.name, 'Seating layout'),
      description: firstPresent(t.description, ''), isActive: t.isActive !== false,
      sections: sections.map(normalizeTemplateSection),
      entrances: (t.entrances || t.template?.entrances || []).map((e, i) => ({ id: e.id, name: firstPresent(e.name, `Entrance ${i + 1}`), type: e.type || 'main' })),
    };
  });
}

// POST /api/seating-templates — create (or update, one-per-venue) the venue's
// seating layout. Backend materializes Section/Row/Seat records from the JSON.
// `sections`: [{ name, type, rows:[{ name, capacity }] }]. seatingTemplateSchema
// requires each section to have ≥1 row, each row a capacity (seats in that row).
export function buildSeatingTemplatePayload({ venueId, name, description, sections }) {
  return {
    name: name || 'Seating layout',
    description: description || '',
    venueId,
    isActive: true,
    template: {
      sections: (sections || []).map((s, i) => ({
        name: s.name || `Section ${i + 1}`,
        type: SEAT_SECTION_TYPES.includes(s.type) ? s.type : 'seating',
        displayOrder: i,
        width: 100,
        height: 50,
        rows: (s.rows || []).map((r, j) => ({
          name: r.name || String.fromCharCode(65 + (j % 26)),
          capacity: Math.max(1, Math.round(number(r.capacity))),
          // A row is a named GROUP OF SEATS — a straight run along a tier, or a
          // table people sit around. Only send the table fields when it is one,
          // so a stadium's payload is byte-for-byte what it always was.
          ...(r.kind === 'table' ? {
            kind: 'table',
            shape: r.shape || 'round',
            ...(r.xPosition != null ? { xPosition: Math.round(number(r.xPosition)) } : {}),
            ...(r.yPosition != null ? { yPosition: Math.round(number(r.yPosition)) } : {}),
            ...(r.rotation != null ? { rotation: Math.round(number(r.rotation)) } : {}),
          } : {}),
        })),
      })),
    },
    metadata: { createdBy: 'back-office', lastUpdated: new Date().toISOString() },
  };
}

/**
 * Turn "12 round tables of 4, in a grid" into the rows a seating template wants.
 *
 * A table IS a row, so a floor of tables is just a section whose rows are
 * tables — which is why this produces rows rather than some parallel structure,
 * and why nothing downstream needed a new concept.
 *
 * `arrangement` positions them on the section's floor. The maths matches the
 * backend's engine so a plan laid out here and one generated server-side land in
 * the same places.
 */
export async function createSeatingTemplate(input, { signal } = {}) {
  const body = await writeJson('/api/seating-templates', { body: buildSeatingTemplatePayload(input), signal });
  return body?.data || body?.template || body;
}

// GET /api/event-pages/:eventId/venue → the linked venue, or null when 404.
export async function loadEventVenue(eventId, { signal } = {}) {
  if (!eventId) return null;
  const r = await fetchBackOfficeJson(`/api/event-pages/${encodeURIComponent(eventId)}/venue`, { signal });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Event venue unavailable'}`);
  const v = r.payload?.data || r.payload || null;
  return v && v.id ? { id: v.id, name: firstPresent(v.name, 'Venue'), capacity: number(v.capacity), venueType: v.venueType || null } : null;
}

// PUT /api/events/:eventId/venue { venueId } — link a venue to an event.
// GET /api/entrances/venue/:venueId → the gates for a venue.
export async function loadVenueEntrances(venueId, { signal } = {}) {
  if (!venueId) return [];
  const r = await fetchBackOfficeJson(`/api/entrances/venue/${encodeURIComponent(venueId)}?limit=500`, { signal }); // all of one venue's gates, not the route's page of 10
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Entrances unavailable'}`);
  return list(r.payload).map((e, i) => ({ id: e.id, name: firstPresent(e.name, `Entrance ${i + 1}`), type: e.type || 'main' }));
}

// POST /api/entrances — create a gate for a venue (needed so a section can be
// mapped to an entranceId when the venue has none yet). entranceSchema: name +
// venueId (+ optional type/position).
export async function createVenueEntrance({ venueId, name, type = 'main' }, { signal } = {}) {
  const body = await writeJson('/api/entrances', { body: { venueId, name, type, position: { x: 0, y: 0, width: 40, height: 20 } }, signal });
  const e = body?.entrance || body?.data || body;
  return { id: e.id, name: firstPresent(e.name, name), type: e.type || type };
}

// POST /api/entrances — create a venue gate from the venue-detail AddGateModal.
// The UI's richer vocabulary (code, direction, feedsInto outdoor→indoor maps) has
// no Entrance column, so only the persisted fields are sent. The gate is read back
// via loadVenueWorkspace's GET /api/entrances/venue/:id (v1 family, NOT v2). The
// backend maps type 'Indoor gate'/'internal' → internal, else external, and the
// boolean active → gateStatus enum.
export async function createVenueGate(venueId, form = {}, { signal } = {}) {
  const body = await writeJson('/api/entrances', {
    body: {
      venueId,
      name: form.name,
      type: form.type,
      capacityPerHour: Number(form.capacity) || 0,
      active: form.active !== false,
      ...(form.notes ? { description: form.notes } : {}),
      ...(form.role ? { role: form.role } : {}),
    },
    signal,
  });
  return body?.data || body;
}

// PATCH /api/entrances/gate/:id — edit a gate or flip its active status. Pass only
// the fields to change (e.g. { active:false } for Deactivate).
export async function updateVenueGate(gateId, patch = {}, { signal } = {}) {
  const body = await writeJson(`/api/entrances/gate/${encodeURIComponent(gateId)}`, {
    method: 'PATCH',
    body: patch,
    signal,
  });
  return body?.data || body;
}

// GET /api/sections/venue/:venueId → the venue's sections (with rows).
// Build the exact `seatConfigSchema` body (every section needs ticketTypeId +
// entranceId; rows need id + integer capacity). `sections` is the UI mapping:
// [{ id, ticketTypeId, entranceId, rows:[{ id, capacity, ticketTypeId? }] }].
export function buildSeatConfigPayload({ eventId, seatingTemplateId, name, isActive = true, sections, slotId }) {
  return {
    eventId,
    seatingTemplateId: seatingTemplateId || undefined,
    name: name || 'Event seating',
    isActive,
    // Per-occurrence seating: when a slotId is given the backend writes the plan
    // and its EventSeatReservation rows scoped to that ScheduleSlot (per-slot
    // inventory: same physical seat is independent across occurrences).
    ...(slotId ? { slotId } : {}),
    config: {
      sections: (sections || []).map((s, i) => ({
        id: s.id,
        ticketTypeId: s.ticketTypeId,
        entranceId: s.entranceId,
        // Which section sells first, for this event. Taken from the array's own position so the
        // stored number and the order the config reads in can never disagree.
        displayOrder: i,
        rows: (s.rows || []).map((r) => ({
          id: r.id,
          capacity: Math.max(0, Math.round(number(r.capacity))),
          ...(r.ticketTypeId ? { ticketTypeId: r.ticketTypeId } : {}),
        })),
      })),
    },
  };
}

// POST /api/seat-configs/put — persist the event seating plan (also links venue
// to the event). Surfaces the backend's joined validation messages.
export async function saveSeatConfig(input, { signal } = {}) {
  const r = await writeJson('/api/seat-configs/put', { body: buildSeatConfigPayload(input), signal });
  return r;
}

// GET /api/seats/events/:eventId/seats/put — publish/generate: bulk-creates the
// EventSeatReservation rows for the mapped sections (the "generate inventory" step).
/**
 * GENERATING SEATS IS NOT A PAGE READ, AND THE 20s DEFAULT IS A PAGE READ'S BUDGET.
 *
 * This call materialises one EventSeatReservation per seat — 20,623 of them for a stadium — against
 * a remote database. It is the single longest-running thing this console asks for, and it was
 * inheriting `fetchBackOfficeJson`'s blanket 20s timeout, which aborts the request client-side.
 *
 * Measured on Hoima City Stadium: "Seating saved" then "Generation failed · No response after 20s",
 * with ZERO seat rows written. That failure is worse than a slow wait, because `saveSeatConfig` has
 * already committed the plan with isActive:true — so the event now advertises reserved seating and
 * has no seats to sell, and nothing on screen says so.
 *
 * Ten minutes is not a guess at how long it takes; it is longer than this can plausibly take, which
 * is the only safe kind of ceiling for an operation whose abort leaves inconsistent state.
 */
export async function publishSeatConfig(eventId, { slotId, signal } = {}) {
  const qs = slotId ? `?slotId=${encodeURIComponent(slotId)}` : '';
  const r = await fetchBackOfficeJson(`/api/seats/events/${encodeURIComponent(eventId)}/seats/put${qs}`, { signal, timeoutMs: 600000 });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.payload?.message || r.statusText || 'Failed to generate seats'}`);
  return r.payload;
}

// DELETE /api/seat-configs/:eventId → remove a seating plan. Pass slotId to
// remove only that occurrence's plan; without it removes the event-wide plan
// (slotId IS NULL) — it does NOT wipe every plan for the event.
export async function deleteSeatConfig(eventId, { slotId, signal } = {}) {
  if (!eventId) throw new Error('An event id is required');
  const qs = slotId ? `?slotId=${encodeURIComponent(slotId)}` : '';
  return writeJson(`/api/seat-configs/${encodeURIComponent(eventId)}${qs}`, { method: 'DELETE', signal });
}

// GET /api/seat-configs/:eventId/full → the full persisted seating plan + config.
// Pass slotId to read the per-occurrence plan for a specific ScheduleSlot.
export async function loadSeatConfigFull(eventId, { slotId, signal } = {}) {
  if (!eventId) return null;
  const qs = slotId ? `?slotId=${encodeURIComponent(slotId)}` : '';
  const r = await fetchBackOfficeJson(`/api/seat-configs/${encodeURIComponent(eventId)}/full${qs}`, { signal });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Seat config unavailable'}`);
  return r.payload?.data || r.payload || null;
}

// --- Blocked seats on an event (real /api/holds) ---------------------------
// Taking chosen seats off sale for ONE event is a hold: each seat's row for the event reads 'held' and
// names the hold (the backend refuses /api/blocks with an eventId — a Block is venue-wide, "this isn't
// a seat"). Only seat holds are listed here; a hold of a ticket-type count has no seats to show.
export async function loadEventBlocks(eventId, { signal } = {}) {
  if (!eventId) return [];
  const r = await fetchBackOfficeJson(`/api/holds/event/${encodeURIComponent(eventId)}?limit=100`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Blocked seats unavailable'}`);
  return list(r.payload)
    .filter((h) => !h.ticketTypeId && ['Active', 'Expiring soon'].includes(firstPresent(h.holdStatus, h.status)))
    .map((h) => ({
      id: h.id,
      name: firstPresent(h.name, 'Blocked seats'),
      description: firstPresent(h.reason, h.description, ''),
      seatCount: number(h.affectedSeats),
      createdAt: firstPresent(h.createdAt, null),
    }));
}

export async function createSeatBlock({ eventId, name, seatIds }, { signal } = {}) {
  const body = await writeJson('/api/holds', { body: { eventId, name: name || 'Blocked seats', holdType: 'Manual admin', seatIds }, signal });
  return body?.data || body;
}

// SEATS PICKED ON A MAP → their Seat ids, for this event only, with each seat's state and hold
// (tkt-backend POST /api/event-seat-map/:eventId/seat-ids, read-only). places: [[rowId, seatNumber]].
export async function resolveSeatIds(eventId, places, { signal } = {}) {
  const res = await writeJson(`/api/event-seat-map/${encodeURIComponent(eventId)}/seat-ids`, { body: { places }, signal });
  return res?.seats || [];
}

// Hold (or block) chosen seats on one event: a named Hold of a type, its seats go 'held'. All or
// nothing — the server refuses if any seat isn't available (409, with how many). /api/holds.
export async function holdEventSeats({ eventId, name, holdType, reason, seatIds, endDate, endTime }, { signal } = {}) {
  // endDate / endTime: a Kampala wall clock when the hold lapses and its seats go back on sale.
  const body = await writeJson('/api/holds', { body: { eventId, name, holdType, reason: reason || undefined, seatIds, ...(endDate ? { endDate, endTime: endTime || undefined } : {}) }, signal });
  return body?.data || body?.hold || body;
}

// Give some of a hold's seats back to sale; the hold stays for the rest. /api/holds/:id/release-seats.
export async function releaseHoldSeats(holdId, seatIds, { reason, signal } = {}) {
  return writeJson(`/api/holds/${encodeURIComponent(holdId)}/release-seats`, { body: { seatIds, ...(reason ? { reason } : {}) }, signal });
}

export async function deleteSeatBlock(holdId, { signal } = {}) {
  return writeJson(`/api/holds/${encodeURIComponent(holdId)}`, { method: 'DELETE', signal });
}

// --- Event admissions / attendance -----------------------------------------
// /api/events/:eventId/admissions/stats/cached → live attendance snapshot.

export async function loadAdmissionStats(eventId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/events/${encodeURIComponent(eventId)}/admissions/stats/cached`, { signal });
  // A failed count is not a count of zero — on a gate screen the difference is "nobody came" vs "we can't see".
  if (!r.ok || r.payload?.success === false) throw new Error(`${r.status} ${r.payload?.error || r.payload?.message || r.statusText || 'Admissions unavailable'}`);
  const d = r.payload?.data || r.payload || {};
  return {
    admittedCount: number(d.admittedCount), pendingCount: number(d.pendingCount), totalAttendees: number(d.totalAttendees),
    scanRate: firstPresent(d.scanRate, '0'), avgTimeBetweenScans: firstPresent(d.avgTimeBetweenScans, '0'),
    lastUpdated: firstPresent(d.lastUpdated, null),
    byTicketType: (d.admissionsByTicketType || []).map((t) => ({
      id: t.ticketTypeId, name: firstPresent(t.ticketTypeName, 'Ticket type'),
      total: number(t.totalTickets), admitted: number(t.admittedCount), pending: number(t.pendingCount), lastScan: firstPresent(t.lastScanTime, null),
    })),
    // Issued/admitted per schedule slot (occurrence), counted on the server like the two breakdowns around it.
    byOccurrence: (d.admissionsByOccurrence || []).map((o) => ({ slotId: o.slotId, issued: number(o.issued), admitted: number(o.admitted) })),
    byGate: (d.formattedGateStats || []).map((g) => ({
      id: g.entranceId, name: firstPresent(g.entranceName, 'Gate'),
      total: number(g.totalAttendees), admitted: number(g.admittedCount), pending: number(g.pendingCount), lastScan: firstPresent(g.lastScanTime, null),
    })),
    devices: (d.scanDevices || []).map((dev) => ({
      id: dev.id, name: firstPresent(dev.name, 'Device'),
      scanCount: number(dev.scanCount), scansLast10Min: number(dev.scansLast10Min), totalSynced: number(dev.totalSynced), lastScan: firstPresent(dev.lastScanTime, null),
    })),
  };
}

// --- Sales-channel attribution ---------------------------------------------
// Backed by /api/orders/channel-attribution — aggregates successful orders by
// misc.source (web/ussd/mcp/dashboard/public/api…) and misc.channel.

const SOURCE_LABELS = {
  direct: 'Direct / Web', web: 'Web', public: 'Public site', dashboard: 'Dashboard (staff)',
  api: 'API', ussd: 'USSD', mcp: 'AI MCP', ai_mcp: 'AI MCP', 'csv-import': 'CSV import',
  'plan-generated': 'Plan-generated', 'box-office': 'Box office',
  // Added 2026-07-01 alongside the producer-tagging fix (tkt-backend) that made
  // these values start actually appearing instead of falling into 'direct'.
  // Just 'App' — `misc.source = 'app'` says an order came from AN app, not which one. One
  // backend serves several (MoMo Ticketing on moments.ug, and whatever ships next), so naming
  // a specific app here would claim more than the value carries. The WHICH is `origin`, and
  // the order detail's Provenance panel is where the two are read together.
  app: 'App',
  whatsapp: 'WhatsApp', 'mpesa-qr': 'M-Pesa QR', subscription: 'Subscription renewal',
  'dashboard-bulk': 'Dashboard (bulk)', 'dashboard-comp': 'Complimentary (dashboard)',
  // Imported from mookh v3 rather than sold through any channel of ours. The order DID happen on
  // v3's web checkout — v3 records `metadata.orderSource` = "online" on all 34,627 of them, one
  // constant with no distinction to carry — so the honest label is where it came FROM, not a
  // channel we invented for it.
  'mookh-v3-migration': 'Migration', 'checkout-enter': 'Web (checkout)',
  'mookh-discover-ai': 'Discover AI',
};

export async function loadChannelAttribution({ signal, eventId, from = null, days } = {}) {
  const params = new URLSearchParams();
  if (eventId) params.set('eventId', eventId);
  // Prefer an absolute `from` — a relative day count is a rolling 24h-multiple window and
  // an unchanging cache key (see lib/dateRange.windowFrom). `days` still carries the
  // literal 'all' sentinel the endpoint expects for all-time.
  if (from) params.set('from', from);
  else if (days) params.set('days', days);
  const qs = params.toString() ? `?${params}` : '';
  const r = await fetchBackOfficeJson(`/api/orders/channel-attribution${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Attribution unavailable'}`);
  const d = r.payload?.data || {};
  const sources = (d.sources || []).map((s) => ({
    ...s,
    label: SOURCE_LABELS[s.source] || titleCase(String(s.source).replace(/[-_]/g, ' ')),
    revenue: number(s.revenue),
  }));
  return { sources, totals: d.totals || { orderCount: 0, ticketCount: 0, revenue: 0, paidCount: 0, compCount: 0 }, rows: d.rows || [], windowDays: d.windowDays, currency: d.currency || null,
    revenueByCurrency: Array.isArray(d.revenueByCurrency) ? d.revenueByCurrency.map((r) => ({ currency: r.currency || null, revenue: number(r.revenue), orders: number(r.orders) })) : [] };
}

// --- Distribution channels -------------------------------------------------
// Backed by /api/distribution-channels. These are ticket DELIVERY-ROUTING
// channels (channelType, routing matrix), not retail channels with revenue —
// sales/revenue attribution isn't modelled, shown as "not tracked".

// A DistributionChannel routes WHAT a channelType (email/whatsapp/print/app) delivers:
// which ticket categories/products it carries + which designs + sender identity, in a
// scope. The two real operator actions are: assign ticketCategories + assign a design.
function normalizeDistributionChannel(row = {}, i = 0) {
  return {
    ...row,
    id: String(firstPresent(row.id, `channel-${i + 1}`)),
    name: firstPresent(row.name, `Channel ${i + 1}`),
    channelType: firstPresent(row.channelType, 'email'),
    status: titleCase(firstPresent(row.status, 'draft')),
    statusRaw: firstPresent(row.status, 'draft'),
    scope: firstPresent(row.scope, 'global'),
    priority: number(firstPresent(row.priority, 1)),
    ticketCategories: Array.isArray(row.ticketCategories) ? row.ticketCategories : [],
    contingents: Array.isArray(row.contingents) ? row.contingents : [],
    productTypes: Array.isArray(row.productTypes) ? row.productTypes : [],
    salesChannels: Array.isArray(row.salesChannels) ? row.salesChannels : [],
    eventIds: Array.isArray(row.eventIds) ? row.eventIds : [],
    ticketDesign: firstPresent(row.ticketDesign, ''),
    accreditationDesign: firstPresent(row.accreditationDesign, ''),
    parkingPassDesign: firstPresent(row.parkingPassDesign, ''),
    emailStructureTemplate: firstPresent(row.emailStructureTemplate, ''),
    senderDomain: firstPresent(row.senderDomain, ''),
    fromName: firstPresent(row.fromName, ''),
    replyTo: firstPresent(row.replyTo, ''),
    eventCount: Array.isArray(row.eventIds) ? row.eventIds.length : 0,
    raw: row,
  };
}

// --- Affiliates (event sales partners — REAL attribution) -----------------
// Backed by /api/affiliates. Distinct from Affiliations (thin name-only) and
// from box-office Operators. Buy-side stamps misc.source = "affiliate:<code>"
// from ?aff= / ?agent= on first touch (tkt-frontend lib/attribution.ts).

const normalizeAffiliate = (row, i = 0) => ({
  ...row,
  id: String(firstPresent(row.id, `affiliate-${i + 1}`)),
  name: firstPresent(row.name, 'Affiliate'),
  code: firstPresent(row.code, ''),
  eventId: String(firstPresent(row.eventId, '')),
  contactName: firstPresent(row.contactName, ''),
  contactEmail: firstPresent(row.contactEmail, ''),
  contactPhone: firstPresent(row.contactPhone, ''),
  status: firstPresent(row.status, 'active'),
  createdAt: firstPresent(row.createdAt, null),
  createdBy: firstPresent(row.createdBy, ''),
  raw: row,
});

export async function loadEventAffiliates(eventId, { signal } = {}) {
  if (!eventId) return { rows: [] };
  const r = await fetchBackOfficeJson(`/api/affiliates/event/${encodeURIComponent(eventId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Affiliates unavailable'}`);
  return { rows: list(r.payload).map(normalizeAffiliate) };
}

export async function createAffiliate({ name, eventId, code, contactName, contactEmail, contactPhone, status = 'active' } = {}, { signal } = {}) {
  const body = {
    name: String(name || '').trim(),
    eventId,
    ...(code ? { code: String(code).trim() } : {}),
    ...(contactName ? { contactName: String(contactName).trim() } : {}),
    ...(contactEmail ? { contactEmail: String(contactEmail).trim() } : {}),
    ...(contactPhone ? { contactPhone: String(contactPhone).trim() } : {}),
    status,
  };
  const payload = await writeJson('/api/affiliates', { body, signal });
  return normalizeAffiliate(payload?.affiliate || payload?.data || payload, 0);
}

export async function updateAffiliate(affiliateId, patch = {}, { signal } = {}) {
  const payload = await writeJson(`/api/affiliates/${encodeURIComponent(affiliateId)}`, {
    method: 'PATCH', body: patch, signal,
  });
  return normalizeAffiliate(payload?.affiliate || payload?.data || payload, 0);
}

export async function deleteAffiliate(affiliateId, { signal } = {}) {
  return writeJson(`/api/affiliates/${encodeURIComponent(affiliateId)}`, { method: 'DELETE', signal });
}

export async function loadAffiliateSales(affiliateId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/affiliates/${encodeURIComponent(affiliateId)}/sales`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Affiliate sales unavailable'}`);
  const sales = r.payload?.sales || r.payload?.data || r.payload || {};
  return {
    affiliate: r.payload?.affiliate || null,
    summary: Array.isArray(sales.summary) ? sales.summary : [],
    totals: sales.totals || { totalTicketsSold: 0, totalRevenue: 0, totalNet: 0, totalUniqueOrders: 0, currency: null },
    pending: sales.pending || { orders: 0, revenue: 0 },
  };
}

export async function generateAffiliateReportLink(affiliateId, { signal } = {}) {
  const payload = await writeJson(`/api/affiliates/${encodeURIComponent(affiliateId)}/generate-link`, { body: {}, signal });
  const data = payload?.data || payload || {};
  return { token: data.token || null, publicUrl: data.publicUrl || null };
}

// Tracking URL buyers open — first-touch ?aff= becomes misc.source=affiliate:<code>.
// Host is a convention (tkt buy-side); operators can re-host the same ?aff= on any
// domain that runs tkt-frontend attribution.
export function buildAffiliateTrackingUrl({ slug, code, base } = {}) {
  const host = (base || (typeof window !== 'undefined' && window.__TKT_BUY_BASE) || getConsumerUrl()).replace(/\/$/, '');
  const path = slug ? `/event/${encodeURIComponent(slug)}` : '/';
  const q = code ? `?aff=${encodeURIComponent(code)}` : '';
  return `${host}${path}${q}`;
}

// --- Affiliations (legacy thin name list — NOT the sales-partner model) ---
// Backed by /api/affiliations. The backend model is thin (id, name, eventId,
// createdBy) — no commission/sales tracking — so the UI shows those as untracked.

// Real per-integration sync history — /api/access-control-integrations/:id/sync-logs.
// A page of runs, newest first: { rows, hasMore } (the route's look-ahead).
export async function loadAcSyncLogs(id, { signal, page = 1, limit = LIST_PAGE_SIZE } = {}) {
  const path = `/api/access-control-integrations/${encodeURIComponent(id)}/sync-logs?limit=${limit}&page=${page}`;
  const response = await fetchBackOfficeJson(path, { signal });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.payload?.error || response.statusText || 'Sync logs unavailable'}`);
  }
  return { rows: list(response.payload).map(normalizeAcRun), hasMore: Boolean(response.payload?.pagination?.hasMore) };
}

// --- Sales setup: promo codes & discounts ----------------------------------
// Backed by /api/promoCodes and /api/discounts. eventId null = org-level.

export async function createPromoCode({ code, name, discountKind = 'pct', discountValue, eventId = null, ticketTypeIds = [], limit = 'unlimited', limitedTo, startDate, endDate, perCustomerLimit, discountCap }, { signal } = {}) {
  const discount = discountKind === 'pct'
    ? { percentage: String(discountValue) }
    : { amount: String(discountValue) };
  const payload = await writeJson('/api/promoCodes', { body: {
    name: name || code,
    ...(code ? { code } : {}),
    limit,
    ...(limit === 'limitedTo' && limitedTo ? { limitedTo } : {}),
    discount,
    // Naming tiers restricts the code to them (PromoCodeTicketType rows); none = every tier.
    ...(ticketTypeIds.length
      ? { applyTo: 'certainTickets', selectedTicketTypes: ticketTypeIds.map((id) => ({ id })) }
      : { applyTo: 'all' }),
    // start/end are required enums; a scheduled date flips them to 'scheduledTime'.
    // These (+ perCustomerLimit) were collected in the UI but never sent, so promos
    // never expired and ignored the per-customer limit.
    ...(startDate ? { start: 'scheduledTime', startDate } : { start: 'now' }),
    // A date-only end is the END of that day: redemption reads endDate + endTime, and with no time it ended at
    // that day's first minute — "valid until 20 Oct" stopped working as 20 Oct began. 23:59, the platform's
    // own default for a date-only end (orderPricing's sales-end fallback).
    ...(endDate ? { end: 'scheduledTime', endDate, endTime: '23:59' } : { end: 'salesEnd' }),
    ...(perCustomerLimit ? { perCustomerLimit: Number(perCustomerLimit) } : {}),
    ...(discountCap ? { discountCap: Number(discountCap) } : {}),
    ...(eventId ? { eventId } : {}),
  }, signal });
  return payload?.data || payload?.promoCode || payload;
}

export async function deletePromoCode(id, { signal } = {}) {
  return writeJson(`/api/promoCodes/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

/**
 * A PROMO CODE AS THE EDITOR HOLDS IT — read from the saved record, and written back as the record.
 *
 * The editor used to start from a mock's defaults and never read the record: "% off", "All
 * categories", per customer 0, whatever the code really was. Its payload builder then derived
 * `applyTo` from a field the form never set, so saving any edit re-scoped the code to every tier
 * (BrakaLand's COMMANDO, limited to "Second Wave: General", would have become a code on all of
 * them). One pair of functions now owns the translation, both ways, so what an operator opens is
 * what is saved and what they save is what they opened.
 *
 * Only what a purchase obeys: the discount, its tiers and whether it stacks on an automatic discount
 * (services/orderPricing), the total uses and the per-customer limit (both inside claimPromoAllotment's
 * one statement) and the window (/validate). Channel and category restrictions are not offered and
 * not sent: no purchase path reads them, so a control would promise a rule that does not exist, and
 * leaving them out of the body keeps whatever a record already holds.
 */
export function promoForm(raw) {
  const r = raw || {};
  const scheduled = (which) => r[which] === 'scheduledTime';
  return {
    code: String(r.code || ''),
    name: String(r.name || ''),
    type: r.discountType === 'amount' ? 'fixed' : 'percent',
    value: Number(r.discount) || 0,
    tierIds: r.applyTo === 'certainTickets' ? (r.ticketTypesForPromoCode || r.selectedTicketTypes || []).map((t) => String(t.id || t)) : [],
    usageLimit: r.limit === 'limitedTo' ? Number(r.limitedTo) || 0 : 0,
    perCustomer: Number(r.perCustomerLimit) || 0,
    noStacking: Boolean(r.noStacking),
    startDay: scheduled('start') ? toLocalDay(r.startDate) : '',
    startTime: scheduled('start') ? String(r.startTime || '00:00').slice(0, 5) : '',
    endDay: scheduled('end') ? toLocalDay(r.endDate) : '',
    endTime: scheduled('end') ? String(r.endTime || '23:59').slice(0, 5) : '',
  };
}

/** The editor's fields → the backend's promoCodeSchema. A blank day means "now" / "when sales end". */
export function promoPayload(form, eventId) {
  const usage = Math.max(0, Math.floor(Number(form.usageLimit) || 0));
  const per = Math.max(0, Math.floor(Number(form.perCustomer) || 0));
  const value = String(Math.max(0, Number(form.value) || 0));
  const tiers = (form.tierIds || []).filter(Boolean);
  return {
    name: (form.name || form.code || '').trim(),
    code: (form.code || '').trim().toUpperCase(),
    discount: form.type === 'fixed' ? { amount: value } : { percentage: value },
    limit: usage > 0 ? 'limitedTo' : 'unlimited',
    ...(usage > 0 ? { limitedTo: usage } : {}),
    perCustomerLimit: per > 0 ? per : null,
    noStacking: Boolean(form.noStacking),
    applyTo: tiers.length ? 'certainTickets' : 'all',
    ...(tiers.length ? { selectedTicketTypes: tiers.map((id) => ({ id })) } : {}),
    // The day is a bare calendar day; writeJson turns it into the local-midnight instant the
    // backend stores, and the clock time rides alongside (lib/absoluteInstant).
    start: form.startDay ? 'scheduledTime' : 'now',
    ...(form.startDay ? { startDate: form.startDay, startTime: form.startTime || '00:00' } : {}),
    end: form.endDay ? 'scheduledTime' : 'salesEnd',
    ...(form.endDay ? { endDate: form.endDay, endTime: form.endTime || '23:59' } : {}),
    eventId: eventId || undefined,
  };
}

// Rich create/update used by EventPromoCodesPanel's editor (full form).
export async function savePromoCode(form, eventId, { signal } = {}) {
  const body = await writeJson('/api/promoCodes', { body: promoPayload(form, eventId), signal });
  const row = body?.promoCode || body?.data || body;
  return normalizePromotionRule(row, 0, 'promo');
}

export async function updatePromoCode(promoCodeId, form, eventId, { signal } = {}) {
  const body = await writeJson(`/api/promoCodes/${encodeURIComponent(promoCodeId)}`, { method: 'PATCH', body: promoPayload(form, eventId), signal });
  const row = body?.promoCode || body?.data || body;
  return normalizePromotionRule(row, 0, 'promo');
}

// --- Ticket types (event ticketing) ----------------------------------------
// Backed by /api/ticketTypes (tkt-backend ticketTypeRoutes.ts): full CRUD +
// toggle-status. Edit/lock operate on an existing ticket type id.

export async function createTicketType(payload, { signal } = {}) {
  const body = await writeJson('/api/ticketTypes', { body: payload, signal });
  return body?.ticketType || body?.data || body;
}

export async function updateTicketType(ticketTypeId, patch, { signal } = {}) {
  const body = await writeJson(`/api/ticketTypes/${encodeURIComponent(ticketTypeId)}`, { method: 'PATCH', body: patch, signal });
  return body?.ticketType || body?.data || body;
}

export async function updateSalesChannelAccess(ticketTypeId, access, { signal } = {}) {
  const body = await writeJson(`/api/ticketTypes/${encodeURIComponent(ticketTypeId)}`, {
    method: 'PATCH',
    body: { channelAccess: Array.isArray(access) ? access : [] },
    signal,
  });
  return body?.data || body;
}

/** The event's whole tier order, first to last — every tier, each once (see the backend's
 *  PUT /api/ticketTypes/event/:eventId/order). Buyers see tiers in this order on every channel. */
export async function saveTierOrder(eventId, ids, { signal } = {}) {
  return writeJson(`/api/ticketTypes/event/${encodeURIComponent(eventId)}/order`, { method: 'PUT', body: { ids }, signal });
}

// --- Discounts: automatic, percentage off, the biggest active one per tier applies ---------------
// Only what checkout applies (services/orderPricing.bestDiscountForTicketType): `discount` is a
// percentage string ("20%"), `applyTo` all tiers or the chosen ones, a start and an end. No usage
// limit is sent: the backend stores one for discounts but nothing enforces it.
export async function createDiscount(body, { signal } = {}) {
  const res = await writeJson('/api/discounts', { method: 'POST', body: { ...body, limit: 'unlimited' }, signal });
  return res?.discount || res?.data || res;
}
export async function updateDiscount(discountId, body, { signal } = {}) {
  const res = await writeJson(`/api/discounts/${encodeURIComponent(discountId)}`, { method: 'PATCH', body, signal });
  return res?.discount || res?.data || res;
}
export async function deleteDiscount(discountId, { signal } = {}) {
  return writeJson(`/api/discounts/${encodeURIComponent(discountId)}`, { method: 'DELETE', signal });
}

/** Hold `qty` tickets of a tier for someone — a count Hold (`/api/holds` with ticketTypeId): off sale
 *  everywhere, through the tier's heldCount, until released. */
export async function holdTickets(ticketTypeId, { eventId, qty, name, note } = {}) {
  return writeJson('/api/holds', { body: { eventId, ticketTypeId, seatCount: qty, name, ...(note ? { reason: note } : {}) } });
}
/** Release a hold — its tickets go back on sale. */
export async function releaseTicketHold(holdId) {
  return writeJson(`/api/holds/${encodeURIComponent(holdId)}/release`, { method: 'POST' });
}

export async function toggleTicketTypeStatus(ticketTypeId, { signal } = {}) {
  return writeJson(`/api/ticketTypes/${encodeURIComponent(ticketTypeId)}/toggle-status`, { method: 'PATCH', body: {}, signal });
}

// --- Financial statements (event settlement / remittance) ------------------
// Mirrors tkt-frontend /finance: POST /api/orders/export/fin/csv returns the
// financial CSV for an event; POST /api/orders/export/fin emails it.

export async function downloadEventFinancialStatement({ eventId, filters = { status: 'successful' }, signal } = {}) {
  if (!eventId) throw new Error('eventId is required for the statement');
  const response = await fetch(`${API_BASE}/api/orders/export/fin/csv`, {
    method: 'POST', signal, credentials: 'include',
    headers: { Accept: 'text/csv, application/json', 'Content-Type': 'application/json', 'x-org-scope': 'all' },
    body: JSON.stringify({ eventId, filters }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let message = text;
    try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
    throw new Error(message || `${response.status} Statement export failed`);
  }
  return response.blob();
}

export async function emailEventFinancialStatement({ email, eventId, filters = { status: 'successful' }, signal } = {}) {
  if (!email) throw new Error('Recipient email is required');
  if (!eventId) throw new Error('eventId is required for the statement');
  return writeJson('/api/orders/export/fin', { body: { email, eventId, filters }, signal });
}

// --- Dashboard OTP (step-up auth) ------------------------------------------
// See /Users/loft/tkt-backend src/routes/dashboardOTPRoutes.ts and
// src/services/DashboardOTPService.ts. The OTP is sent to the user's email
// (Redis-backed, 10-min TTL, one-time use). The protected route consumes the
// code via the X-OTP header.

export async function sendDashboardOTP({ action = 'dashboard_action', signal } = {}) {
  const response = await fetch(`${API_BASE}/api/dashboard-otp/send`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `${response.status} Dashboard OTP request failed`);
  }
  return payload;
}

export async function checkDashboardOTP({ action = 'dashboard_action', signal } = {}) {
  const response = await fetchBackOfficeJson(`/api/dashboard-otp/check/${encodeURIComponent(action)}`, { signal });
  if (!response.ok) return { hasValidOTP: false };
  return response.payload || { hasValidOTP: false };
}

export async function createOrder(payload, { signal } = {}) {
  const response = await fetch(`${API_BASE}/api/orders`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data?.error || data?.errors?.[0] || data?.message || `${response.status} Order create failed`;
    const err = new Error(message);
    err.payload = data;
    throw err;
  }
  return data?.order || data;
}

// --- Order confirmation via dashboard OTP --------------------------------
// See /Users/loft/tkt-backend src/routes/orderDashboardRoutes.ts:194.
// The OTP is consumed by the requireDashboardOTP middleware — we pass it
// in the X-OTP header so the backend can validate it from the request.

// TWO routes settle an order and they take different proofs:
//   POST /api/orders/dashboardconfirm  → requireDashboardOTP  (an emailed code)
//   POST /api/orders/boxofficeconfirm  → requireBiometricPasskey (a fingerprint/face)
// Both end in the same confirmOrder handler. The biometric route has existed and been
// mounted the whole time with no caller, so enrolling a device changed nothing anywhere.
// Passing `biometric` here routes to it; passing `otp` keeps the mailed-code path.
export async function confirmOrderDashboard({ orderId, paymentMethod, otp, biometric, reason, transactionId, signal } = {}) {
  if (!otp && !biometric) throw new Error('Confirming an order needs an OTP or an enrolled biometric device');
  const response = await fetch(`${API_BASE}/api/orders/${biometric ? 'boxofficeconfirm' : 'dashboardconfirm'}`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: {
      Accept: 'application/json', 'x-org-scope': 'all',
      'Content-Type': 'application/json',
      ...(biometric ? { 'X-Biometric-Credential': biometric } : { 'X-OTP': otp }),
    },
    body: JSON.stringify({ orderId, paymentMethod, reason, transactionId }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data?.error || data?.message || `${response.status} Order confirm failed`;
    const err = new Error(message);
    err.payload = data;
    throw err;
  }
  return data?.data || data;
}

// --- Background jobs (tkt-backend /api/jobs) -------------------------------
// Every job the backend knows — what it looks after, whether it runs on this deployment (and why not), what it
// is doing, what its runs came to. runJob starts one on the worker that owns the
// jobs and returns at once; the run is watched through loadJobs. Platform admins only.
export async function loadJobs({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/jobs', { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} The jobs could not be read`);
  return r.payload?.data || { jobs: [] };
}
export const runJob = (name) =>
  writeJson(`/api/jobs/${encodeURIComponent(name)}/run`, { body: {} });

// --- Manual "recheck payment" ----------------------------------------------
// POST /api/orders/:orderId/refresh — the SAME endpoint mtn-ticketing's buyer checkout
// polls (useRefreshPaymentStatus in ~/mtn-ticketing/src/hooks/useOrder.ts) while a payment
// is in flight. It doesn't just re-read the DB row — it actively asks the attempt's OWN
// gateway for the transaction's current status (verifyAndCompleteMoMoOrder →
// verifyAttemptOnGateway, in orderPaymentMoMoRoutes.ts) and settles through the same engine
// every webhook uses, so a recheck can never double-issue or bypass installment accounting.
// The real use case here: an order the webhook never reached (wrong callback URL, a network
// blip, the provider retried and gave up) sits "pending" forever until someone asks again —
// this is that "ask again". Currently wired for momo/mookhpay/mpesa attempts; a pesapal or
// flutterwave attempt returns "not pollable via this gateway; awaiting webhook" (their poll
// APIs aren't wired into that switch yet), which recheckOrderPayment surfaces honestly rather
// than pretending it worked.
export async function recheckOrderPayment(orderId, { signal } = {}) {
  if (!orderId) throw new Error('orderId is required');
  const response = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderId)}/refresh`, {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: '{}',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.status === 'error') {
    throw new Error(data?.message || `${response.status} Payment recheck failed`);
  }
  return data; // { status: 'successful'|'pending'|'failed', message, verification }
}

/**
 * Save the Venue Studio's geometry to Venue.metadata.geometry (tkt-backend PUT /api/v2/venues/:id/
 * geometry). `baseRevision` is the revision the editor started from (0 for a venue with none); if
 * someone saved since, the backend refuses with 409 and this throws its message. Resolves to
 * { revision, savedAt, savedBy }.
 */
export async function saveVenueGeometry(venueId, geometry, baseRevision, { signal } = {}) {
  const res = await writeJson(`/api/v2/venues/${encodeURIComponent(venueId)}/geometry`, { method: 'PUT', body: { geometry, baseRevision }, signal });
  return res?.data ?? res;
}

/**
 * Replay a sale through Best Available's simulator (tkt-backend GET /api/event-seat-map/:eventId/
 * simulate, read-only). The answer is a stream of server-sent events — start, progress, a result
 * per policy, done or error — each handed to `onEvent(name, data)` as it arrives. Aborting `signal`
 * stops the run on the server too. Resolves when the stream ends.
 */
export async function streamSimulation(eventId, params, { onEvent, signal } = {}) {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)]));
  const response = await fetch(`${API_BASE}${withOrgScope(`/api/event-seat-map/${encodeURIComponent(eventId)}/simulate?${q}`)}`, { credentials: 'include', signal, headers: { Accept: 'text/event-stream' } });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `${response.status} request failed`);
  }
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let cut;
    while ((cut = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, cut); buffer = buffer.slice(cut + 2);
      const name = /^event: ?(.*)$/m.exec(block)?.[1], data = block.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).replace(/^ /, '')).join('\n');
      if (name) onEvent?.(name, data ? JSON.parse(data) : null);
    }
  }
}

// THE EVENT'S FILL ORDER (tkt-backend /api/event-seat-map/:eventId/fill-order): the sections in the
// order checkout hands seats out, and whether the seats follow it. Saving changes what checkout
// sells next, at once; `base` is the order the editor started from (409 if someone changed it).
export async function loadFillOrder(eventId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/event-seat-map/${encodeURIComponent(eventId)}/fill-order`, { signal });
  if (!r.ok) throw new Error(r.payload?.error || `${r.status} request failed`);
  return r.payload;
}
export async function saveFillOrder(eventId, sections, base, { signal } = {}) {
  const res = await writeJson(`/api/event-seat-map/${encodeURIComponent(eventId)}/fill-order`, { method: 'PUT', body: { sections, base }, signal });
  return res?.data ?? res;
}

// THE CANONICAL VENUE MODEL (tkt-backend /api/v2/venue-model): what Quick Setup and Venue Canvas
// (src/modules/venue-port) load and publish. A failed write keeps its status and body (err.status,
// err.body), because the editor answers them differently: someone published first (409 conflict), an
// upcoming event has seats the change removes (409 units-in-use), the structure is invalid (422).
export async function loadVenueModel(venueId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v2/venue-model/${encodeURIComponent(venueId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Venue unavailable'}`);
  return r.payload; // { data: model | null, venue }
}
async function venueModelWrite(path, body, method = 'POST') {
  const response = await fetch(`${API_BASE}${withReaderClock(path)}`, {
    method, credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'x-org-scope': 'all' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error(payload?.error || `${response.status} request failed`);
    err.status = response.status; err.body = payload || {};
    throw err;
  }
  return payload?.data ?? payload;
}
export const createVenueModel = (input) => venueModelWrite('/api/v2/venue-model', input);
export const commitVenueModel = (venueId, input) => venueModelWrite(`/api/v2/venue-model/${encodeURIComponent(venueId)}/commits`, input);
// An existing venue brought into the venue model (adoption): what it would become as `typeId` (or the type
// its record suggests), nothing written; then the adoption itself ({ typeId, expectedHash }).
export async function previewVenueAdoption(venueId, typeId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v2/venue-model/${encodeURIComponent(venueId)}?adopt=${encodeURIComponent(typeId || '')}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Venue unavailable'}`);
  return r.payload; // { data: null, venue, adoption: { typeId, hash, report, check } }, or { data } when adopted already
}
export const adoptVenueModel = (venueId, input) => venueModelWrite(`/api/v2/venue-model/${encodeURIComponent(venueId)}/adopt`, input);
// The venue's own holds and blocks (every event at the venue), keyed by the Canvas's seat units.
export async function loadVenueHolds(venueId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v2/venue-model/${encodeURIComponent(venueId)}/holds`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Venue holds unavailable'}`);
  return list(r.payload?.data || r.payload);
}
// The events held at a venue (tkt-backend GET /api/v2/venues/:id/events: id, title, date, startTime,
// status; newest first, at most 100), for the venue page.
export async function loadVenueEvents(venueId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v2/venues/${encodeURIComponent(venueId)}/events`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Venue events unavailable'}`);
  return list(r.payload?.data || r.payload);
}
// A venue's contingents (allocations), for the venue page's Allocation pools tab. A selection becomes a
// contingent mapping on the server, which knows the seats; /api/contingents keeps the contingent.
export async function loadVenueContingents(venueId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v2/venue-model/${encodeURIComponent(venueId)}/contingents`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Allocations unavailable'}`);
  return list(r.payload?.data || r.payload);
}
// What changed at a venue, newest first (publishes, venue holds, allocations, the venue audit log).
export async function loadVenueChanges(venueId, { signal } = {}) {
  const r = await fetchBackOfficeJson(`/api/v2/venue-model/${encodeURIComponent(venueId)}/changes`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Changes unavailable'}`);
  return list(r.payload?.data || r.payload);
}
export const contingentMappingOf = (venueId, selection) => venueModelWrite(`/api/v2/venue-model/${encodeURIComponent(venueId)}/contingent-mapping`, selection);
export const createContingent = (input) => venueModelWrite('/api/contingents', input);
export const updateContingent = (id, patch) => venueModelWrite(`/api/contingents/${encodeURIComponent(id)}`, patch, 'PATCH');
export const releaseContingent = (id) => venueModelWrite(`/api/contingents/${encodeURIComponent(id)}/release`, {});
export const holdVenueSeats = (venueId, input) => venueModelWrite(`/api/v2/venue-model/${encodeURIComponent(venueId)}/holds`, input);
export const releaseVenueSeats = (venueId, unitIds) => venueModelWrite(`/api/v2/venue-model/${encodeURIComponent(venueId)}/holds/release`, { unitIds });

// The venue's operations log (tkt-backend VenueAuditLog: seat-manager blocks and holds, plan
// publishes, gate assignments), newest first, at most 200. A caller outside the venue's scope gets
// an empty list with a `note`, not an error.
export async function loadVenueAuditLog(venueId, { signal } = {}) {
  if (!venueId) return [];
  const r = await fetchBackOfficeJson(`/api/venue-audit-logs?venueId=${encodeURIComponent(venueId)}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || r.statusText || 'Venue log unavailable'}`);
  return list(r.payload?.data || r.payload);
}

export async function loadVenueWorkspace(venueId, { signal } = {}) {
  // v1 venue workspace (NOT v2). Gates = Entrance records, sections = Section
  // records, seat-plans = seating-templates (v1 has no /seat-plans, /events or
  // /audit-logs children — those 404). Per-venue event list has no v1 endpoint;
  // the venue payload carries upcoming/pastEventsCount and recentActivity.
  const id = encodeURIComponent(venueId);
  const paths = {
    venue: venueEndpoint(venueId),
    // ONE venue's gates and sections — all of them (a stadium has tens). Both routes default to a page of 10, so
    // Mandela read "Gates 10" with 31 on prod and Hoima 10 sections of 53.
    gates: `/api/entrances/venue/${id}?limit=500`,
    sections: `/api/sections/venue/${id}?limit=500`,
    seatPlans: `/api/seating-templates?venueId=${id}`,
  };
  const entries = await Promise.all(Object.entries(paths).map(async ([key, path]) => {
    try {
      const response = await fetchBackOfficeJson(path, { signal });
      if (!response.ok) throw new Error(`${response.status} ${response.statusText || 'Request failed'}`);
      return [key, response.payload, null];
    } catch (error) {
      return [key, null, error?.message || 'Request failed'];
    }
  }));
  const payloads = Object.fromEntries(entries.map(([key, payload]) => [key, payload]));
  const errors = Object.fromEntries(entries.filter(([, , error]) => error).map(([key, , error]) => [key, error]));
  const unwrappedVenue = unwrap(payloads.venue);
  const venuePayload = list(payloads.venue)[0] || (unwrappedVenue && !Array.isArray(unwrappedVenue) && typeof unwrappedVenue === 'object' ? unwrappedVenue : null);
  // Sections: prefer the seating TEMPLATE's materialized sections (authoritative —
  // the real layout, e.g. 32) over /api/sections/venue (unreliable: the createTemplate
  // wart leaves Section.venueId NULL, so that endpoint under-returns). Fall back to
  // the raw section rows only when the venue has no template.
  const rawTemplates = list(payloads.seatPlans);
  const tmplSections = (rawTemplates[0]?.sections?.length ? rawTemplates[0].sections : rawTemplates[0]?.template?.sections) || [];
  const sectionSource = tmplSections.length ? tmplSections : list(payloads.sections);
  const sections = sectionSource.map(normalizeVenueSection);
  if (venuePayload && sectionSource.length && !Array.isArray(venuePayload.sections)) venuePayload.sections = sectionSource;
  return {
    venue: venuePayload ? normalizeVenue(venuePayload) : null,
    // The RAW detail row as well. GET /api/venues/:id hand-builds a 12-key response
    // (id, name, description, address, status, capacity, entrancesCount, sectionsCount,
    // upcoming/pastEventsCount, recentActivity, metadata) while GET /api/venues returns
    // the whole 39-column row. Normalizing each separately and spreading the thin one
    // over the fat one let every OMITTED key overwrite a real value with a placeholder —
    // measured on one venue: city Kampala→null, country Uganda→null, type Stadium→'Venue',
    // updated '31 Jul, 10:19'→'—', onboarded '31 Jul 2026'→'TBD'. Normalizers are pure
    // functions of a raw row, so the fix is to merge the RAWS and normalize the union once.
    venueRaw: venuePayload || null,
    gates: list(payloads.gates).map(normalizeVenueGate),
    sections,
    seatPlans: rawTemplates.map(normalizeVenueSeatPlan),
    events: [],
    auditLogs: Array.isArray(venuePayload?.recentActivity) ? venuePayload.recentActivity : [],
    errors,
    paths,
  };
}

// --- WhatsApp Calls (real Meta Business Calling API — genuine WebRTC audio, not
// a click-to-call link) — the backend (WhatsAppCallService, whatsAppCallRoutes,
// WebSocketService) already implements the full ring→answer→talk→end lifecycle;
// this is the first real-time push consumer in the back-office. -----------------

// Real prod networking IS a subdomain swap, same idiom as getBackendUrl's backend.<domain> — a `ws.`
// host over wss:, e.g. wss://ws.moments.ug/ws. That host stays: it is the one already proven to carry
// an upgrade, and it now proxies to the app's own port instead of a listener of its own.
//
// LOCALLY THERE IS NO PORT TO SWAP ANY MORE. The backend used to run a second Bun.serve on WSPORT
// (3008) purely for this socket; /ws is now a route on the same Hono app as the REST calls
// (tkt-backend src/index.ts), so nothing listens on 3008 and the dev URL is just the API's own
// host and port with the scheme swapped.
export const getWhatsAppWsUrl = () => {
  if (import.meta.env.VITE_TKT_WS_URL) return import.meta.env.VITE_TKT_WS_URL;
  return derivedHostUrl('ws', () => API_BASE.replace(/^http/, 'ws'), { ws: true }) + '/ws';
};

const CALL_STATUS = { RINGING: 'ringing', PRE_ACCEPTED: 'pre_accepted', ACCEPTED: 'accepted', REJECTED: 'rejected', TERMINATED: 'terminated' };

const normalizeWhatsAppCall = (row) => ({
  ...row,
  id: row.id,
  callId: row.callId,
  from: row.from,
  to: row.to,
  status: row.status,
  wasAnswered: Boolean(row.wasAnswered),
  contactName: row.metadata?.contactName || '',
  startedAt: row.startedAt,
  endedAt: row.endedAt || null,
});

/** A page of call history, newest first — `{ rows, hasMore }`; `last` (the last call shown) is where the next starts. */
export async function loadWhatsAppCalls({ limit = LIST_PAGE_SIZE, last, status, signal } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set('status', status);
  if (last?.startedAt) params.set('before', last.startedAt);
  const r = await fetchBackOfficeJson(`/api/whatsapp-calls/calls?${params.toString()}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Call history unavailable'}`);
  return { rows: list(r.payload?.data).map(normalizeWhatsAppCall), hasMore: Boolean(r.payload?.hasMore) };
}

export async function acceptWhatsAppCall(callId, sdp, { signal } = {}) {
  return writeJson(`/api/whatsapp-calls/calls/${encodeURIComponent(callId)}/accept`, { body: { sdp }, signal });
}
export async function rejectWhatsAppCall(callId, { signal } = {}) {
  return writeJson(`/api/whatsapp-calls/calls/${encodeURIComponent(callId)}/reject`, { body: {}, signal });
}
export async function endWhatsAppCall(callId, { signal } = {}) {
  return writeJson(`/api/whatsapp-calls/calls/${encodeURIComponent(callId)}/end`, { body: {}, signal });
}

export { CALL_STATUS };

// ─── Split payments & instalment plans (the operator surface) ────────────────────────────────
//
// Backed by /api/split-payments in tkt-backend (routes/splitAdminRoutes.ts). Every figure these
// return is DERIVED SERVER-SIDE from the order's own columns — `frozenTotal`, `amountPaid`, the
// plan in `misc`, and the PaymentAttempt rows. Nothing in this console recomputes money: two
// implementations of "how far has this got" is how two screens come to disagree, which is the
// exact bug the back-office prototype was rewritten to remove.

const splitEndpoint = (path = '') => `/api/split-payments${path}`;

// fetchBackOfficeJson returns a {ok, status, payload} ENVELOPE and applies withOrgScope itself.
// Both facts were missed on the first pass here: the readers below wrapped the path in
// withOrgScope a second time (producing `?orgScope=all&orgScope=all`) and then read `res.effective`
// off the envelope, which is always undefined — so the tier editor silently rendered its hardcoded
// fallbacks instead of the real policy, and looked entirely correct while doing it. One unwrapper,
// used by every reader, so neither mistake can be made once per call site.
const readSplitJson = async (path, { signal } = {}) => {
  const res = await fetchBackOfficeJson(path, { signal });
  if (!res.ok) throw new Error(res.payload?.error || `${res.status} ${res.statusText || 'Split payments request failed'}`);
  return res.payload;
};

/**
 * Arrangements — orders being collected in parts, whether by several people (a split) or over
 * time (an instalment plan).
 *
 * `windowDays` is not decoration and should be surfaced to the operator. The backend bounds the
 * scan on `updatedAt`; unbounded, the list took 58 SECONDS on production (1.5M orders, 9 of them
 * arrangements) because `ORDER BY updatedAt DESC LIMIT n` can never stop early when fewer than n
 * rows match. Default 90 days ≈ 1s. Pass 'all' deliberately, and expect it to be slow.
 *
 * `windowDays: 'all'` with neither an event nor an org scope is REFUSED by the endpoint (400,
 * `unbounded_requires_scope`) rather than served slowly — readSplitJson surfaces that message
 * verbatim, so let it reach the operator instead of rewording it here.
 */
export async function loadSplitArrangements({
  kind, eventId, health, search, page, limit, windowDays, includeUnstarted, signal,
} = {}) {
  const qs = new URLSearchParams();
  if (kind && kind !== 'all') qs.set('kind', kind);
  if (eventId) qs.set('eventId', eventId);
  // Comma-separated on the wire; the caller may hand us either shape.
  if (health) qs.set('health', Array.isArray(health) ? health.join(',') : health);
  if (search) qs.set('search', search);
  if (page) qs.set('page', String(page));
  if (limit) qs.set('limit', String(limit));
  // 0 is not a valid window, so a plain falsy check is right — but 'all' must survive it.
  if (windowDays != null && windowDays !== '') qs.set('windowDays', String(windowDays));
  // Arrangements no money has arrived on are hidden by default, as abandoned checkouts are on the
  // orders list; this is the operator asking to see them.
  if (includeUnstarted) qs.set('includeUnstarted', 'true');
  const suffix = qs.toString() ? `?${qs}` : '';
  return readSplitJson(splitEndpoint(`/arrangements${suffix}`), { signal });
}

/** One arrangement in full — money, terms, resolved policy, and the movements behind the figure.
 *  Addressable by order UUID or by the human reference an operator is holding. */
export async function loadSplitArrangement(orderId, { signal } = {}) {
  return readSplitJson(splitEndpoint(`/arrangements/${encodeURIComponent(orderId)}`), { signal });
}

/** Events with at least one split- or instalment-eligible tier, their resolved rules, and what
 *  the live arrangements on each are holding. */
export async function loadSplitEvents({ limit, signal } = {}) {
  const suffix = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  return readSplitJson(splitEndpoint(`/events${suffix}`), { signal });
}

/** One event's split setup: every ticket type (including the ones switched OFF — that is the
 *  answer to "why can't my customers split this?"), the rules in force, and what the running
 *  arrangements are holding. */
export async function loadSplitEvent(eventId, { signal } = {}) {
  return readSplitJson(splitEndpoint(`/events/${encodeURIComponent(eventId)}`), { signal });
}
/**
 * The policy, as THREE LAYERS plus the effective result — platform, the event's overrides, the
 * tier's own tightening. Kept separate on purpose: a flat effective object makes an inherited
 * value and a hand-set one look identical, which is how somebody edits the wrong level.
 *
 * Also carries the vocabulary (expiry behaviours with their `supported` flag, retention policies,
 * health states) so this console never keeps a second copy of wording that is shown to buyers.
 */
export async function loadSplitPolicy({ eventId, ticketTypeId, signal } = {}) {
  const qs = new URLSearchParams();
  if (eventId) qs.set('eventId', eventId);
  if (ticketTypeId) qs.set('ticketTypeId', ticketTypeId);
  const suffix = qs.toString() ? `?${qs}` : '';
  return readSplitJson(splitEndpoint(`/policy${suffix}`), { signal });
}

/**
 * Write an event's overrides and/or its per-tier deposit floors.
 *
 * THREE STATES, and they mean different things — pass a key with a value to SET it, pass it as
 * `null` to CLEAR it back to the platform default, omit it to leave it alone. The response's
 * `effective` is what will actually bind after clamping and tier-tightening, which is not
 * necessarily what was sent; render that rather than the form's own state.
 */
export async function updateSplitEventPolicy(eventId, patch, { signal } = {}) {
  return writeJson(splitEndpoint(`/events/${encodeURIComponent(eventId)}/policy`), { method: 'PATCH', body: patch, signal });
}

/** The per-tier instalment deposit FLOOR, in whole units of the event's currency.
 *
 *  It lives on the EVENT (`metadata.minDepositAmount[tierId]`), not on the ticket type — models/
 *  TicketType.ts refuses new columns because ~94 of its 186 query sites do not pin `attributes`.
 *  So this is an event PATCH even though the control sits beside a tier. It is MONEY, never a
 *  percentage: "at least UGX 50,000" is answerable on sight, "10%" of UGX 450,000 is not.
 *  Pass null to remove the floor. */
/**
 * Send one contributor their payment link again.
 *
 * The REAL endpoint (routes/orderPayLinkRoutes.ts), not a stub: it renders the split invite and
 * mails the address frozen on that slot. Two things ride on its answer and both must reach the
 * operator verbatim —
 *   · it is RATE LIMITED to one send per order+slot per 10 minutes, and answers 429 with a human
 *     sentence ("They were reminded a moment ago. You can send another in 7 minutes."), because
 *     without a bound anyone holding a link could mail a stranger on repeat from our domain;
 *   · a slot with no email cannot be reminded at all, and says so rather than silently succeeding.
 *
 * Keyed by the ORDER REFERENCE, not the uuid — that is what the public pay/nudge surface resolves.
 */
export async function sendSplitNudge(orderReference, slot, { signal } = {}) {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderReference)}/nudge/${slot}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    signal,
  });
  const body = await res.json().catch(() => ({}));
  // The backend's own sentence, surfaced as-is. Replacing it with "Request failed" would throw away
  // the only part an operator can act on.
  if (!res.ok) throw new Error(body?.error || `${res.status} Could not send the reminder`);
  return body;
}

/**
 * Reassign who each slot of a split is for.
 *
 * THE PLAN IS NOT SETTABLE HERE and that is deliberate on the backend: how many payers a split has
 * and the smallest contribution come from the ticket type's own policy, read at checkout. "A rule
 * chosen by the person it is meant to bind is not a rule." All this does is say WHO each existing
 * slot belongs to — which is the difference between reshaping a split and creating one.
 *
 * Shares are `{firstName, lastName, email, phone}` in slot order. The backend re-freezes the
 * amounts against the committed total and refuses an allocation that cannot complete the order.
 * It also mails the newly-named slots, bounded to one blast per order per 10 minutes — the response
 * says whether it actually sent.
 */
export async function assignSplitShares(orderReference, shares, { signal } = {}) {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderReference)}/split`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shares }),
    signal,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `${res.status} Could not reshape the split`);
  return body;
}

// --- Privacy / DSAR queue (REAL entity) -------------------------------------
// Data-subject requests submitted through the legal portal (legal.<apex>) — subject access,
// erasure, and compliance contact. Backed by the PrivacyRequest model via
// /api/privacy-requests, which is a SEPARATE mount from the unauthenticated /api/privacy the
// public forms post to: these rows are the personal data of people writing in ABOUT their
// personal data, so every read here proves the caller first.
//
// The deadline is NOT computed in this file. It arrives on every row as `sla`, from the backend,
// because it is a statutory obligation and a browser clock is not evidence of one — and because a
// second implementation here would eventually disagree with the queue it is meant to describe.
const PRIVACY_TYPE_LABEL = {
  data_access: 'Access request',
  data_deletion: 'Erasure request',
  contact: 'Compliance enquiry',
};
const PRIVACY_STATUS_TONE = { new: 'amber', in_progress: 'blue', completed: 'green', rejected: 'neutral' };
const PRIVACY_URGENCY_TONE = { breached: 'red', 'due-soon': 'amber', 'on-track': 'green', closed: 'neutral' };

const normalizePrivacyRequest = (r) => ({
  ...r,
  id: String(r.id),
  type: r.type,
  typeLabel: PRIVACY_TYPE_LABEL[r.type] || titleCase(r.type || ''),
  // A deletion request has no name field on the form — only an email. Falling back to the email
  // keeps the row identifiable instead of rendering a blank cell where a person should be.
  name: firstPresent(r.name, r.email),
  email: r.email,
  company: r.company || null,
  reason: r.reason || null,
  details: r.details || null,
  message: r.message || null,
  ipAddress: r.ipAddress || null,
  userAgent: r.userAgent || null,
  status: r.status,
  statusTone: PRIVACY_STATUS_TONE[r.status] || 'neutral',
  resolvedAt: r.resolvedAt || null,
  createdAt: r.createdAt,
  sla: r.sla || null,
  urgencyTone: PRIVACY_URGENCY_TONE[r.sla?.urgency] || 'neutral',
  raw: r,
});

export async function loadPrivacyRequests({ status, type, search, overdue, page = 1, limit = LIST_PAGE_SIZE, signal } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (type) qs.set('type', type);
  if (search) qs.set('search', search);
  if (overdue) qs.set('overdue', 'true');
  qs.set('limit', String(limit));
  qs.set('offset', String((page - 1) * limit));
  const r = await fetchBackOfficeJson(`/api/privacy-requests?${qs}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Privacy requests unavailable'}`);
  return {
    rows: list(r.payload?.data).map(normalizePrivacyRequest),
    hasMore: !!r.payload?.pagination?.hasMore,
    slaDays: number(r.payload?.slaDays, 30),
  };
}

export async function loadPrivacyRequestSummary({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/privacy-requests/summary', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Summary unavailable'}`);
  return r.payload || {};
}

export async function setPrivacyRequestStatus(id, status, { signal } = {}) {
  const body = await writeJson(`/api/privacy-requests/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status }, signal });
  return body?.data ? normalizePrivacyRequest(body.data) : null;
}


/**
 * STOP A RUNNING REPORT QUERY FOR REAL.
 *
 * Aborting the fetch only stops this browser waiting — Postgres keeps executing and keeps a
 * pooled connection busy until it finishes. This sends a genuine `pg_cancel_backend` to the
 * database. It reports how many backends it actually cancelled, so "nothing matched" (already
 * finished, wrong id) is distinguishable from "stopped it".
 */
export async function cancelReportRun({ runId, olderThanSeconds } = {}) {
  const body = runId ? { runId } : { olderThanSeconds };
  const r = await writeJson('/api/reports/runtime/cancel', { body });
  return r?.data || r;
}

/** Report queries executing right now — only report queries, never anything transactional. */
export async function loadRunningReports(minSeconds = 0) {
  const r = await fetchBackOfficeJson(`/api/reports/runtime/running?minSeconds=${Number(minSeconds) || 0}`);
  return r?.payload?.data || [];
}

// ─── Email design library ────────────────────────────────────────────────────
//
// Backed by /api/email-designs. This is the EMAIL half of the design library — separate from
// /api/designs (tickets, accreditation), because the two render through different engines: a
// ticket is Mustache over stored HTML, an email is a list of typed blocks rendered by the shared
// kit. Sharing a client here would mean one set of functions whose meaning depended on a field.
//
// The console does NOT hold its own catalog of email blocks. It used to — `EMAIL_BLOCKS` and
// `EMAIL_TEMPLATES` in data/comms.js, invented keys matching nothing the backend renders — so the
// editor could offer a "Hero image" that no email had and never show a field that existed. The
// backend describes its own templates now, and this asks it.

/**
 * The emails THIS DEPLOYMENT sends, plus the ones it has no design for.
 *
 * Returns the catalog rather than a bare array, because three of the four fields are the point:
 *
 *   templates   the designs this tenant lists, including the not-yet-convertible ones
 *               (`editable: false`, plus where their markup still lives)
 *   undesigned  capabilities this deployment CANNOT send — the half of the truth a list of
 *               what exists leaves out, and the reason it is not just a shorter array
 *   tenant      which deployment answered. Deliberately from the BACKEND: the console's own
 *               `detectBrand()` reads window.location.host, which answers "whose console is
 *               this" rather than "whose backend am I talking to", and those can differ
 *   groupOrder  the capability groups, in the order to render them
 */
export async function loadEmailTemplates({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/email-designs/templates', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Email templates unavailable'}`);
  return {
    tenant: r.payload?.tenant ?? null,
    templates: list(r.payload),
    undesigned: Array.isArray(r.payload?.undesigned) ? r.payload.undesigned : [],
    groupOrder: Array.isArray(r.payload?.groupOrder) ? r.payload.groupOrder : [],
  };
}

/** The block palette — every block type an email may contain, with its editable fields. */
export async function loadEmailBlockPalette({ signal } = {}) {
  const r = await fetchBackOfficeJson('/api/email-designs/blocks', { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Block palette unavailable'}`);
  return list(r.payload);
}

/**
 * What the editor draws for one template: each block, whether it can be switched off or reworded,
 * the fields that actually take effect, and the wording currently in force.
 *
 * Pass `designId` to open on what was last saved rather than the defaults. Fields the template
 * COMPUTES are absent by design — the backend omits them rather than returning them disabled,
 * because a greyed input still implies "yours to set one day" and these never will be.
 */
export async function loadEmailTemplateBlocks(key, { designId, signal } = {}) {
  const qs = designId ? `?designId=${encodeURIComponent(designId)}` : '';
  const r = await fetchBackOfficeJson(`/api/email-designs/templates/${encodeURIComponent(key)}${qs}`, { signal });
  if (!r.ok) throw new Error(r.payload?.reason || r.payload?.error || `${r.status} Could not load this email`);
  return r.payload;
}

/** Start a design from a template. Created as a DRAFT — it changes no live email until published. */
export async function createEmailDesign({ name, templateKey, eventIds } = {}, { signal } = {}) {
  return writeJson('/api/email-designs/designs', {
    body: { name, templateKey, ...(eventIds?.length ? { eventIds } : {}) }, signal,
  });
}

/** Save wording, and publish or unpublish. The backend sanitises the blocks before storing, so
 *  anything the editor should not have sent is dropped rather than persisted. */
export async function saveEmailDesign(id, { name, status, blocks } = {}, { signal } = {}) {
  return writeJson(`/api/email-designs/designs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: {
      ...(name !== undefined ? { name } : {}),
      ...(status !== undefined ? { status } : {}),
      ...(blocks !== undefined ? { blocks } : {}),
    },
    signal,
  });
}

/** Which design WOULD apply to an event, and why. The question asked when an email did not look
 *  the way somebody expected. */
export async function resolveEmailDesignFor({ eventPageId, organizationId } = {}, { signal } = {}) {
  const qs = new URLSearchParams();
  if (eventPageId) qs.set('eventPageId', eventPageId);
  if (organizationId) qs.set('organizationId', organizationId);
  const r = await fetchBackOfficeJson(`/api/email-designs/resolve${qs.toString() ? `?${qs}` : ''}`, { signal });
  if (!r.ok) throw new Error(`${r.status} ${r.payload?.error || 'Could not resolve'}`);
  return r.payload;
}

/** Preview URL for an iframe. Rendered against the template's SAMPLE data, never a real order —
 *  a preview that pulled a live order would be a way to read someone's payment details through a
 *  design screen. `raw` drops the brand shell, which is what an editor pane wants. */
export function emailPreviewUrl(key, { designId, raw } = {}) {
  const qs = new URLSearchParams();
  if (designId) qs.set('designId', designId);
  if (raw) qs.set('raw', '1');
  return `${API_BASE}/api/email-designs/templates/${encodeURIComponent(key)}/preview${qs.toString() ? `?${qs}` : ''}`;
}

/**
 * Render UNSAVED wording — what the editor's live preview uses.
 *
 * Returns HTML for an iframe `srcDoc`. Goes through the same sanitiser the save path uses, so the
 * preview shows what WOULD be stored rather than a more permissive rendering that would make a
 * doomed edit look like it took.
 */
export async function previewEmailDraft(key, blocks, { raw, signal } = {}) {
  const qs = raw ? '?raw=1' : '';
  const res = await fetch(`${API_BASE}/api/email-designs/templates/${encodeURIComponent(key)}/preview${qs}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocks }),
    signal,
  });
  if (!res.ok) throw new Error(`${res.status} Preview unavailable`);
  return res.text();
}

/**
 * Send this email design to YOURSELF, so checking it costs a click rather than a real payment.
 *
 * There is no recipient argument, deliberately: the backend always sends to the logged-in user's
 * own address. An endpoint that mails arbitrary designed HTML to an address of the caller's
 * choosing, from our sending domain, is a spam cannon — the convenience of typing a colleague's
 * address is not worth building one. Subject carries a [TEST] prefix so it can never be mistaken
 * for a genuine receipt sitting next to it in an inbox.
 *
 * Pass the blocks currently on screen to test unsaved wording; omit them to test what is stored.
 */
export async function sendEmailDesignTest(key, { blocks, designId } = {}, { signal } = {}) {
  return writeJson(`/api/email-designs/templates/${encodeURIComponent(key)}/test`, {
    body: { ...(blocks !== undefined ? { blocks } : {}), ...(designId ? { designId } : {}) },
    signal,
  });
}

// ── VENUE-ISSUED ACCESS CODES (the QR inventory tab) ─────────────────────────────────────────────
// A venue that runs its own turnstiles hands over a code per seat; uploaded here, each seat's code
// becomes the signature of the ticket sold on it. Backend: src/routes/accessCodeRoutes.ts.
export const accessCodeTemplateUrl = (eventId) => `${API_BASE}${withOrgScope(`/api/access-codes/template/${encodeURIComponent(eventId)}`)}`;
// Every seat of the event and its state, as a JSON file (a recurring event: one occurrence's seats).
export const eventSeatsExportUrl = (eventId, slotId) => `${API_BASE}${withOrgScope(`/api/seats/events/${encodeURIComponent(eventId)}/seats/export${slotId ? `?slotId=${encodeURIComponent(slotId)}` : ''}`)}`;
// Every code of the event as CSV — the stock the venue loads into its own access system.
/** The code every ticket already carries (ours from buy, or its seat's) — read from the tickets. */
export const accessCodeIssuedUrl = (eventId) => `${API_BASE}${withOrgScope(`/api/access-codes/${encodeURIComponent(eventId)}/issued`)}`;
export const accessCodeExportUrl = (eventId) => `${API_BASE}${withOrgScope(`/api/access-codes/${encodeURIComponent(eventId)}/export`)}`;
/** One upload's codes with their seats (GET /api/access-codes/:eventId/upload?batch=). */
export const accessCodeUploadUrl = (eventId, batch) => `${API_BASE}${withOrgScope(`/api/access-codes/${encodeURIComponent(eventId)}/upload?batch=${encodeURIComponent(batch)}`)}`;
const unwrapAccessCodes = (r) => { if (!r.ok) throw new Error(r.payload?.error || `${r.status} ${r.statusText}`); return r.payload; };
export async function fetchAccessCodeStatus(eventId, { signal } = {}) {
  return unwrapAccessCodes(await fetchBackOfficeJson(`/api/access-codes/${encodeURIComponent(eventId)}`, { signal }));
}
export async function fetchAccessCodes(eventId, { q = '', status = '', batch = '', limit = LIST_PAGE_SIZE, offset = 0, signal } = {}) {
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (q) qs.set('q', q); if (status) qs.set('status', status); if (batch) qs.set('batch', batch);
  return unwrapAccessCodes(await fetchBackOfficeJson(`/api/access-codes/${encodeURIComponent(eventId)}/codes?${qs}`, { signal }));
}
// Multipart, not JSON: the file goes up as the operator picked it. dryRun=true answers what WOULD
// happen, row by row; the same call with dryRun=false performs exactly that. skipErrors leaves the
// listed bad rows out — the operator's explicit say-so after seeing them.
export async function uploadAccessCodes(eventId, file, { dryRun = true, skipErrors = false, signal } = {}) {
  const form = new FormData();
  form.append('file', file, file.name || 'codes.csv');
  form.append('dryRun', String(dryRun));
  form.append('skipErrors', String(skipErrors));
  const response = await fetch(`${API_BASE}${withOrgScope(`/api/access-codes/${encodeURIComponent(eventId)}/upload`)}`, {
    method: 'POST', body: form, signal, credentials: 'include', headers: { Accept: 'application/json', 'x-org-scope': 'all' },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok && !payload?.counts) throw new Error(payload?.error || `${response.status} upload failed`);
  return payload;
}
export async function clearAccessCodes(eventId, { signal } = {}) {
  return writeJson(`/api/access-codes/${encodeURIComponent(eventId)}`, { method: 'DELETE', signal });
}
